import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Bot,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Archive,
  Copy,
  Clock3,
  Lock,
  Plus,
  FileText,
  Flag,
  FolderKanban,
  GripVertical,
  KanbanSquare,
  ListChecks,
  ListTodo,
  MessageCircle,
  MoreHorizontal,
  NotebookPen,
  Play,
  Search,
  Settings,
  Target,
  Trash2,
  RotateCcw,
  Users,
  X,
  Circle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../layout/Header';

const PROJETS_ALLOWED_EMAILS = [
  'dany.bourassa@bzinc.ca',
  'eric.hains@bzinc.ca',
  'ehains@bzinc.ca',
  'philippe.bolduc@bzinc.ca',
  'jean-michel.caouette@bzinc.ca',
  'jean-pierre.leclerc@bzinc.ca',
  'francis.marquis@bzinc.ca',
  'pierre-luc.fortin@bzinc.ca',
  'vincent.bourassa@bzinc.ca',
  'stephane.paradis@bzinc.ca',
  'mikael.drouin-bouchard@bzinc.ca',
  'eric.durand@bzinc.ca',
];

type ProjectPrimaryContact = {
  id?: number;
  role_code?: string;
  role_label?: string;
  contact_id?: number | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
};

type ProjectSourceLink = {
  id?: number;
  source_module?: string;
  source_record_id?: string;
  source_title?: string;
  source_url?: string | null;
  source_status?: string | null;
  accepted_at?: string | null;
  contract_end_date?: string | null;
  amount_label?: string | null;
  summary?: string | null;
  metadata?: Record<string, unknown> | null;
};

type ProjectOperationalStatus = 'needs_assignment' | 'needs_framing' | 'taken_in_charge' | 'framing' | 'in_operations' | 'waiting_client' | 'done' | 'archived';

type ProjectRecord = {
  id: number;
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  progress?: number;
  start_date?: string | null;
  end_date?: string | null;
  project_type?: string;
  nom_client?: string;
  idclient?: number | null;
  has_access?: boolean;
  followup_text?: string | null;
  followup_updated_at?: string | null;
  responsible_contact_id?: number | null;
  responsible_contact_name?: string | null;
  responsible_contact_email?: string | null;
  archived?: boolean;
  archived_at?: string | null;
  archived_by_email?: string | null;
  archived_reason?: string | null;
  archived_previous_status?: string | null;
  project_operational_status?: ProjectOperationalStatus | string | null;
  operational_status_updated_at?: string | null;
  operational_status_updated_by_email?: string | null;
  source_module?: string | null;
  source_record_id?: string | null;
  source_summary?: string | null;
  contract_end_date?: string | null;
  primary_contacts?: ProjectPrimaryContact[];
  source_links?: ProjectSourceLink[];
};

type ProjectArchiveFilter = 'active' | 'archived';

type ProjectCopyFormState = {
  title: string;
  status: string;
  keepDates: boolean;
  shiftDays: string;
  copyTasks: boolean;
  copyResponsible: boolean;
  copyDeliverables: boolean;
  copyJournal: boolean;
  copyTimeEntries: boolean;
};

const buildProjectCopyForm = (project: ProjectRecord): ProjectCopyFormState => ({
  title: `Copie de ${project.title}`,
  status: 'Planifié',
  keepDates: true,
  shiftDays: '0',
  copyTasks: true,
  copyResponsible: true,
  copyDeliverables: false,
  copyJournal: false,
  copyTimeEntries: false,
});

type SharedPortfolioRecord = {
  id: number;
  key?: string | null;
  name: string;
  description?: string | null;
  owner_user_id?: number;
  owner_name?: string | null;
  owner_email?: string | null;
  role?: string | null;
  visibility?: string | null;
  members?: ProjectContactOption[];
  projects: ProjectRecord[];
};

type PortfolioKey = string;
type ProjectListView = 'all' | PortfolioKey;

type ProjectListOrders = Partial<Record<ProjectListView, number[]>>;

const UNASSIGNED_PROJECTS_VIEW = '__unassigned__';

const DEFAULT_PROJECT_LIST_VIEW_BY_EMAIL: Partial<Record<string, ProjectListView>> = {
  'eric.hains@bzinc.ca': 'eric',
  'ehains@bzinc.ca': 'eric',
  'philippe.bolduc@bzinc.ca': 'philippe',
  'mikael.drouin-bouchard@bzinc.ca': 'mikael',
};

const getDefaultProjectListView = (email?: string | null): ProjectListView => {
  if (!email) return 'all';
  return DEFAULT_PROJECT_LIST_VIEW_BY_EMAIL[email.toLowerCase()] || 'all';
};

type PortfolioCreateFormState = {
  ownerUserId: string;
  ownerName: string;
  ownerEmail: string;
  stakeholderUsers: ProjectContactOption[];
  name: string;
  description: string;
  visibility: 'public' | 'private';
};

const emptyPortfolioCreateForm = (): PortfolioCreateFormState => ({
  ownerUserId: '',
  ownerName: '',
  ownerEmail: '',
  stakeholderUsers: [],
  name: '',
  description: '',
  visibility: 'private',
});

type ProjectCreateFormState = {
  title: string;
  idclient: string;
  description: string;
  portfolioId: string;
  project_template: string;
  project_type: 'strategique' | 'tactique' | 'operationnel';
  category: string;
  priority: 'Basse' | 'Moyenne' | 'Haute' | 'Critique';
  status: 'Planifié' | 'En cours' | 'À risque' | 'Bloqué' | 'Terminé' | 'En pause' | 'Brouillon';
  start_date: string;
  end_date: string;
  tech: string;
  is_remuneration_variable: boolean;
  ownerContactId: string;
  responsibleContacts: ProjectContactOption[];
  accountableContacts: ProjectContactOption[];
  consultedContacts: ProjectContactOption[];
  informedContacts: ProjectContactOption[];
  createStarterTasks: boolean;
  createStarterMilestones: boolean;
  selectedBlockCodes: string[];
};

const PROJECT_CREATE_CATEGORIES = [
  'Programmation',
  'Administration',
  'RH',
  'Cloud',
  'Service TI',
  "Solution d'Affaire",
  'Cybersécurité',
  'Marketing',
  'Ventes',
  'Télécom',
  'CE',
  'CA',
  'Licences',
  'Infrastructure',
  'Gouvernance',
];

const PROJECT_CREATE_TEMPLATES = [
  { value: 'generic', label: 'Projet générique', category: 'Service TI', project_type: 'operationnel' as const, priority: 'Moyenne' as const },
  { value: 'cyber-loi25', label: 'Cyber / Loi 25', category: 'Cybersécurité', project_type: 'tactique' as const, priority: 'Haute' as const },
  { value: 'usecure-phishing', label: 'uSecure / phishing', category: 'Cybersécurité', project_type: 'operationnel' as const, priority: 'Moyenne' as const },
  { value: 'loi25-phishing', label: 'Loi 25 + hameçonnage', category: 'Cybersécurité', project_type: 'tactique' as const, priority: 'Haute' as const },
  { value: 'prise-en-charge-ti', label: 'Prise en charge TI', category: 'Service TI', project_type: 'tactique' as const, priority: 'Haute' as const },
  { value: 'developpement-prisme', label: 'Développement / PRIsME', category: 'Programmation', project_type: 'tactique' as const, priority: 'Moyenne' as const },
  { value: 'interne-operations', label: 'Interne / opérations', category: 'Administration', project_type: 'operationnel' as const, priority: 'Moyenne' as const },
];

type ProjectTemplateBlockItem = {
  id?: number;
  item_type: 'task' | 'milestone' | 'deliverable' | 'journal';
  title: string;
  description?: string | null;
  priority?: string | null;
  status?: string | null;
  relative_days?: number | null;
  sort_order?: number;
  active?: boolean;
  metadata?: Record<string, unknown>;
};

type ProjectTemplateBlock = {
  id?: number;
  code: string;
  name: string;
  description?: string | null;
  category?: string | null;
  block_type?: string | null;
  active?: boolean;
  sort_order?: number;
  required?: boolean;
  default_selected?: boolean;
  items?: ProjectTemplateBlockItem[];
};

type ProjectTemplateDefinition = {
  id?: number;
  code: string;
  name: string;
  description?: string | null;
  category?: string | null;
  project_type: 'strategique' | 'tactique' | 'operationnel';
  priority: 'Basse' | 'Moyenne' | 'Haute' | 'Critique';
  active?: boolean;
  blocks: ProjectTemplateBlock[];
};

const fallbackProjectTemplates: ProjectTemplateDefinition[] = PROJECT_CREATE_TEMPLATES.map((template) => ({
  code: template.value,
  name: template.label,
  category: template.category,
  project_type: template.project_type,
  priority: template.priority,
  blocks: [],
}));

const emptyProjectCreateForm = (portfolioId?: number | null): ProjectCreateFormState => ({
  title: '',
  idclient: '',
  description: '',
  portfolioId: portfolioId ? String(portfolioId) : '',
  project_template: 'generic',
  project_type: 'operationnel',
  category: 'Service TI',
  priority: 'Moyenne',
  status: 'Planifié',
  start_date: '',
  end_date: '',
  tech: '',
  is_remuneration_variable: false,
  ownerContactId: '',
  responsibleContacts: [],
  accountableContacts: [],
  consultedContacts: [],
  informedContacts: [],
  createStarterTasks: true,
  createStarterMilestones: true,
  selectedBlockCodes: [],
});

type ProjectTaskSummary = {
  id: number;
  titre: string;
  status_text?: string;
  priority_text?: string;
  percent?: number;
  date_echeance?: string | null;
};

type ProjectDecisionSummary = {
  id: number;
  commentaire: string;
  priority?: string;
  type_commentaire?: string;
  date_commentaire?: string | null;
};

type ProjectRiskSummary = {
  id: string;
  title: string;
  level: string;
  source: string;
};

type ProjectActivitySummary = {
  kind: 'task' | 'comment' | 'meeting' | string;
  title: string;
  detail?: string;
  occurred_at?: string | null;
};

type ProjectOverview = {
  project: ProjectRecord;
  health: {
    label: string;
    tone: 'green' | 'amber' | 'red' | string;
    score: number | null;
  };
  dataQuality?: {
    level: 'insufficient' | 'partial' | 'good' | string;
    label: string;
    message: string;
    missingSignals: string[];
  };
  recommendedActions?: Array<{
    id: string;
    title: string;
    description: string;
    target: SectionKey;
    tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' | string;
  }>;
  metrics: {
    totalTasks: number;
    completedTasks: number;
    delayedTasks: number;
    atRiskTasks: number;
    averageProgress: number;
    completionRate: number;
  };
  nextMilestone: ProjectTaskSummary | null;
  criticalTasks: ProjectTaskSummary[];
  openDecisions: ProjectDecisionSummary[];
  topRisks: ProjectRiskSummary[];
  recentActivity: ProjectActivitySummary[];
};

type SectionKey = 'overview' | 'cadrage' | 'tasks' | 'journal' | 'deliverables' | 'agent';

type SectionCard = {
  key: SectionKey;
  title: string;
  description: string;
  icon: typeof FolderKanban;
  state: 'ready' | 'ready_next' | 'planned';
};
type TaskFilter = 'all' | 'critical' | 'late' | 'at_risk' | 'blocked' | 'upcoming' | 'completed' | 'milestones' | 'unscheduled' | 'unassigned';
type ProjectTaskMode = 'list' | 'kanban' | 'gantt' | 'calendar';
type ProjectTaskGanttMode = 'tasks' | 'sections';
type MyTaskQuickFilter = 'active' | 'today' | 'week' | 'late' | 'blocked' | 'unscheduled' | 'completed';
type AgentHomeView = 'my-tasks' | 'portfolio' | 'agent';

type AgentSuggestion = {
  id: number;
  project_id?: number | null;
  portfolio_id?: number | null;
  target_user_id?: number | null;
  rule_code: string;
  type: 'echeancier' | 'hygiene' | 'ressources' | 'sante' | 'communication' | 'requalification' | string;
  severity: 'info' | 'moyenne' | 'elevee' | string;
  title: string;
  body: string;
  confidence: number;
  evidence?: Array<{ kind?: string; id?: number | string; label?: string }>;
  proposed_action?: { action?: string; payload?: Record<string, unknown> } | null;
  score_breakdown?: Record<string, unknown> | null;
  status: string;
  reject_reason?: string | null;
  snoozed_until?: string | null;
  expires_at?: string | null;
  run_id?: number | null;
  decided_at?: string | null;
  last_seen_at?: string | null;
  created_at?: string | null;
  project_title?: string | null;
  client_name?: string | null;
};

type AgentSuggestionsResponse = {
  suggestions: AgentSuggestion[];
  summary: { total: number; elevee: number; moyenne: number; info: number };
};

type AgentWorkItem = {
  id: number;
  source_type: string;
  source_id: string;
  project_id?: number | null;
  client_id?: number | null;
  target_user_id: number;
  role_code: 'pm' | 'cyber' | 'ti' | string;
  role_label?: string | null;
  type: string;
  severity: 'info' | 'moyenne' | 'elevee' | string;
  title: string;
  body: string;
  reason?: string | null;
  due_at?: string | null;
  do_on?: string | null;
  estimated_minutes?: number;
  urgency_score?: number;
  impact_score?: number;
  confidence?: number;
  status: 'proposed' | 'planned' | 'done' | 'dismissed' | 'snoozed' | string;
  evidence?: Array<{ kind?: string; id?: number | string; label?: string }>;
  proposed_action?: { action?: string; payload?: Record<string, unknown> } | null;
  score_breakdown?: Record<string, unknown> | null;
  planned_for?: string | null;
  project_title?: string | null;
  client_name?: string | null;
};

type AgentDailyPlanBlock = {
  startsAt?: string | null;
  durationMinutes?: number;
  workItemId: number;
  title: string;
  reason?: string | null;
  projectId?: number | null;
  projectTitle?: string | null;
  clientId?: number | null;
  clientName?: string | null;
  linkedTaskId?: number | null;
  linkedTaskTitle?: string | null;
  severity?: string;
};

type AgentDailyPlanResponse = {
  role: { code: string; label: string };
  date: string;
  generatedAt: string;
  period?: { start: string; end: string; capacityMinutes: number; planKind: string };
  planId?: number;
  stats?: { collected: number; created: number; updated: number };
  priorities: AgentWorkItem[];
  blocks: AgentDailyPlanBlock[];
  later: AgentWorkItem[];
  summary: { total: number; elevated: number; plannedMinutes: number; capacityMinutes?: number; loadPercent?: number; byType?: Record<string, number> };
};

type AgentWorkItemReasoning = {
  executiveSummary?: string;
  nextBestAction?: string;
  whyNow?: string;
  questions?: string[];
  guardrails?: string[];
};

type AgentControlCenterResponse = {
  metrics?: Record<string, number>;
  byRule?: Array<{ rule: string; status: string; count: number }>;
  byProject?: Array<{ id: number; title: string; count: number; max_urgency?: number }>;
};

type AgentRunResult = {
  run?: { id: number; status: string; started_at?: string; finished_at?: string; stats?: Record<string, number> };
  suggestions?: AgentSuggestion[];
  stats?: { evaluees?: number; creees?: number; resolues?: number; dedupliquees?: number };
};

type AgentRoutineTemplate = {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  rule_codes?: string[];
  default_schedule?: AgentSchedule;
  allowed_channels?: string[];
  min_interval_hours?: number;
};

type AgentSchedule = {
  freq?: 'ouvrables' | 'quotidien' | 'hebdo' | string;
  time?: string;
  days?: string[];
  tz?: string;
};

type UserAgentRoutine = {
  id: number;
  template_id: number;
  template_code?: string;
  template_name?: string;
  template_description?: string | null;
  rule_codes?: string[];
  schedule?: AgentSchedule;
  scope?: { kind?: string; id?: number };
  channel?: 'inapp' | 'inapp_courriel' | 'teams' | string;
  cap?: number;
  enabled?: boolean;
  last_run_at?: string | null;
  next_run_at?: string | null;
};

type AgentMetrics = {
  activeRoutines: number;
  suggestions7d: number;
  acceptanceRate: number;
};

type AgentRunLog = {
  id: number;
  routine_id?: number | null;
  routine_name?: string | null;
  routine_code?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  status: string;
  stats?: Record<string, number>;
  error?: string | null;
};

type ProjectTaskAssignee = {
  id_contact?: number;
  type_responsability?: string;
  description?: string;
  nom_client?: string;
  email_primary?: string;
  avatar?: string;
};

type ProjectTaskItem = {
  id: number;
  id_projet?: number;
  titre: string;
  description?: string | null;
  status_text?: string | null;
  priority_text?: string | null;
  percent?: number | null;
  time_spent_minutes?: number | null;
  safe_percent?: number | null;
  date_debut?: string | null;
  date_echeance?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  task_order?: number | null;
  id_jalon?: number | null;
  isjalon?: boolean;
  is_completed?: boolean;
  is_late?: boolean;
  is_at_risk?: boolean;
  is_blocked?: boolean;
  is_mine?: boolean;
  days_until_due?: number | null;
  days_overdue?: number;
  urgency_level?: 'late' | 'at_risk' | 'unscheduled' | 'completed' | 'upcoming' | 'normal' | string;
  assignees?: ProjectTaskAssignee[];
  comments_count?: number;
};

type ProjectTaskComment = {
  id: number;
  id_tache?: number | null;
  id_projet?: number | null;
  id_contact?: number | null;
  commentaire: string;
  priority?: string | null;
  type_commentaire?: string | null;
  date_commentaire?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  nom_client?: string | null;
  email_primary?: string | null;
};

type ProjectTaskTimeEntry = {
  id: number;
  id_tache?: number | null;
  id_projet?: number | null;
  created_by_email?: string | null;
  created_by_name?: string | null;
  work_date: string;
  work_time: string;
  entry_type: string;
  duration_minutes: number;
  comment?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type ProjectContactOption = {
  id: number;
  nom_client: string;
  email_primary?: string | null;
  type_client?: string | null;
  avatar?: string | null;
};

type ProjectTasksResponse = {
  tasks: ProjectTaskItem[];
  summary: {
    total: number;
    completed: number;
    late: number;
    atRisk: number;
    blocked: number;
    mine: number;
    critical: number;
    milestones: number;
    unscheduled: number;
    unassigned: number;
    averageProgress: number;
  };
  currentUserEmail?: string | null;
  recommendedActions: Array<{
    id: string;
    title: string;
    description: string;
    filter: TaskFilter;
    tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' | string;
  }>;
};

type MyTaskItem = ProjectTaskItem & {
  project_title?: string | null;
  client_name?: string | null;
  my_responsibility?: string | null;
  my_assignment_description?: string | null;
  latest_comment?: string | null;
  latest_comment_priority?: string | null;
  latest_comment_date?: string | null;
  latest_comment_author?: string | null;
};

type MyTasksResponse = {
  tasks: MyTaskItem[];
  summary: {
    total: number;
    active: number;
    completed: number;
    late: number;
    blocked: number;
    dueToday: number;
    dueThisWeek: number;
  };
  currentUserEmail?: string | null;
};

type ProjectMilestoneItem = ProjectTaskItem & {
  updated_at?: string | null;
  linked_tasks_count?: number;
  linked_tasks_progress?: number;
  linked_tasks?: ProjectTaskItem[];
  pilot_signals?: ProjectJournalFeedItem[];
  pilot_signals_count?: number;
  pilot_signals_attention_count?: number;
  pilot_decisions_open?: number;
  pilot_risks_open?: number;
  pilot_comments_count?: number;
};

type ProjectMilestonesResponse = {
  milestones: ProjectMilestoneItem[];
  summary: {
    total: number;
    completed: number;
    late: number;
    atRisk: number;
    unscheduled: number;
    open: number;
    nextMilestone: ProjectMilestoneItem | null;
  };
  recommendedActions: Array<{
    id: string;
    title: string;
    description: string;
    tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' | string;
  }>;
};

type ProjectJournalEntryType = 'note' | 'decision' | 'risk' | 'communication' | string;

type ProjectJournalEntry = {
  id: number;
  id_projet?: number;
  entry_type: ProjectJournalEntryType;
  title: string;
  content?: string | null;
  status?: string | null;
  priority?: string | null;
  owner_contact_id?: number | null;
  owner_name?: string | null;
  owner_email?: string | null;
  target_date?: string | null;
  visibility?: 'internal' | 'client_candidate' | 'client_visible' | string;
  linked_task_id?: number | null;
  linked_task_title?: string | null;
  linked_milestone_id?: number | null;
  linked_milestone_title?: string | null;
  metadata?: Record<string, unknown> | null;
  created_by_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  closed_at?: string | null;
  is_overdue?: boolean;
  needs_attention?: boolean;
};

type ProjectJournalFeedItem = {
  id: string;
  source: 'journal' | 'task_comment' | string;
  source_id?: number;
  entry_type: ProjectJournalEntryType | 'task_comment';
  title: string;
  content?: string | null;
  status?: string | null;
  priority?: string | null;
  target_date?: string | null;
  visibility?: 'internal' | 'client_candidate' | 'client_visible' | string;
  occurred_at?: string | null;
  actor_name?: string | null;
  actor_email?: string | null;
  owner_contact_id?: number | null;
  linked_task_id?: number | null;
  linked_task_title?: string | null;
  linked_milestone_id?: number | null;
  linked_milestone_title?: string | null;
  is_overdue?: boolean;
  needs_attention?: boolean;
};

type ProjectJournalResponse = {
  entries: ProjectJournalEntry[];
  attentionEntries: ProjectJournalEntry[];
  recentEntries: ProjectJournalEntry[];
  feedItems?: ProjectJournalFeedItem[];
  summary: {
    total: number;
    feedItems?: number;
    commentsRecent?: number;
    open: number;
    attention: number;
    overdue: number;
    decisionsOpen: number;
    risksOpen: number;
    communicationsOpen: number;
    notesOpen: number;
  };
  recommendedActions: Array<{
    id: string;
    title: string;
    description: string;
    tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' | string;
  }>;
};

type JournalFormState = {
  open: boolean;
  entryType: 'note' | 'decision' | 'risk' | 'communication';
  title: string;
  content: string;
  priority: 'normal' | 'Haute' | 'Critique';
  targetDate: string;
  visibility: 'internal' | 'client_candidate';
  ownerContactId: string;
  linkedTaskId: string;
  linkedMilestoneId: string;
  saving: boolean;
  message: string | null;
  error: string | null;
};

type DeleteTaskConfirmState = {
  task: ProjectTaskItem;
  saving: boolean;
  error: string | null;
};

type JournalActionState = {
  savingId: string | null;
  message: string | null;
  error: string | null;
};

type JournalEditState = {
  itemId: string | null;
  sourceId: number | null;
  entryType: 'note' | 'decision' | 'risk' | 'communication';
  title: string;
  content: string;
  status: string;
  priority: string;
  targetDate: string;
  visibility: 'internal' | 'client_candidate' | 'client_visible';
  ownerContactId: string;
  linkedTaskId: string;
  linkedMilestoneId: string;
  saving: boolean;
  error: string | null;
};

type DeliverableStatus = 'to_produce' | 'in_progress' | 'in_review' | 'delivered' | 'blocked';
type DeliverableVisibility = 'internal' | 'client_candidate' | 'client_visible';

type ProjectDeliverable = {
  id: number;
  id_projet?: number;
  title: string;
  description?: string | null;
  status: DeliverableStatus | string;
  target_date?: string | null;
  owner_contact_id?: number | null;
  owner_name?: string | null;
  owner_email?: string | null;
  visibility: DeliverableVisibility | string;
  linked_task_id?: number | null;
  linked_task_title?: string | null;
  linked_milestone_id?: number | null;
  linked_milestone_title?: string | null;
  internal_notes?: string | null;
  created_by_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  delivered_at?: string | null;
  is_overdue?: boolean;
  needs_attention?: boolean;
};

type ProjectDeliverablesResponse = {
  deliverables: ProjectDeliverable[];
  summary: {
    total: number;
    open: number;
    delivered: number;
    blocked: number;
    clientVisible: number;
    overdue: number;
  };
  recommendedActions: Array<{
    id: string;
    title: string;
    description: string;
    tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' | string;
  }>;
};

type DeliverableFormState = {
  open: boolean;
  title: string;
  description: string;
  status: DeliverableStatus;
  targetDate: string;
  ownerContactId: string;
  visibility: DeliverableVisibility;
  linkedTaskId: string;
  linkedMilestoneId: string;
  internalNotes: string;
  saving: boolean;
  message: string | null;
  error: string | null;
};

type DeliverableActionState = {
  savingId: number | null;
  message: string | null;
  error: string | null;
};

type ProjectCadrageState = {
  projectType: string;
  requestedBy: string;
  requestDate: string;
  targetDate: string;
  businessNeed: string;
  objective: string;
  finalDeliverable: string;
  successCriteria: string;
  scopeIncluded: string;
  scopeExcluded: string;
  constraints: string;
  assumptions: string;
  stakeholdersInternal: string;
  stakeholdersClient: string;
  approvers: string;
  notes: string;
  raciAssignments: PmbokRaciAssignments;
};

type RaciRole = 'R' | 'A' | 'C' | 'I';

type PmbokRaciAssignments = Record<RaciRole, number[]>;

type RaciContact = {
  id: number;
  client_id?: number | null;
  nom_client?: string;
  type_client?: string | null;
  email_primary?: string | null;
  nom_societe?: string;
  is_bz?: boolean;
  has_avatar?: boolean;
  avatar_url?: string | null;
  avatar?: string | null;
};

type OfficialClientOption = {
  id: number;
  nom_client: string;
  type_client?: string;
  email_primary?: string;
  statut?: boolean;
};

const RACI_ROLES: Array<{
  value: RaciRole;
  title: string;
  subtitle: string;
  badgeClass: string;
  activeClass: string;
  panelClass: string;
}> = [
  { value: 'R', title: 'Responsable', subtitle: 'Exécute le travail', badgeClass: 'bg-blue-600 text-white', activeClass: 'border-blue-500 bg-blue-50', panelClass: 'border-blue-200 bg-blue-50' },
  { value: 'A', title: 'Accountable', subtitle: 'Décide et assume', badgeClass: 'bg-emerald-600 text-white', activeClass: 'border-emerald-500 bg-emerald-50', panelClass: 'border-emerald-200 bg-emerald-50' },
  { value: 'C', title: 'Consulted', subtitle: 'Donne son expertise', badgeClass: 'bg-amber-500 text-white', activeClass: 'border-amber-500 bg-amber-50', panelClass: 'border-amber-200 bg-amber-50' },
  { value: 'I', title: 'Informed', subtitle: 'Doit être informé', badgeClass: 'bg-slate-600 text-white', activeClass: 'border-slate-500 bg-slate-50', panelClass: 'border-slate-200 bg-slate-50' },
];

const emptyRaciAssignments = (): PmbokRaciAssignments => ({ R: [], A: [], C: [], I: [] });

const normalizeRaciAssignments = (value: unknown): PmbokRaciAssignments => {
  const base = emptyRaciAssignments();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return base;
  const source = value as Partial<Record<RaciRole, unknown>>;
  RACI_ROLES.forEach((role) => {
    const rawIds = source[role.value];
    const ids: unknown[] = Array.isArray(rawIds) ? rawIds : [];
    base[role.value] = ids
      .map((id) => Number(id))
      .filter((id, index, list) => Number.isInteger(id) && id > 0 && list.indexOf(id) === index);
    if (role.value === 'A') base[role.value] = base[role.value].slice(0, 1);
  });
  return base;
};

const getContactDisplayName = (contact?: RaciContact | null) => {
  if (!contact) return 'Contact inconnu';
  return contact.nom_client || contact.email_primary || `Contact ${contact.id}`;
};

function PmbokContactAvatar({ contact, sizeClass = 'h-9 w-9' }: { contact?: RaciContact | null; sizeClass?: string }) {
  const [failed, setFailed] = useState(false);
  const avatarUrl = contact?.avatar_url || (contact?.id ? `/api/serq/partie-prenante/${contact.id}/avatar` : null);
  if (avatarUrl && !failed) {
    return (
      <span className={`${sizeClass} inline-block flex-shrink-0 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200`}>
        <img src={avatarUrl} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      </span>
    );
  }
  const name = getContactDisplayName(contact);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '??';
  return (
    <span className={`${sizeClass} inline-flex flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700 ring-1 ring-slate-300`}>
      {contact?.avatar || initials}
    </span>
  );
}

const PREVIEW_PROJECTS: ProjectRecord[] = [
  {
    id: 101,
    title: 'Migration M365 — Client Alpha',
    description: 'Projet de modernisation collaborative avec gouvernance légère, migration par vagues et coordination PM + coordonnateur.',
    status: 'En cours',
    priority: 'Haute',
    progress: 62,
    project_type: 'Transformation TI',
    nom_client: 'Client Alpha',
  },
  {
    id: 102,
    title: 'Sécurisation réseau — Client Boréal',
    description: 'Mandat cybersécurité avec plan d’actions priorisé, dépendances fournisseur et suivi hebdomadaire.',
    status: 'À risque',
    priority: 'Critique',
    progress: 38,
    project_type: 'Cybersécurité',
    nom_client: 'Client Boréal',
  },
  {
    id: 103,
    title: 'Refonte documentaire RH — Client Cime',
    description: 'Structuration de livrables RH, ateliers de validation et visibilité client minimale.',
    status: 'Planifié',
    priority: 'Moyenne',
    progress: 14,
    project_type: 'Organisation',
    nom_client: 'Client Cime',
  },
];

const PREVIEW_SHARED_PORTFOLIOS: SharedPortfolioRecord[] = [
  {
    id: 501,
    name: 'Portefeuille Direction — migrations actives',
    description: 'Vue partagée explicitement avec toi pour suivre les projets prioritaires.',
    owner_name: 'Direction BZ',
    owner_email: 'direction@bzinc.ca',
    role: 'viewer',
    visibility: 'shared',
    projects: [
      { ...PREVIEW_PROJECTS[0], has_access: true },
      { id: 104, title: 'Projet confidentiel — accès requis', nom_client: 'Client Delta', status: 'En cours', priority: 'Haute', progress: 20, has_access: false },
    ],
  },
];

const PREVIEW_OVERVIEWS: Record<number, ProjectOverview> = {
  101: {
    project: PREVIEW_PROJECTS[0],
    health: { label: 'Sous contrôle avec points d’attention', tone: 'amber', score: 76 },
    dataQuality: { level: 'good', label: 'Lecture fiable', message: 'La vue d’ensemble dispose des signaux de base attendus.', missingSignals: [] },
    recommendedActions: [{ id: 'review-risk-signals', title: 'Revoir les signaux de risque', description: '3 tâche(s) sont à risque ou haute priorité.', target: 'journal', tone: 'amber' }],
    metrics: { totalTasks: 28, completedTasks: 17, delayedTasks: 1, atRiskTasks: 3, averageProgress: 62, completionRate: 61 },
    nextMilestone: { id: 9001, titre: 'Migration vague 2 terminée', status_text: 'En cours', priority_text: 'Haute', percent: 45, date_echeance: '2026-05-22' },
    criticalTasks: [
      { id: 8101, titre: 'Valider les boîtes partagées restantes', status_text: 'À risque', priority_text: 'Haute', percent: 40, date_echeance: '2026-05-14' },
      { id: 8102, titre: 'Confirmer la fenêtre de migration client', status_text: 'En cours', priority_text: 'Critique', percent: 70, date_echeance: '2026-05-16' },
    ],
    openDecisions: [
      { id: 7101, commentaire: 'Décider si la vague 3 inclut les archives ou si elles passent en lot séparé.', priority: 'Haute', type_commentaire: 'Décision', date_commentaire: '2026-05-07' },
    ],
    topRisks: [
      { id: 'risk-preview-1', title: 'Retard possible sur la validation client', level: 'Modéré', source: 'preview' },
    ],
    recentActivity: [
      { kind: 'task', title: 'Plan de communication mis à jour', detail: 'Terminé', occurred_at: '2026-05-08' },
      { kind: 'meeting', title: 'Suivi hebdomadaire client', detail: 'Compte rendu à valider', occurred_at: '2026-05-07' },
    ],
  },
  102: {
    project: PREVIEW_PROJECTS[1],
    health: { label: 'À surveiller', tone: 'red', score: 48 },
    dataQuality: { level: 'partial', label: 'Lecture partielle', message: 'Lecture basée sur les données disponibles; à compléter: décisions ouvertes.', missingSignals: ['décisions ouvertes'] },
    recommendedActions: [{ id: 'review-delays', title: 'Traiter les tâches en retard', description: '4 tâche(s) demandent une action rapide.', target: 'tasks', tone: 'red' }, { id: 'capture-decision', title: 'Documenter les décisions clés', description: 'Aucune décision ouverte n’est détectée dans le cockpit.', target: 'journal', tone: 'slate' }],
    metrics: { totalTasks: 19, completedTasks: 7, delayedTasks: 4, atRiskTasks: 6, averageProgress: 38, completionRate: 37 },
    nextMilestone: { id: 9002, titre: 'Plan de remédiation validé', status_text: 'À risque', priority_text: 'Critique', percent: 30, date_echeance: '2026-05-15' },
    criticalTasks: [
      { id: 8201, titre: 'Obtenir approbation pare-feu', status_text: 'Bloqué', priority_text: 'Critique', percent: 20, date_echeance: '2026-05-06' },
      { id: 8202, titre: 'Planifier fenêtre de changement', status_text: 'À risque', priority_text: 'Haute', percent: 35, date_echeance: '2026-05-13' },
    ],
    openDecisions: [],
    topRisks: [
      { id: 'risk-preview-2', title: '4 tâche(s) en retard', level: 'Élevé', source: 'preview' },
      { id: 'risk-preview-3', title: 'Dépendance fournisseur non confirmée', level: 'Modéré', source: 'preview' },
    ],
    recentActivity: [
      { kind: 'comment', title: 'Client demande une nouvelle plage horaire', detail: 'Note', occurred_at: '2026-05-08' },
    ],
  },
  103: {
    project: PREVIEW_PROJECTS[2],
    health: { label: 'Stable', tone: 'green', score: 88 },
    dataQuality: { level: 'good', label: 'Lecture fiable', message: 'La vue d’ensemble dispose des signaux de base attendus.', missingSignals: [] },
    recommendedActions: [{ id: 'define-next-work', title: 'Continuer la structuration', description: 'Le projet est sain; poursuivre la préparation des tâches et livrables.', target: 'tasks', tone: 'green' }],
    metrics: { totalTasks: 9, completedTasks: 1, delayedTasks: 0, atRiskTasks: 0, averageProgress: 14, completionRate: 11 },
    nextMilestone: { id: 9003, titre: 'Atelier de cadrage terminé', status_text: 'Planifié', priority_text: 'Moyenne', percent: 0, date_echeance: '2026-05-24' },
    criticalTasks: [],
    openDecisions: [],
    topRisks: [],
    recentActivity: [{ kind: 'task', title: 'Projet créé et cadrage initial démarré', detail: 'Planifié', occurred_at: '2026-05-08' }],
  },
};

const PREVIEW_TASKS: Record<number, ProjectTasksResponse> = {
  101: {
    tasks: [
      { id: 8101, id_jalon: 8103, titre: 'Valider les boîtes partagées restantes', status_text: 'À risque', priority_text: 'Haute', percent: 40, safe_percent: 40, date_echeance: '2026-05-14', is_late: false, is_at_risk: true, is_completed: false, days_until_due: 6, days_overdue: 0, urgency_level: 'at_risk', assignees: [{ nom_client: 'Coordonnateur BZ', type_responsability: 'R' }], comments_count: 2 },
      { id: 8102, id_jalon: 8103, titre: 'Confirmer la fenêtre de migration client', status_text: 'En cours', priority_text: 'Critique', percent: 70, safe_percent: 70, date_echeance: '2026-05-16', is_late: false, is_at_risk: true, is_completed: false, days_until_due: 8, days_overdue: 0, urgency_level: 'at_risk', assignees: [{ nom_client: 'PM BZ', type_responsability: 'A' }], comments_count: 1 },
      { id: 8103, titre: 'Migration vague 2 terminée', status_text: 'En cours', priority_text: 'Haute', percent: 45, safe_percent: 45, date_echeance: '2026-05-22', isjalon: true, is_late: false, is_at_risk: false, is_completed: false, days_until_due: 14, days_overdue: 0, urgency_level: 'upcoming', assignees: [], comments_count: 0 },
    ],
    summary: { total: 3, completed: 0, late: 0, atRisk: 2, blocked: 0, mine: 0, critical: 1, milestones: 1, unscheduled: 0, unassigned: 1, averageProgress: 52 },
    recommendedActions: [{ id: 'review-risk', title: 'Revoir les tâches à risque', description: '2 tâche(s) demandent une surveillance.', filter: 'at_risk', tone: 'amber' }, { id: 'assign-tasks', title: 'Clarifier les responsables', description: '1 tâche n’a pas d’assignation RACI.', filter: 'unassigned', tone: 'slate' }],
  },
  102: {
    tasks: [
      { id: 8201, titre: 'Obtenir approbation pare-feu', status_text: 'Bloqué', priority_text: 'Critique', percent: 20, safe_percent: 20, date_echeance: '2026-05-06', is_late: true, is_at_risk: true, is_completed: false, days_until_due: -2, days_overdue: 2, urgency_level: 'blocked', is_blocked: true, assignees: [], comments_count: 0 },
      { id: 8202, titre: 'Planifier fenêtre de changement', status_text: 'À risque', priority_text: 'Haute', percent: 35, safe_percent: 35, date_echeance: '2026-05-13', is_late: false, is_at_risk: true, is_completed: false, days_until_due: 5, days_overdue: 0, urgency_level: 'at_risk', assignees: [{ nom_client: 'Technicien réseau', type_responsability: 'R' }], comments_count: 1 },
    ],
    summary: { total: 2, completed: 0, late: 1, atRisk: 2, blocked: 1, mine: 0, critical: 1, milestones: 0, unscheduled: 0, unassigned: 1, averageProgress: 28 },
    recommendedActions: [{ id: 'review-late', title: 'Traiter les retards', description: '1 tâche est en retard.', filter: 'late', tone: 'red' }, { id: 'assign-tasks', title: 'Clarifier les responsables', description: '1 tâche n’a pas d’assignation RACI.', filter: 'unassigned', tone: 'slate' }],
  },
  103: {
    tasks: [],
    summary: { total: 0, completed: 0, late: 0, atRisk: 0, blocked: 0, mine: 0, critical: 0, milestones: 0, unscheduled: 0, unassigned: 0, averageProgress: 0 },
    recommendedActions: [{ id: 'create-first-tasks', title: 'Créer les premières tâches', description: 'Ce projet n’a pas encore de structure opérationnelle.', filter: 'all', tone: 'blue' }],
  },
};

const PREVIEW_MY_TASKS: MyTasksResponse = {
  tasks: [
    {
      ...PREVIEW_TASKS[102].tasks[0],
      project_title: PREVIEW_PROJECTS[1].title,
      client_name: PREVIEW_PROJECTS[1].nom_client,
      my_responsibility: 'R',
      latest_comment: 'Approbation fournisseur requise avant exécution.',
      latest_comment_author: 'PM BZ',
      latest_comment_date: '2026-05-08',
    },
    {
      ...PREVIEW_TASKS[101].tasks[0],
      project_title: PREVIEW_PROJECTS[0].title,
      client_name: PREVIEW_PROJECTS[0].nom_client,
      my_responsibility: 'R',
      latest_comment: 'Deux boîtes restent à confirmer avant fermeture du lot.',
      latest_comment_author: 'Technicien BZ',
      latest_comment_date: '2026-05-09',
    },
    {
      ...PREVIEW_TASKS[101].tasks[1],
      project_title: PREVIEW_PROJECTS[0].title,
      client_name: PREVIEW_PROJECTS[0].nom_client,
      my_responsibility: 'A',
      latest_comment: 'Le client préfère une fenêtre en fin de journée.',
      latest_comment_author: 'Coordonnateur BZ',
      latest_comment_date: '2026-05-10',
    },
  ],
  summary: { total: 3, active: 3, completed: 0, late: 1, blocked: 1, dueToday: 0, dueThisWeek: 2 },
  currentUserEmail: 'preview@bzinc.ca',
};

const buildPreviewMilestones = (projectId: number): ProjectMilestonesResponse => {
  const milestones = (PREVIEW_TASKS[projectId]?.tasks || [])
    .filter((task) => task.isjalon)
    .map((task) => {
      const linkedTasks = (PREVIEW_TASKS[projectId]?.tasks || []).filter((candidate) => candidate.id_jalon === task.id && !candidate.isjalon);
      return {
        ...task,
        linked_tasks: linkedTasks,
        linked_tasks_count: linkedTasks.length,
        linked_tasks_progress: linkedTasks.length > 0 ? Math.round(linkedTasks.reduce((sum, candidate) => sum + getTaskEffectiveProgress(candidate), 0) / linkedTasks.length) : 0,
        updated_at: new Date().toISOString(),
      };
    });
  const openMilestones = milestones.filter((milestone) => !milestone.is_completed);
  const nextMilestone = openMilestones.find((milestone) => milestone.date_echeance) || null;
  return {
    milestones,
    summary: {
      total: milestones.length,
      completed: milestones.filter((milestone) => milestone.is_completed).length,
      late: milestones.filter((milestone) => milestone.is_late).length,
      atRisk: milestones.filter((milestone) => milestone.is_at_risk && !milestone.is_completed).length,
      unscheduled: milestones.filter((milestone) => !milestone.date_echeance && !milestone.is_completed).length,
      open: openMilestones.length,
      nextMilestone,
    },
    recommendedActions: milestones.length === 0
      ? [{ id: 'create-first-milestone', title: 'Créer les premiers jalons', description: 'Aucun jalon ne structure encore l’échéancier.', tone: 'blue' }]
      : [],
  };
};

const buildPreviewJournal = (projectId: number): ProjectJournalResponse => {
  const entries: ProjectJournalEntry[] = projectId === 101 ? [
    { id: 9101, entry_type: 'decision', title: 'Inclure les archives dans la vague 3?', content: 'Arbitrage requis avant de confirmer le prochain lot de migration.', status: 'open', priority: 'Haute', target_date: '2026-05-15', visibility: 'internal', linked_milestone_id: 8103, linked_milestone_title: 'Migration vague 2 terminée', owner_name: 'PM BZ', needs_attention: true, is_overdue: false, updated_at: '2026-05-10' },
    { id: 9102, entry_type: 'risk', title: 'Fenêtre client non confirmée', content: 'Risque de glissement si la fenêtre de migration n’est pas verrouillée.', status: 'open', priority: 'Critique', target_date: '2026-05-14', visibility: 'internal', linked_task_id: 8102, linked_task_title: 'Confirmer la fenêtre de migration client', owner_name: 'Coordonnateur BZ', needs_attention: true, is_overdue: false, updated_at: '2026-05-09' },
    { id: 9103, entry_type: 'note', title: 'Client préfère les communications en fin de journée', content: 'À retenir pour les prochains suivis hebdomadaires.', status: 'open', priority: 'normal', visibility: 'internal', needs_attention: false, is_overdue: false, updated_at: '2026-05-08' },
  ] : projectId === 102 ? [
    { id: 9201, entry_type: 'risk', title: 'Approbation pare-feu bloquée', content: 'Le projet peut glisser si l’approbation fournisseur n’arrive pas.', status: 'open', priority: 'Critique', target_date: '2026-05-06', visibility: 'internal', linked_task_id: 8201, linked_task_title: 'Obtenir approbation pare-feu', owner_name: 'PM BZ', needs_attention: true, is_overdue: true, updated_at: '2026-05-08' },
    { id: 9202, entry_type: 'communication', title: 'Préparer suivi client sur dépendance fournisseur', content: 'Message court à valider avant envoi.', status: 'open', priority: 'Haute', target_date: '2026-05-13', visibility: 'client_candidate', needs_attention: true, is_overdue: false, updated_at: '2026-05-08' },
  ] : [];
  const openEntries = entries.filter((entry) => !['closed', 'resolved', 'decided', 'sent', 'archived'].includes(String(entry.status || '').toLowerCase()));
  const attentionEntries = entries.filter((entry) => entry.needs_attention || entry.is_overdue).slice(0, 5);
  const feedItems: ProjectJournalFeedItem[] = [
    ...entries.map((entry) => ({
      id: `journal-${entry.id}`,
      source: 'journal',
      source_id: entry.id,
      entry_type: entry.entry_type,
      title: entry.title,
      content: entry.content,
      status: entry.status,
      priority: entry.priority,
      visibility: entry.visibility,
      occurred_at: entry.updated_at,
      actor_name: entry.owner_name,
      linked_task_id: entry.linked_task_id,
      linked_task_title: entry.linked_task_title,
      linked_milestone_id: entry.linked_milestone_id,
      linked_milestone_title: entry.linked_milestone_title,
      needs_attention: entry.needs_attention,
      is_overdue: entry.is_overdue,
    })),
    ...(projectId === 101 ? [
      { id: 'comment-preview-1', source: 'task_comment', source_id: 9901, entry_type: 'task_comment', title: 'Commentaire sur tâche', content: 'Le client confirme que la fenêtre de migration de jeudi reste préférable.', priority: 'Normal', status: 'posted', visibility: 'internal', occurred_at: '2026-05-10', actor_name: 'Coordonnateur BZ', linked_task_id: 8102, linked_task_title: 'Confirmer la fenêtre de migration client', linked_milestone_id: 8103, linked_milestone_title: 'Migration vague 2 terminée', needs_attention: false, is_overdue: false },
      { id: 'comment-preview-2', source: 'task_comment', source_id: 9902, entry_type: 'task_comment', title: 'Commentaire sur tâche', content: 'Deux boîtes partagées restent à valider avant fermeture du lot.', priority: 'Haute', status: 'posted', visibility: 'internal', occurred_at: '2026-05-09', actor_name: 'Technicien BZ', linked_task_id: 8101, linked_task_title: 'Valider les boîtes partagées restantes', linked_milestone_id: 8103, linked_milestone_title: 'Migration vague 2 terminée', needs_attention: true, is_overdue: false },
    ] : []),
  ].sort((a, b) => new Date(b.occurred_at || '').getTime() - new Date(a.occurred_at || '').getTime());
  return {
    entries,
    attentionEntries,
    recentEntries: [...entries].sort((a, b) => new Date(b.updated_at || '').getTime() - new Date(a.updated_at || '').getTime()).slice(0, 8),
    feedItems,
    summary: {
      total: entries.length,
      feedItems: feedItems.length,
      commentsRecent: feedItems.filter((item) => item.source === 'task_comment').length,
      open: openEntries.length,
      attention: attentionEntries.length,
      overdue: entries.filter((entry) => entry.is_overdue).length,
      decisionsOpen: openEntries.filter((entry) => entry.entry_type === 'decision').length,
      risksOpen: openEntries.filter((entry) => entry.entry_type === 'risk').length,
      communicationsOpen: openEntries.filter((entry) => entry.entry_type === 'communication').length,
      notesOpen: openEntries.filter((entry) => entry.entry_type === 'note').length,
    },
    recommendedActions: entries.length === 0
      ? [{ id: 'start-journal', title: 'Démarrer Notes & suivis', description: 'Aucune décision, note, risque ou communication n’est encore capturée.', tone: 'blue' }]
      : [],
  };
};

const PREVIEW_DELIVERABLES: Record<number, ProjectDeliverablesResponse> = {
  101: {
    deliverables: [
      { id: 10101, title: 'Plan de migration vague 2', description: 'Document de séquencement et responsabilités pour la prochaine vague.', status: 'in_review', target_date: '2026-05-17', owner_name: 'PM BZ', visibility: 'client_candidate', linked_milestone_id: 8103, linked_milestone_title: 'Migration vague 2 terminée', internal_notes: 'Valider la section risques avant diffusion.', needs_attention: true, is_overdue: false, updated_at: '2026-05-10' },
      { id: 10102, title: 'Compte rendu de suivi client', description: 'Résumé officiel du suivi hebdomadaire.', status: 'delivered', target_date: '2026-05-09', owner_name: 'Coordonnateur BZ', visibility: 'client_visible', delivered_at: '2026-05-09', needs_attention: false, is_overdue: false, updated_at: '2026-05-09' },
      { id: 10103, title: 'Liste des boîtes partagées validées', description: 'Preuve de validation avant bascule.', status: 'blocked', target_date: '2026-05-14', owner_name: 'Technicien BZ', visibility: 'internal', linked_task_id: 8101, linked_task_title: 'Valider les boîtes partagées restantes', internal_notes: 'Deux boîtes restent à confirmer.', needs_attention: true, is_overdue: false, updated_at: '2026-05-10' },
    ],
    summary: { total: 3, open: 2, delivered: 1, blocked: 1, clientVisible: 1, overdue: 0 },
    recommendedActions: [{ id: 'review-blocked-deliverables', title: 'Débloquer les livrables', description: '1 livrable bloque la remise attendue.', tone: 'red' }],
  },
  102: {
    deliverables: [
      { id: 10201, title: 'Plan de remédiation cybersécurité', description: 'Actions priorisées et dépendances fournisseur.', status: 'in_progress', target_date: '2026-05-16', owner_name: 'Consultant GRC', visibility: 'client_candidate', needs_attention: true, is_overdue: false, updated_at: '2026-05-08' },
      { id: 10202, title: 'Preuve approbation changement pare-feu', description: 'Trace d’approbation requise avant exécution.', status: 'blocked', target_date: '2026-05-06', owner_name: 'PM BZ', visibility: 'internal', linked_task_id: 8201, linked_task_title: 'Obtenir approbation pare-feu', needs_attention: true, is_overdue: true, updated_at: '2026-05-08' },
    ],
    summary: { total: 2, open: 2, delivered: 0, blocked: 1, clientVisible: 0, overdue: 1 },
    recommendedActions: [{ id: 'review-overdue-deliverables', title: 'Traiter les livrables en retard', description: '1 livrable a une date cible dépassée.', tone: 'red' }],
  },
  103: {
    deliverables: [],
    summary: { total: 0, open: 0, delivered: 0, blocked: 0, clientVisible: 0, overdue: 0 },
    recommendedActions: [{ id: 'create-first-deliverable', title: 'Créer les premiers livrables', description: 'Aucun output concret n’est encore suivi pour ce projet.', tone: 'blue' }],
  },
};

const SECTION_CARDS: SectionCard[] = [
  {
    key: 'overview',
    title: 'Vue d’ensemble',
    description: 'Statut, prochain jalon, tâches critiques, décisions ouvertes et activité récente.',
    icon: FolderKanban,
    state: 'ready',
  },
  {
    key: 'cadrage',
    title: 'Cadrage PMBOK',
    description: 'Charte légère, portée, exclusions, parties prenantes et approbations.',
    icon: ClipboardCheck,
    state: 'ready',
  },
  {
    key: 'tasks',
    title: 'Tâches',
    description: 'Travail réel du projet : liste, tableau Kanban, calendrier, retards, blocages et actions rapides.',
    icon: ListTodo,
    state: 'ready',
  },
  {
    key: 'journal',
    title: 'Suivis de projet',
    description: 'Notes rapides, décisions, risques, suivis client et commentaires au même endroit — priorisés pour l’action.',
    icon: NotebookPen,
    state: 'ready_next',
  },
  {
    key: 'deliverables',
    title: 'Documents',
    description: 'Livrables, fichiers attendus, états simples et visibilité client minimale.',
    icon: FileText,
    state: 'ready_next',
  },
  {
    key: 'agent',
    title: 'Suggestions',
    description: 'Signaux déterministes en lecture seule, avec décision humaine obligatoire.',
    icon: Bot,
    state: 'ready',
  },
];

function Badge({ children, tone = 'slate' }: { children: ReactNode; tone?: 'slate' | 'green' | 'amber' | 'blue' | 'red' }) {
  const classes = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${classes[tone]}`}>{children}</span>;
}

function getDateOnlyValue(value?: string | null) {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
}

function parseDateOnlyLocal(value?: string | null) {
  const dateOnly = getDateOnlyValue(value);
  if (!dateOnly) return null;
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(value?: string | null) {
  const date = parseDateOnlyLocal(value);
  if (!date || Number.isNaN(date.getTime())) return 'Date à confirmer';
  return new Intl.DateTimeFormat('fr-CA', { dateStyle: 'medium' }).format(date);
}

function formatDateInputValue(value?: string | null) {
  return getDateOnlyValue(value);
}

const PROJECT_OPERATIONAL_STATUS_LABELS: Record<string, { label: string; tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' }> = {
  needs_assignment: { label: 'À attribuer', tone: 'amber' },
  needs_framing: { label: 'À cadrer', tone: 'blue' },
  taken_in_charge: { label: 'Pris en charge', tone: 'blue' },
  framing: { label: 'Cadrage en cours', tone: 'blue' },
  in_operations: { label: 'En opérations', tone: 'green' },
  waiting_client: { label: 'En attente client', tone: 'amber' },
  done: { label: 'Terminé', tone: 'green' },
  archived: { label: 'Archivé', tone: 'slate' },
};

function getProjectOperationalStatus(project?: ProjectRecord | null) {
  const key = project?.project_operational_status || (project?.archived ? 'archived' : 'in_operations');
  return PROJECT_OPERATIONAL_STATUS_LABELS[String(key)] || PROJECT_OPERATIONAL_STATUS_LABELS.in_operations;
}

function getProjectPrimaryContact(project: ProjectRecord) {
  const contacts = Array.isArray(project.primary_contacts) ? project.primary_contacts : [];
  return contacts.find((contact) => contact.role_code === 'client_primary') || contacts[0] || null;
}

function getProjectSourceLink(project: ProjectRecord) {
  const links = Array.isArray(project.source_links) ? project.source_links : [];
  return links[0] || null;
}

function isProjectInIntake(project: ProjectRecord) {
  return ['needs_assignment', 'needs_framing', 'taken_in_charge', 'framing'].includes(String(project.project_operational_status || ''));
}

function cadrageChecklistItems(project: ProjectRecord) {
  const sourceLink = getProjectSourceLink(project);
  const status = String(project.project_operational_status || '');
  const dynamicProject = project as ProjectRecord & Record<string, unknown>;
  const hasResponsible = Boolean(dynamicProject.responsible_contact_id || dynamicProject.responsable || dynamicProject.owner_name || dynamicProject.pm_name);
  const hasInitialDeliverables = Boolean(dynamicProject.deliverables_count || dynamicProject.deliverable_count || dynamicProject.expected_deliverables_count || project.source_summary?.toLowerCase().includes('livrable'));
  const hasNextMilestone = Boolean(dynamicProject.next_milestone_title || dynamicProject.next_milestone_due_date || project.end_date || project.contract_end_date);
  const hasCadrageStarted = ['framing', 'in_operations'].includes(status) || Boolean(dynamicProject.cadrage_updated_at || dynamicProject.pmbok_cadrage_updated_at);
  return [
    { label: 'Client officiel lié', done: Boolean(project.idclient || project.nom_client), target: 'cadrage' as SectionKey },
    { label: 'Contact client principal confirmé', done: Boolean(getProjectPrimaryContact(project)), target: 'cadrage' as SectionKey },
    { label: 'Responsable projet RACI défini', done: hasResponsible, target: 'cadrage' as SectionKey },
    { label: 'Cadrage PMBOK commencé', done: hasCadrageStarted, target: 'cadrage' as SectionKey },
    { label: 'Livrables opérationnels créés ou confirmés', done: hasInitialDeliverables, target: 'deliverables' as SectionKey },
    { label: 'Prochain jalon défini', done: hasNextMilestone || ['framing', 'in_operations'].includes(status), target: 'tasks' as SectionKey },
    { label: 'Origine / source tracée', done: Boolean(project.source_module || sourceLink || project.source_summary), target: 'journal' as SectionKey },
  ];
}

function getCadrageChecklistSummary(project: ProjectRecord) {
  const items = cadrageChecklistItems(project);
  const done = items.filter((item) => item.done).length;
  return { items, done, total: items.length, complete: done === items.length };
}

function toneFromHealth(tone?: string): 'green' | 'amber' | 'red' | 'slate' {
  if (tone === 'green' || tone === 'amber' || tone === 'red') return tone;
  return 'slate';
}

function toneFromValue(tone?: string): 'green' | 'amber' | 'red' | 'blue' | 'slate' {
  if (tone === 'green' || tone === 'amber' || tone === 'red' || tone === 'blue') return tone;
  return 'slate';
}

type ProjectTimeHealth = {
  label: string;
  detail: string;
  tone: 'green' | 'amber' | 'red' | 'blue' | 'slate';
  icon: 'check' | 'calendar' | 'clock' | 'alert' | 'lock';
};

function formatProjectTimeHealthDuration(days: number) {
  const absoluteDays = Math.abs(days);
  if (absoluteDays >= 180) {
    return `${(absoluteDays / 365).toFixed(1).replace('.', ',')} an`;
  }
  if (absoluteDays >= 60) {
    return `${Math.round(absoluteDays / 30)} mois`;
  }
  if (absoluteDays >= 14) {
    return `${Math.round(absoluteDays / 7)} sem.`;
  }
  return `${absoluteDays} j`;
}

function getProjectTimeHealth(project: ProjectRecord, hasAccess = true): ProjectTimeHealth {
  if (!hasAccess) {
    return { label: 'Accès requis', detail: 'Projet non accessible', tone: 'red', icon: 'lock' };
  }

  const progress = typeof project.progress === 'number' ? Math.max(0, Math.min(100, project.progress)) : 0;
  const normalizedStatus = String(project.status || '').toLowerCase();
  const isCompleted = progress >= 100 || /termin|compl[ée]t|completed|done|livr|ferm|closed/.test(normalizedStatus);
  const endDate = parseDateOnlyLocal(project.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!endDate || Number.isNaN(endDate.getTime())) {
    return { label: 'À planifier', detail: 'Date de fin à confirmer', tone: 'slate', icon: 'calendar' };
  }

  const daysLeft = Math.ceil((endDate.getTime() - today.getTime()) / 86_400_000);

  if (isCompleted) {
    if (daysLeft <= 0) {
      return { label: 'Terminé', detail: `Depuis ${formatProjectTimeHealthDuration(daysLeft)}`, tone: 'green', icon: 'check' };
    }
    return { label: 'Terminé', detail: `Fin prévue dans ${formatProjectTimeHealthDuration(daysLeft)}`, tone: 'green', icon: 'check' };
  }

  if (daysLeft < 0) {
    return { label: 'En retard', detail: `${formatProjectTimeHealthDuration(daysLeft)} dépassé`, tone: 'red', icon: 'alert' };
  }

  if (daysLeft <= 84) {
    return { label: 'À surveiller', detail: `Fin dans ${formatProjectTimeHealthDuration(daysLeft)}`, tone: 'amber', icon: 'clock' };
  }

  return { label: 'En bonne voie', detail: `Fin dans ${formatProjectTimeHealthDuration(daysLeft)}`, tone: 'green', icon: 'check' };
}

function getOverviewDataQuality(overview: ProjectOverview) {
  if (overview.dataQuality) return overview.dataQuality;
  const missingSignals = [
    ...(overview.metrics.totalTasks === 0 ? ['aucune tâche'] : []),
    ...(!overview.nextMilestone ? ['aucun jalon ouvert'] : []),
    ...(overview.recentActivity.length === 0 ? ['aucune activité récente'] : []),
  ];
  return {
    level: overview.metrics.totalTasks === 0 ? 'insufficient' : missingSignals.length > 0 ? 'partial' : 'good',
    label: overview.metrics.totalTasks === 0 ? 'Données insuffisantes' : missingSignals.length > 0 ? 'Lecture partielle' : 'Lecture fiable',
    message: overview.metrics.totalTasks === 0
      ? 'Ce projet doit être structuré avant que la santé projet soit fiable.'
      : missingSignals.length > 0
        ? `Lecture basée sur les données disponibles; à compléter: ${missingSignals.join(', ')}.`
        : 'La vue d’ensemble dispose des signaux de base attendus.',
    missingSignals,
  };
}

function getRecommendedActions(overview: ProjectOverview) {
  if (overview.recommendedActions?.length) return overview.recommendedActions;
  return [
    ...(overview.metrics.totalTasks === 0 ? [{ id: 'add-tasks', title: 'Structurer les premières tâches', description: 'Aucune tâche n’est encore disponible pour piloter ce projet.', target: 'tasks' as SectionKey, tone: 'blue' }] : []),
    ...(overview.metrics.delayedTasks > 0 ? [{ id: 'review-delays', title: 'Traiter les tâches en retard', description: `${overview.metrics.delayedTasks} tâche(s) demandent une action rapide.`, target: 'tasks' as SectionKey, tone: 'red' }] : []),
    ...(!overview.nextMilestone ? [{ id: 'define-milestone', title: 'Définir le prochain jalon', description: 'Aucun jalon ouvert ne structure l’échéancier du projet.', target: 'tasks' as SectionKey, tone: 'amber' }] : []),
    ...(overview.metrics.atRiskTasks > 0 ? [{ id: 'review-risk-signals', title: 'Revoir les signaux de risque', description: `${overview.metrics.atRiskTasks} tâche(s) sont à risque ou haute priorité.`, target: 'journal' as SectionKey, tone: 'amber' }] : []),
  ].slice(0, 5);
}

function ProjectOverviewAgentPreview({ projectId, onSelectTasks }: { projectId: number; onSelectTasks: () => void }) {
  const [suggestions, setSuggestions] = useState<AgentSuggestion[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/projet-v2/agent/suggestions?projectId=${projectId}&limit=3`, { credentials: 'include' })
      .then((response) => response.json().then((payload) => ({ ok: response.ok, payload })))
      .then(({ ok, payload }) => {
        if (!cancelled && ok) setSuggestions(payload.suggestions || []);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div className="rounded-xl border border-slate-300 bg-white px-4 py-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">Suggestions</h4>
          <p className="mt-1 text-xs text-slate-500">Extrait lecture seule des signaux agentiques actifs.</p>
        </div>
        <button type="button" onClick={onSelectTasks} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100">
          Voir tout
        </button>
      </div>
      {suggestions.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">Aucune suggestion active pour ce projet.</div>
      ) : (
        <div className="grid gap-2">
          {suggestions.slice(0, 3).map((suggestion) => (
            <div key={suggestion.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={getAgentSeverityTone(suggestion.severity)}>{suggestion.severity}</Badge>
                <Badge tone="slate">{suggestion.type}</Badge>
              </div>
              <p className="mt-1 line-clamp-1 text-sm font-semibold text-slate-900">{suggestion.title}</p>
              <p className="mt-1 line-clamp-2 text-xs text-slate-600">{suggestion.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-3">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="h-48 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ children }: { children: string }) {
  return <div className="rounded-xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500">{children}</div>;
}

const emptyProjectCadrageState = (): ProjectCadrageState => ({
  projectType: '',
  requestedBy: '',
  requestDate: '',
  targetDate: '',
  businessNeed: '',
  objective: '',
  finalDeliverable: '',
  successCriteria: '',
  scopeIncluded: '',
  scopeExcluded: '',
  constraints: '',
  assumptions: '',
  stakeholdersInternal: '',
  stakeholdersClient: '',
  approvers: '',
  notes: '',
  raciAssignments: emptyRaciAssignments(),
});

function CadrageField({ label, value, onChange, placeholder, textarea = false }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string; textarea?: boolean }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          rows={4}
          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
        />
      )}
    </label>
  );
}

function PmbokRaciMatrix({
  contacts,
  assignments,
  onChange,
  loadingContacts,
}: {
  contacts: RaciContact[];
  assignments: PmbokRaciAssignments;
  onChange: (assignments: PmbokRaciAssignments) => void;
  loadingContacts: boolean;
}) {
  const [activeRole, setActiveRole] = useState<RaciRole>('R');
  const [search, setSearch] = useState('');
  const contactsById = useMemo(() => new Map(contacts.map((contact) => [Number(contact.id), contact])), [contacts]);
  const assignedIds = useMemo(() => new Set(Object.values(assignments).flat()), [assignments]);
  const activeRoleInfo = RACI_ROLES.find((role) => role.value === activeRole) || RACI_ROLES[0];
  const accountableCount = assignments.A.length;

  const filteredContacts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return contacts;
    return contacts.filter((contact) => [
      contact.nom_client,
      contact.email_primary,
      contact.type_client,
      contact.nom_societe,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [contacts, search]);

  const toggleContact = (role: RaciRole, contactId: number) => {
    if (role === 'A') {
      onChange({
        ...assignments,
        A: assignments.A.includes(contactId) ? [] : [contactId],
      });
      return;
    }

    onChange({
      ...assignments,
      [role]: assignments[role].includes(contactId)
        ? assignments[role].filter((id) => id !== contactId)
        : [...assignments[role], contactId],
    });
  };

  const removeContact = (role: RaciRole, contactId: number) => {
    onChange({ ...assignments, [role]: assignments[role].filter((id) => id !== contactId) });
  };

  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h5 className="text-sm font-semibold text-slate-950">Matrice RACI PMBOK</h5>
          <p className="mt-1 text-xs text-slate-500">Clique une responsabilité pour choisir les personnes avec leur avatar PRIsME.</p>
        </div>
        <Badge tone={accountableCount === 1 ? 'green' : 'amber'}>1 A maximum</Badge>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {RACI_ROLES.map((role) => {
          const selectedContacts = assignments[role.value].map((id) => contactsById.get(id)).filter(Boolean) as RaciContact[];
          const isActive = activeRole === role.value;
          return (
            <button
              key={role.value}
              type="button"
              onClick={() => setActiveRole(role.value)}
              className={`min-h-[12rem] rounded-xl border-2 bg-white p-3 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md ${isActive ? role.activeClass : 'border-slate-200'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-base font-black ${role.badgeClass}`}>{role.value}</span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-950">{role.title}</div>
                    <div className="truncate text-xs text-slate-500">{role.subtitle}</div>
                  </div>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">{selectedContacts.length}</span>
              </div>

              <div className="mt-3 space-y-2">
                {selectedContacts.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 px-3 py-5 text-center text-xs text-slate-400">Clique pour assigner</div>
                ) : (
                  selectedContacts.slice(0, 4).map((contact) => (
                    <div key={contact.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-2">
                      <PmbokContactAvatar contact={contact} sizeClass="h-8 w-8" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-slate-900">{getContactDisplayName(contact)}</div>
                        <div className="truncate text-[11px] text-slate-500">{contact.nom_societe || contact.type_client || 'Partie prenante'}</div>
                      </div>
                    </div>
                  ))
                )}
                {selectedContacts.length > 4 ? <div className="text-xs font-medium text-slate-500">+{selectedContacts.length - 4} autre(s)</div> : null}
              </div>
            </button>
          );
        })}
      </div>

      <div className={`mt-4 rounded-xl border p-4 ${activeRoleInfo.panelClass}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm font-black ${activeRoleInfo.badgeClass}`}>{activeRoleInfo.value}</span>
            <div>
              <div className="text-sm font-semibold text-slate-950">{activeRoleInfo.title}</div>
              <div className="text-xs text-slate-600">{activeRoleInfo.subtitle}</div>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher un user..."
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        {loadingContacts ? (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {[...Array(6)].map((_, index) => <div key={index} className="h-16 animate-pulse rounded-lg bg-white/70" />)}
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">Aucun utilisateur trouvé.</div>
        ) : (
          <div className="mt-4 grid max-h-80 gap-2 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-3">
            {filteredContacts.map((contact) => {
              const selected = assignments[activeRole].includes(contact.id);
              const selectedElsewhere = !selected && assignedIds.has(contact.id);
              return (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => toggleContact(activeRole, contact.id)}
                  className={`flex min-w-0 items-center gap-3 rounded-lg border bg-white px-3 py-2 text-left transition hover:border-blue-300 hover:shadow-sm ${selected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200'} ${selectedElsewhere ? 'opacity-80' : ''}`}
                >
                  <PmbokContactAvatar contact={contact} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900">{getContactDisplayName(contact)}</div>
                    <div className="truncate text-xs text-slate-500">{contact.nom_societe || contact.type_client || contact.email_primary || 'Partie prenante'}</div>
                  </div>
                  <span className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border text-xs font-bold ${selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 text-slate-300'}`}>
                    {selected ? '✓' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {assignments[activeRole].length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {assignments[activeRole].map((contactId) => {
              const contact = contactsById.get(contactId);
              return (
                <button
                  key={contactId}
                  type="button"
                  onClick={() => removeContact(activeRole, contactId)}
                  className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:border-rose-300 hover:text-rose-700"
                >
                  <PmbokContactAvatar contact={contact} sizeClass="h-6 w-6" />
                  <span className="truncate">{getContactDisplayName(contact)}</span>
                  <span aria-hidden>×</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProjectOfficialClientSelector({
  project,
  clients,
  contacts,
  loadingClients,
  saving,
  search,
  onSearchChange,
  onClientChange,
}: {
  project: ProjectRecord;
  clients: OfficialClientOption[];
  contacts: RaciContact[];
  loadingClients: boolean;
  saving: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onClientChange: (clientId: number | null) => void;
}) {
  const selectedClient = clients.find((client) => Number(client.id) === Number(project.idclient));
  const query = search.trim().toLowerCase();
  const filteredClients = clients
    .filter((client) => !query || [
      client.nom_client,
      client.type_client,
      client.email_primary,
    ].some((value) => String(value || '').toLowerCase().includes(query)))
    .slice(0, 80);
  const clientContacts = contacts.filter((contact) => !contact.is_bz);

  return (
    <section className="rounded-xl border border-slate-300 border-l-4 border-l-indigo-500 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Client officiel PRIsME</h4>
          <p className="text-xs text-slate-500">Lie ce projet à l'instance client de base PRIsME pour filtrer les contacts et les coordonnées.</p>
        </div>
        {selectedClient ? <Badge tone="blue">Lié à {selectedClient.nom_client}</Badge> : <Badge tone="amber">Client à choisir</Badge>}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]">
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Rechercher un client officiel..."
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <select
            value={project.idclient ?? ''}
            disabled={loadingClients || saving}
            onChange={(event) => onClientChange(event.target.value ? Number(event.target.value) : null)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-400"
          >
            <option value="">{loadingClients ? 'Chargement des clients...' : 'Aucun client officiel lié'}</option>
            {filteredClients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.nom_client}{client.type_client ? ` - ${client.type_client}` : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500">
            {saving ? 'Sauvegarde du lien client...' : selectedClient ? `Client ID ${selectedClient.id}` : 'Choisir un client officiel mettra aussi à jour la liste des contacts client du RACI.'}
          </p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Coordonnées client</p>
            <Badge tone="slate">{clientContacts.length}</Badge>
          </div>
          {selectedClient?.email_primary ? <p className="mt-2 truncate text-xs text-slate-600">{selectedClient.email_primary}</p> : null}
          <div className="mt-3 max-h-36 space-y-2 overflow-y-auto pr-1">
            {clientContacts.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-xs text-slate-500">Aucun contact client lié pour l’instant.</p>
            ) : (
              clientContacts.slice(0, 8).map((contact) => (
                <div key={contact.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-2">
                  <PmbokContactAvatar contact={contact} sizeClass="h-8 w-8" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-slate-900">{getContactDisplayName(contact)}</div>
                    <div className="truncate text-[11px] text-slate-500">{contact.email_primary || contact.type_client || 'Coordonnée à compléter'}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProjectCadrageSection({ project, isPreviewMode, onProjectUpdated }: { project: ProjectRecord; isPreviewMode: boolean; onProjectUpdated: (projectId: number, updates: Partial<ProjectRecord>) => Promise<void> }) {
  const projectId = project.id;
  const [form, setForm] = useState<ProjectCadrageState>(emptyProjectCadrageState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [raciContacts, setRaciContacts] = useState<RaciContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [officialClients, setOfficialClients] = useState<OfficialClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSaving, setClientSaving] = useState(false);

  const updateField = (field: keyof ProjectCadrageState, value: string | PmbokRaciAssignments) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    let cancelled = false;

    const loadCadrage = async () => {
      setLoading(true);
      setError(null);

      if (isPreviewMode) {
        if (!cancelled) {
          setForm({
            ...emptyProjectCadrageState(),
            projectType: 'Interne / prévente',
            requestedBy: 'Sponsor interne',
            objective: 'Clarifier le besoin, la portée, les parties prenantes et les livrables avant de lancer le projet.',
            finalDeliverable: 'Cadrage projet validé et prêt à transformer en plan de travail.',
            raciAssignments: { R: [1], A: [2], C: [3], I: [4] },
          });
          setLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(`/api/projet-v2/projects/${projectId}/ui-state/pmbok-cadrage`, { credentials: 'include' });
        if (!response.ok) throw new Error(`Impossible de charger le cadrage (HTTP ${response.status})`);
        const data = await response.json();
        const state = data?.state && typeof data.state === 'object' ? data.state : {};
        if (!cancelled) setForm({ ...emptyProjectCadrageState(), ...state, raciAssignments: normalizeRaciAssignments(state.raciAssignments) });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCadrage();
    return () => {
      cancelled = true;
    };
  }, [isPreviewMode, projectId]);

  useEffect(() => {
    let cancelled = false;

    const loadContacts = async () => {
      if (isPreviewMode) {
        if (!cancelled) {
          setRaciContacts([
            { id: 1, nom_client: 'PM BZ', type_client: 'Chargé de projet', nom_societe: 'BZ inc.', is_bz: true },
            { id: 2, nom_client: 'Sponsor interne', type_client: 'Direction', nom_societe: 'BZ inc.', is_bz: true },
            { id: 3, nom_client: 'Expert cybersécurité', type_client: 'Conseiller', nom_societe: 'BZ inc.', is_bz: true },
            { id: 4, nom_client: 'Contact client', type_client: 'Client', nom_societe: project.nom_client || 'Client' },
          ]);
          setLoadingContacts(false);
        }
        return;
      }

      try {
        setLoadingContacts(true);
        const response = await fetch('/api/parties-prenantes', { credentials: 'include' });
        if (!response.ok) throw new Error(`Contacts indisponibles (HTTP ${response.status})`);
        const data = await response.json();
        const projectClientId = project.idclient == null ? null : Number(project.idclient);
        const contacts = (Array.isArray(data) ? data : [])
          .filter((contact) => {
            const clientId = contact.client_id == null ? null : Number(contact.client_id);
            return contact.is_bz === true || (projectClientId != null && clientId === projectClientId);
          })
          .map((contact) => ({ ...contact, id: Number(contact.id) }))
          .filter((contact) => Number.isInteger(contact.id) && contact.id > 0);
        if (!cancelled) setRaciContacts(contacts);
      } catch (err) {
        console.error('Erreur chargement parties prenantes PMBOK RACI:', err);
        if (!cancelled) setRaciContacts([]);
      } finally {
        if (!cancelled) setLoadingContacts(false);
      }
    };

    loadContacts();
    return () => {
      cancelled = true;
    };
  }, [isPreviewMode, project.idclient, project.nom_client]);

  useEffect(() => {
    let cancelled = false;

    const loadOfficialClients = async () => {
      if (isPreviewMode) {
        if (!cancelled) {
          setOfficialClients([
            { id: 101, nom_client: 'Client Alpha', type_client: 'Manufacturier', statut: true },
            { id: 102, nom_client: project.nom_client || 'Client officiel', type_client: 'Client', statut: true },
          ]);
        }
        return;
      }

      try {
        setLoadingClients(true);
        const response = await fetch('/api/clients', { credentials: 'include' });
        if (!response.ok) throw new Error(`Clients indisponibles (HTTP ${response.status})`);
        const data = await response.json();
        const clients = (Array.isArray(data) ? data : [])
          .map((client) => ({
            ...client,
            id: Number(client.id),
            nom_client: String(client.nom_client || '').trim(),
          }))
          .filter((client) => Number.isInteger(client.id) && client.id > 0 && client.nom_client && client.statut !== false);
        if (!cancelled) setOfficialClients(clients);
      } catch (err) {
        console.error('Erreur chargement clients officiels PRIsME:', err);
        if (!cancelled) setOfficialClients([]);
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    };

    loadOfficialClients();
    return () => {
      cancelled = true;
    };
  }, [isPreviewMode, project.nom_client]);

  const changeOfficialClient = async (clientId: number | null) => {
    setMessage(null);
    setError(null);
    const selectedClient = officialClients.find((client) => Number(client.id) === Number(clientId));

    if (isPreviewMode) {
      setMessage('Preview seulement : le client officiel serait lié sur un vrai projet.');
      return;
    }

    try {
      setClientSaving(true);
      await onProjectUpdated(projectId, {
        idclient: clientId,
        nom_client: selectedClient?.nom_client || undefined,
      });
      setMessage(selectedClient ? `Projet lié au client officiel ${selectedClient.nom_client}.` : 'Lien client officiel retiré.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de lier le client officiel');
    } finally {
      setClientSaving(false);
    }
  };

  const saveCadrage = async () => {
    setMessage(null);
    setError(null);

    if (isPreviewMode) {
      setMessage('Preview seulement : le cadrage serait sauvegardé sur un vrai projet.');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/ui-state/pmbok-cadrage`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: form }),
      });
      if (!response.ok) throw new Error(`Sauvegarde impossible (HTTP ${response.status})`);
      setMessage('Cadrage sauvegardé.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, index) => <div key={index} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}</div>;

  return (
    <div className="space-y-5">
      <ProjectOfficialClientSelector
        project={project}
        clients={officialClients}
        contacts={raciContacts}
        loadingClients={loadingClients}
        saving={clientSaving}
        search={clientSearch}
        onSearchChange={setClientSearch}
        onClientChange={changeOfficialClient}
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="blue">PMBOK léger</Badge>
            <Badge tone="slate">Réutilisable</Badge>
          </div>
          <h3 className="mt-2 text-lg font-semibold text-slate-950">Cadrage projet</h3>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">Un canevas simple pour clarifier la charte, la portée et les parties prenantes sans rendre le projet lourd à remplir.</p>
        </div>
        <button type="button" onClick={saveCadrage} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:bg-slate-300">
          <ClipboardCheck className="h-4 w-4" /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>

      {message ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <section className="rounded-xl border border-slate-300 border-l-4 border-l-blue-500 bg-white p-4 shadow-sm">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-slate-900">1. Charte interne</h4>
          <p className="text-xs text-slate-500">Le minimum utile pour comprendre pourquoi le projet existe et ce qu’il doit produire.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <CadrageField label="Type de projet" value={form.projectType} onChange={(value) => updateField('projectType', value)} placeholder="Interne, client, prévente…" />
          <CadrageField label="Demandé par" value={form.requestedBy} onChange={(value) => updateField('requestedBy', value)} placeholder="Nom du demandeur" />
          <CadrageField label="Date de demande" value={form.requestDate} onChange={(value) => updateField('requestDate', value)} placeholder="AAAA-MM-JJ" />
          <CadrageField label="Date cible de validation du cadrage" value={form.targetDate} onChange={(value) => updateField('targetDate', value)} placeholder="AAAA-MM-JJ" />
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <CadrageField textarea label="Besoin d’affaires" value={form.businessNeed} onChange={(value) => updateField('businessNeed', value)} placeholder="Pourquoi lance-t-on ce projet maintenant?" />
          <CadrageField textarea label="Objectif" value={form.objective} onChange={(value) => updateField('objective', value)} placeholder="Quel résultat cherche-t-on?" />
          <CadrageField textarea label="Livrable final" value={form.finalDeliverable} onChange={(value) => updateField('finalDeliverable', value)} placeholder="Document, offre, décision, plan, rapport…" />
          <CadrageField textarea label="Critères de succès" value={form.successCriteria} onChange={(value) => updateField('successCriteria', value)} placeholder="À quoi voit-on que c’est réussi?" />
        </div>
      </section>

      <section className="rounded-xl border border-slate-300 border-l-4 border-l-amber-500 bg-white p-4 shadow-sm">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-slate-900">2. Portée et limites</h4>
          <p className="text-xs text-slate-500">Ce qui est inclus, ce qui ne l’est pas et les limites à surveiller.</p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <CadrageField textarea label="Inclus" value={form.scopeIncluded} onChange={(value) => updateField('scopeIncluded', value)} placeholder="Ce que le projet doit couvrir." />
          <CadrageField textarea label="Exclusions" value={form.scopeExcluded} onChange={(value) => updateField('scopeExcluded', value)} placeholder="Ce qui est volontairement hors portée." />
          <CadrageField textarea label="Contraintes" value={form.constraints} onChange={(value) => updateField('constraints', value)} placeholder="Temps, budget, ressources, accès, disponibilité…" />
          <CadrageField textarea label="Hypothèses" value={form.assumptions} onChange={(value) => updateField('assumptions', value)} placeholder="Ce qu’on suppose vrai jusqu’à validation." />
        </div>
      </section>

      <section className="rounded-xl border border-slate-300 border-l-4 border-l-emerald-500 bg-white p-4 shadow-sm">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-slate-900">3. Parties prenantes et approbations</h4>
          <p className="text-xs text-slate-500">Qui demande, qui contribue, qui décide, qui doit être consulté.</p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <CadrageField textarea label="Parties prenantes internes" value={form.stakeholdersInternal} onChange={(value) => updateField('stakeholdersInternal', value)} placeholder="Responsable, sponsor, contributeurs, équipe interne…" />
          <CadrageField textarea label="Parties prenantes client" value={form.stakeholdersClient} onChange={(value) => updateField('stakeholdersClient', value)} placeholder="Contacts client connus ou à identifier." />
          <CadrageField textarea label="Approbations requises" value={form.approvers} onChange={(value) => updateField('approvers', value)} placeholder="Qui doit valider avant la prochaine étape?" />
          <CadrageField textarea label="Notes internes" value={form.notes} onChange={(value) => updateField('notes', value)} placeholder="Stratégie, éléments sensibles, points à ne pas oublier." />
        </div>
        <PmbokRaciMatrix
          contacts={raciContacts}
          assignments={form.raciAssignments}
          onChange={(assignments) => updateField('raciAssignments', assignments)}
          loadingContacts={loadingContacts}
        />
      </section>
    </div>
  );
}

function ProjectOverviewSection({ overview, onSelectSection }: { overview: ProjectOverview; onSelectSection: (section: SectionKey) => void }) {
  const healthTone = toneFromHealth(overview.health.tone);
  const dataQuality = getOverviewDataQuality(overview);
  const recommendedActions = getRecommendedActions(overview);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Santé projet</p>
            <Badge tone={healthTone}>{overview.health.label}</Badge>
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">{overview.health.score == null ? 'À structurer' : `${overview.health.score}%`}</p>
          <p className="mt-1 text-xs text-slate-500">{dataQuality.level === 'insufficient' ? 'Score suspendu tant que le projet n’a pas assez de données.' : 'Score calculé depuis les tâches, retards et signaux de risque.'}</p>
        </div>

        <div className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <ListChecks className="h-4 w-4" />
            <p className="text-xs font-semibold uppercase tracking-wide">Tâches</p>
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">
            {overview.metrics.completedTasks}/{overview.metrics.totalTasks}
          </p>
          <p className="mt-1 text-xs text-slate-500">{overview.metrics.completionRate}% complétées</p>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-xs font-semibold uppercase tracking-wide">Points d’attention</p>
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">{overview.metrics.delayedTasks + overview.metrics.atRiskTasks}</p>
          <p className="mt-1 text-xs text-slate-500">{overview.metrics.delayedTasks} en retard · {overview.metrics.atRiskTasks} à risque</p>
        </div>

        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <Target className="h-4 w-4" />
            <p className="text-xs font-semibold uppercase tracking-wide">Prochain jalon</p>
          </div>
          <p className="mt-3 line-clamp-1 text-sm font-semibold text-slate-900">{overview.nextMilestone?.titre || 'Aucun jalon ouvert'}</p>
          <p className="mt-1 text-xs text-slate-500">{formatDate(overview.nextMilestone?.date_echeance)}</p>
        </div>
      </div>

      <div className={`rounded-xl border px-4 py-4 ${dataQuality.level === 'insufficient' ? 'border-slate-300 bg-slate-50' : dataQuality.level === 'partial' ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-950">Qualité de lecture : {dataQuality.label}</h4>
            <p className="mt-1 text-xs text-slate-700">{dataQuality.message}</p>
          </div>
          <Badge tone={dataQuality.level === 'good' ? 'green' : dataQuality.level === 'partial' ? 'amber' : 'slate'}>{dataQuality.missingSignals.length === 0 ? 'Complet' : `${dataQuality.missingSignals.length} signal(aux) manquant(s)`}</Badge>
        </div>
      </div>

      <div className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-blue-950">Actions recommandées</h4>
            <p className="mt-1 text-xs text-blue-800">Le cockpit propose les prochaines actions selon les données disponibles.</p>
          </div>
          <div className="grid w-full gap-2 md:grid-cols-2 xl:w-auto xl:min-w-[520px]">
            {recommendedActions.length === 0 ? (
              <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-800 shadow-sm">Aucune action urgente détectée</div>
            ) : recommendedActions.map((action) => (
              <button key={action.id} type="button" onClick={() => onSelectSection(action.target)} className="rounded-lg border border-blue-300 bg-white px-3 py-2 text-left shadow-sm transition hover:border-blue-500 hover:bg-blue-100">
                <span className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-blue-900">{action.title}</span>
                  <Badge tone={toneFromValue(action.tone)}>{SECTION_CARDS.find((section) => section.key === action.target)?.title || 'Ouvrir'}</Badge>
                </span>
                <span className="mt-1 block text-xs text-slate-600">{action.description}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <ProjectOverviewAgentPreview projectId={overview.project.id} onSelectTasks={() => onSelectSection('tasks')} />

      <div className="grid gap-4 xl:grid-cols-3">
        <section className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Tâches critiques</h4>
              <p className="text-xs text-slate-500">Retards, risques et hautes priorités.</p>
            </div>
            <Badge tone="amber">{overview.criticalTasks.length}</Badge>
          </div>
          {overview.criticalTasks.length === 0 ? (
            <EmptyState>Aucune tâche critique détectée avec les données actuelles.</EmptyState>
          ) : (
            <div className="space-y-3">
              {overview.criticalTasks.map((task) => (
                <div key={task.id} className="rounded-lg border border-amber-200 border-l-4 border-l-amber-500 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-medium text-slate-900">{task.titre}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge tone="slate">{task.status_text || 'Statut n/d'}</Badge>
                    <Badge tone="amber">{task.priority_text || 'Priorité n/d'}</Badge>

                  </div>
                  <p className="mt-2 text-xs text-slate-500">Échéance : {formatDate(task.date_echeance)}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Décisions ouvertes</h4>
              <p className="text-xs text-slate-500">Transition temporaire depuis les commentaires.</p>
            </div>
            <Badge tone="blue">{overview.openDecisions.length}</Badge>
          </div>
          {overview.openDecisions.length === 0 ? (
            <EmptyState>Aucune décision ouverte détectée. Le domaine décision dédié arrive en phase C.</EmptyState>
          ) : (
            <div className="space-y-3">
              {overview.openDecisions.map((decision) => (
                <div key={decision.id} className="rounded-lg border border-blue-200 border-l-4 border-l-blue-500 bg-blue-50 px-3 py-3">
                  <p className="line-clamp-3 text-sm font-medium text-slate-900">{decision.commentaire}</p>
                  <p className="mt-2 text-xs text-slate-500">{decision.type_commentaire || 'Décision'} · {formatDate(decision.date_commentaire)}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Signaux de risque</h4>
              <p className="text-xs text-slate-500">Lecture initiale dérivée des tâches; le registre de risques viendra en phase C.</p>
            </div>
            <Badge tone={overview.topRisks.length > 0 ? 'red' : 'green'}>{overview.topRisks.length}</Badge>
          </div>
          {overview.topRisks.length === 0 ? (
            <EmptyState>Aucun signal de risque détecté depuis les tâches actuelles.</EmptyState>
          ) : (
            <div className="space-y-3">
              {overview.topRisks.map((risk) => (
                <div key={risk.id} className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-3">
                  <p className="text-sm font-medium text-rose-900">{risk.title}</p>
                  <p className="mt-2 text-xs text-rose-700">Niveau : {risk.level}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-slate-500" />
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Activité récente</h4>
            <p className="text-xs text-slate-500">Dernières tâches, notes et réunions liées au projet.</p>
          </div>
        </div>
        {overview.recentActivity.length === 0 ? (
          <EmptyState>Aucune activité récente détectée.</EmptyState>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {overview.recentActivity.map((activity, index) => (
              <div key={`${activity.kind}-${index}`} className="rounded-lg border border-slate-200 border-l-4 border-l-slate-400 bg-slate-50 px-3 py-3">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <CalendarClock className="h-3.5 w-3.5" />
                  <span>{formatDate(activity.occurred_at)}</span>
                </div>
                <p className="mt-2 line-clamp-2 text-sm font-medium text-slate-900">{activity.title}</p>
                <p className="mt-1 text-xs text-slate-500">{activity.detail || activity.kind}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

type TaskActionState = {
  selectedTask: ProjectTaskItem | null;
  panelMode: 'actions' | 'comments' | 'time';
  titleEditing: boolean;
  titleDraft: string;
  statusText: string;
  priorityText: string;
  percent: string;
  timeSpentHours: string;
  startDate: string;
  dueDate: string;
  blockNote: string;
  assigneeId: string;
  reminderDraft: string;
  comments: ProjectTaskComment[];
  newComment: string;
  commentsLoading: boolean;
  timeEntries: ProjectTaskTimeEntry[];
  timeEntriesLoading: boolean;
  timeFormDate: string;
  timeFormTime: string;
  timeFormType: string;
  timeFormDurationHours: string;
  timeFormComment: string;
  editingTimeEntryId: number | null;
  timeEditDate: string;
  timeEditTime: string;
  timeEditType: string;
  timeEditDurationHours: string;
  timeEditComment: string;
  saving: boolean;
  message: string | null;
  error: string | null;
};

type NewTaskState = {
  open: boolean;
  titre: string;
  description: string;
  statusText: string;
  priorityText: string;
  percent: string;
  startDate: string;
  dueDate: string;
  assigneeId: string;
  isJalon: boolean;
  milestoneId: string;
  saving: boolean;
  message: string | null;
  error: string | null;
};

type MilestoneFormState = Omit<NewTaskState, 'isJalon' | 'milestoneId'>;

type MilestoneEditState = MilestoneFormState & {
  selectedMilestone: ProjectMilestoneItem | null;
};

const emptyNewTaskState = (isJalon = false): NewTaskState => ({
  open: false,
  titre: '',
  description: '',
  statusText: 'À faire',
  priorityText: 'Moyenne',
  percent: '0',
  startDate: '',
  dueDate: '',
  assigneeId: '',
  isJalon,
  milestoneId: '',
  saving: false,
  message: null,
  error: null,
});

const sortTasksByManualOrder = (tasks: ProjectTaskItem[]) => [...tasks].sort((a, b) => {
  const orderA = typeof a.task_order === 'number' ? a.task_order : Number.MAX_SAFE_INTEGER;
  const orderB = typeof b.task_order === 'number' ? b.task_order : Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return a.id - b.id;
});

const reorderList = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const orderProjectsByIds = (projects: ProjectRecord[], projectIds: number[] = []) => {
  if (projectIds.length === 0) return projects;
  const orderMap = new Map(projectIds.map((id, index) => [id, index]));
  return [...projects].sort((a, b) => {
    const orderA = orderMap.has(a.id) ? orderMap.get(a.id)! : Number.MAX_SAFE_INTEGER;
    const orderB = orderMap.has(b.id) ? orderMap.get(b.id)! : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return projects.findIndex((project) => project.id === a.id) - projects.findIndex((project) => project.id === b.id);
  });
};

const mergeReorderedVisibleProjects = (orderedProjects: ProjectRecord[], reorderedVisibleProjectIds: number[]) => {
  const visibleIds = new Set(reorderedVisibleProjectIds);
  const reorderedQueue = [...reorderedVisibleProjectIds];
  return orderedProjects.map((project) => visibleIds.has(project.id) ? reorderedQueue.shift()! : project.id);
};

const getPortfolioKey = (portfolio: Pick<SharedPortfolioRecord, 'id' | 'key' | 'name'>): PortfolioKey => {
  if (portfolio.key && portfolio.key !== 'all') return portfolio.key;
  return `portfolio-${portfolio.id}`;
};

const getPortfolioShortLabel = (portfolio: SharedPortfolioRecord) => (
  portfolio.name.replace(/^Portefeuille\s+/i, '').trim() || portfolio.name
);

const buildProjectListViews = (orgPortfolios: SharedPortfolioRecord[]): Array<{ key: ProjectListView; label: string; description: string; icon: typeof FolderKanban }> => [
  { key: 'all', label: 'Tous les projets', description: 'Tous les projets PRIsME visibles par l’équipe', icon: ListChecks },
  { key: UNASSIGNED_PROJECTS_VIEW, label: 'À assigner', description: 'Projets sans responsable RACI à clarifier', icon: ClipboardCheck },
  ...orgPortfolios.map((portfolio) => ({
    key: getPortfolioKey(portfolio),
    label: portfolio.name,
    description: portfolio.description || `Portefeuille de ${portfolio.owner_name || 'responsable à confirmer'}`,
    icon: portfolio.key === 'philippe' ? FolderKanban : Users,
  })),
];

const orderedPayload = (tasks: ProjectTaskItem[], idJalon: number | null) => tasks.map((task, index) => ({
  id: task.id,
  task_order: (index + 1) * 10,
  id_jalon: task.isjalon ? null : idJalon,
}));

const formatTimeSpentInput = (minutes?: number | null) => {
  const safeMinutes = Math.max(0, Number(minutes || 0));
  if (!safeMinutes) return '';
  const hours = safeMinutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(2)));
};

const formatTimeSpentLabel = (minutes?: number | null) => {
  const safeMinutes = Math.max(0, Number(minutes || 0));
  if (!safeMinutes) return '0 h';
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;
  if (!hours) return `${remainingMinutes} min`;
  if (!remainingMinutes) return `${hours} h`;
  return `${hours} h ${remainingMinutes} min`;
};

const parseTimeSpentMinutes = (value: string) => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return 0;
  const hours = Number(normalized);
  if (!Number.isFinite(hours) || hours < 0) return null;
  const minutes = Math.round(hours * 60);
  if (minutes % 15 !== 0) return null;
  return minutes;
};

const getTodayDateInput = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const getCurrentTimeInput = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const normalizeTimeInput = (value?: string | null) => String(value || '').slice(0, 5);

interface ProjetsV2Props {
  previewMode?: boolean;
}

type PortfolioProjectTableProps = {
  projects: ProjectRecord[];
  orgPortfolios: SharedPortfolioRecord[];
  portfolioProjectIds: Record<string, number[]>;
  unassignedProjectIds: Set<number>;
  activeProjectListView: ProjectListView;
  onOpenProject: (projectId: number) => void;
  onAddProject: (projectId: number, portfolioKey: PortfolioKey) => void;
  onRemoveProject: (projectId: number, portfolioKey: PortfolioKey) => void;
  onFollowupSaved: (projectId: number, followup: string, updatedAt?: string | null) => void;
  onProjectUpdated: (projectId: number, updates: Partial<ProjectRecord>) => Promise<void>;
  onReorderProjects: (reorderedVisibleProjectIds: number[]) => Promise<void>;
  onAssignProjectResponsible: (projectId: number, contactId: number) => Promise<void>;
  responsibleContacts: ProjectContactOption[];
  onCopyProject: (project: ProjectRecord) => void;
  onArchiveProject: (project: ProjectRecord) => void;
  onRestoreProject: (project: ProjectRecord) => void;
  isPreviewMode: boolean;
};

const PROJECT_TABLE_GRID_CLASSES = 'xl:grid-cols-[32px_minmax(240px,1.4fr)_minmax(130px,0.75fr)_minmax(150px,0.8fr)_minmax(170px,0.9fr)_170px_minmax(320px,1.5fr)_96px]';
type PortfolioProjectSortKey = 'project' | 'client' | 'health' | 'progress' | 'dates' | 'followup';
type PortfolioProjectSort = { key: PortfolioProjectSortKey; direction: 'asc' | 'desc' } | null;

const getProjectResponsibleContactId = (project?: ProjectRecord | null) => {
  const contactId = Number(project?.responsible_contact_id);
  return Number.isInteger(contactId) && contactId > 0 ? contactId : null;
};

const getProjectHasResponsible = (project?: ProjectRecord | null) => {
  if (!project) return false;
  if (getProjectResponsibleContactId(project)) return true;
  const dynamicProject = project as ProjectRecord & Record<string, unknown>;
  return [
    dynamicProject.responsible_contact_name,
    dynamicProject.responsable,
    dynamicProject.owner_name,
    dynamicProject.pm_name,
  ].some((value) => String(value || '').trim().length > 0);
};

type ProjectRowActionsMenuProps = {
  project: ProjectRecord;
  hasAccess: boolean;
  canAssignProjectResponsible: boolean;
  onAssignProjectResponsible: () => void;
  canRemoveFromPortfolio: boolean;
  onRemoveFromPortfolio: () => void;
  onCopyProject: () => void;
  onArchiveProject: () => void;
  onRestoreProject: () => void;
};

function ProjectRowActionsMenu({ project, hasAccess, canAssignProjectResponsible, onAssignProjectResponsible, canRemoveFromPortfolio, onRemoveFromPortfolio, onCopyProject, onArchiveProject, onRestoreProject }: ProjectRowActionsMenuProps) {
  const itemClass = 'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <details className="relative ml-auto w-fit" onClick={(event) => event.stopPropagation()}>
      <summary
        aria-label={`Actions pour ${project.title}`}
        className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 [&::-webkit-details-marker]:hidden"
      >
        <MoreHorizontal className="h-4 w-4" />
      </summary>
      <div className="absolute right-0 z-30 mt-2 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 text-left shadow-xl ring-1 ring-slate-950/5">
        {canAssignProjectResponsible ? (
          <button type="button" disabled={!hasAccess} onClick={onAssignProjectResponsible} className={`${itemClass} hover:text-blue-700`}>
            <Users className="h-3.5 w-3.5" /> Réassigner
          </button>
        ) : null}
        {canRemoveFromPortfolio ? (
          <button type="button" onClick={onRemoveFromPortfolio} className={`${itemClass} hover:text-rose-700`}>
            <X className="h-3.5 w-3.5" /> Retirer
          </button>
        ) : null}
        {project.archived ? (
          <button type="button" disabled={!hasAccess} onClick={onRestoreProject} className={`${itemClass} hover:text-emerald-700`}>
            <RotateCcw className="h-3.5 w-3.5" /> Restaurer
          </button>
        ) : (
          <>
            <button type="button" disabled={!hasAccess} onClick={onCopyProject} className={`${itemClass} hover:text-blue-700`}>
              <Copy className="h-3.5 w-3.5" /> Copier
            </button>
            <button type="button" disabled={!hasAccess} onClick={onArchiveProject} className={`${itemClass} hover:text-amber-700`}>
              <Archive className="h-3.5 w-3.5" /> Archiver
            </button>
          </>
        )}
      </div>
    </details>
  );
}

function ProjectIntakeTransitionSection({ projects, onOpenProject, onTakeCharge, takingChargeId }: { projects: ProjectRecord[]; onOpenProject: (projectId: number, section?: SectionKey) => void; onTakeCharge: (project: ProjectRecord) => void; takingChargeId: number | null }) {
  const intakeProjects = projects.filter(isProjectInIntake);
  const missingContacts = intakeProjects.filter((project) => !getProjectPrimaryContact(project)).length;
  const fromPrismeSources = intakeProjects.filter((project) => project.source_module || getProjectSourceLink(project)).length;

  return (
    <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="amber">À cadrer / attribuer</Badge>
            <h3 className="text-lg font-black text-slate-950">Projets non prêts pour les opérations</h3>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            File de transition pour les projets provenant d’autres sections de PRIsME — offres acceptées, documents, ODS ou suivis — qui doivent être attribués et cadrés avant la livraison.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-xl border border-amber-200 bg-white px-3 py-2"><p className="font-black text-slate-950">{intakeProjects.length}</p><p className="text-slate-500">à traiter</p></div>
          <div className="rounded-xl border border-blue-200 bg-white px-3 py-2"><p className="font-black text-slate-950">{fromPrismeSources}</p><p className="text-slate-500">avec origine</p></div>
          <div className="rounded-xl border border-rose-200 bg-white px-3 py-2"><p className="font-black text-slate-950">{missingContacts}</p><p className="text-slate-500">sans contact</p></div>
        </div>
      </div>

      {intakeProjects.length ? (
        <div className="mt-4 grid gap-3 xl:grid-cols-2">
          {intakeProjects.slice(0, 8).map((project) => {
            const status = getProjectOperationalStatus(project);
            const contact = getProjectPrimaryContact(project);
            const sourceLink = getProjectSourceLink(project);
            const checklist = getCadrageChecklistSummary(project);
            return (
              <article key={project.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={status.tone}>{status.label}</Badge>
                      {project.source_module || sourceLink?.source_module ? <Badge tone="blue">Source PRIsME</Badge> : <Badge tone="slate">Origine à préciser</Badge>}
                    </div>
                    <h4 className="mt-2 truncate text-sm font-black text-slate-950">{project.title}</h4>
                    <p className="mt-1 text-xs text-slate-500">{project.nom_client || 'Client à confirmer'}</p>
                  </div>
                  <button type="button" onClick={() => onOpenProject(project.id)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-blue-300 hover:bg-blue-50">Ouvrir</button>
                </div>
                <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="font-black text-slate-800">Contacts principaux</p>
                    <p className="mt-1">{contact?.contact_name || 'À confirmer'}</p>
                    {contact?.contact_email ? <p className="truncate text-blue-700">{contact.contact_email}</p> : null}
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="font-black text-slate-800">Origine de l’offre</p>
                    <p className="mt-1 line-clamp-2">{sourceLink?.summary || project.source_summary || 'Source à documenter'}</p>
                    <p className="mt-1 text-slate-500">Date de fin du contrat: {formatDate(sourceLink?.contract_end_date || project.contract_end_date)}</p>
                  </div>
                </div>
                <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50/70 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-black uppercase tracking-wide text-amber-900">Checklist de cadrage</p>
                    <Badge tone={checklist.complete ? 'green' : 'amber'}>{checklist.done}/{checklist.total}</Badge>
                  </div>
                  <div className="mt-2 grid gap-1 text-xs text-slate-700 sm:grid-cols-2">
                    {checklist.items.map((item) => (
                      <button key={item.label} type="button" onClick={() => onOpenProject(project.id, item.target)} className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-left hover:bg-white hover:text-blue-800" title={`Ouvrir ${item.target}`}>
                        {item.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                        {item.label}
                      </button>
                    ))}
                  </div>
                  {!checklist.complete ? <p className="mt-2 text-xs font-bold text-amber-900">À compléter avant opérations — passage en opérations non recommandé.</p> : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => onTakeCharge(project)} disabled={takingChargeId === project.id} className="rounded-lg bg-blue-700 px-3 py-2 text-xs font-black text-white hover:bg-blue-800 disabled:cursor-wait disabled:opacity-60">
                    {takingChargeId === project.id ? 'Prise en charge…' : 'Prendre en charge'}
                  </button>
                  <button type="button" onClick={() => onOpenProject(project.id, 'cadrage')} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-blue-300 hover:bg-blue-50">Cadrer le projet</button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-dashed border-amber-300 bg-white p-4 text-sm text-slate-600">
          Aucun projet en attente d’attribution ou de cadrage. Les nouveaux projets créés depuis une offre acceptée arriveront ici avant d’être envoyés en opérations.
        </div>
      )}
    </section>
  );
}

function PortfolioProjectTable({ projects, orgPortfolios, portfolioProjectIds, unassignedProjectIds, activeProjectListView, onOpenProject, onAddProject, onRemoveProject, onFollowupSaved, onProjectUpdated, onReorderProjects, onAssignProjectResponsible, responsibleContacts, onCopyProject, onArchiveProject, onRestoreProject, isPreviewMode }: PortfolioProjectTableProps) {
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [editingDueProjectId, setEditingDueProjectId] = useState<number | null>(null);
  const [dateDraft, setDateDraft] = useState<{ start_date: string; end_date: string }>({ start_date: '', end_date: '' });
  const [draft, setDraft] = useState('');
  const [savingProjectId, setSavingProjectId] = useState<number | null>(null);
  const [savingDueProjectId, setSavingDueProjectId] = useState<number | null>(null);
  const [savingResponsibleProjectId, setSavingResponsibleProjectId] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [sortConfig, setSortConfig] = useState<PortfolioProjectSort>(null);
  const [error, setError] = useState<string | null>(null);
  const [assignmentProject, setAssignmentProject] = useState<ProjectRecord | null>(null);
  const [assignmentResponsibleSearch, setAssignmentResponsibleSearch] = useState('');
  const [assignmentPortfolioSearch, setAssignmentPortfolioSearch] = useState('');
  const [selectedResponsibleByProject, setSelectedResponsibleByProject] = useState<Record<number, number>>({});
  const followupTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const assignProjectResponsible = async (projectId: number, contactId: number) => {
    if (!contactId || savingResponsibleProjectId === projectId) return;
    try {
      setSavingResponsibleProjectId(projectId);
      setError(null);
      await onAssignProjectResponsible(projectId, contactId);
      setSelectedResponsibleByProject((current) => ({ ...current, [projectId]: contactId }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’assigner le responsable.');
    } finally {
      setSavingResponsibleProjectId(null);
    }
  };

  const openAssignmentModal = (project: ProjectRecord) => {
    setAssignmentProject(project);
    setAssignmentResponsibleSearch('');
    setAssignmentPortfolioSearch('');
    setError(null);
  };

  const currentAssignmentPortfolioKeys = assignmentProject
    ? orgPortfolios
      .map((portfolio) => ({ ...portfolio, resolvedKey: getPortfolioKey(portfolio) }))
      .filter((portfolio) => portfolioProjectIds[portfolio.resolvedKey]?.includes(assignmentProject.id))
      .map((portfolio) => portfolio.resolvedKey)
    : [];
  const selectedResponsibleId = assignmentProject
    ? selectedResponsibleByProject[assignmentProject.id] ?? getProjectResponsibleContactId(assignmentProject)
    : null;
  const selectedResponsibleContact = selectedResponsibleId
    ? responsibleContacts.find((contact) => Number(contact.id) === selectedResponsibleId)
    : null;
  const normalizedResponsibleSearch = assignmentResponsibleSearch.trim().toLowerCase();
  const filteredResponsibleContacts = responsibleContacts.filter((contact) => {
    if (!normalizedResponsibleSearch) return true;
    return `${contact.nom_client || ''} ${contact.email_primary || ''}`.toLowerCase().includes(normalizedResponsibleSearch);
  });
  const normalizedPortfolioSearch = assignmentPortfolioSearch.trim().toLowerCase();
  const assignmentPortfolioOptions = orgPortfolios
    .map((portfolio) => ({ ...portfolio, resolvedKey: getPortfolioKey(portfolio) }))
    .filter((portfolio) => {
      if (!normalizedPortfolioSearch) return true;
      return `${portfolio.name || ''} ${portfolio.owner_name || ''} ${portfolio.description || ''}`.toLowerCase().includes(normalizedPortfolioSearch);
    });

  const sortedProjects = useMemo(() => {
    if (!sortConfig) return projects;
    const textCompare = (a?: string | null, b?: string | null) => String(a || '').localeCompare(String(b || ''), 'fr', { sensitivity: 'base', numeric: true });
    const dateValue = (value?: string | null) => {
      if (!value) return Number.POSITIVE_INFINITY;
      const time = new Date(value).getTime();
      return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
    };
    const healthRank = (project: ProjectRecord) => {
      if (project.has_access === false) return 5;
      const health = getProjectTimeHealth(project, true);
      const ranks: Record<string, number> = { red: 0, amber: 1, slate: 2, blue: 3, green: 4 };
      return ranks[health.tone] ?? 2;
    };
    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    return projects
      .map((project, index) => ({ project, index }))
      .sort((a, b) => {
        let result = 0;
        if (sortConfig.key === 'project') result = textCompare(a.project.title, b.project.title);
        if (sortConfig.key === 'client') result = textCompare(a.project.nom_client, b.project.nom_client);
        if (sortConfig.key === 'health') result = healthRank(a.project) - healthRank(b.project);
        if (sortConfig.key === 'progress') result = (Number(a.project.progress) || 0) - (Number(b.project.progress) || 0);
        if (sortConfig.key === 'dates') {
          result = dateValue(a.project.end_date) - dateValue(b.project.end_date);
          if (result === 0) result = dateValue(a.project.start_date) - dateValue(b.project.start_date);
        }
        if (sortConfig.key === 'followup') {
          result = dateValue(a.project.followup_updated_at) - dateValue(b.project.followup_updated_at);
          if (result === 0) result = textCompare(a.project.followup_text, b.project.followup_text);
        }
        return result === 0 ? a.index - b.index : result * direction;
      })
      .map(({ project }) => project);
  }, [projects, sortConfig]);

  const handleSort = (key: PortfolioProjectSortKey) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const SortHeader = ({ sortKey, align = 'left', children }: { sortKey: PortfolioProjectSortKey; align?: 'left' | 'center'; children: ReactNode }) => {
    const active = sortConfig?.key === sortKey;
    const Icon = active ? (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => handleSort(sortKey)}
        className={`inline-flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold uppercase tracking-wide transition hover:bg-white hover:text-blue-700 ${align === 'center' ? 'justify-center' : 'justify-start'} ${active ? 'text-blue-700' : 'text-slate-500'}`}
        title="Cliquer pour trier"
        aria-label={`Trier par ${String(children)}`}
      >
        <span>{children}</span>
        <Icon className="h-3.5 w-3.5 shrink-0" />
      </button>
    );
  };

  useEffect(() => {
    if (!followupTextareaRef.current) return;
    followupTextareaRef.current.style.height = 'auto';
    followupTextareaRef.current.style.height = `${followupTextareaRef.current.scrollHeight}px`;
  }, [draft, editingProjectId]);

  const startEditingFollowup = (project: ProjectRecord) => {
    setEditingProjectId(project.id);
    setDraft(project.followup_text || '');
    setError(null);
  };

  const saveFollowup = async (project: ProjectRecord) => {
    const nextFollowup = draft.trim();
    if (nextFollowup === (project.followup_text || '').trim()) {
      setEditingProjectId(null);
      return;
    }

    if (isPreviewMode) {
      onFollowupSaved(project.id, nextFollowup, new Date().toISOString());
      setEditingProjectId(null);
      return;
    }

    try {
      setSavingProjectId(project.id);
      setError(null);
      const response = await fetch(`/api/projet-v2/projects/${project.id}/followup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ followup: nextFollowup }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible d’enregistrer le suivi (HTTP ${response.status})`);
      onFollowupSaved(project.id, data.followup?.followup_text ?? nextFollowup, data.followup?.followup_updated_at ?? null);
      setEditingProjectId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur suivi');
    } finally {
      setSavingProjectId(null);
    }
  };

  const saveProjectDateDraft = async (project: ProjectRecord) => {
    const nextStartDate = dateDraft.start_date || null;
    const nextEndDate = dateDraft.end_date || null;
    const currentStartDate = formatDateInputValue(project.start_date);
    const currentEndDate = formatDateInputValue(project.end_date);
    const updates: Partial<ProjectRecord> = {};

    if ((nextStartDate || '') !== currentStartDate) updates.start_date = nextStartDate;
    if ((nextEndDate || '') !== currentEndDate) updates.end_date = nextEndDate;

    if (Object.keys(updates).length === 0) {
      setEditingDueProjectId(null);
      return;
    }

    try {
      setSavingDueProjectId(project.id);
      setError(null);
      await onProjectUpdated(project.id, updates);
      setEditingDueProjectId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de modifier les dates.');
    } finally {
      setSavingDueProjectId(null);
    }
  };

  const startEditingProjectDates = (project: ProjectRecord) => {
    setDateDraft({
      start_date: formatDateInputValue(project.start_date),
      end_date: formatDateInputValue(project.end_date),
    });
    setEditingDueProjectId(project.id);
    setError(null);
  };

  const cancelEditingProjectDates = () => {
    setEditingDueProjectId(null);
    setDateDraft({ start_date: '', end_date: '' });
  };

  const handleFollowupKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>, project: ProjectRecord) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      saveFollowup(project);
    }
    if (event.key === 'Escape') {
      setEditingProjectId(null);
      setDraft(project.followup_text || '');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (sortConfig) return;
    if (!result.destination || result.destination.index === result.source.index) return;
    const reorderedProjects = reorderList(projects, result.source.index, result.destination.index);
    setError(null);
    setIsReordering(true);
    try {
      await onReorderProjects(reorderedProjects.map((project) => project.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de sauvegarder le nouvel ordre des projets.');
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <div className="overflow-x-auto p-3">
      <div className="min-w-[1320px] space-y-2">
        <div className={`hidden gap-3 rounded-xl border border-l-4 border-transparent bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 xl:grid ${PROJECT_TABLE_GRID_CLASSES}`}>
          <span aria-hidden="true" />
          <SortHeader sortKey="project">Projet</SortHeader>
          <SortHeader sortKey="client">Client</SortHeader>
          <SortHeader sortKey="health" align="center">Santé</SortHeader>
          <SortHeader sortKey="progress" align="center">Progression</SortHeader>
          <SortHeader sortKey="dates">Dates</SortHeader>
          <SortHeader sortKey="followup">Suivi</SortHeader>
          <span className="text-right">Actions</span>
        </div>
        {sortConfig ? <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">Tri actif : le glisser-déposer est désactivé. Clique trois fois sur l’en-tête actif pour revenir à l’ordre manuel.</div> : null}
        {isReordering ? <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">Enregistrement du nouvel ordre...</div> : null}
        {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`portfolio-projects-${activeProjectListView}`}>
            {(dropProvided, dropSnapshot) => (
              <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} className={`space-y-2 rounded-xl transition ${dropSnapshot.isDraggingOver ? 'bg-blue-50/60' : ''}`}>
                {sortedProjects.map((project, index) => {
                const progress = typeof project.progress === 'number' ? Math.max(0, Math.min(100, project.progress)) : 0;
                const hasAccess = project.has_access !== false;
                const isProjectAssigned = getProjectHasResponsible(project)
                  || Boolean(selectedResponsibleByProject[project.id])
                  || (activeProjectListView !== UNASSIGNED_PROJECTS_VIEW && !unassignedProjectIds.has(project.id));
                const activePortfolioKey = activeProjectListView === 'all' ? null : activeProjectListView;
                const isInActivePortfolio = activePortfolioKey ? Boolean(portfolioProjectIds[activePortfolioKey]?.includes(project.id)) : false;
                const timeHealth = getProjectTimeHealth(project, hasAccess);
                const TimeHealthIcon = timeHealth.icon === 'check'
                  ? CheckCircle2
                  : timeHealth.icon === 'alert'
                    ? AlertTriangle
                    : timeHealth.icon === 'lock'
                      ? Lock
                      : timeHealth.icon === 'clock'
                        ? Clock3
                        : CalendarClock;
                return (
                  <Draggable key={project.id} draggableId={`project-${project.id}`} index={index} isDragDisabled={isReordering || Boolean(sortConfig)}>
                    {(dragProvided, snapshot) => (
                      <article
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={dragProvided.draggableProps.style}
                        className={`group cursor-grab rounded-xl border border-l-4 px-4 py-3 shadow-sm transition active:cursor-grabbing ${snapshot.isDragging ? 'rotate-[0.2deg] border-blue-400 border-l-blue-700 bg-blue-50 shadow-xl ring-2 ring-blue-200' : hasAccess ? 'border-slate-300 border-l-slate-400 bg-white hover:border-blue-400 hover:border-l-blue-600 hover:bg-blue-50/70' : 'border-slate-300 border-l-slate-300 bg-slate-100 opacity-75'}`}
                      >
                        <div className={`grid gap-3 xl:items-center ${PROJECT_TABLE_GRID_CLASSES}`}>
                          <span
                            aria-hidden="true"
                            title="Glisser pour réorganiser"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition group-hover:bg-slate-100 group-hover:text-slate-700"
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>

                          <button type="button" disabled={!hasAccess} onClick={() => onOpenProject(project.id)} className="group min-w-0 text-left disabled:cursor-not-allowed">
                            <span className="flex min-w-0 items-center gap-2 font-semibold text-slate-950 group-hover:text-blue-700">
                              <span className="truncate">{project.title}</span>
                              {hasAccess ? <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-blue-600" /> : <Lock className="h-4 w-4 shrink-0 text-slate-400" />}
                            </span>
                          </button>

                          <div className="min-w-0 text-sm text-slate-600">
                            <span className="xl:hidden text-xs font-semibold text-slate-400">Client · </span>
                            <span className="truncate">{project.nom_client || 'Client à confirmer'}</span>
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${timeHealth.tone === 'green' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : timeHealth.tone === 'red' ? 'border-rose-200 bg-rose-50 text-rose-700' : timeHealth.tone === 'blue' ? 'border-blue-200 bg-blue-50 text-blue-700' : timeHealth.tone === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                                <TimeHealthIcon className="h-4 w-4" />
                              </span>
                              <span className="truncate text-sm font-semibold text-slate-800">{timeHealth.label}</span>
                            </div>
                            <p className="mt-1 truncate text-xs text-slate-500">{timeHealth.detail}</p>
                          </div>

                          <div className="flex min-w-0 items-center gap-2">
                            <div className="h-2 min-w-0 flex-1 rounded-full bg-slate-100">
                              <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="w-10 shrink-0 text-right text-xs font-semibold text-slate-600">{typeof project.progress === 'number' ? `${project.progress}%` : 'n/d'}</span>
                          </div>

                          <div className="text-sm text-slate-600">
                            <span className="xl:hidden text-xs font-semibold text-slate-400">Dates · </span>
                            {editingDueProjectId === project.id ? (
                              <div
                                className="grid gap-1"
                                onClick={(event) => event.stopPropagation()}
                                onPointerDown={(event) => event.stopPropagation()}
                              >
                                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  Début
                                  <input
                                    type="date"
                                    autoFocus
                                    value={dateDraft.start_date}
                                    disabled={savingDueProjectId === project.id}
                                    onChange={(event) => setDateDraft((current) => ({ ...current, start_date: event.target.value }))}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Escape') cancelEditingProjectDates();
                                      if (event.key === 'Enter') void saveProjectDateDraft(project);
                                    }}
                                    className="w-full rounded-lg border border-blue-300 bg-white px-2.5 py-1.5 text-sm font-normal text-slate-800 outline-none ring-2 ring-blue-100 disabled:opacity-60"
                                  />
                                </label>
                                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  Fin
                                  <input
                                    type="date"
                                    value={dateDraft.end_date}
                                    disabled={savingDueProjectId === project.id}
                                    onChange={(event) => setDateDraft((current) => ({ ...current, end_date: event.target.value }))}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Escape') cancelEditingProjectDates();
                                      if (event.key === 'Enter') void saveProjectDateDraft(project);
                                    }}
                                    className="w-full rounded-lg border border-blue-300 bg-white px-2.5 py-1.5 text-sm font-normal text-slate-800 outline-none ring-2 ring-blue-100 disabled:opacity-60"
                                  />
                                </label>
                                <div className="flex gap-1">
                                  <button type="button" disabled={savingDueProjectId === project.id} onClick={() => void saveProjectDateDraft(project)} className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-60">OK</button>
                                  <button type="button" disabled={savingDueProjectId === project.id} onClick={cancelEditingProjectDates} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Annuler</button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={!hasAccess || savingDueProjectId === project.id}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (hasAccess) startEditingProjectDates(project);
                                }}
                                title="Cliquer pour modifier les dates"
                                className="rounded-lg border border-transparent px-2 py-1 text-left transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900 disabled:cursor-not-allowed disabled:text-slate-400"
                              >
                                {savingDueProjectId === project.id ? 'Enregistrement...' : (
                                  <span className="grid gap-0.5">
                                    <span>Début: {formatDate(project.start_date)}</span>
                                    <span>Fin: {formatDate(project.end_date)}</span>
                                  </span>
                                )}
                              </button>
                            )}
                          </div>

                          <div className="min-w-0">
                            {editingProjectId === project.id ? (
                              <textarea
                                ref={followupTextareaRef}
                                autoFocus
                                value={draft}
                                maxLength={240}
                                disabled={savingProjectId === project.id}
                                onChange={(event) => setDraft(event.target.value)}
                                onBlur={() => saveFollowup(project)}
                                onKeyDown={(event) => handleFollowupKeyDown(event, project)}
                                onClick={(event) => event.stopPropagation()}
                                rows={1}
                                placeholder="Écrire un court suivi…"
                                className="max-h-40 w-full resize-none overflow-y-auto rounded-lg border border-blue-300 bg-white px-2.5 py-1.5 text-sm leading-5 text-slate-800 outline-none ring-2 ring-blue-100"
                              />
                            ) : (
                              <button
                                type="button"
                                onDoubleClick={() => hasAccess && startEditingFollowup(project)}
                                onClick={(event) => event.stopPropagation()}
                                disabled={!hasAccess}
                                title="Double-clique pour modifier le suivi"
                                className="block w-full truncate rounded-lg border border-transparent px-2 py-1 text-left text-sm text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900 disabled:cursor-not-allowed disabled:text-slate-400"
                              >
                                {project.followup_text || 'Double-clique pour ajouter un suivi'}
                              </button>
                            )}
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            {hasAccess && !isProjectAssigned ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openAssignmentModal(project);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-900 shadow-sm outline-none hover:border-blue-500 hover:bg-blue-100"
                              >
                                <Users className="h-3.5 w-3.5" /> Assigner
                              </button>
                            ) : null}
                            <ProjectRowActionsMenu
                              project={project}
                              hasAccess={hasAccess}
                              canAssignProjectResponsible={hasAccess && isProjectAssigned}
                              onAssignProjectResponsible={() => openAssignmentModal(project)}
                              canRemoveFromPortfolio={Boolean(activePortfolioKey && isInActivePortfolio)}
                              onRemoveFromPortfolio={() => activePortfolioKey && onRemoveProject(project.id, activePortfolioKey)}
                              onCopyProject={() => onCopyProject(project)}
                              onArchiveProject={() => onArchiveProject(project)}
                              onRestoreProject={() => onRestoreProject(project)}
                            />
                          </div>
                        </div>
                      </article>
                    )}
                  </Draggable>
                );
                })}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {assignmentProject ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" onClick={() => setAssignmentProject(null)}>
          <section className="max-h-[88vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={activeProjectListView === UNASSIGNED_PROJECTS_VIEW ? 'amber' : 'blue'}>{activeProjectListView === UNASSIGNED_PROJECTS_VIEW ? 'À assigner' : 'Classement'}</Badge>
                  <Badge tone="slate">{assignmentProject.nom_client || 'Client à confirmer'}</Badge>
                </div>
                <h3 className="mt-2 truncate text-lg font-semibold text-slate-950">{assignmentProject.title}</h3>
                <p className="mt-1 text-sm text-slate-500">Assigne un responsable et ajoute le projet aux portefeuilles pertinents.</p>
              </div>
              <button type="button" onClick={() => setAssignmentProject(null)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                Fermer
              </button>
            </div>

            <div className="grid max-h-[70vh] gap-0 overflow-y-auto lg:grid-cols-2">
              <div className="border-b border-slate-200 p-5 lg:border-b-0 lg:border-r">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-950">Responsable projet</h4>
                    <p className="mt-1 text-xs text-slate-500">Un responsable RACI initial par projet.</p>
                  </div>
                  <Badge tone={selectedResponsibleId ? 'green' : 'slate'}>{selectedResponsibleId ? 'Sélectionné' : 'À choisir'}</Badge>
                </div>
                <div className="relative mt-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input value={assignmentResponsibleSearch} onChange={(event) => setAssignmentResponsibleSearch(event.target.value)} placeholder="Chercher un responsable..." className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                </div>
                <div className="mt-3 grid max-h-[46vh] gap-2 overflow-y-auto pr-1">
                  {filteredResponsibleContacts.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">Aucun responsable trouvé.</div> : null}
                  {selectedResponsibleId ? (
                    <p className="text-xs font-semibold text-emerald-700">
                      Sélection actuelle: {selectedResponsibleContact?.nom_client || assignmentProject.responsible_contact_name || `contact #${selectedResponsibleId}`}
                    </p>
                  ) : null}
                  {filteredResponsibleContacts.map((contact) => {
                    const selected = selectedResponsibleId === Number(contact.id);
                    return (
                      <button
                        key={contact.id}
                        type="button"
                        disabled={savingResponsibleProjectId === assignmentProject.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          void assignProjectResponsible(assignmentProject.id, Number(contact.id));
                        }}
                        className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition disabled:cursor-wait disabled:opacity-60 ${selected ? 'border-emerald-300 bg-emerald-50 ring-2 ring-emerald-100' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50'}`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-slate-900">{contact.nom_client}</span>
                          {contact.email_primary ? <span className="block truncate text-xs text-slate-500">{contact.email_primary}</span> : null}
                        </span>
                        {selected ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" /> : <Plus className="h-4 w-4 shrink-0 text-slate-400" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-950">Portefeuilles</h4>
                    <p className="mt-1 text-xs text-slate-500">Ajoute le projet aux vues de pilotage nécessaires.</p>
                  </div>
                  <Badge tone="blue">{currentAssignmentPortfolioKeys.length} sélectionné(s)</Badge>
                </div>
                <div className="relative mt-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input value={assignmentPortfolioSearch} onChange={(event) => setAssignmentPortfolioSearch(event.target.value)} placeholder="Chercher un portefeuille..." className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                </div>
                <div className="mt-3 grid max-h-[46vh] gap-2 overflow-y-auto pr-1">
                  {assignmentPortfolioOptions.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">Aucun portefeuille trouvé.</div> : null}
                  {assignmentPortfolioOptions.map((portfolio) => {
                    const selected = currentAssignmentPortfolioKeys.includes(portfolio.resolvedKey);
                    return (
                      <button
                        key={portfolio.resolvedKey}
                        type="button"
                        disabled={selected}
                        onClick={(event) => {
                          event.stopPropagation();
                          onAddProject(assignmentProject.id, portfolio.resolvedKey);
                        }}
                        className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition disabled:cursor-default ${selected ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50'}`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-slate-900">{portfolio.name}</span>
                          <span className="block truncate text-xs text-slate-500">{portfolio.owner_name || 'Responsable à confirmer'}</span>
                        </span>
                        {selected ? <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-600" /> : <Plus className="h-4 w-4 shrink-0 text-slate-400" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

const TASK_FILTERS: Array<{ key: TaskFilter; label: string }> = [
  { key: 'all', label: 'Toutes' },
  { key: 'critical', label: 'Critiques' },
  { key: 'late', label: 'En retard' },
  { key: 'at_risk', label: 'À risque' },
  { key: 'blocked', label: 'Bloquées' },
  { key: 'upcoming', label: 'À venir' },
  { key: 'completed', label: 'Terminées' },
  { key: 'milestones', label: 'Jalons' },
  { key: 'unscheduled', label: 'Sans échéance' },
  { key: 'unassigned', label: 'Sans responsable' },
];

const PROJECT_TASK_MODES: Array<{ key: ProjectTaskMode; label: string; icon: typeof ListTodo }> = [
  { key: 'list', label: 'Liste', icon: ListTodo },
  { key: 'kanban', label: 'Kanban', icon: KanbanSquare },
  { key: 'gantt', label: 'Gantt', icon: Activity },
  { key: 'calendar', label: 'Calendrier', icon: CalendarClock },
];


function ProjectCopyModal({ project, form, saving, error, onChange, onClose, onSubmit }: {
  project: ProjectRecord;
  form: ProjectCopyFormState;
  saving: boolean;
  error: string | null;
  onChange: (updates: Partial<ProjectCopyFormState>) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const toggle = (key: keyof ProjectCopyFormState) => onChange({ [key]: !form[key] } as Partial<ProjectCopyFormState>);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 px-4">
      <div role="dialog" aria-modal="true" aria-label="Copier le projet" className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-slate-950">Copier le projet</h3>
            <p className="mt-1 text-sm text-slate-500">Créer un nouveau projet à partir de « {project.title} ».</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60" aria-label="Fermer"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid gap-4 overflow-y-auto px-5 py-5">
          <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Nom du nouveau projet
            <input autoFocus value={form.title} disabled={saving} onChange={(event) => onChange({ title: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Statut initial
              <select value={form.status} disabled={saving} onChange={(event) => onChange({ status: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                <option>Planifié</option><option>En cours</option><option>Brouillon</option>
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Décaler les dates (jours)
              <input
                type="number"
                value={form.shiftDays}
                disabled={saving || !form.keepDates}
                onChange={(event) => onChange({ shiftDays: event.target.value })}
                aria-describedby="copy-project-shift-days-help"
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              />
              <span id="copy-project-shift-days-help" className="text-xs font-normal text-slate-500">Nombre de jours à ajouter ou retirer. Ex. 7 = +1 semaine, -7 = reculer d’une semaine.</span>
            </label>
          </div>
          <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            {[
              ['keepDates', 'Garder les échéances'],
              ['copyTasks', 'Copier les tâches / interventions'],
              ['copyResponsible', 'Copier les responsables'],
              ['copyDeliverables', 'Copier les documents liés'],
              ['copyJournal', 'Copier les notes internes'],
              ['copyTimeEntries', 'Copier le temps réel saisi'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <input type="checkbox" checked={Boolean(form[key as keyof ProjectCopyFormState])} disabled={saving} onChange={() => toggle(key as keyof ProjectCopyFormState)} className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                {label}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500">Par défaut, PRIsME copie la structure utile sans copier l’historique réel, les temps et les livrables remis.</p>
          {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Annuler</button>
          <button type="button" onClick={onSubmit} disabled={saving} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60">{saving ? 'Copie...' : 'Créer la copie'}</button>
        </div>
      </div>
    </div>
  );
}

function ProjectArchiveModal({ project, reason, saving, error, onReasonChange, onClose, onSubmit }: {
  project: ProjectRecord;
  reason: string;
  saving: boolean;
  error: string | null;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 px-4">
      <div role="dialog" aria-modal="true" aria-label="Archiver le projet" className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div><h3 className="text-base font-semibold text-slate-950">Archiver le projet</h3><p className="mt-1 text-sm text-slate-500">Le projet restera consultable dans l’onglet Archivés et pourra être restauré.</p></div>
          <button type="button" onClick={onClose} disabled={saving} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60" aria-label="Fermer"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid gap-4 px-5 py-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">{project.title}</div>
          <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Raison d’archivage optionnelle
            <textarea value={reason} disabled={saving} onChange={(event) => onReasonChange(event.target.value)} rows={3} className="resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
          </label>
          {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Annuler</button>
          <button type="button" onClick={onSubmit} disabled={saving} className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60">{saving ? 'Archivage...' : 'Archiver'}</button>
        </div>
      </div>
    </div>
  );
}

function ProjectCreateWizardModal({
  form,
  saving,
  error,
  clients,
  clientsLoading,
  contacts,
  contactsLoading,
  contactSearch,
  responsibleOptions,
  responsibleOptionsLoading,
  portfolios,
  templates,
  allBlocks,
  templatesLoading,
  onOpenBlockConfig,
  onContactSearchChange,
  onChange,
  onClose,
  onSubmit,
}: {
  form: ProjectCreateFormState;
  saving: boolean;
  error: string | null;
  clients: OfficialClientOption[];
  clientsLoading: boolean;
  contacts: ProjectContactOption[];
  contactsLoading: boolean;
  contactSearch: string;
  responsibleOptions: ProjectContactOption[];
  responsibleOptionsLoading: boolean;
  portfolios: SharedPortfolioRecord[];
  templates: ProjectTemplateDefinition[];
  allBlocks: ProjectTemplateBlock[];
  templatesLoading: boolean;
  onOpenBlockConfig: () => void;
  onContactSearchChange: (value: string) => void;
  onChange: (updates: Partial<ProjectCreateFormState>) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const [step, setStep] = useState(0);
  const selectedIds = new Set([
    form.ownerContactId,
    ...form.responsibleContacts.map((contact) => String(contact.id)),
    ...form.accountableContacts.map((contact) => String(contact.id)),
    ...form.consultedContacts.map((contact) => String(contact.id)),
    ...form.informedContacts.map((contact) => String(contact.id)),
  ].filter(Boolean));
  const steps = ['Créer', 'Modèle', 'Lancement'];
  const canSubmit = form.title.trim().length > 0 && !saving;
  const selectedPortfolio = portfolios.find((portfolio) => String(portfolio.id) === form.portfolioId) || null;
  const selectedClient = clients.find((client) => String(client.id) === form.idclient) || null;
  const ownerContact = responsibleOptions.find((contact) => String(contact.id) === form.ownerContactId)
    || contacts.find((contact) => String(contact.id) === form.ownerContactId)
    || null;
  const templateOptions = templates.length > 0 ? templates : fallbackProjectTemplates;
  const selectedTemplate = templateOptions.find((template) => template.code === form.project_template) || templateOptions[0];
  const blocksByCode = new Map(allBlocks.map((block) => [block.code, block]));
  const includedTemplateBlocks = selectedTemplate?.blocks?.length
    ? selectedTemplate.blocks.map((block) => ({ ...(blocksByCode.get(block.code) || {}), ...block, items: blocksByCode.get(block.code)?.items || block.items || [] }))
    : allBlocks;
  const templateBlockCodes = new Set(includedTemplateBlocks.map((block) => block.code));
  const requiredBlockCodes = new Set(includedTemplateBlocks.filter((block) => block.required).map((block) => block.code));
  const allSelectableBlocks = allBlocks.length > 0
    ? allBlocks.map((block) => ({
      ...block,
      required: requiredBlockCodes.has(block.code) || block.required,
      default_selected: includedTemplateBlocks.find((templateBlock) => templateBlock.code === block.code)?.default_selected ?? block.default_selected,
    }))
    : includedTemplateBlocks;
  const selectedBlockCodes = new Set([
    ...form.selectedBlockCodes,
    ...Array.from(requiredBlockCodes),
  ]);
  const selectedBlockDetails = allSelectableBlocks.filter((block) => selectedBlockCodes.has(block.code));
  const selectedBlockItems = selectedBlockDetails.flatMap((block) => block.items || []);
  const includedSelectableBlocks = allSelectableBlocks.filter((block) => templateBlockCodes.has(block.code));
  const additionalSelectableBlocks = allSelectableBlocks.filter((block) => !templateBlockCodes.has(block.code));
  const blockCounts = selectedBlockItems.reduce((acc, item) => {
    const key = item.item_type === 'milestone' ? 'jalons' : item.item_type === 'deliverable' ? 'livrables' : item.item_type === 'journal' ? 'journal' : 'taches';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, { taches: 0, jalons: 0, livrables: 0, journal: 0 });

  const addContact = (field: 'responsibleContacts' | 'accountableContacts' | 'consultedContacts' | 'informedContacts', contact: ProjectContactOption) => {
    if (selectedIds.has(String(contact.id))) return;
    onChange({ [field]: [...form[field], contact] } as Partial<ProjectCreateFormState>);
  };
  const removeContact = (field: 'responsibleContacts' | 'accountableContacts' | 'consultedContacts' | 'informedContacts', contactId: number) => {
    onChange({ [field]: form[field].filter((contact) => Number(contact.id) !== Number(contactId)) } as Partial<ProjectCreateFormState>);
  };
  const roleGroups: Array<{ field: 'responsibleContacts' | 'accountableContacts' | 'consultedContacts' | 'informedContacts'; label: string; role: string; tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' }> = [
    { field: 'responsibleContacts', label: 'Responsables', role: 'R', tone: 'blue' },
    { field: 'accountableContacts', label: 'Approbateurs', role: 'A', tone: 'green' },
    { field: 'consultedContacts', label: 'Consultés', role: 'C', tone: 'amber' },
    { field: 'informedContacts', label: 'Informés', role: 'I', tone: 'slate' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 px-4">
      <div role="dialog" aria-modal="true" aria-label="Créer un projet" className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-slate-950">Créer un projet</h3>
            <p className="mt-1 text-sm text-slate-500">Wizard de lancement Projets V2.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
          <div className="grid gap-2 sm:grid-cols-5">
            {steps.map((label, index) => (
              <button key={label} type="button" disabled={saving} onClick={() => setStep(index)} className={`rounded-lg border px-3 py-2 text-left text-xs font-semibold transition ${step === index ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50'}`}>
                <span className="block text-[10px] uppercase tracking-wide opacity-75">Étape {index + 1}</span>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          {step === 0 ? (
            <div className="grid gap-4">
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Nom du projet
                <input autoFocus value={form.title} disabled={saving} onChange={(event) => onChange({ title: event.target.value })} placeholder="Ex: Migration Microsoft 365 - Client ABC" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Client officiel
                  <select value={form.idclient} disabled={saving || clientsLoading} onChange={(event) => onChange({ idclient: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                    <option value="">{clientsLoading ? 'Chargement...' : 'À confirmer'}</option>
                    {clients.map((client) => <option key={client.id} value={client.id}>{client.nom_client}</option>)}
                  </select>
                </label>
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Portefeuille
                  <select value={form.portfolioId} disabled={saving} onChange={(event) => onChange({ portfolioId: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                    <option value="">Aucun portefeuille</option>
                    {portfolios.map((portfolio) => <option key={portfolio.id} value={portfolio.id}>{portfolio.name}</option>)}
                  </select>
                </label>
              </div>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Responsable projet
                <select value={form.ownerContactId} disabled={saving || responsibleOptionsLoading} onChange={(event) => onChange({ ownerContactId: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                  <option value="">{responsibleOptionsLoading ? 'Chargement...' : 'À assigner plus tard'}</option>
                  {responsibleOptions.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}</option>)}
                </select>
              </label>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Résumé court
                <textarea value={form.description} disabled={saving} onChange={(event) => onChange({ description: event.target.value })} rows={3} placeholder="Résumé très court. Le cadrage détaillé se complète dans l’onglet Cadrage PMBOK." className="resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
              </label>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Modèle de projet
                <select
                  value={form.project_template}
                  disabled={saving}
                  onChange={(event) => {
                    const template = templateOptions.find((item) => item.code === event.target.value) || templateOptions[0];
                    const defaultBlockCodes = (template.blocks || []).filter((block) => block.default_selected !== false || block.required).map((block) => block.code);
                    onChange({
                      project_template: template.code,
                      category: template.category || form.category,
                      project_type: template.project_type,
                      priority: template.priority,
                      selectedBlockCodes: defaultBlockCodes,
                    });
                  }}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                >
                  {templateOptions.map((template) => <option key={template.code} value={template.code}>{template.name}</option>)}
                </select>
              </label>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Niveau
                <select value={form.project_type} disabled={saving} onChange={(event) => onChange({ project_type: event.target.value as ProjectCreateFormState['project_type'] })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                  <option value="strategique">Stratégique</option>
                  <option value="tactique">Tactique</option>
                  <option value="operationnel">Opérationnel</option>
                </select>
              </label>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Catégorie
                <select value={form.category} disabled={saving} onChange={(event) => onChange({ category: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                  {PROJECT_CREATE_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Priorité
                <select value={form.priority} disabled={saving} onChange={(event) => onChange({ priority: event.target.value as ProjectCreateFormState['priority'] })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                  {['Basse', 'Moyenne', 'Haute', 'Critique'].map((priority) => <option key={priority}>{priority}</option>)}
                </select>
              </label>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Statut initial
                <select value={form.status} disabled={saving} onChange={(event) => onChange({ status: event.target.value as ProjectCreateFormState['status'] })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100">
                  {['Planifié', 'En cours', 'À risque', 'Bloqué', 'Terminé', 'En pause', 'Brouillon'].map((status) => <option key={status}>{status}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800 md:col-span-2">
                <input type="checkbox" checked={form.is_remuneration_variable} disabled={saving} onChange={(event) => onChange({ is_remuneration_variable: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                Projet lié à rémunération variable
              </label>
              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Blocs du projet</p>
                    <p className="mt-1 text-xs font-normal text-slate-500">{templatesLoading ? 'Chargement des blocs...' : `${selectedBlockDetails.length} bloc(s) sélectionné(s) sur ${allSelectableBlocks.length || includedTemplateBlocks.length}`}</p>
                  </div>
                  <button type="button" onClick={onOpenBlockConfig} disabled={saving} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-blue-400 hover:bg-blue-50">
                    <Settings className="h-3.5 w-3.5" /> Reconfigurer les blocs
                  </button>
                </div>
                <div className="mt-3 grid gap-3">
                  {allSelectableBlocks.length === 0 ? <div className="text-xs font-normal text-slate-500">Aucun bloc chargé.</div> : null}
                  {[
                    { label: 'Inclus dans le modèle', blocks: includedSelectableBlocks },
                    { label: 'Blocs additionnels disponibles', blocks: additionalSelectableBlocks },
                  ].filter((group) => group.blocks.length > 0).map((group) => (
                    <div key={group.label}>
                      <p className="mb-2 text-[11px] font-bold uppercase text-slate-500">{group.label}</p>
                      <div className="grid gap-2 md:grid-cols-2">
                        {group.blocks.map((block) => {
                          const checked = selectedBlockCodes.has(block.code);
                          const required = requiredBlockCodes.has(block.code);
                          const included = templateBlockCodes.has(block.code);
                          return (
                            <label key={block.code} className={`flex min-h-20 items-start gap-2 rounded-lg border p-3 text-sm ${checked ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={saving || required}
                                onChange={(event) => {
                                  const next = new Set(form.selectedBlockCodes);
                                  if (event.target.checked) next.add(block.code);
                                  else next.delete(block.code);
                                  requiredBlockCodes.forEach((code) => next.add(code));
                                  onChange({ selectedBlockCodes: Array.from(next) });
                                }}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700"
                              />
                              <span className="min-w-0">
                                <span className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-900">{block.name}{required ? <Badge tone="blue">Requis</Badge> : null}{included && !required ? <Badge tone="slate">Modèle</Badge> : null}{block.block_type === 'dynamic' ? <Badge tone="amber">Dynamique</Badge> : null}</span>
                                <span className="mt-1 block text-xs font-normal text-slate-600">{block.description || block.category || 'Bloc de projet'}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Date de début
                  <input type="date" value={form.start_date} disabled={saving} onChange={(event) => onChange({ start_date: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
                </label>
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Date de fin prévue
                  <input type="date" value={form.end_date} disabled={saving} onChange={(event) => onChange({ end_date: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
                </label>
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800 md:col-span-2">Technologies / mots-clés
                  <input value={form.tech} disabled={saving} onChange={(event) => onChange({ tech: event.target.value })} placeholder="Microsoft 365, Azure, Sécurité, Migration" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
                </label>
              </div>
            </div>
          ) : null}

          {false ? (
            <div className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Date de début
                  <input type="date" value={form.start_date} disabled={saving} onChange={(event) => onChange({ start_date: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
                </label>
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Date de fin prévue
                  <input type="date" value={form.end_date} disabled={saving} onChange={(event) => onChange({ end_date: event.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
                </label>
              </div>
              <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Technologies / mots-clés
                <input value={form.tech} disabled={saving} onChange={(event) => onChange({ tech: event.target.value })} placeholder="Microsoft 365, Azure, Sécurité, Migration" className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
              </label>
            </div>
          ) : null}

          {false ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
              <div className="grid gap-3">
                {roleGroups.map((group) => (
                  <div key={group.role} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{group.role} - {group.label}</p>
                      <Badge tone={group.tone}>{form[group.field].length}</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {form[group.field].length === 0 ? <span className="text-xs text-slate-500">Aucun contact choisi.</span> : null}
                      {form[group.field].map((contact) => (
                        <span key={contact.id} className="inline-flex max-w-full items-center gap-2 rounded-lg bg-white px-2 py-1 text-xs font-semibold text-slate-800 ring-1 ring-slate-200">
                          <PmbokContactAvatar contact={contact} sizeClass="h-6 w-6" />
                          <span className="max-w-44 truncate">{contact.nom_client}</span>
                          <button type="button" disabled={saving} onClick={() => removeContact(group.field, contact.id)} className="inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100" aria-label={`Retirer ${contact.nom_client}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input value={contactSearch} disabled={saving} onChange={(event) => onContactSearchChange(event.target.value)} placeholder="Rechercher une partie prenante" className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100" />
                </div>
                <div className="max-h-[420px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1">
                  {contactsLoading ? <div className="px-3 py-3 text-sm text-slate-500">Chargement...</div> : null}
                  {!contactsLoading && contacts.length === 0 ? <div className="px-3 py-3 text-sm text-slate-500">Aucun contact trouvé.</div> : null}
                  {contacts.map((contact) => (
                    <div key={contact.id} className="rounded-lg px-2 py-2 hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <PmbokContactAvatar contact={contact} sizeClass="h-8 w-8" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">{contact.nom_client}</p>
                          <p className="truncate text-xs text-slate-500">{contact.email_primary || contact.type_client || 'Contact'}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {roleGroups.map((group) => (
                          <button key={group.role} type="button" disabled={saving || selectedIds.has(String(contact.id))} onClick={() => addContact(group.field, contact)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400">
                            {group.role}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="grid gap-4">
              <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <input type="checkbox" checked={form.createStarterTasks} disabled={saving} onChange={(event) => onChange({ createStarterTasks: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                  Créer les tâches de départ
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <input type="checkbox" checked={form.createStarterMilestones} disabled={saving} onChange={(event) => onChange({ createStarterMilestones: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                  Créer les jalons initiaux
                </label>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                <h4 className="text-sm font-semibold text-blue-950">Résumé</h4>
                <div className="mt-3 grid gap-2 text-sm text-blue-950 md:grid-cols-2">
                  <p><span className="font-semibold">Projet:</span> {form.title || 'À nommer'}</p>
                  <p><span className="font-semibold">Client:</span> {selectedClient?.nom_client || 'À confirmer'}</p>
                  <p><span className="font-semibold">Portefeuille:</span> {selectedPortfolio?.name || 'Aucun'}</p>
                  <p><span className="font-semibold">Modèle:</span> {selectedTemplate.name}</p>
                  <p><span className="font-semibold">Niveau:</span> {form.project_type}</p>
                  <p><span className="font-semibold">Catégorie:</span> {form.category}</p>
                  <p><span className="font-semibold">Priorité:</span> {form.priority}</p>
                  <p><span className="font-semibold">Responsable projet RACI initial:</span> {ownerContact?.nom_client || form.responsibleContacts[0]?.nom_client || 'Créateur du projet'}</p>
                  <p><span className="font-semibold">RACI complet:</span> à compléter dans l’onglet Cadrage PMBOK</p>
                  <p><span className="font-semibold">Blocs:</span> {selectedBlockDetails.length}</p>
                  <p><span className="font-semibold">Prévu:</span> {blockCounts.taches} tâches, {blockCounts.jalons} jalons, {blockCounts.livrables} livrables</p>
                </div>
              </div>
            </div>
          ) : null}

          {error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">{error}</div> : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60">Annuler</button>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={saving || step === 0} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50">Précédent</button>
            {step < steps.length - 1 ? (
              <button type="button" onClick={() => setStep((current) => Math.min(steps.length - 1, current + 1))} disabled={saving} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:opacity-60">Suivant</button>
            ) : (
              <button type="button" onClick={onSubmit} disabled={!canSubmit} className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300">
                <Plus className="h-4 w-4" /> {saving ? 'Création...' : 'Créer le projet'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectBlockConfigModal({
  blocks,
  saving,
  error,
  onClose,
  onSave,
}: {
  blocks: ProjectTemplateBlock[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (block: ProjectTemplateBlock, items: ProjectTemplateBlockItem[]) => void;
}) {
  const [selectedCode, setSelectedCode] = useState(blocks[0]?.code || '');
  const selectedBlock = blocks.find((block) => block.code === selectedCode) || blocks[0] || null;
  const [name, setName] = useState(selectedBlock?.name || '');
  const [description, setDescription] = useState(selectedBlock?.description || '');
  const [category, setCategory] = useState(selectedBlock?.category || '');
  const [blockType, setBlockType] = useState(selectedBlock?.block_type || 'static');
  const [active, setActive] = useState(selectedBlock?.active !== false);
  const [itemsJson, setItemsJson] = useState('[]');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedBlock) return;
    setName(selectedBlock.name || '');
    setDescription(selectedBlock.description || '');
    setCategory(selectedBlock.category || '');
    setBlockType(selectedBlock.block_type || 'static');
    setActive(selectedBlock.active !== false);
    setItemsJson(JSON.stringify((selectedBlock.items || []).map((item) => ({
      item_type: item.item_type,
      title: item.title,
      description: item.description || '',
      priority: item.priority || '',
      status: item.status || '',
      relative_days: item.relative_days ?? null,
      sort_order: item.sort_order || 0,
      active: item.active !== false,
    })), null, 2));
    setLocalError(null);
  }, [selectedBlock]);

  const submit = () => {
    if (!selectedBlock) return;
    try {
      const parsed = JSON.parse(itemsJson);
      if (!Array.isArray(parsed)) throw new Error('Le contenu items doit être un tableau JSON.');
      onSave({ ...selectedBlock, name, description, category, block_type: blockType, active }, parsed);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'JSON invalide.');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 px-4">
      <div role="dialog" aria-modal="true" aria-label="Reconfigurer les blocs" className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-slate-950">Reconfiguration durable des blocs</h3>
            <p className="mt-1 text-sm text-slate-500">Les changements sont sauvegardés en base et réutilisés par les prochains projets.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden md:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="overflow-y-auto border-r border-slate-200 bg-slate-50 p-3">
            {blocks.map((block) => (
              <button key={block.code} type="button" onClick={() => setSelectedCode(block.code)} className={`mb-2 w-full rounded-lg border px-3 py-2 text-left text-sm ${selectedBlock?.code === block.code ? 'border-blue-300 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'}`}>
                <span className="block font-semibold">{block.name}</span>
                <span className="mt-0.5 block truncate text-xs text-slate-500">{block.category || block.code}</span>
              </button>
            ))}
          </aside>
          <div className="grid gap-4 overflow-y-auto p-5">
            {!selectedBlock ? <EmptyState>Aucun bloc disponible.</EmptyState> : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Nom du bloc
                    <input value={name} disabled={saving} onChange={(event) => setName(event.target.value)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                  </label>
                  <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Catégorie
                    <input value={category || ''} disabled={saving} onChange={(event) => setCategory(event.target.value)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                  </label>
                  <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Type
                    <select value={blockType || 'static'} disabled={saving} onChange={(event) => setBlockType(event.target.value)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100">
                      <option value="static">Statique</option>
                      <option value="dynamic">Dynamique</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                    <input type="checkbox" checked={active} disabled={saving} onChange={(event) => setActive(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                    Bloc actif
                  </label>
                </div>
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Description
                  <textarea value={description || ''} disabled={saving} onChange={(event) => setDescription(event.target.value)} rows={3} className="resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                </label>
                <label className="grid gap-1.5 text-sm font-semibold text-slate-800">Items du bloc
                  <textarea value={itemsJson} disabled={saving} onChange={(event) => setItemsJson(event.target.value)} rows={14} spellCheck={false} className="font-mono resize-none rounded-xl border border-slate-300 px-3 py-2 text-xs font-normal outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                </label>
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                  Types permis : task, milestone, deliverable, journal. Les dates utilisent relative_days à partir de la date de début du projet.
                </div>
                {(localError || error) ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">{localError || error}</div> : null}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60">Annuler</button>
          <button type="button" onClick={submit} disabled={saving || !selectedBlock} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:bg-slate-300">
            <Settings className="h-4 w-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder le bloc'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatePortfolioModal({
  form,
  saving,
  error,
  ownerContacts,
  ownerContactsLoading,
  ownerSearch,
  stakeholderContacts,
  stakeholderContactsLoading,
  stakeholderSearch,
  onOwnerSearchChange,
  onStakeholderSearchChange,
  onChange,
  onClose,
  onSubmit,
}: {
  form: PortfolioCreateFormState;
  saving: boolean;
  error: string | null;
  ownerContacts: ProjectContactOption[];
  ownerContactsLoading: boolean;
  ownerSearch: string;
  stakeholderContacts: ProjectContactOption[];
  stakeholderContactsLoading: boolean;
  stakeholderSearch: string;
  onOwnerSearchChange: (value: string) => void;
  onStakeholderSearchChange: (value: string) => void;
  onChange: (updates: Partial<PortfolioCreateFormState>) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const selectedOwner = ownerContacts.find((contact) => String(contact.id) === form.ownerUserId) || null;
  const selectedStakeholderIds = new Set(form.stakeholderUsers.map((contact) => String(contact.id)));
  const canSubmit = form.ownerUserId.trim().length > 0 && form.name.trim().length > 0 && !saving;
  const handleOwnerSelect = (contact: ProjectContactOption) => {
    const ownerName = contact.nom_client || contact.email_primary || '';
    onChange({
      ownerUserId: String(contact.id),
      ownerName,
      ownerEmail: contact.email_primary || '',
      stakeholderUsers: form.stakeholderUsers.filter((stakeholder) => String(stakeholder.id) !== String(contact.id)),
      name: form.name || (ownerName.trim() ? `Portefeuille ${ownerName.trim()}` : ''),
    });
  };
  const toggleStakeholder = (contact: ProjectContactOption) => {
    if (String(contact.id) === form.ownerUserId) return;
    const contactId = String(contact.id);
    const exists = selectedStakeholderIds.has(contactId);
    onChange({
      stakeholderUsers: exists
        ? form.stakeholderUsers.filter((stakeholder) => String(stakeholder.id) !== contactId)
        : [...form.stakeholderUsers, contact],
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 px-4">
      <div role="dialog" aria-modal="true" aria-label="Créer un portefeuille" className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-slate-950">Créer un portefeuille</h3>
            <p className="mt-1 text-sm text-slate-500">Informations de base pour créer une vue de pilotage par personne.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4 overflow-y-auto px-5 py-5">
          <div className="grid gap-2 text-sm font-semibold text-slate-800">
            Propriétaire de la vue
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={ownerSearch}
                disabled={saving}
                onChange={(event) => onOwnerSearchChange(event.target.value)}
                placeholder="Rechercher un utilisateur PRIsME"
                className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              />
            </div>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1">
              {ownerContactsLoading ? (
                <div className="px-3 py-3 text-sm font-normal text-slate-500">Chargement des utilisateurs...</div>
              ) : ownerContacts.length === 0 ? (
                <div className="px-3 py-3 text-sm font-normal text-slate-500">Aucun utilisateur trouvé.</div>
              ) : (
                ownerContacts.map((contact) => {
                  const selected = String(contact.id) === form.ownerUserId;
                  return (
                    <button
                      key={contact.id}
                      type="button"
                      disabled={saving}
                      onClick={() => handleOwnerSelect(contact)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition ${selected ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'}`}
                    >
                      <PmbokContactAvatar contact={contact} sizeClass="h-8 w-8" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-900">{contact.nom_client}</span>
                        <span className="block truncate text-xs font-normal text-slate-500">{contact.email_primary || contact.type_client || 'Courriel non inscrit'}</span>
                      </span>
                      {selected ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-blue-700" /> : null}
                    </button>
                  );
                })
              )}
            </div>
            {selectedOwner ? (
              <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-900">
                <PmbokContactAvatar contact={selectedOwner} sizeClass="h-7 w-7" />
                <span className="min-w-0 truncate">Propriétaire de la vue choisi : {selectedOwner.nom_client}</span>
              </div>
            ) : null}
          </div>

          <div className="grid gap-2 text-sm font-semibold text-slate-800">
            Membres avec accès
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={stakeholderSearch}
                disabled={saving}
                onChange={(event) => onStakeholderSearchChange(event.target.value)}
                placeholder="Rechercher un employé ou un client"
                className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm font-normal text-slate-900 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
              />
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-1">
              {stakeholderContactsLoading ? (
                <div className="px-3 py-3 text-sm font-normal text-slate-500">Chargement des parties prenantes...</div>
              ) : stakeholderContacts.length === 0 ? (
                <div className="px-3 py-3 text-sm font-normal text-slate-500">Aucune partie prenante trouvée.</div>
              ) : (
                <div className="grid max-h-44 gap-1 overflow-y-auto">
                  {stakeholderContacts.map((contact) => {
                    const contactId = String(contact.id);
                    const isOwner = contactId === form.ownerUserId;
                    const selected = selectedStakeholderIds.has(contactId);
                    return (
                      <button
                        key={contact.id}
                        type="button"
                        disabled={saving || isOwner}
                        onClick={() => toggleStakeholder(contact)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition ${selected ? 'bg-emerald-50 ring-1 ring-emerald-200' : isOwner ? 'cursor-not-allowed bg-slate-100 opacity-75' : 'bg-white hover:bg-slate-100'}`}
                      >
                        <PmbokContactAvatar contact={contact} sizeClass="h-8 w-8" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-900">{contact.nom_client}</span>
                          <span className="block truncate text-xs font-normal text-slate-500">{isOwner ? 'Propriétaire principal' : contact.email_primary || contact.type_client || 'Courriel non inscrit'}</span>
                        </span>
                        {selected ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-700" /> : isOwner ? <Lock className="h-4 w-4 flex-shrink-0 text-slate-400" /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {form.stakeholderUsers.length > 0 ? (
              <div className="flex flex-wrap gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2">
                {form.stakeholderUsers.map((contact) => (
                  <span key={contact.id} className="inline-flex max-w-full items-center gap-2 rounded-lg bg-white px-2 py-1 text-xs font-semibold text-emerald-950 ring-1 ring-emerald-200">
                    <PmbokContactAvatar contact={contact} sizeClass="h-6 w-6" />
                    <span className="max-w-40 truncate">{contact.nom_client}</span>
                    <button type="button" onClick={() => toggleStakeholder(contact)} disabled={saving} className="inline-flex h-5 w-5 items-center justify-center rounded-md text-emerald-800 hover:bg-emerald-100 disabled:opacity-60" aria-label={`Retirer ${contact.nom_client}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs font-normal text-slate-500">Optionnel. Les employés et clients sélectionnés auront accès au portefeuille avec le responsable.</p>
            )}
          </div>

          <input
            type="hidden"
            value={form.ownerName}
            readOnly
          />

          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-semibold text-slate-800">
              Nom du portefeuille
              <input
                value={form.name}
                disabled={saving}
                onChange={(event) => onChange({ name: event.target.value })}
                placeholder="Portefeuille Marie Tremblay"
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-slate-800">
              Courriel
              <input
                type="email"
                value={form.ownerEmail}
                readOnly
                placeholder="Fourni par l'utilisateur choisi"
                className="rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-normal text-slate-600 outline-none"
              />
            </label>
          </div>

          <label className="grid gap-1.5 text-sm font-semibold text-slate-800">
            Description
            <textarea
              value={form.description}
              disabled={saving}
              onChange={(event) => onChange({ description: event.target.value })}
              rows={3}
              placeholder="Ex: Projets suivis par Marie pour les mandats cybersécurité."
              className="resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
            />
          </label>

          <div className="grid gap-2">
            <p className="text-sm font-semibold text-slate-800">Accès</p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'private' as const, label: 'Privé responsable' },
                { value: 'public' as const, label: 'Partagé équipe' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={saving}
                  onClick={() => onChange({ visibility: option.value })}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${form.visibility === option.value ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">{error}</div> : null}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60">Annuler</button>
          <button type="button" onClick={onSubmit} disabled={!canSubmit} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300">
            <Plus className="h-4 w-4" /> Créer le portefeuille
          </button>
        </div>
      </div>
    </div>
  );
}

function taskMatchesFilter(task: ProjectTaskItem, filter: TaskFilter) {
  if (filter === 'all') return true;
  if (filter === 'critical') return (task.priority_text || '').toLowerCase().includes('critique');
  if (filter === 'late') return Boolean(task.is_late);
  if (filter === 'at_risk') return Boolean(task.is_at_risk);
  if (filter === 'blocked') return Boolean(task.is_blocked);
  if (filter === 'upcoming') return task.urgency_level === 'upcoming' || (!task.is_completed && !task.is_late && !!task.date_echeance);
  if (filter === 'completed') return Boolean(task.is_completed);
  if (filter === 'milestones') return Boolean(task.isjalon);
  if (filter === 'unscheduled') return !taskHasRequiredDates(task) && !task.is_completed;
  if (filter === 'unassigned') return !task.assignees || task.assignees.length === 0;
  return true;
}

function getTaskEffectiveProgress(task: ProjectTaskItem) {
  if (task.is_completed || /termin|compl[ée]t|completed|done|livr|ferm|closed/i.test(String(task.status_text || ''))) {
    return 100;
  }
  const progress = Number(task.safe_percent ?? task.percent ?? 0);
  return Number.isFinite(progress) ? Math.max(0, Math.min(100, progress)) : 0;
}

function getResponsibleLabel(task: ProjectTaskItem) {
  if (!task.assignees || task.assignees.length === 0) return 'Sans responsable';
  return task.assignees.map((assignee) => assignee.nom_client || assignee.email_primary || 'Responsable à préciser').slice(0, 2).join(', ');
}

function taskHasRequiredDates(task: ProjectTaskItem) {
  return Boolean(getDateOnlyValue(task.date_debut) && getDateOnlyValue(task.date_echeance));
}

function getTaskDateRangeLabel(task: ProjectTaskItem) {
  return taskHasRequiredDates(task)
    ? `${formatDate(task.date_debut)} -> ${formatDate(task.date_echeance)}`
    : 'Dates à compléter';
}

function getTaskSignal(task: ProjectTaskItem) {
  if (task.is_late) return { label: `En retard${task.days_overdue ? ` de ${task.days_overdue} j` : ''}`, tone: 'red' as const };
  if (task.is_blocked) return { label: 'Bloquée', tone: 'red' as const };
  if (task.is_at_risk) return { label: 'À risque', tone: 'amber' as const };
  if (task.is_completed) return { label: 'Terminée', tone: 'green' as const };
  if (!taskHasRequiredDates(task)) return { label: 'Dates à compléter', tone: 'amber' as const };
  if (typeof task.days_until_due === 'number' && task.days_until_due >= 0) return { label: `${task.days_until_due} j restant(s)`, tone: task.days_until_due <= 7 ? 'amber' as const : 'blue' as const };
  return { label: 'À suivre', tone: 'slate' as const };
}

function getTaskSecondaryLine(task: ProjectTaskItem) {
  return `${getTaskDateRangeLabel(task)} · ${task.status_text || 'Statut à préciser'}`;
}

function ProjectTasksSection({ projectId, tasksData, deliverablesData, loading, error, isPreviewMode, onTasksChanged, onTaskOptimisticUpdate, onSelectFilter, focusedTaskId }: { projectId: number; tasksData: ProjectTasksResponse | null; deliverablesData: ProjectDeliverablesResponse | null; loading: boolean; error: string | null; isPreviewMode: boolean; onTasksChanged: () => Promise<void>; onTaskOptimisticUpdate: (taskId: number, updates: Partial<ProjectTaskItem>) => void; onSelectFilter?: (filter: TaskFilter) => void; focusedTaskId?: number | null }) {
  const [activeFilter, setActiveFilter] = useState<TaskFilter>('all');
  const [search, setSearch] = useState('');
  const [contacts, setContacts] = useState<ProjectContactOption[]>([]);
  const [newTask, setNewTask] = useState<NewTaskState>(() => emptyNewTaskState(false));
  const [actionState, setActionState] = useState<TaskActionState>({ selectedTask: null, panelMode: 'actions', titleEditing: false, titleDraft: '', statusText: '', priorityText: '', percent: '', timeSpentHours: '', startDate: '', dueDate: '', blockNote: '', assigneeId: '', reminderDraft: '', comments: [], newComment: '', commentsLoading: false, timeEntries: [], timeEntriesLoading: false, timeFormDate: getTodayDateInput(), timeFormTime: getCurrentTimeInput(), timeFormType: 'Cybersécurité', timeFormDurationHours: '0.25', timeFormComment: '', editingTimeEntryId: null, timeEditDate: '', timeEditTime: '', timeEditType: 'Cybersécurité', timeEditDurationHours: '0.25', timeEditComment: '', saving: false, message: null, error: null });
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteTaskConfirmState | null>(null);
  const [reorderState, setReorderState] = useState<{ saving: boolean; message: string | null; error: string | null }>({ saving: false, message: null, error: null });
  const [completionState, setCompletionState] = useState<{ savingId: number | null; message: string | null; error: string | null }>({ savingId: null, message: null, error: null });
  const [agentSuggestions, setAgentSuggestions] = useState<AgentSuggestion[]>([]);
  const [agentMessage, setAgentMessage] = useState<string | null>(null);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [agentSavingId, setAgentSavingId] = useState<number | null>(null);
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<number[]>([]);
  const [nativeTaskDrag, setNativeTaskDrag] = useState<{ taskId: number; kind: 'section' | 'task' } | null>(null);
  const [taskDropTarget, setTaskDropTarget] = useState<{ milestoneId: number | null; index: number } | null>(null);
  const lastTaskDragEndAtRef = useRef(0);
  const taskAutoScrollFrameRef = useRef<number | null>(null);
  const taskAutoScrollVelocityRef = useRef(0);

  const stopTaskAutoScroll = () => {
    taskAutoScrollVelocityRef.current = 0;
    if (taskAutoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(taskAutoScrollFrameRef.current);
      taskAutoScrollFrameRef.current = null;
    }
  };

  const runTaskAutoScroll = () => {
    const velocity = taskAutoScrollVelocityRef.current;
    if (!velocity) {
      taskAutoScrollFrameRef.current = null;
      return;
    }
    window.scrollBy({ top: velocity, left: 0, behavior: 'auto' });
    taskAutoScrollFrameRef.current = window.requestAnimationFrame(runTaskAutoScroll);
  };

  const updateTaskAutoScroll = (clientY: number) => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const threshold = Math.min(140, Math.max(80, Math.round(viewportHeight * 0.16)));
    const maxVelocity = 26;
    const distanceFromBottom = viewportHeight - clientY;
    let velocity = 0;

    if (clientY < threshold) {
      velocity = -Math.ceil(maxVelocity * ((threshold - clientY) / threshold));
    } else if (distanceFromBottom < threshold) {
      velocity = Math.ceil(maxVelocity * ((threshold - distanceFromBottom) / threshold));
    }

    taskAutoScrollVelocityRef.current = velocity;
    if (velocity && taskAutoScrollFrameRef.current === null) {
      taskAutoScrollFrameRef.current = window.requestAnimationFrame(runTaskAutoScroll);
    } else if (!velocity) {
      stopTaskAutoScroll();
    }
  };

  useEffect(() => {
    if (isPreviewMode) {
      setCollapsedSectionIds([]);
      return;
    }

    let cancelled = false;
    const loadCollapsedSections = async () => {
      try {
        const response = await fetch(`/api/projet-v2/projects/${projectId}/ui-state/collapsed-sections`, { credentials: 'include' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
        const ids = Array.isArray(data.state?.collapsedSectionIds) ? data.state.collapsedSectionIds : [];
        if (!cancelled) setCollapsedSectionIds(ids.map((id: unknown) => Number(id)).filter((id: number) => !Number.isNaN(id)));
      } catch {
        if (!cancelled) setCollapsedSectionIds([]);
      }
    };
    loadCollapsedSections();
    return () => {
      cancelled = true;
    };
  }, [isPreviewMode, projectId]);

  useEffect(() => {
    if (!nativeTaskDrag) {
      stopTaskAutoScroll();
      return;
    }

    const handleWindowDragOver = (event: globalThis.DragEvent) => {
      updateTaskAutoScroll(event.clientY);
    };

    window.addEventListener('dragover', handleWindowDragOver);
    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      stopTaskAutoScroll();
    };
  }, [nativeTaskDrag]);

  const saveCollapsedSections = async (ids: number[]) => {
    if (isPreviewMode) return;
    try {
      await fetch(`/api/projet-v2/projects/${projectId}/ui-state/collapsed-sections`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: { collapsedSectionIds: ids } }),
      });
    } catch {
      // Non bloquant : l'état visuel courant reste appliqué, même si la persistance échoue temporairement.
    }
  };

  const toggleSectionCollapsed = (sectionId: number) => {
    setCollapsedSectionIds((current) => {
      const next = current.includes(sectionId) ? current.filter((id) => id !== sectionId) : [...current, sectionId];
      saveCollapsedSections(next);
      return next;
    });
  };

  const filteredTasks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (tasksData?.tasks || []).filter((task) => {
      const matchesFilter = taskMatchesFilter(task, activeFilter);
      const matchesSearch = !query || [task.titre, task.description, task.status_text, task.priority_text, getResponsibleLabel(task)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
      return matchesFilter && matchesSearch;
    });
  }, [activeFilter, search, tasksData]);

  const childTasksByMilestone = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (tasksData?.tasks || [])
      .filter((task) => !task.isjalon && task.id_jalon)
      .filter((task) => {
        const matchesFilter = activeFilter === 'milestones' ? true : taskMatchesFilter(task, activeFilter);
        const matchesSearch = !query || [task.titre, task.description, task.status_text, task.priority_text, getResponsibleLabel(task)]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
        return matchesFilter && matchesSearch;
      })
      .reduce<Record<number, ProjectTaskItem[]>>((groups, task) => {
        const milestoneId = Number(task.id_jalon);
        groups[milestoneId] = groups[milestoneId] || [];
        groups[milestoneId].push(task);
        return groups;
      }, {});
  }, [activeFilter, search, tasksData]);

  const orderedChildTasksByMilestone = useMemo(() => Object.fromEntries(
    Object.entries(childTasksByMilestone).map(([milestoneId, tasks]) => [milestoneId, sortTasksByManualOrder(tasks)])
  ) as Record<number, ProjectTaskItem[]>, [childTasksByMilestone]);

  const allChildTasksByMilestone = useMemo(() => (tasksData?.tasks || [])
    .filter((task) => !task.isjalon && task.id_jalon)
    .reduce<Record<number, ProjectTaskItem[]>>((groups, task) => {
      const milestoneId = Number(task.id_jalon);
      groups[milestoneId] = groups[milestoneId] || [];
      groups[milestoneId].push(task);
      return groups;
    }, {}), [tasksData]);

  const sectionProgressById = useMemo(() => Object.fromEntries(
    Object.entries(allChildTasksByMilestone).map(([milestoneId, tasks]) => [
      Number(milestoneId),
      tasks.length > 0 ? Math.round(tasks.reduce((sum, task) => sum + getTaskEffectiveProgress(task), 0) / tasks.length) : 0,
    ]),
  ) as Record<number, number>, [allChildTasksByMilestone]);

  const getTaskDisplayProgress = (task: ProjectTaskItem) => (
    task.isjalon && Object.prototype.hasOwnProperty.call(sectionProgressById, task.id)
      ? sectionProgressById[task.id]
      : getTaskEffectiveProgress(task)
  );

  const visibleTasks = useMemo(() => {
    const visibleMilestoneIds = new Set(filteredTasks.filter((task) => task.isjalon).map((task) => task.id));
    return sortTasksByManualOrder(filteredTasks.filter((task) => task.isjalon || !task.id_jalon || !visibleMilestoneIds.has(Number(task.id_jalon))));
  }, [filteredTasks]);

  const selectFilter = (filter: TaskFilter) => {
    setActiveFilter(filter);
    onSelectFilter?.(filter);
  };

  const buildTaskPanelState = (task: ProjectTaskItem, panelMode: TaskActionState['panelMode']): TaskActionState => ({
    selectedTask: task,
    panelMode,
    titleEditing: false,
    titleDraft: task.titre || '',
    statusText: task.status_text || '',
    priorityText: task.priority_text || '',
    percent: String(getTaskDisplayProgress(task)),
    timeSpentHours: formatTimeSpentInput(task.time_spent_minutes),
    startDate: task.date_debut ? String(task.date_debut).slice(0, 10) : '',
    dueDate: task.date_echeance ? String(task.date_echeance).slice(0, 10) : '',
    blockNote: '',
    assigneeId: task.assignees?.[0]?.id_contact ? String(task.assignees[0].id_contact) : '',
    reminderDraft: '',
    comments: [],
    newComment: '',
    commentsLoading: false,
    timeEntries: [],
    timeEntriesLoading: false,
    timeFormDate: getTodayDateInput(),
    timeFormTime: getCurrentTimeInput(),
    timeFormType: 'Cybersécurité',
    timeFormDurationHours: '0.25',
    timeFormComment: '',
    editingTimeEntryId: null,
    timeEditDate: '',
    timeEditTime: '',
    timeEditType: 'Cybersécurité',
    timeEditDurationHours: '0.25',
    timeEditComment: '',
    saving: false,
    message: null,
    error: null,
  });

  const openTaskActions = (task: ProjectTaskItem) => {
    if (actionState.selectedTask?.id === task.id && actionState.panelMode === 'actions') {
      closeTaskActions();
      return;
    }
    setActionState(buildTaskPanelState(task, 'actions'));
  };

  useEffect(() => {
    if (!focusedTaskId || !tasksData?.tasks?.length) return;
    const focusedTask = tasksData.tasks.find((task) => task.id === focusedTaskId);
    if (!focusedTask) return;
    setActiveFilter('all');
    setSearch('');
    setActionState(buildTaskPanelState(focusedTask, 'actions'));
    window.setTimeout(() => {
      document.getElementById(`project-task-${focusedTask.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [focusedTaskId, tasksData?.tasks]);

  const loadAgentSuggestions = useCallback(async () => {
    if (isPreviewMode) {
      setAgentSuggestions([]);
      return;
    }
    try {
      const response = await fetch(`/api/projet-v2/agent/suggestions?projectId=${projectId}&limit=25`, { credentials: 'include' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setAgentSuggestions(payload.suggestions || []);
      setAgentError(null);
    } catch (err) {
      setAgentError(err instanceof Error ? err.message : 'Erreur suggestions');
    }
  }, [isPreviewMode, projectId]);

  useEffect(() => {
    loadAgentSuggestions();
  }, [loadAgentSuggestions]);

  const openSuggestionEvidence = (evidence: { kind?: string; id?: number | string }, suggestion: AgentSuggestion) => {
    if (evidence.kind !== 'tache') return;
    const task = tasksData?.tasks.find((item) => item.id === Number(evidence.id));
    if (!task) return;
    openTaskActions(task);
    if (suggestion.proposed_action?.action === 'set_due_date') {
      setActionState((current) => ({ ...current, message: 'Suggestion: compléter la date de fin, puis confirmer avec Mettre à jour.' }));
    } else if (suggestion.proposed_action?.action === 'assign_owner') {
      setActionState((current) => ({ ...current, message: 'Suggestion: choisir un responsable RACI-R, puis confirmer avec Réassigner.' }));
    }
  };

  const decideProjectSuggestion = async (suggestion: AgentSuggestion, action: 'accept' | 'convert' | 'reject' | 'snooze', rejectReason?: string) => {
    if (isPreviewMode) {
      setAgentMessage('Preview seulement : décision simulée.');
      return;
    }
    try {
      setAgentSavingId(suggestion.id);
      setAgentError(null);
      const response = await fetch(`/api/projet-v2/agent/suggestions/${suggestion.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'reject' ? { action, rejectReason } : action === 'snooze' ? { action, snoozeDays: 7 } : { action }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setAgentMessage('Décision enregistrée. Annulable pendant 10 secondes.');
      if (action === 'convert') {
        const firstEvidence = suggestion.evidence?.find((item) => item.kind === 'tache');
        if (firstEvidence) openSuggestionEvidence(firstEvidence, suggestion);
        if (suggestion.proposed_action?.action === 'create_task') {
          setNewTask({ ...emptyNewTaskState(false), open: true, titre: String(suggestion.proposed_action.payload?.title || suggestion.title) });
        }
      }
      await loadAgentSuggestions();
    } catch (err) {
      setAgentError(err instanceof Error ? err.message : 'Erreur décision suggestion');
    } finally {
      setAgentSavingId(null);
    }
  };

  const undoProjectSuggestion = async (suggestion: AgentSuggestion) => {
    if (isPreviewMode) return;
    try {
      setAgentSavingId(suggestion.id);
      const response = await fetch(`/api/projet-v2/agent/suggestions/${suggestion.id}/undo`, { method: 'POST', credentials: 'include' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setAgentMessage('Décision annulée.');
      await loadAgentSuggestions();
    } catch (err) {
      setAgentError(err instanceof Error ? err.message : 'Erreur annulation');
    } finally {
      setAgentSavingId(null);
    }
  };

  const closeTaskActions = () => setActionState((current) => ({ ...current, selectedTask: null }));

  const startEditingTaskTitle = () => {
    setActionState((current) => current.selectedTask
      ? { ...current, titleEditing: true, titleDraft: current.selectedTask.titre || '', message: null, error: null }
      : current);
  };

  const cancelEditingTaskTitle = () => {
    setActionState((current) => current.selectedTask
      ? { ...current, titleEditing: false, titleDraft: current.selectedTask.titre || '' }
      : current);
  };

  const saveTaskTitle = async () => {
    const task = actionState.selectedTask;
    if (!task || !actionState.titleEditing || actionState.saving) return;

    const titre = actionState.titleDraft.trim();
    if (!titre) {
      setActionState((current) => ({ ...current, error: task.isjalon ? 'Nom de section requis.' : 'Titre de tâche requis.' }));
      return;
    }

    if (titre === (task.titre || '').trim()) {
      setActionState((current) => ({ ...current, titleEditing: false, titleDraft: task.titre || '' }));
      return;
    }

    if (isPreviewMode) {
      setActionState((current) => current.selectedTask
        ? { ...current, selectedTask: { ...current.selectedTask, titre }, titleEditing: false, titleDraft: titre, message: `${task.isjalon ? 'Section' : 'Tâche'} renommée en preview.`, error: null }
        : current);
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setActionState((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de renommer (HTTP ${response.status})`);

      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
      setActionState((current) => ({
        ...current,
        selectedTask: data.task ? { ...current.selectedTask, ...data.task } : current.selectedTask ? { ...current.selectedTask, titre } : null,
        titleEditing: false,
        titleDraft: data.task?.titre || titre,
        saving: false,
        message: `${task.isjalon ? 'Section' : 'Tâche'} renommée.`,
        error: null,
      }));
    } catch (err) {
      setActionState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur de renommage' }));
    }
  };

  const handleTaskTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditingTaskTitle();
    }
  };

  const captureScrollPosition = () => ({ x: window.scrollX, y: window.scrollY });

  const restoreScrollPosition = (position: { x: number; y: number }) => {
    const restore = () => window.scrollTo({ left: position.x, top: position.y, behavior: 'auto' });
    window.requestAnimationFrame(restore);
    window.setTimeout(restore, 50);
    window.setTimeout(restore, 150);
  };

  const loadContacts = async () => {
    if (isPreviewMode || contacts.length > 0) return;
    const response = await fetch('/api/projet-v2/contacts', { credentials: 'include' });
    if (!response.ok) throw new Error(`Impossible de charger les contacts (HTTP ${response.status})`);
    const data = await response.json();
    setContacts(Array.isArray(data.contacts) ? data.contacts : []);
  };

  const loadTaskComments = async (task: ProjectTaskItem) => {
    if (isPreviewMode) {
      const previewComments: ProjectTaskComment[] = task.comments_count
        ? [
            { id: task.id * 100 + 1, commentaire: 'Commentaire simulé : vérifier le prochain pas avec le responsable.', priority: 'Normal', date_commentaire: new Date().toISOString(), nom_client: 'Preview PRIsME' },
          ]
        : [];
      setActionState((current) => ({ ...current, comments: previewComments, commentsLoading: false }));
      return;
    }

    try {
      setActionState((current) => ({ ...current, commentsLoading: true, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/comments`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de charger les commentaires (HTTP ${response.status})`);
      setActionState((current) => ({ ...current, comments: Array.isArray(data.comments) ? data.comments : [], commentsLoading: false }));
    } catch (err) {
      setActionState((current) => ({ ...current, commentsLoading: false, error: err instanceof Error ? err.message : 'Erreur commentaires' }));
    }
  };

  const openTaskComments = (task: ProjectTaskItem) => {
    if (actionState.selectedTask?.id === task.id && actionState.panelMode === 'comments') {
      closeTaskActions();
      return;
    }
    setActionState(buildTaskPanelState(task, 'comments'));
    setTimeout(() => loadTaskComments(task), 0);
  };

  const loadTaskTimeEntries = async (task: ProjectTaskItem) => {
    if (task.isjalon) return;

    if (isPreviewMode) {
      const previewEntries: ProjectTaskTimeEntry[] = Number(task.time_spent_minutes || 0) > 0
        ? [{
            id: task.id * 1000 + 1,
            id_tache: task.id,
            id_projet: task.id_projet || projectId,
            created_by_name: 'Preview PRIsME',
            created_by_email: 'preview@prisme.local',
            work_date: getTodayDateInput(),
            work_time: getCurrentTimeInput(),
            entry_type: 'Cybersécurité',
            duration_minutes: Number(task.time_spent_minutes || 0),
            comment: 'Entrée de temps simulée.',
            created_at: new Date().toISOString(),
          }]
        : [];
      setActionState((current) => ({ ...current, timeEntries: previewEntries, timeEntriesLoading: false }));
      return;
    }

    try {
      setActionState((current) => ({ ...current, timeEntriesLoading: true, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/time-entries`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de charger les entrées de temps (HTTP ${response.status})`);
      setActionState((current) => ({
        ...current,
        timeEntries: Array.isArray(data.entries) ? data.entries : [],
        timeEntriesLoading: false,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: Number(data.taskTotalMinutes ?? current.selectedTask.time_spent_minutes ?? 0) } : current.selectedTask,
        timeSpentHours: formatTimeSpentInput(Number(data.taskTotalMinutes ?? current.selectedTask?.time_spent_minutes ?? 0)),
        editingTimeEntryId: null,
      }));
    } catch (err) {
      setActionState((current) => ({ ...current, timeEntriesLoading: false, error: err instanceof Error ? err.message : 'Erreur entrées de temps' }));
    }
  };

  const addTaskComment = async () => {
    const task = actionState.selectedTask;
    if (!task) return;
    const commentaire = actionState.newComment.trim();
    if (!commentaire) {
      setActionState((current) => ({ ...current, error: 'Écris un commentaire avant de l’ajouter.' }));
      return;
    }

    if (isPreviewMode) {
      const comment: ProjectTaskComment = { id: Date.now(), commentaire, priority: 'Normal', date_commentaire: new Date().toISOString(), nom_client: 'Preview PRIsME' };
      setActionState((current) => ({ ...current, comments: [comment, ...current.comments], newComment: '', message: 'Commentaire simulé ajouté.', error: null }));
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setActionState((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentaire, priority: 'Normal' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible d’ajouter le commentaire (HTTP ${response.status})`);
      setActionState((current) => ({ ...current, comments: data.comment ? [data.comment, ...current.comments] : current.comments, newComment: '', saving: false, message: 'Commentaire ajouté.', error: null }));
      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
    } catch (err) {
      setActionState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur commentaire' }));
    }
  };

  const addTaskTimeEntry = async () => {
    const task = actionState.selectedTask;
    if (!task || task.isjalon) return;

    const durationMinutes = parseTimeSpentMinutes(actionState.timeFormDurationHours);
    if (!actionState.timeFormDate) {
      setActionState((current) => ({ ...current, error: 'Choisis la date du temps.' }));
      return;
    }
    if (!actionState.timeFormTime) {
      setActionState((current) => ({ ...current, error: 'Choisis l’heure du temps.' }));
      return;
    }
    if (durationMinutes === null || durationMinutes <= 0) {
      setActionState((current) => ({ ...current, error: 'Durée invalide. Utilise des heures par tranche de 0.25, donc 15 minutes.' }));
      return;
    }

    if (isPreviewMode) {
      const entry: ProjectTaskTimeEntry = {
        id: Date.now(),
        id_tache: task.id,
        id_projet: projectId,
        created_by_name: 'Preview PRIsME',
        created_by_email: 'preview@prisme.local',
        work_date: actionState.timeFormDate,
        work_time: actionState.timeFormTime,
        entry_type: actionState.timeFormType,
        duration_minutes: durationMinutes,
        comment: actionState.timeFormComment.trim() || null,
        created_at: new Date().toISOString(),
      };
      const nextTotal = Number(task.time_spent_minutes || 0) + durationMinutes;
      onTaskOptimisticUpdate(task.id, { time_spent_minutes: nextTotal });
      setActionState((current) => ({
        ...current,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: nextTotal } : current.selectedTask,
        timeEntries: [entry, ...current.timeEntries],
        timeSpentHours: formatTimeSpentInput(nextTotal),
        timeFormDurationHours: '0.25',
        timeFormComment: '',
        message: 'Entrée de temps simulée.',
        error: null,
      }));
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setActionState((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/time-entries`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          work_date: actionState.timeFormDate,
          work_time: actionState.timeFormTime,
          entry_type: actionState.timeFormType,
          duration_minutes: durationMinutes,
          comment: actionState.timeFormComment,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible d’ajouter le temps (HTTP ${response.status})`);
      const nextTotal = Number(data.taskTotalMinutes ?? task.time_spent_minutes ?? 0);
      onTaskOptimisticUpdate(task.id, { time_spent_minutes: nextTotal });
      setActionState((current) => ({
        ...current,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: nextTotal } : current.selectedTask,
        timeEntries: data.entry ? [data.entry, ...current.timeEntries] : current.timeEntries,
        timeSpentHours: formatTimeSpentInput(nextTotal),
        timeFormDurationHours: '0.25',
        timeFormComment: '',
        saving: false,
        message: 'Temps ajouté.',
        error: null,
      }));
      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
    } catch (err) {
      setActionState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur ajout temps' }));
    }
  };

  const startEditingTimeEntry = (entry: ProjectTaskTimeEntry) => {
    setActionState((current) => ({
      ...current,
      editingTimeEntryId: entry.id,
      timeEditDate: formatDateInputValue(entry.work_date) || getTodayDateInput(),
      timeEditTime: normalizeTimeInput(entry.work_time) || getCurrentTimeInput(),
      timeEditType: entry.entry_type || 'Cybersécurité',
      timeEditDurationHours: formatTimeSpentInput(entry.duration_minutes) || '0.25',
      timeEditComment: entry.comment || '',
      message: null,
      error: null,
    }));
  };

  const cancelEditingTimeEntry = () => {
    setActionState((current) => ({
      ...current,
      editingTimeEntryId: null,
      timeEditDate: '',
      timeEditTime: '',
      timeEditType: 'Cybersécurité',
      timeEditDurationHours: '0.25',
      timeEditComment: '',
      message: null,
      error: null,
    }));
  };

  const saveTimeEntryEdit = async (entry: ProjectTaskTimeEntry) => {
    const task = actionState.selectedTask;
    if (!task || task.isjalon) return;

    const durationMinutes = parseTimeSpentMinutes(actionState.timeEditDurationHours);
    if (!actionState.timeEditDate) {
      setActionState((current) => ({ ...current, error: 'Choisis la date du log de temps.' }));
      return;
    }
    if (!actionState.timeEditTime) {
      setActionState((current) => ({ ...current, error: 'Choisis l’heure du log de temps.' }));
      return;
    }
    if (durationMinutes === null || durationMinutes <= 0) {
      setActionState((current) => ({ ...current, error: 'Durée invalide. Utilise des heures par tranche de 0.25, donc 15 minutes.' }));
      return;
    }

    const nextEntry: ProjectTaskTimeEntry = {
      ...entry,
      work_date: actionState.timeEditDate,
      work_time: actionState.timeEditTime,
      entry_type: actionState.timeEditType,
      duration_minutes: durationMinutes,
      comment: actionState.timeEditComment.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const durationDelta = durationMinutes - Number(entry.duration_minutes || 0);

    if (isPreviewMode) {
      const nextTotal = Math.max(0, Number(task.time_spent_minutes || 0) + durationDelta);
      onTaskOptimisticUpdate(task.id, { time_spent_minutes: nextTotal });
      setActionState((current) => ({
        ...current,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: nextTotal } : current.selectedTask,
        timeEntries: current.timeEntries.map((item) => item.id === entry.id ? nextEntry : item),
        timeSpentHours: formatTimeSpentInput(nextTotal),
        editingTimeEntryId: null,
        message: 'Log de temps simulé modifié.',
        error: null,
      }));
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setActionState((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/time-entries/${entry.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          work_date: actionState.timeEditDate,
          work_time: actionState.timeEditTime,
          entry_type: actionState.timeEditType,
          duration_minutes: durationMinutes,
          comment: actionState.timeEditComment,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de modifier le log de temps (HTTP ${response.status})`);
      const nextTotal = Number(data.taskTotalMinutes ?? Math.max(0, Number(task.time_spent_minutes || 0) + durationDelta));
      onTaskOptimisticUpdate(task.id, { time_spent_minutes: nextTotal });
      setActionState((current) => ({
        ...current,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: nextTotal } : current.selectedTask,
        timeEntries: current.timeEntries.map((item) => item.id === entry.id ? (data.entry || nextEntry) : item),
        timeSpentHours: formatTimeSpentInput(nextTotal),
        editingTimeEntryId: null,
        saving: false,
        message: 'Log de temps modifié.',
        error: null,
      }));
      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
    } catch (err) {
      setActionState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur modification temps' }));
    }
  };

  const deleteTimeEntry = async (entry: ProjectTaskTimeEntry) => {
    const task = actionState.selectedTask;
    if (!task || task.isjalon) return;
    if (!window.confirm('Supprimer ce log de temps?')) return;

    const deletedMinutes = Number(entry.duration_minutes || 0);
    if (isPreviewMode) {
      const nextTotal = Math.max(0, Number(task.time_spent_minutes || 0) - deletedMinutes);
      onTaskOptimisticUpdate(task.id, { time_spent_minutes: nextTotal });
      setActionState((current) => ({
        ...current,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: nextTotal } : current.selectedTask,
        timeEntries: current.timeEntries.filter((item) => item.id !== entry.id),
        timeSpentHours: formatTimeSpentInput(nextTotal),
        editingTimeEntryId: null,
        message: 'Log de temps simulé supprimé.',
        error: null,
      }));
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setActionState((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/time-entries/${entry.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de supprimer le log de temps (HTTP ${response.status})`);
      const nextTotal = Number(data.taskTotalMinutes ?? Math.max(0, Number(task.time_spent_minutes || 0) - deletedMinutes));
      onTaskOptimisticUpdate(task.id, { time_spent_minutes: nextTotal });
      setActionState((current) => ({
        ...current,
        selectedTask: current.selectedTask ? { ...current.selectedTask, time_spent_minutes: nextTotal } : current.selectedTask,
        timeEntries: current.timeEntries.filter((item) => item.id !== entry.id),
        timeSpentHours: formatTimeSpentInput(nextTotal),
        editingTimeEntryId: null,
        saving: false,
        message: 'Log de temps supprimé.',
        error: null,
      }));
      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
    } catch (err) {
      setActionState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur suppression temps' }));
    }
  };

  const openDeleteConfirmation = (task: ProjectTaskItem) => {
    setDeleteConfirm({ task, saving: false, error: null });
  };

  const confirmDeleteTask = async () => {
    const task = deleteConfirm?.task;
    if (!task || deleteConfirm.saving) return;

    if (isPreviewMode) {
      setDeleteConfirm(null);
      setActionState((current) => ({ ...current, selectedTask: null }));
      setReorderState({ saving: false, message: `${task.isjalon ? 'Section' : 'Tâche'} supprimée en preview.`, error: null });
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setDeleteConfirm((current) => current ? { ...current, saving: true, error: null } : current);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Suppression impossible (HTTP ${response.status})`);

      setDeleteConfirm(null);
      setActionState((current) => ({ ...current, selectedTask: null }));
      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
      setReorderState({ saving: false, message: `${task.isjalon ? 'Section' : 'Tâche'} supprimée.`, error: null });
    } catch (err) {
      setDeleteConfirm((current) => current ? { ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur de suppression' } : current);
    }
  };

  const createTask = async () => {
    const titre = newTask.titre.trim();
    if (!titre) {
      setNewTask((current) => ({ ...current, error: 'Titre de tâche requis.' }));
      return;
    }
    if (!newTask.startDate || !newTask.dueDate) {
      setNewTask((current) => ({ ...current, error: 'Date de début et date de fin obligatoires.' }));
      return;
    }

    if (isPreviewMode) {
      setNewTask(emptyNewTaskState(false));
      return;
    }

    try {
      setNewTask((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titre,
          description: newTask.description,
          status_text: newTask.statusText,
          priority_text: newTask.priorityText,
          percent: newTask.percent,
          date_debut: newTask.startDate,
          date_echeance: newTask.dueDate,
          assigneeId: newTask.assigneeId,
          isjalon: newTask.isJalon,
          id_jalon: newTask.isJalon ? null : newTask.milestoneId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de créer la tâche (HTTP ${response.status})`);
      await onTasksChanged();
      setNewTask(emptyNewTaskState(false));
      if (data.task) {
        openTaskActions({ ...data.task, safe_percent: data.task.percent ?? 0, assignees: [], comments_count: 0 });
      }
    } catch (err) {
      setNewTask((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur création tâche' }));
    }
  };

  const saveTaskOrder = async (items: Array<{ id: number; task_order: number; id_jalon: number | null }>) => {
    if (isPreviewMode) return;
    const scrollPosition = window.scrollY;
    setReorderState({ saving: true, message: null, error: null });
    try {
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/reorder`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de sauvegarder l’ordre (HTTP ${response.status})`);
      await onTasksChanged();
      const restoreScroll = () => window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' });
      window.requestAnimationFrame(restoreScroll);
      window.setTimeout(restoreScroll, 50);
      setReorderState({ saving: false, message: 'Ordre sauvegardé.', error: null });
    } catch (err) {
      setReorderState({ saving: false, message: null, error: err instanceof Error ? err.message : 'Erreur de réordonnancement' });
    }
  };

  const getTaskSourceMilestoneId = (task: ProjectTaskItem) => task.isjalon ? null : (task.id_jalon ? Number(task.id_jalon) : null);

  const getTaskDropList = (milestoneId: number | null) => milestoneId === null ? visibleTasks : (orderedChildTasksByMilestone[milestoneId] || []);

  const moveTaskToDropTarget = (movedTask: ProjectTaskItem, destinationMilestoneId: number | null, rawDestinationIndex: number) => {
    lastTaskDragEndAtRef.current = Date.now();

    if (movedTask.isjalon && destinationMilestoneId !== null) return;

    const sourceMilestoneId = getTaskSourceMilestoneId(movedTask);
    const sourceTasks = getTaskDropList(sourceMilestoneId);
    const destinationTasks = sourceMilestoneId === destinationMilestoneId ? sourceTasks : getTaskDropList(destinationMilestoneId);
    const sourceIndex = sourceTasks.findIndex((task) => task.id === movedTask.id);
    if (sourceIndex === -1) return;

    const adjustedDestinationIndex = sourceMilestoneId === destinationMilestoneId && sourceIndex < rawDestinationIndex
      ? rawDestinationIndex - 1
      : rawDestinationIndex;
    if (sourceMilestoneId === destinationMilestoneId && sourceIndex === adjustedDestinationIndex) return;

    if (sourceMilestoneId === destinationMilestoneId) {
      const withoutMoved = sourceTasks.filter((task) => task.id !== movedTask.id);
      const nextTasks = [...withoutMoved];
      nextTasks.splice(Math.max(0, Math.min(adjustedDestinationIndex, nextTasks.length)), 0, movedTask);
      saveTaskOrder(orderedPayload(nextTasks, destinationMilestoneId));
      return;
    }

    const nextSourceTasks = sourceTasks.filter((task) => task.id !== movedTask.id);
    const nextDestinationTasks = [...destinationTasks];
    nextDestinationTasks.splice(Math.max(0, Math.min(rawDestinationIndex, nextDestinationTasks.length)), 0, movedTask);

    saveTaskOrder([
      ...orderedPayload(nextSourceTasks, sourceMilestoneId),
      ...orderedPayload(nextDestinationTasks, destinationMilestoneId),
    ]);
  };

  const handleNativeTaskDragStart = (event: DragEvent<HTMLElement>, task: ProjectTaskItem) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(task.id));
    setNativeTaskDrag({ taskId: task.id, kind: task.isjalon ? 'section' : 'task' });
  };

  const handleNativeTaskDragEnd = () => {
    setNativeTaskDrag(null);
    setTaskDropTarget(null);
    stopTaskAutoScroll();
  };

  const handleTaskDropZoneDragOver = (event: DragEvent<HTMLElement>, milestoneId: number | null, index: number) => {
    if (!nativeTaskDrag) return;
    if (nativeTaskDrag.kind === 'section' && milestoneId !== null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    updateTaskAutoScroll(event.clientY);
    setTaskDropTarget({ milestoneId, index });
  };

  const handleTaskDropZoneDrop = (event: DragEvent<HTMLElement>, milestoneId: number | null, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    const taskId = Number.parseInt(event.dataTransfer.getData('text/plain'), 10);
    const movedTask = (tasksData?.tasks || []).find((task) => task.id === taskId);
    setNativeTaskDrag(null);
    setTaskDropTarget(null);
    stopTaskAutoScroll();
    if (!movedTask) return;
    moveTaskToDropTarget(movedTask, milestoneId, index);
  };

  const shouldIgnorePostDragClick = () => Date.now() - lastTaskDragEndAtRef.current < 350;

  const renderTaskDropZone = (milestoneId: number | null, index: number) => {
    const isDisabled = nativeTaskDrag?.kind === 'section' && milestoneId !== null;
    const isActive = !isDisabled && taskDropTarget?.milestoneId === milestoneId && taskDropTarget.index === index;
    return (
      <div
        onDragOver={(event) => handleTaskDropZoneDragOver(event, milestoneId, index)}
        onDrop={(event) => handleTaskDropZoneDrop(event, milestoneId, index)}
        className={`rounded-lg transition-all ${isDisabled ? 'h-1' : isActive ? 'h-7 border border-blue-200 bg-blue-100' : 'h-2 bg-transparent'}`}
        aria-hidden="true"
      />
    );
  };

  const runTaskAction = async (kind: 'update' | 'block' | 'unblock' | 'assign' | 'reminder') => {
    const task = actionState.selectedTask;
    if (!task) return;

    if (isPreviewMode) {
      setActionState((current) => ({ ...current, message: 'Action simulée en preview. En mode connecté, elle écrira dans PRISME_PROJETNEW.', error: null }));
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setActionState((current) => ({ ...current, saving: true, message: null, error: null }));
      let response: Response;
      if (kind === 'update') {
        if (!actionState.startDate || !actionState.dueDate) {
          throw new Error('Date de début et date de fin obligatoires.');
        }
        const updatePayload: Record<string, string | number | null> = {
          status_text: actionState.statusText,
          priority_text: actionState.priorityText,
          date_debut: actionState.startDate,
          date_echeance: actionState.dueDate,
        };
        if (task.isjalon) updatePayload.percent = actionState.percent;
        response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}`, {
          method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload),
        });
      } else if (kind === 'block' || kind === 'unblock') {
        response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/block`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ blocked: kind === 'block', note: actionState.blockNote }),
        });
      } else if (kind === 'assign') {
        if (!actionState.assigneeId) throw new Error('Choisis un responsable avant de réassigner.');
        response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/assignee`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId: actionState.assigneeId, responsibility: 'R' }),
        });
      } else {
        response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}/reminder-draft`, { method: 'POST', credentials: 'include' });
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Action impossible (HTTP ${response.status})`);

      if (kind === 'reminder') {
        setActionState((current) => ({ ...current, reminderDraft: data.message || '', message: 'Brouillon de relance généré.', saving: false }));
        return;
      }

      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
      setActionState((current) => ({ ...current, saving: false, message: 'Action appliquée.', error: null }));
    } catch (err) {
      setActionState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur inconnue' }));
    }
  };

  const toggleTaskCompletion = async (task: ProjectTaskItem) => {
    if (task.isjalon || completionState.savingId) return;

    const completed = Boolean(task.is_completed);
    const nextStatus = completed ? 'En cours' : 'Terminé';
    const nextPercent = completed ? 1 : 100;
    const optimisticUpdate: Partial<ProjectTaskItem> = {
      status_text: nextStatus,
      percent: nextPercent,
      safe_percent: nextPercent,
      is_completed: !completed,
      urgency_level: !completed ? 'completed' : 'normal',
      is_late: !completed ? false : task.is_late,
      is_at_risk: !completed ? false : task.is_at_risk,
    };

    if (isPreviewMode) {
      onTaskOptimisticUpdate(task.id, optimisticUpdate);
      setCompletionState({ savingId: null, message: null, error: null });
      return;
    }

    try {
      const scrollPosition = captureScrollPosition();
      setCompletionState({ savingId: task.id, message: null, error: null });
      onTaskOptimisticUpdate(task.id, optimisticUpdate);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status_text: nextStatus, percent: nextPercent }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Action impossible (HTTP ${response.status})`);
      await onTasksChanged();
      restoreScrollPosition(scrollPosition);
      setCompletionState({ savingId: null, message: null, error: null });
    } catch (err) {
      onTaskOptimisticUpdate(task.id, task);
      setCompletionState({ savingId: null, message: null, error: err instanceof Error ? err.message : 'Erreur inconnue' });
    }
  };

  const renderTaskCompletionButton = (task: ProjectTaskItem) => {
    if (task.isjalon) return null;

    const completed = Boolean(task.is_completed);
    const saving = completionState.savingId === task.id;
    const Icon = completed ? CheckCircle2 : Circle;
    return (
      <button
        type="button"
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation();
          void toggleTaskCompletion(task);
        }}
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border bg-white shadow-sm transition ${
          completed
            ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
            : 'border-slate-200 text-slate-400 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600'
        } disabled:cursor-default disabled:opacity-100`}
        aria-label={completed ? `Remettre ${task.titre} en cours` : `Marquer ${task.titre} comme terminée`}
        title={completed ? 'Remettre en cours' : 'Marquer terminée'}
      >
        <Icon className={`h-5 w-5 ${saving ? 'animate-pulse' : ''}`} />
      </button>
    );
  };

  useEffect(() => {
    if (actionState.selectedTask) {
      if (!isPreviewMode) {
        loadContacts().catch((err) => setActionState((current) => ({ ...current, error: err instanceof Error ? err.message : 'Erreur contacts' })));
      }
      loadTaskComments(actionState.selectedTask);
      loadTaskTimeEntries(actionState.selectedTask);
    }
    if (newTask.open && !isPreviewMode) {
      loadContacts().catch((err) => setNewTask((current) => ({ ...current, error: err instanceof Error ? err.message : 'Erreur contacts' })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionState.selectedTask?.id, newTask.open, isPreviewMode]);

  const renderTaskTimeTab = (task: ProjectTaskItem) => {
    const totalMinutes = Math.max(0, Number(task.time_spent_minutes || 0));

    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Interventions enregistrées</p>
              <p className="mt-1 text-2xl font-bold text-blue-950">{formatTimeSpentLabel(totalMinutes)}</p>
            </div>
            <Badge tone="blue">{actionState.timeEntries.length} entrée(s)</Badge>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-indigo-700" />
              <p className="text-sm font-semibold text-slate-950">Ajouter une intervention</p>
            </div>
            <Badge tone="slate">Type: Cybersécurité</Badge>
          </div>
          <div className="grid gap-3 md:grid-cols-6">
            <label className="space-y-1 text-xs font-semibold text-slate-600">
              Technicien
              <input value={tasksData?.currentUserEmail || 'Utilisateur connecté'} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-normal text-slate-500" />
            </label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">
              Date
              <input type="date" value={actionState.timeFormDate} onChange={(event) => setActionState((current) => ({ ...current, timeFormDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
            </label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">
              Heure
              <input type="time" value={actionState.timeFormTime} onChange={(event) => setActionState((current) => ({ ...current, timeFormTime: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
            </label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">
              Type
              <select value={actionState.timeFormType} onChange={(event) => setActionState((current) => ({ ...current, timeFormType: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                <option value="Cybersécurité">Cybersécurité</option>
              </select>
            </label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">
              Durée
              <input type="number" min="0.25" step="0.25" inputMode="decimal" value={actionState.timeFormDurationHours} onChange={(event) => setActionState((current) => ({ ...current, timeFormDurationHours: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
            </label>
            <label className="space-y-1 text-xs font-semibold text-slate-600 md:col-span-6">
              Commentaire
              <textarea value={actionState.timeFormComment} onChange={(event) => setActionState((current) => ({ ...current, timeFormComment: event.target.value }))} rows={3} placeholder="Travail effectué, contexte, notes de facturation..." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500">Durée en heures. 0.25 = 15 minutes, 0.5 = 30 minutes, 1 = 1 heure.</p>
            <button type="button" disabled={actionState.saving} onClick={addTaskTimeEntry} className="rounded-lg bg-indigo-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:opacity-60">
              Ajouter l’intervention
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
            <p className="text-sm font-semibold text-slate-950">Liste des interventions</p>
            <button type="button" onClick={() => loadTaskTimeEntries(task)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              Rafraîchir
            </button>
          </div>
          {actionState.timeEntriesLoading ? (
            <div className="px-4 py-3 text-sm text-slate-500">Chargement des entrées de temps...</div>
          ) : actionState.timeEntries.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">Aucun temps saisi pour cette tâche.</div>
          ) : (
            <div className="space-y-3 p-4">
              {actionState.timeEntries.map((entry) => (
                actionState.editingTimeEntryId === entry.id ? (
                  <div key={entry.id} className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{entry.created_by_name || entry.created_by_email || 'Utilisateur'}</p>
                        <p className="truncate text-xs text-slate-500">{entry.created_by_email || 'Courriel non disponible'}</p>
                      </div>
                      <Badge tone="blue">Modification</Badge>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1 text-xs font-semibold text-slate-600">
                        Date
                        <input type="date" value={actionState.timeEditDate} onChange={(event) => setActionState((current) => ({ ...current, timeEditDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-normal text-slate-800" />
                      </label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">
                        Heure
                        <input type="time" value={actionState.timeEditTime} onChange={(event) => setActionState((current) => ({ ...current, timeEditTime: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-normal text-slate-800" />
                      </label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">
                        Type
                        <select value={actionState.timeEditType} onChange={(event) => setActionState((current) => ({ ...current, timeEditType: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-normal text-slate-800">
                          <option value="Cybersécurité">Cybersécurité</option>
                        </select>
                      </label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">
                        Durée
                        <input type="number" min="0.25" step="0.25" inputMode="decimal" value={actionState.timeEditDurationHours} onChange={(event) => setActionState((current) => ({ ...current, timeEditDurationHours: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-800" />
                      </label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600 sm:col-span-2">
                        Commentaire
                        <textarea value={actionState.timeEditComment} onChange={(event) => setActionState((current) => ({ ...current, timeEditComment: event.target.value }))} rows={3} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-normal text-slate-800" />
                      </label>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                      <button type="button" disabled={actionState.saving} onClick={() => saveTimeEntryEdit(entry)} className="rounded-lg bg-indigo-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-800 disabled:opacity-60">Sauver</button>
                      <button type="button" disabled={actionState.saving} onClick={cancelEditingTimeEntry} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Annuler</button>
                      <button type="button" disabled={actionState.saving} onClick={() => deleteTimeEntry(entry)} className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60">Supprimer</button>
                    </div>
                  </div>
                ) : (
                  <div key={entry.id} onDoubleClick={() => startEditingTimeEntry(entry)} title="Double-clique pour modifier ce log de temps" className="cursor-pointer rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50/40">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{entry.created_by_name || entry.created_by_email || 'Utilisateur'}</p>
                        <p className="truncate text-xs text-slate-500">{entry.created_by_email || 'Courriel non disponible'}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone="blue">{entry.entry_type || 'Cybersécurité'}</Badge>
                        <span className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-900">{formatTimeSpentLabel(entry.duration_minutes)}</span>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date</p>
                        <p className="mt-1 text-xs font-semibold text-slate-800">{formatDate(entry.work_date)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Heure</p>
                        <p className="mt-1 text-xs font-semibold text-slate-800">{normalizeTimeInput(entry.work_time)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Durée</p>
                        <p className="mt-1 text-xs font-semibold text-slate-800">{formatTimeSpentLabel(entry.duration_minutes)}</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Commentaire</p>
                      <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">{entry.comment || 'Aucun commentaire'}</p>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button type="button" onClick={() => startEditingTimeEntry(entry)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Modifier</button>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTaskSidePanel = () => {
    const task = actionState.selectedTask;
    if (!task) return null;
    const linkedTaskDeliverables = (deliverablesData?.deliverables || []).filter((deliverable) => Number(deliverable.linked_task_id) === Number(task.id));

    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/20 backdrop-blur-[1px]" onClick={closeTaskActions}>
        <aside
          role="dialog"
          aria-modal="true"
          aria-label={`Modifier ${task.titre}`}
          onClick={(event) => event.stopPropagation()}
          className="flex h-full w-full max-w-2xl flex-col border-l border-slate-200 bg-white shadow-2xl"
        >
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Badge tone={task.isjalon ? 'blue' : 'slate'}>{task.isjalon ? 'Section' : 'Tâche'}</Badge>
                  <Badge tone={getTaskSignal(task).tone}>{getTaskSignal(task).label}</Badge>
                </div>
                {actionState.titleEditing ? (
                  <input
                    autoFocus
                    value={actionState.titleDraft}
                    onChange={(event) => setActionState((current) => ({ ...current, titleDraft: event.target.value }))}
                    onBlur={saveTaskTitle}
                    onKeyDown={handleTaskTitleKeyDown}
                    onFocus={(event) => event.currentTarget.select()}
                    disabled={actionState.saving}
                    aria-label={task.isjalon ? 'Nom de la section' : 'Nom de la tâche'}
                    className="w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-lg font-semibold leading-tight text-slate-950 outline-none ring-2 ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500"
                  />
                ) : (
                  <h5
                    onDoubleClick={startEditingTaskTitle}
                    title={task.isjalon ? 'Double-clique pour renommer la section' : 'Double-clique pour renommer la tâche'}
                    className="cursor-text rounded-lg py-1 text-lg font-semibold leading-tight text-slate-950 hover:bg-slate-50"
                  >
                    {task.titre}
                  </h5>
                )}
                {task.description ? <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{task.description}</p> : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => openDeleteConfirmation(task)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:border-red-300 hover:bg-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Supprimer
                </button>
                <button type="button" onClick={closeTaskActions} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Fermer</button>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {actionState.message ? <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{actionState.message}</div> : null}
            {actionState.error ? <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{actionState.error}</div> : null}

            <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
              <button
                type="button"
                onClick={() => setActionState((current) => ({ ...current, panelMode: 'actions', message: null, error: null }))}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${actionState.panelMode !== 'time' ? 'bg-slate-900 text-white shadow-sm' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                Détails
              </button>
              {!task.isjalon ? (
                <button
                  type="button"
                  onClick={() => setActionState((current) => ({ ...current, panelMode: 'time', message: null, error: null }))}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${actionState.panelMode === 'time' ? 'bg-indigo-700 text-white shadow-sm' : 'border border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:text-indigo-800'}`}
                >
                  <Clock3 className="h-3.5 w-3.5" />
                  Interventions
                </button>
              ) : null}
            </div>

            {actionState.panelMode === 'time' && !task.isjalon ? renderTaskTimeTab(task) : (
            <>
            <div className="mb-4 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 sm:grid-cols-6">
              <div><span className="block font-semibold text-slate-900">Responsable</span>{getResponsibleLabel(task)}</div>
              <div><span className="block font-semibold text-slate-900">Début</span>{task.date_debut ? formatDate(task.date_debut) : 'À préciser'}</div>
              <div><span className="block font-semibold text-slate-900">Fin prévue</span>{task.date_echeance ? formatDate(task.date_echeance) : 'À préciser'}</div>
              <div><span className="block font-semibold text-slate-900">Signal</span>{getTaskSignal(task).label}</div>
              <div><span className="block font-semibold text-slate-900">Temps passé</span>{formatTimeSpentLabel(task.time_spent_minutes)}</div>
              <div><span className="block font-semibold text-slate-900">Commentaires</span>{task.comments_count || 0}</div>
            </div>

            <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-950">Documents et livrables liés</p>
                <Badge tone={linkedTaskDeliverables.length > 0 ? 'blue' : 'slate'}>{linkedTaskDeliverables.length} élément(s)</Badge>
              </div>
              {linkedTaskDeliverables.length === 0 ? (
                <p className="text-xs text-slate-500">Aucun document ou livrable n’est encore relié à cette tâche.</p>
              ) : (
                <div className="space-y-2">
                  {linkedTaskDeliverables.map((deliverable) => {
                    const status = getDeliverableStatusMeta(deliverable.status);
                    const isScanReport = /Rapport de balayage/i.test(deliverable.title || '') || /Gestion documentaire #/i.test(deliverable.internal_notes || '');
                    return (
                      <div key={deliverable.id} className="rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-slate-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge tone={status.tone}>{status.label}</Badge>
                          {isScanReport ? <Badge tone="blue">Rapport de balayage</Badge> : null}
                          <span className="font-semibold text-slate-950">{deliverable.title}</span>
                        </div>
                        {deliverable.internal_notes ? <p className="mt-1 whitespace-pre-wrap text-[11px] leading-relaxed text-slate-500">{deliverable.internal_notes}</p> : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-blue-950">Suggestions liées</p>
                <Badge tone="blue">{agentSuggestions.filter((suggestion) => (suggestion.evidence || []).some((item) => item.kind === 'tache' && Number(item.id) === task.id)).length}</Badge>
              </div>
              <div className="space-y-2">
                {agentSuggestions.filter((suggestion) => (suggestion.evidence || []).some((item) => item.kind === 'tache' && Number(item.id) === task.id)).length === 0 ? (
                  <div className="rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs text-blue-800">Aucune suggestion active liée à cette tâche.</div>
                ) : agentSuggestions
                  .filter((suggestion) => (suggestion.evidence || []).some((item) => item.kind === 'tache' && Number(item.id) === task.id))
                  .map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      compact
                      saving={agentSavingId === suggestion.id}
                      onDecide={decideProjectSuggestion}
                      onUndo={undoProjectSuggestion}
                    />
                  ))}
              </div>
            </div>

            <div className={`grid gap-3 ${task.isjalon ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Statut
                <select value={actionState.statusText} onChange={(event) => setActionState((current) => ({ ...current, statusText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                  {['À faire', 'En cours', 'À risque', 'Bloqué', 'Terminé'].map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Priorité
                <select value={actionState.priorityText} onChange={(event) => setActionState((current) => ({ ...current, priorityText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                  {['Basse', 'Moyenne', 'Haute', 'Critique'].map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                </select>
              </label>
              {task.isjalon ? (
                <label className="space-y-1 text-xs font-semibold text-slate-600">
                  Progression de section
                  <input type="number" min="0" max="100" value={actionState.percent} onChange={(event) => setActionState((current) => ({ ...current, percent: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
                </label>
              ) : null}
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Date de début
                <input type="date" value={actionState.startDate} onChange={(event) => setActionState((current) => ({ ...current, startDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Date de fin
                <input type="date" value={actionState.dueDate} onChange={(event) => setActionState((current) => ({ ...current, dueDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={actionState.saving} onClick={() => runTaskAction('update')} className="rounded-lg bg-indigo-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:opacity-60">Mettre à jour</button>
              <button type="button" disabled={actionState.saving} onClick={() => runTaskAction(task.is_blocked ? 'unblock' : 'block')} className="rounded-lg bg-rose-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rose-800 disabled:opacity-60">{task.is_blocked ? 'Lever le blocage' : 'Marquer bloquée'}</button>
              <button type="button" disabled={actionState.saving} onClick={() => runTaskAction('reminder')} className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-800 hover:bg-indigo-50 disabled:opacity-60">Préparer relance</button>
              <button
                type="button"
                disabled={actionState.saving}
                onClick={() => openDeleteConfirmation(task)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:border-red-300 hover:bg-red-100 disabled:opacity-60"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Note de blocage / déblocage
                <textarea value={actionState.blockNote} onChange={(event) => setActionState((current) => ({ ...current, blockNote: event.target.value }))} rows={3} placeholder="Cause du blocage, prochain pas, dépendance à lever..." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
              <div className="space-y-1 text-xs font-semibold text-slate-600">
                Réassigner responsable RACI-R
                <div className="flex gap-2">
                  <select value={actionState.assigneeId} onChange={(event) => setActionState((current) => ({ ...current, assigneeId: event.target.value }))} className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                    <option value="">Choisir un contact</option>
                    {contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}{contact.email_primary ? ` — ${contact.email_primary}` : ''}</option>)}
                  </select>
                  <button type="button" disabled={actionState.saving || (!isPreviewMode && contacts.length === 0)} onClick={() => runTaskAction('assign')} className="rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-60">Réassigner</button>
                </div>
                <p className="text-[11px] font-normal text-slate-500">La réassignation remplace le responsable R existant pour cette tâche.</p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-indigo-700" />
                  <p className="text-xs font-semibold text-slate-900">Commentaires de la tâche</p>
                  <Badge tone="slate">{actionState.comments.length}</Badge>
                </div>
                <button type="button" onClick={() => loadTaskComments(task)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  Rafraîchir
                </button>
              </div>
              <div className="space-y-2">
                {actionState.commentsLoading ? (
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Chargement des commentaires…</div>
                ) : actionState.comments.length === 0 ? (
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Aucun commentaire pour cette tâche. Tu peux être le premier à en ajouter un.</div>
                ) : actionState.comments.map((comment) => (
                  <div key={comment.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800">{comment.nom_client || comment.email_primary || 'Utilisateur'}</p>
                      <p className="text-[11px] text-slate-500">{formatDate(comment.date_commentaire || comment.created_at)}</p>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{comment.commentaire}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <textarea value={actionState.newComment} onChange={(event) => setActionState((current) => ({ ...current, newComment: event.target.value }))} rows={2} placeholder="Ajouter un commentaire à cette tâche..." className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800" />
                <button type="button" disabled={actionState.saving} onClick={addTaskComment} className="rounded-lg bg-indigo-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:opacity-60">
                  Ajouter
                </button>
              </div>
            </div>

            {actionState.reminderDraft ? (
              <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-indigo-950">Brouillon de relance</p>
                  <button type="button" onClick={() => navigator.clipboard?.writeText(actionState.reminderDraft)} className="rounded-lg border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-800 hover:bg-indigo-50">Copier</button>
                </div>
                <pre className="whitespace-pre-wrap text-xs text-slate-700">{actionState.reminderDraft}</pre>
              </div>
            ) : null}
            </>
            )}
          </div>
        </aside>
      </div>
    );
  };

  const renderDeleteConfirmation = () => {
    if (!deleteConfirm) return null;
    const task = deleteConfirm.task;
    const linkedTasksCount = task.isjalon ? (orderedChildTasksByMilestone[task.id]?.length || 0) : 0;

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 px-4">
        <div role="dialog" aria-modal="true" aria-label="Confirmer la suppression" className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-6 shadow-2xl">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-700">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h5 className="text-base font-bold text-slate-950">Supprimer {task.isjalon ? 'cette section' : 'cette tâche'}?</h5>
              <p className="mt-1 text-sm text-slate-600">
                Cette action retirera définitivement <span className="font-semibold text-slate-900">{task.titre}</span>.
              </p>
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
            {task.isjalon
              ? `${linkedTasksCount} tâche(s) liée(s) seront conservée(s), mais détachée(s) de cette section.`
              : 'Les commentaires et assignations liés à cette tâche seront supprimés avec elle.'}
          </div>
          {deleteConfirm.error ? (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{deleteConfirm.error}</div>
          ) : null}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              disabled={deleteConfirm.saving}
              onClick={() => setDeleteConfirm(null)}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={deleteConfirm.saving}
              onClick={confirmDeleteTask}
              className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {deleteConfirm.saving ? 'Suppression...' : 'Confirmer la suppression'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <OverviewSkeleton />;
  }

  if (error) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }

  if (!tasksData) {
    return <EmptyState>Les tâches ne sont pas encore chargées pour ce projet.</EmptyState>;
  }

  const { summary } = tasksData;
  const sectionOptions = tasksData.tasks.filter((task) => task.isjalon);
  const tasksMissingRequiredDates = tasksData.tasks.filter((task) => !task.is_completed && !taskHasRequiredDates(task));
  const summaryTiles: Array<{ filter: TaskFilter; label: string; value: number; tone: 'slate' | 'emerald' | 'rose' | 'amber' }> = [
    { filter: 'all', label: 'Total', value: summary.total, tone: 'slate' },
    { filter: 'completed', label: 'Terminées', value: summary.completed, tone: 'emerald' },
    { filter: 'late', label: 'En retard', value: summary.late, tone: 'rose' },
    { filter: 'at_risk', label: 'À risque', value: summary.atRisk, tone: 'amber' },
    { filter: 'blocked', label: 'Bloquées', value: summary.blocked, tone: 'rose' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Tâches du projet</h4>
          <p className="text-xs text-slate-500">Vue commune de toutes les tâches, commentaires et actions intégrées aux cartes de tâches.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="green">Tâches actives</Badge>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {summaryTiles.map((tile) => {
          const isActive = activeFilter === tile.filter;
          const baseClasses = {
            slate: 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:bg-slate-100',
            emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-100',
            rose: 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-500 hover:bg-rose-100',
            amber: 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-500 hover:bg-amber-100',
          }[tile.tone];
          const valueClasses = {
            slate: 'text-slate-900',
            emerald: 'text-emerald-800',
            rose: 'text-rose-800',
            amber: 'text-amber-800',
          }[tile.tone];
          return (
            <button
              key={tile.filter}
              type="button"
              onClick={() => selectFilter(tile.filter)}
              aria-pressed={isActive}
              className={`rounded-xl border p-4 text-left shadow-sm transition ${baseClasses} ${isActive ? 'ring-2 ring-blue-200 ring-offset-1' : ''}`}
            >
              <p className={`text-xs font-semibold uppercase tracking-wide ${isActive ? 'text-blue-800' : ''}`}>{tile.label}</p>
              <p className={`mt-2 text-2xl font-semibold ${isActive ? 'text-blue-900' : valueClasses}`}>{tile.value}</p>
            </button>
          );
        })}
      </div>

      {tasksMissingRequiredDates.length > 0 ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">{tasksMissingRequiredDates.length} tâche(s) existante(s) ont une date de début ou de fin à compléter.</span>
            <button type="button" onClick={() => selectFilter('unscheduled')} className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100">
              Voir à corriger
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h5 className="text-sm font-semibold text-blue-950">Suggestions</h5>
            <p className="mt-1 text-xs text-blue-800">Règles déterministes du moteur agentique. Les actions ouvrent le bon endroit, sans écriture automatique.</p>
          </div>
          <div className="w-full space-y-2 xl:max-w-4xl">
            {agentMessage ? <div className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700">{agentMessage}</div> : null}
            {agentError ? <div className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700">{agentError}</div> : null}
            {agentSuggestions.length === 0 ? (
              <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-800 shadow-sm">Aucune suggestion active détectée</div>
            ) : agentSuggestions.slice(0, 4).map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                compact
                saving={agentSavingId === suggestion.id}
                onDecide={decideProjectSuggestion}
                onUndo={undoProjectSuggestion}
                onOpenEvidence={openSuggestionEvidence}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-300 bg-slate-50 px-4 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h5 className="text-sm font-semibold text-slate-900">Créer dans le projet</h5>
            <p className="mt-1 text-xs text-slate-500">Une section regroupe les tâches; une tâche est un élément d’exécution rattachable à une section.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setNewTask({ ...emptyNewTaskState(false), open: true })} className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800">
              Nouvelle tâche
            </button>
            <button type="button" onClick={() => setNewTask({ ...emptyNewTaskState(true), open: true })} className="rounded-lg border border-blue-500 bg-blue-100 px-4 py-2 text-xs font-semibold text-blue-900 shadow-sm hover:bg-blue-200">
              Nouvelle section
            </button>
            {newTask.open ? <button type="button" onClick={() => setNewTask(emptyNewTaskState(false))} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100">Fermer</button> : null}
          </div>
        </div>
        {newTask.open ? (
          <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            {newTask.error ? <div className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700">{newTask.error}</div> : null}
            <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {newTask.isJalon ? 'Nom de la section' : 'Titre de la tâche'}
                <input value={newTask.titre} onChange={(event) => setNewTask((current) => ({ ...current, titre: event.target.value }))} placeholder={newTask.isJalon ? 'Ex. Avril - Gouvernance – gestion' : 'Ex. Confirmer la date de migration'} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Statut
                <select value={newTask.statusText} onChange={(event) => setNewTask((current) => ({ ...current, statusText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                  {['À faire', 'En cours', 'À risque', 'Bloqué', 'Terminé'].map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Priorité
                <select value={newTask.priorityText} onChange={(event) => setNewTask((current) => ({ ...current, priorityText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                  {['Basse', 'Moyenne', 'Haute', 'Critique'].map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                </select>
              </label>
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Description
                <textarea value={newTask.description} onChange={(event) => setNewTask((current) => ({ ...current, description: event.target.value }))} rows={2} placeholder={newTask.isJalon ? 'Objectif ou portée de cette section...' : 'Contexte, résultat attendu, dépendance...'} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Date de début
                <input type="date" value={newTask.startDate} onChange={(event) => setNewTask((current) => ({ ...current, startDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Date de fin
                <input type="date" value={newTask.dueDate} onChange={(event) => setNewTask((current) => ({ ...current, dueDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" />
              </label>
              {!newTask.isJalon ? (
                <label className="space-y-1 text-xs font-semibold text-slate-600">
                  Section
                  <select value={newTask.milestoneId} onChange={(event) => setNewTask((current) => ({ ...current, milestoneId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                    <option value="">Sans section</option>
                    {sectionOptions.map((section) => <option key={section.id} value={section.id}>{section.titre}</option>)}
                  </select>
                </label>
              ) : null}
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                Responsable R
                <select value={newTask.assigneeId} onChange={(event) => setNewTask((current) => ({ ...current, assigneeId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                  <option value="">À assigner plus tard</option>
                  {contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}{contact.email_primary ? ` — ${contact.email_primary}` : ''}</option>)}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs font-semibold text-slate-600">
                Type : <span className={newTask.isJalon ? 'text-blue-700' : 'text-slate-900'}>{newTask.isJalon ? 'Section de regroupement' : 'Tâche d’exécution'}</span>
              </div>
              <button type="button" disabled={newTask.saving} onClick={createTask} className="rounded-lg bg-emerald-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60">
                {newTask.isJalon ? 'Créer la section' : 'Créer la tâche'}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-3">
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher une tâche..." className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
      </div>

      {tasksData.tasks.length === 0 ? (
        <EmptyState>Aucune tâche pour ce projet. La prochaine action est de structurer les premières tâches.</EmptyState>
      ) : visibleTasks.length === 0 ? (
        <EmptyState>Aucune tâche ne correspond au filtre ou à la recherche.</EmptyState>
      ) : (
        <div className="space-y-3">
          {reorderState.message ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{reorderState.message}</div> : null}
          {reorderState.error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{reorderState.error}</div> : null}
          {completionState.message ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{completionState.message}</div> : null}
          {completionState.error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{completionState.error}</div> : null}
          <div className="space-y-1">
          {visibleTasks.map((task, index) => {
            const signal = getTaskSignal(task);
            const isSectionCollapsed = task.isjalon && collapsedSectionIds.includes(task.id);
            return (
              <div key={task.id}>
                {renderTaskDropZone(null, index)}
              <div
                id={`project-task-${task.id}`}
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (shouldIgnorePostDragClick()) return;
                  if (target.closest('button, input, select, textarea, a')) return;
                  openTaskActions(task);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openTaskActions(task);
                  }
                }}
                className={`${task.isjalon ? 'rounded-2xl border border-blue-300 border-l-4 border-l-blue-700 bg-blue-50 px-4 py-4 shadow-md' : 'rounded-lg border border-slate-300 border-l-4 border-l-slate-400 bg-white px-3 py-2 shadow-sm'} transition ${actionState.selectedTask?.id === task.id ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'hover:border-blue-400 hover:bg-blue-50/70'} ${nativeTaskDrag?.taskId === task.id ? 'opacity-60 ring-2 ring-blue-200' : ''}`}
              >
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <button
                        type="button"
                        draggable={!reorderState.saving}
                        onDragStart={(event) => handleNativeTaskDragStart(event, task)}
                        onDragEnd={handleNativeTaskDragEnd}
                        onClick={(event) => event.stopPropagation()}
                        className="inline-flex h-7 w-7 shrink-0 cursor-grab items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition hover:bg-slate-50 hover:text-slate-700 active:cursor-grabbing"
                        aria-label={`Déplacer ${task.titre}`}
                        title="Glisser pour déplacer"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      {task.isjalon ? <Badge tone="blue">Section</Badge> : null}
                      <Badge tone={signal.tone}>{signal.label}</Badge>
                      <h5 className={`min-w-[220px] flex-1 truncate ${task.isjalon ? 'text-lg font-black tracking-tight text-slate-950 md:text-xl' : 'text-sm font-semibold text-slate-900'}`}>{task.titre}</h5>
                    </div>
                    <p className="mt-1 line-clamp-1 text-xs text-slate-500">{getTaskSecondaryLine(task)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:justify-end">
                    <Badge tone="slate">{task.status_text || 'Statut n/d'}</Badge>
                    <Badge tone={(task.priority_text || '').toLowerCase().includes('critique') ? 'red' : (task.priority_text || '').toLowerCase().includes('haute') ? 'amber' : 'slate'}>{task.priority_text || 'Priorité n/d'}</Badge>
                    <Badge tone={taskHasRequiredDates(task) ? 'blue' : 'amber'}>{getTaskDateRangeLabel(task)}</Badge>
                    <Badge tone={task.assignees && task.assignees.length > 0 ? 'green' : 'slate'}>{task.assignees && task.assignees.length > 0 ? task.assignees.map((assignee) => assignee.nom_client || 'Assigné').slice(0, 1).join(', ') : 'Sans responsable'}</Badge>
                    <Badge tone="slate">{task.comments_count || 0} comm.</Badge>
                    {renderTaskCompletionButton(task)}
                    {task.isjalon ? <span className="w-12 text-right text-xs font-semibold text-slate-700">{getTaskDisplayProgress(task)}%</span> : null}
                  </div>
                </div>
                {task.isjalon ? (
                  <div className="mt-4 overflow-hidden rounded-xl border border-blue-200 border-l-4 border-l-blue-500 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 bg-blue-100 px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSectionCollapsed(task.id);
                          }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-blue-300 bg-white text-blue-700 hover:bg-blue-50"
                          aria-label={isSectionCollapsed ? 'Ouvrir la section' : 'Fermer la section'}
                          title={isSectionCollapsed ? 'Ouvrir la section' : 'Fermer la section'}
                        >
                          <ChevronRight className={`h-4 w-4 transition-transform ${isSectionCollapsed ? '' : 'rotate-90'}`} />
                        </button>
                        <ListChecks className="h-4 w-4 text-blue-600" />
                        <p className="text-xs font-bold uppercase tracking-wide text-blue-900">Tâches sous cette section</p>
                      </div>
                      <Badge tone="blue">{orderedChildTasksByMilestone[task.id]?.length || 0} tâche(s)</Badge>
                    </div>
                    {isSectionCollapsed ? (
                      <div className="bg-slate-50 px-4 py-3 text-xs font-medium text-slate-500">
                        Section fermée — {orderedChildTasksByMilestone[task.id]?.length || 0} tâche(s) masquée(s).
                      </div>
                    ) : (
                    <div
                      onDragOver={(event) => {
                        if ((orderedChildTasksByMilestone[task.id] || []).length === 0) {
                          handleTaskDropZoneDragOver(event, task.id, 0);
                        }
                      }}
                      onDrop={(event) => {
                        if ((orderedChildTasksByMilestone[task.id] || []).length === 0) {
                          handleTaskDropZoneDrop(event, task.id, 0);
                        }
                      }}
                      className={`min-h-[72px] space-y-1 p-1 transition ${taskDropTarget?.milestoneId === task.id ? 'bg-blue-100 ring-2 ring-blue-200' : 'bg-blue-50/40'}`}
                    >
                      {(orderedChildTasksByMilestone[task.id] || []).map((childTask, childIndex) => {
                        const childSignal = getTaskSignal(childTask);
                        return (
                          <div key={childTask.id}>
                            {renderTaskDropZone(task.id, childIndex)}
                          <div
                            id={`project-task-${childTask.id}`}
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              const target = event.target as HTMLElement;
                              if (shouldIgnorePostDragClick()) return;
                              if (target.closest('button, input, select, textarea, a')) return;
                              openTaskActions(childTask);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openTaskActions(childTask);
                              }
                            }}
                            className={`grid w-full gap-2 rounded-lg border border-l-4 px-4 py-3 text-left shadow-sm transition hover:border-blue-400 hover:bg-blue-50 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center ${actionState.selectedTask?.id === childTask.id ? 'border-indigo-500 border-l-indigo-600 bg-indigo-50 ring-2 ring-indigo-100' : 'border-slate-200 border-l-slate-300 bg-white'} ${nativeTaskDrag?.taskId === childTask.id ? 'opacity-60 ring-2 ring-blue-200' : ''}`}
                          >
                            <button
                              type="button"
                              draggable={!reorderState.saving}
                              onDragStart={(event) => handleNativeTaskDragStart(event, childTask)}
                              onDragEnd={handleNativeTaskDragEnd}
                              onClick={(event) => event.stopPropagation()}
                              className="inline-flex h-8 w-8 cursor-grab items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition hover:bg-slate-50 hover:text-slate-700 active:cursor-grabbing"
                              aria-label={`Déplacer ${childTask.titre}`}
                              title="Glisser pour déplacer"
                            >
                              <GripVertical className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (shouldIgnorePostDragClick()) return;
                                openTaskActions(childTask);
                              }}
                              className="min-w-0 text-left"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-900 md:text-[15px]">{childTask.titre}</p>
                                <p className="mt-1 truncate text-xs text-slate-500">{getTaskSecondaryLine(childTask)}</p>
                              </div>
                            </button>
                              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                                <Badge tone={childSignal.tone}>{childSignal.label}</Badge>
                                {childTask.priority_text ? <Badge tone={(childTask.priority_text || '').toLowerCase().includes('critique') ? 'red' : (childTask.priority_text || '').toLowerCase().includes('haute') ? 'amber' : 'slate'}>{childTask.priority_text}</Badge> : null}
                                <Badge tone={childTask.assignees && childTask.assignees.length > 0 ? 'green' : 'slate'}>{childTask.assignees && childTask.assignees.length > 0 ? childTask.assignees.map((assignee) => assignee.nom_client || 'Assigné').slice(0, 1).join(', ') : 'Sans responsable'}</Badge>
                                {renderTaskCompletionButton(childTask)}
                              </div>
                          </div>
                          </div>
                        );
                      })}
                      {renderTaskDropZone(task.id, orderedChildTasksByMilestone[task.id]?.length || 0)}
                    </div>
                    )}
                  </div>
                ) : null}
              </div>
              </div>
            );
          })}
          {renderTaskDropZone(null, visibleTasks.length)}
          </div>
        </div>
      )}
      {renderTaskSidePanel()}
      {renderDeleteConfirmation()}
    </div>
  );
}

const DAY_MS = 86400000;

const parseGanttDate = (value?: string | null) => {
  if (!value) return null;
  const dateOnly = String(value).slice(0, 10);
  const [year, month, day] = dateOnly.split('-').map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const addGanttDays = (date: Date, days: number) => new Date(date.getTime() + days * DAY_MS);

const diffGanttDays = (start: Date, end: Date) => Math.max(0, Math.round((end.getTime() - start.getTime()) / DAY_MS));

const getTaskGanttStart = (task: ProjectTaskItem) => parseGanttDate(task.date_debut);

const getTaskGanttEnd = (task: ProjectTaskItem) => parseGanttDate(task.date_echeance);

function ProjectTasksGantt({ tasksData, loading, error, onOpenTask }: { tasksData: ProjectTasksResponse | null; loading: boolean; error: string | null; onOpenTask?: (task: ProjectTaskItem) => void }) {
  const [mode, setMode] = useState<ProjectTaskGanttMode>('tasks');

  if (loading) {
    return <div className="space-y-3">{[...Array(4)].map((_, index) => <div key={index} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }
  if (error) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }
  if (!tasksData) {
    return <EmptyState>Les tâches ne sont pas encore chargées.</EmptyState>;
  }

  const taskRows = sortTasksByManualOrder(tasksData.tasks.filter((task) => mode === 'sections' ? Boolean(task.isjalon) : !task.isjalon));
  const childTasksBySection = tasksData.tasks
    .filter((task) => !task.isjalon && task.id_jalon)
    .reduce<Record<number, ProjectTaskItem[]>>((groups, task) => {
      const sectionId = Number(task.id_jalon);
      groups[sectionId] = groups[sectionId] || [];
      groups[sectionId].push(task);
      return groups;
    }, {});

  const rows = taskRows.map((task) => {
    const children = task.isjalon ? childTasksBySection[task.id] || [] : [];
    const rowSource = task.isjalon && children.length > 0 ? children : [task];
    const dates = rowSource.flatMap((item) => {
      const end = getTaskGanttEnd(item);
      const start = getTaskGanttStart(item);
      if (!start || !end) return [];
      return [start, end].filter((date): date is Date => Boolean(date));
    });
    const start = dates.length > 0 ? new Date(Math.min(...dates.map((date) => date.getTime()))) : getTaskGanttStart(task);
    const end = dates.length > 0 ? new Date(Math.max(...dates.map((date) => date.getTime()))) : getTaskGanttEnd(task);
    const progress = task.isjalon && children.length > 0
      ? Math.round(children.reduce((sum, child) => sum + getTaskEffectiveProgress(child), 0) / children.length)
      : getTaskEffectiveProgress(task);
    return { task, children, start, end, progress };
  }).filter((row) => row.start && row.end) as Array<{ task: ProjectTaskItem; children: ProjectTaskItem[]; start: Date; end: Date; progress: number }>;

  const unscheduledCount = taskRows.length - rows.length;
  const minStart = rows.length > 0 ? new Date(Math.min(...rows.map((row) => row.start.getTime()))) : null;
  const maxEnd = rows.length > 0 ? new Date(Math.max(...rows.map((row) => row.end.getTime()))) : null;
  const rangeStart = minStart ? addGanttDays(minStart, -1) : null;
  const rangeEnd = maxEnd ? addGanttDays(maxEnd, 1) : null;
  const totalDays = rangeStart && rangeEnd ? Math.max(1, diffGanttDays(rangeStart, rangeEnd)) : 1;
  const monthMarkers = rangeStart && rangeEnd
    ? Array.from({ length: totalDays + 1 }, (_, index) => addGanttDays(rangeStart, index))
        .filter((date, index) => index === 0 || date.getUTCDate() === 1 || index === totalDays)
    : [];
  const today = parseGanttDate(new Date().toISOString());
  const todayOffset = today && rangeStart && rangeEnd && today >= rangeStart && today <= rangeEnd
    ? (diffGanttDays(rangeStart, today) / totalDays) * 100
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">Gantt du projet</h4>
          <p className="mt-1 text-xs text-slate-500">Lecture visuelle des débuts, fins, échéances et progression.</p>
        </div>
        <div className="inline-flex rounded-xl border border-slate-300 bg-slate-100 p-1 shadow-inner">
          {[
            { key: 'tasks' as const, label: 'Tâches' },
            { key: 'sections' as const, label: 'Sections' },
          ].map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setMode(option.key)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${mode === option.key ? 'bg-blue-700 text-white shadow-sm' : 'text-slate-700 hover:bg-white hover:text-blue-800'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState>{`Aucune ${mode === 'sections' ? 'section' : 'tâche'} avec date exploitable pour bâtir le Gantt.`}</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-300 bg-white shadow-sm">
          <div className="min-w-[980px]">
            <div className="grid grid-cols-[280px_minmax(620px,1fr)] border-b border-slate-200 bg-slate-50">
              <div className="border-r border-slate-200 px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">{mode === 'sections' ? 'Section' : 'Tâche'}</div>
              <div className="relative px-4 py-3">
                <div className="flex justify-between text-xs font-semibold text-slate-500">
                  <span>{rangeStart ? formatDate(rangeStart.toISOString()) : 'Début'}</span>
                  <span>{rangeEnd ? formatDate(rangeEnd.toISOString()) : 'Fin'}</span>
                </div>
                <div className="relative mt-2 h-5">
                  {monthMarkers.map((date) => {
                    const left = rangeStart ? (diffGanttDays(rangeStart, date) / totalDays) * 100 : 0;
                    return (
                      <div key={date.toISOString()} className="absolute top-0 -translate-x-1/2 text-[11px] font-semibold text-slate-400" style={{ left: `${Math.max(0, Math.min(100, left))}%` }}>
                        {date.toLocaleDateString('fr-CA', { month: 'short', timeZone: 'UTC' })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {rows.map(({ task, children, start, end, progress }) => {
                const signal = getTaskSignal(task);
                const left = (diffGanttDays(rangeStart!, start) / totalDays) * 100;
                const width = Math.max(2, (Math.max(1, diffGanttDays(start, end) + 1) / totalDays) * 100);
                const progressWidth = Math.max(0, Math.min(100, progress));
                const clampedLeft = Math.max(0, Math.min(100, left));
                const clampedWidth = Math.min(100 - clampedLeft, width);
                const isSectionRow = task.isjalon;
                const visibleProgressWidth = progressWidth > 0 ? progressWidth : 3;
                return (
                  <div key={task.id} className={`grid grid-cols-[280px_minmax(620px,1fr)] transition-colors ${isSectionRow ? 'bg-blue-50/70 hover:bg-blue-100/70' : 'hover:bg-blue-50/50'}`}>
                    <div className={`min-w-0 border-r px-4 py-3 ${isSectionRow ? 'border-blue-200 border-l-4 border-l-blue-500 bg-white/70' : 'border-slate-100'}`}>
                      <div className="flex items-center gap-2">
                        <Badge tone={isSectionRow ? 'blue' : signal.tone}>{isSectionRow ? 'Phase / Jalon' : signal.label}</Badge>
                        {mode === 'sections' ? <Badge tone="slate">{children.length} tâche(s)</Badge> : null}
                      </div>
                      <button type="button" onClick={() => onOpenTask?.(task)} className={`mt-2 block max-w-full truncate text-left hover:text-blue-800 ${isSectionRow ? 'text-base font-extrabold text-blue-950' : 'text-sm font-semibold text-slate-950'}`}>
                        {task.titre}
                      </button>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{formatDate(start.toISOString())} → {formatDate(end.toISOString())}</span>
                        {isSectionRow ? <span className="font-semibold text-blue-700">{progress}% complété</span> : null}
                      </div>
                    </div>
                    <div className={`relative px-4 ${isSectionRow ? 'py-5' : 'py-4'}`}>
                      {todayOffset !== null ? <div className="absolute inset-y-0 w-px bg-rose-400/70" style={{ left: `calc(${todayOffset}% + 1rem)` }} /> : null}
                      <div className={`relative rounded-lg ${isSectionRow ? 'h-12 border border-blue-200 bg-blue-950/5 shadow-inner' : 'h-9 bg-slate-100'}`}>
                        <div
                          className={`absolute top-1/2 -translate-y-1/2 overflow-visible rounded-full shadow-sm ${isSectionRow ? 'h-8 bg-blue-700 ring-2 ring-blue-200 ring-offset-1 ring-offset-white' : signal.tone === 'red' ? 'h-5 bg-rose-600' : signal.tone === 'amber' ? 'h-5 bg-amber-500' : 'h-5 bg-emerald-600'}`}
                          style={{ left: `${clampedLeft}%`, width: `${clampedWidth}%` }}
                          title={`${task.titre} · ${formatDate(start.toISOString())} à ${formatDate(end.toISOString())}`}
                        >
                          <div className={`h-full rounded-full ${isSectionRow ? 'bg-cyan-300/90' : 'bg-white/30'}`} style={{ width: `${visibleProgressWidth}%` }} />
                          {isSectionRow ? (
                            <span className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-extrabold text-blue-800 shadow-sm">
                              {progress}%
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                        <span>{getResponsibleLabel(task)}</span>
                        <span className={`font-semibold ${isSectionRow ? 'text-blue-800' : 'text-slate-700'}`}>{progress}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {unscheduledCount > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
          {unscheduledCount} {mode === 'sections' ? 'section(s)' : 'tâche(s)'} sans date exploitable sont masquées dans le Gantt.
        </div>
      ) : null}
    </div>
  );
}

function ProjectTasksKanban({ tasksData, loading, error, onOpenTask }: { tasksData: ProjectTasksResponse | null; loading: boolean; error: string | null; onOpenTask?: (task: ProjectTaskItem) => void }) {
  if (loading) {
    return <div className="space-y-3">{[...Array(3)].map((_, index) => <div key={index} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }
  if (error) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }
  if (!tasksData) {
    return <EmptyState>Les tâches ne sont pas encore chargées.</EmptyState>;
  }

  const taskItems = sortTasksByManualOrder(tasksData.tasks.filter((task) => !task.isjalon));
  const columns = [
    {
      key: 'todo',
      title: 'À faire',
      tone: 'slate' as const,
      tasks: taskItems.filter((task) => !task.is_completed && !task.is_blocked && !task.is_late && !task.is_at_risk && getTaskEffectiveProgress(task) === 0),
    },
    {
      key: 'active',
      title: 'En cours',
      tone: 'blue' as const,
      tasks: taskItems.filter((task) => !task.is_completed && !task.is_blocked && !task.is_late && !task.is_at_risk && getTaskEffectiveProgress(task) > 0),
    },
    {
      key: 'attention',
      title: 'À surveiller',
      tone: 'amber' as const,
      tasks: taskItems.filter((task) => !task.is_completed && (task.is_blocked || task.is_late || task.is_at_risk)),
    },
    {
      key: 'done',
      title: 'Terminé',
      tone: 'green' as const,
      tasks: taskItems.filter((task) => task.is_completed),
    },
  ];

  return (
    <div className="grid gap-3 xl:grid-cols-4">
      {columns.map((column) => (
        <div key={column.key} className="min-w-0 rounded-xl border border-slate-300 bg-slate-100 p-3 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-slate-900">{column.title}</h4>
            <Badge tone={column.tone}>{column.tasks.length}</Badge>
          </div>
          <div className="space-y-2">
            {column.tasks.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-center text-xs text-slate-500">Aucune tâche</div>
            ) : column.tasks.map((task) => {
              const progress = getTaskEffectiveProgress(task);
              return (
                <article key={task.id} role="button" tabIndex={0} onClick={() => onOpenTask?.(task)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenTask?.(task); } }} className="cursor-pointer rounded-lg border border-slate-300 border-l-4 border-l-slate-400 bg-white p-3 shadow-sm hover:border-blue-400 hover:bg-blue-50/60">
                  <div className="flex items-start justify-between gap-2">
                    <h5 className="min-w-0 text-sm font-semibold text-slate-900">{task.titre}</h5>
                    {task.is_blocked ? <Badge tone="red">Bloquée</Badge> : task.is_late ? <Badge tone="red">Retard</Badge> : task.is_at_risk ? <Badge tone="amber">Risque</Badge> : null}
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                    <span>{getResponsibleLabel(task)}</span>
                    <span>{formatDate(task.date_echeance)}</span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function myTaskMatchesFilter(task: MyTaskItem, filter: MyTaskQuickFilter) {
  if (filter === 'active') return !task.is_completed;
  if (filter === 'today') return !task.is_completed && task.days_until_due === 0;
  if (filter === 'week') return !task.is_completed && typeof task.days_until_due === 'number' && task.days_until_due >= 0 && task.days_until_due <= 7;
  if (filter === 'late') return Boolean(task.is_late);
  if (filter === 'blocked') return Boolean(task.is_blocked);
  if (filter === 'unscheduled') return !task.date_echeance && !task.is_completed;
  if (filter === 'completed') return Boolean(task.is_completed);
  return true;
}

function getMyTaskSection(task: MyTaskItem) {
  if (!task.is_completed && !task.date_echeance) return 'unscheduled';
  if (!task.is_completed && typeof task.days_until_due === 'number' && task.days_until_due > 7 && !task.is_blocked && !task.is_late && !task.is_at_risk) return 'later';
  return 'priority';
}

function getMyTaskSectionMeta(section: ReturnType<typeof getMyTaskSection>) {
  if (section === 'later') {
    return {
      title: 'Plus tard',
      description: 'Tâches actives prévues après les 7 prochains jours, en ordre de date de fin.',
    };
  }
  if (section === 'unscheduled') {
    return {
      title: 'Sans échéance',
      description: 'Tâches actives à planifier parce qu’elles n’ont pas encore de date de fin.',
    };
  }
  return null;
}

function ProjectMyTasksSection({
  data,
  loading,
  error,
  onReload,
  onOpenProject,
  isPreviewMode,
}: {
  data: MyTasksResponse | null;
  loading: boolean;
  error: string | null;
  onReload: (options?: { showLoading?: boolean }) => Promise<void>;
  onOpenProject: (projectId: number) => void;
  isPreviewMode: boolean;
}) {
  const [activeFilter, setActiveFilter] = useState<MyTaskQuickFilter>('active');
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<'agent' | 'default'>('agent');
  const [actionState, setActionState] = useState<{ taskId: number | null; message: string | null; error: string | null }>({ taskId: null, message: null, error: null });
  const [briefSuggestions, setBriefSuggestions] = useState<AgentSuggestion[]>([]);
  const [briefSavingId, setBriefSavingId] = useState<number | null>(null);
  const [briefMessage, setBriefMessage] = useState<string | null>(null);
  const [briefError, setBriefError] = useState<string | null>(null);

  const filters: Array<{ key: MyTaskQuickFilter; label: string; count: number; tone: 'slate' | 'blue' | 'amber' | 'red' | 'green' }> = [
    { key: 'active', label: 'Actives', count: data?.summary.active || 0, tone: 'blue' },
    { key: 'today', label: 'Aujourd’hui', count: data?.summary.dueToday || 0, tone: 'amber' },
    { key: 'week', label: 'Cette semaine', count: data?.summary.dueThisWeek || 0, tone: 'blue' },
    { key: 'late', label: 'En retard', count: data?.summary.late || 0, tone: 'red' },
    { key: 'blocked', label: 'Bloquées', count: data?.summary.blocked || 0, tone: 'red' },
    { key: 'unscheduled', label: 'Sans échéance', count: (data?.tasks || []).filter((task) => !task.date_echeance && !task.is_completed).length, tone: 'slate' },
    { key: 'completed', label: 'Terminées', count: data?.summary.completed || 0, tone: 'green' },
  ];

  const normalizedSearch = search.trim().toLowerCase();
  const agentTaskScore = useMemo(() => {
    const scores = new Map<number, number>();
    briefSuggestions.forEach((suggestion) => {
      const score = Number(suggestion.score_breakdown?.score || 0);
      (suggestion.evidence || []).forEach((item) => {
        if (item.kind === 'tache') scores.set(Number(item.id), Math.max(scores.get(Number(item.id)) || 0, score));
      });
    });
    return scores;
  }, [briefSuggestions]);

  const visibleTasks = (data?.tasks || []).filter((task) => {
    const matchesFilter = myTaskMatchesFilter(task, activeFilter);
    const matchesSearch = !normalizedSearch || [
      task.titre,
      task.description,
      task.project_title,
      task.client_name,
      task.latest_comment,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(normalizedSearch));
    return matchesFilter && matchesSearch;
  }).sort((a, b) => {
    if (sortMode !== 'agent') return 0;
    return (agentTaskScore.get(b.id) || 0) - (agentTaskScore.get(a.id) || 0);
  });

  const loadBriefSuggestions = useCallback(async () => {
    if (isPreviewMode) {
      setBriefSuggestions([]);
      return;
    }
    try {
      const response = await fetch('/api/projet-v2/agent/suggestions?brief=1&limit=5', { credentials: 'include' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setBriefSuggestions(payload.suggestions || []);
    } catch (err) {
      setBriefError(err instanceof Error ? err.message : 'Erreur brief agent');
    }
  }, [isPreviewMode]);

  useEffect(() => {
    loadBriefSuggestions();
  }, [loadBriefSuggestions]);

  const decideBriefSuggestion = async (suggestion: AgentSuggestion, action: 'accept' | 'convert' | 'reject' | 'snooze', rejectReason?: string) => {
    if (isPreviewMode) {
      setBriefMessage('Preview seulement : décision simulée.');
      return;
    }
    try {
      setBriefSavingId(suggestion.id);
      const response = await fetch(`/api/projet-v2/agent/suggestions/${suggestion.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'reject' ? { action, rejectReason } : action === 'snooze' ? { action, snoozeDays: 7 } : { action }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setBriefMessage('Décision enregistrée. Annulable pendant 10 secondes.');
      await loadBriefSuggestions();
    } catch (err) {
      setBriefError(err instanceof Error ? err.message : 'Erreur décision');
    } finally {
      setBriefSavingId(null);
    }
  };

  const undoBriefSuggestion = async (suggestion: AgentSuggestion) => {
    if (isPreviewMode) return;
    try {
      setBriefSavingId(suggestion.id);
      const response = await fetch(`/api/projet-v2/agent/suggestions/${suggestion.id}/undo`, { method: 'POST', credentials: 'include' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setBriefMessage('Décision annulée.');
      await loadBriefSuggestions();
    } catch (err) {
      setBriefError(err instanceof Error ? err.message : 'Erreur annulation');
    } finally {
      setBriefSavingId(null);
    }
  };

  const visibleTaskRows = visibleTasks.map((task, index) => {
    const section = getMyTaskSection(task);
    const previousSection = index > 0 ? getMyTaskSection(visibleTasks[index - 1]) : null;
    const sectionMeta = section !== previousSection ? getMyTaskSectionMeta(section) : null;
    return { task, sectionMeta };
  });

  const runQuickStatus = async (task: MyTaskItem, statusText: 'En cours' | 'Terminé') => {
    if (!task.id_projet) {
      setActionState({ taskId: task.id, message: null, error: 'Projet introuvable pour cette tâche.' });
      return;
    }
    if (isPreviewMode) {
      setActionState({ taskId: task.id, message: `Action simulée: ${statusText}.`, error: null });
      return;
    }

    try {
      setActionState({ taskId: task.id, message: null, error: null });
      const response = await fetch(`/api/projet-v2/projects/${task.id_projet}/tasks/${task.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status_text: statusText,
          percent: statusText === 'Terminé' ? 100 : Math.max(1, Number(task.safe_percent ?? task.percent ?? 0)),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `Action impossible (HTTP ${response.status})`);
      const previousScrollY = window.scrollY;
      await onReload({ showLoading: false });
      requestAnimationFrame(() => {
        window.scrollTo({ top: previousScrollY, behavior: 'auto' });
      });
      setActionState({ taskId: task.id, message: statusText === 'Terminé' ? 'Tâche terminée.' : 'Tâche mise en cours.', error: null });
    } catch (err) {
      setActionState({ taskId: task.id, message: null, error: err instanceof Error ? err.message : 'Erreur inconnue' });
    }
  };

  if (loading) {
    return <div className="space-y-3 p-5">{[...Array(4)].map((_, index) => <div key={index} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }

  if (error) {
    return <div className="m-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }

  return (
    <div className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="blue">Terrain</Badge>
            <h2 className="text-xl font-semibold text-slate-950">Mes tâches</h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Ta file d’exécution personnelle: la prochaine bonne action, le projet, l’échéance et le signal utile.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-right sm:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actives</p>
            <p className="text-lg font-bold text-slate-950">{data?.summary.active || 0}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">7 jours</p>
            <p className="text-lg font-bold text-amber-950">{data?.summary.dueThisWeek || 0}</p>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Retard</p>
            <p className="text-lg font-bold text-rose-950">{data?.summary.late || 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
            <p className="text-lg font-bold text-slate-950">{data?.summary.total || 0}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-blue-950">Brief du matin</h3>
            <p className="mt-1 text-xs text-blue-800">Top 5 déterministe du jour, plafonné et diversifié par projet.</p>
          </div>
          <Badge tone="blue">{briefSuggestions.length}/5</Badge>
        </div>
        {briefMessage ? <div className="mb-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700">{briefMessage}</div> : null}
        {briefError ? <div className="mb-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700">{briefError}</div> : null}
        {briefSuggestions.length === 0 ? (
          <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-800">Aucune suggestion prioritaire dans le brief.</div>
        ) : (
          <div className="grid gap-2 xl:grid-cols-2">
            {briefSuggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                compact
                saving={briefSavingId === suggestion.id}
                onDecide={decideBriefSuggestion}
                onUndo={undoBriefSuggestion}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {filters.map((filter) => {
          const active = activeFilter === filter.key;
          return (
            <button
              key={filter.key}
              type="button"
              onClick={() => setActiveFilter(filter.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${active ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}
            >
              {filter.label}
              <Badge tone={active ? 'blue' : filter.tone}>{filter.count}</Badge>
            </button>
          );
        })}
        <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher tâche, projet, client…" className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
        </div>
        <select value={sortMode} onChange={(event) => setSortMode(event.target.value as 'agent' | 'default')} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm">
          <option value="agent">Priorité agent</option>
          <option value="default">Tri standard</option>
        </select>
      </div>

      {!data || data.tasks.length === 0 ? (
        <EmptyState>Aucune tâche ne t’est assignée pour le moment.</EmptyState>
      ) : visibleTasks.length === 0 ? (
        <EmptyState>Aucune tâche ne correspond au filtre actuel.</EmptyState>
      ) : (
        <div className="space-y-3">
          {visibleTaskRows.map(({ task, sectionMeta }) => {
            const signal = getTaskSignal(task);
            const projectId = Number(task.id_projet);
            const highPriority = (task.priority_text || '').toLowerCase().includes('haute') || (task.priority_text || '').toLowerCase().includes('critique');
            return (
              <Fragment key={task.id}>
                {sectionMeta ? (
                  <div className="pt-2">
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-slate-200" />
                      <div className="text-center">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-600">{sectionMeta.title}</p>
                        <p className="text-xs text-slate-500">{sectionMeta.description}</p>
                      </div>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                  </div>
                ) : null}
                <article className="rounded-xl border border-slate-300 border-l-4 border-l-slate-400 bg-white p-4 shadow-sm transition hover:border-blue-400 hover:bg-blue-50/50">
                  <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={signal.tone}>{signal.label}</Badge>
                        {task.my_responsibility ? <Badge tone="slate">{task.my_responsibility}</Badge> : null}
                        {highPriority ? <Badge tone={(task.priority_text || '').toLowerCase().includes('critique') ? 'red' : 'amber'}>{task.priority_text}</Badge> : null}
                      </div>
                      <h3 className="mt-2 text-base font-bold leading-tight text-slate-950">{task.titre}</h3>
                      <p className="mt-1 text-sm text-slate-600">
                        {task.client_name || 'Client à confirmer'} · {task.project_title || `Projet #${task.id_projet || 'n/d'}`}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span className="rounded-lg bg-slate-100 px-2 py-1">Fin prévue: {formatDate(task.date_echeance)}</span>
                        <span className="rounded-lg bg-slate-100 px-2 py-1">{task.status_text || 'Statut à préciser'}</span>
                        {typeof task.safe_percent === 'number' ? <span className="rounded-lg bg-slate-100 px-2 py-1">{task.safe_percent}%</span> : null}
                      </div>
                      {task.latest_comment ? (
                        <p className="mt-3 line-clamp-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                          <span className="font-semibold text-slate-900">{task.latest_comment_author || 'Note'}: </span>{task.latest_comment}
                        </p>
                      ) : task.description ? (
                        <p className="mt-3 line-clamp-2 text-sm text-slate-600">{task.description}</p>
                      ) : null}
                      {actionState.taskId === task.id && actionState.message ? (
                        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{actionState.message}</div>
                      ) : null}
                      {actionState.taskId === task.id && actionState.error ? (
                        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{actionState.error}</div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <button type="button" onClick={() => onOpenProject(projectId)} disabled={!projectId} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 shadow-sm hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50">
                        Ouvrir
                      </button>
                      {!task.is_completed ? (
                        <>
                          <button type="button" disabled={actionState.taskId === task.id && !actionState.message && !actionState.error} onClick={() => runQuickStatus(task, 'En cours')} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50">
                            Mettre en cours
                          </button>
                          <button type="button" disabled={actionState.taskId === task.id && !actionState.message && !actionState.error} onClick={() => runQuickStatus(task, 'Terminé')} className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-50">
                            Terminer
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </article>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectMilestonesSection({ projectId, milestonesData, tasksData, loading, error, isPreviewMode, onSelectTasks, onOpenTask, onMilestonesChanged }: { projectId: number; milestonesData: ProjectMilestonesResponse | null; tasksData: ProjectTasksResponse | null; loading: boolean; error: string | null; isPreviewMode: boolean; onSelectTasks: () => void; onOpenTask?: (task: ProjectTaskItem) => void; onMilestonesChanged: () => Promise<void> }) {
  const [activeTimelineBucket, setActiveTimelineBucket] = useState<'late' | 'next7' | 'next30' | 'later' | 'unscheduled' | 'completed'>('next30');
  const [contacts, setContacts] = useState<ProjectContactOption[]>([]);
  const [newMilestone, setNewMilestone] = useState<MilestoneFormState>({ open: false, titre: '', description: '', statusText: 'À faire', priorityText: 'Moyenne', percent: '0', startDate: '', dueDate: '', assigneeId: '', saving: false, message: null, error: null });
  const [editMilestone, setEditMilestone] = useState<MilestoneEditState>({ selectedMilestone: null, open: false, titre: '', description: '', statusText: 'À faire', priorityText: 'Moyenne', percent: '0', startDate: '', dueDate: '', assigneeId: '', saving: false, message: null, error: null });
  const [linkMilestoneId, setLinkMilestoneId] = useState<number | null>(null);
  const [linkTaskId, setLinkTaskId] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkMessage, setLinkMessage] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [milestoneAction, setMilestoneAction] = useState<{ savingId: number | null; message: string | null; error: string | null }>({ savingId: null, message: null, error: null });

  const groupedMilestones = useMemo(() => {
    const groups = {
      late: [] as ProjectMilestoneItem[],
      upcoming: [] as ProjectMilestoneItem[],
      unscheduled: [] as ProjectMilestoneItem[],
      completed: [] as ProjectMilestoneItem[],
    };

    (milestonesData?.milestones || []).forEach((milestone) => {
      if (milestone.is_completed) groups.completed.push(milestone);
      else if (milestone.is_late) groups.late.push(milestone);
      else if (!milestone.date_echeance) groups.unscheduled.push(milestone);
      else groups.upcoming.push(milestone);
    });

    return groups;
  }, [milestonesData]);

  const groupConfigs = [
    { key: 'late' as const, title: 'En retard', tone: 'red' as const, empty: 'Aucun jalon en retard.' },
    { key: 'upcoming' as const, title: 'À venir', tone: 'blue' as const, empty: 'Aucun jalon à venir daté.' },
    { key: 'unscheduled' as const, title: 'Sans date', tone: 'amber' as const, empty: 'Aucun jalon sans date.' },
    { key: 'completed' as const, title: 'Complétés', tone: 'green' as const, empty: 'Aucun jalon complété.' },
  ];

  const timelineGroups = useMemo(() => {
    const groups = {
      late: [] as ProjectMilestoneItem[],
      next7: [] as ProjectMilestoneItem[],
      next30: [] as ProjectMilestoneItem[],
      later: [] as ProjectMilestoneItem[],
      unscheduled: [] as ProjectMilestoneItem[],
      completed: [] as ProjectMilestoneItem[],
    };

    (milestonesData?.milestones || []).forEach((milestone) => {
      if (milestone.is_completed) {
        groups.completed.push(milestone);
        return;
      }
      if (milestone.is_late) {
        groups.late.push(milestone);
        return;
      }
      if (!milestone.date_echeance) {
        groups.unscheduled.push(milestone);
        return;
      }

      const days = typeof milestone.days_until_due === 'number'
        ? milestone.days_until_due
        : Math.ceil((new Date(milestone.date_echeance).getTime() - Date.now()) / 86400000);

      if (days <= 7) groups.next7.push(milestone);
      else if (days <= 30) groups.next30.push(milestone);
      else groups.later.push(milestone);
    });

    return groups;
  }, [milestonesData]);

  const timelineConfigs = [
    { key: 'late' as const, title: 'En retard', subtitle: 'À traiter', tone: 'red' as const },
    { key: 'next7' as const, title: '7 jours', subtitle: 'Très proche', tone: 'amber' as const },
    { key: 'next30' as const, title: '30 jours', subtitle: 'À venir', tone: 'blue' as const },
    { key: 'later' as const, title: 'Plus tard', subtitle: 'Planifié', tone: 'slate' as const },
    { key: 'unscheduled' as const, title: 'Sans date', subtitle: 'À structurer', tone: 'amber' as const },
    { key: 'completed' as const, title: 'Complétés', subtitle: 'Terminé', tone: 'green' as const },
  ];

  const activeLinkMilestone = milestonesData?.milestones.find((milestone) => milestone.id === linkMilestoneId) || null;
  const availableTasksForLink = (tasksData?.tasks || []).filter((task) => !task.isjalon && task.id_jalon !== activeLinkMilestone?.id);

  const openMilestoneLinking = (milestone: ProjectMilestoneItem) => {
    setLinkMilestoneId(milestone.id);
    setLinkTaskId('');
    setLinkMessage(null);
    setLinkError(null);
    setNewMilestone((current) => ({ ...current, open: false }));
    setEditMilestone((current) => ({ ...current, selectedMilestone: null, open: false }));
  };

  const updateTaskMilestoneLink = async (taskId: number, milestoneId: number | null) => {
    if (isPreviewMode) {
      setLinkMessage(milestoneId ? 'Lien simulé en preview.' : 'Dissociation simulée en preview.');
      setLinkError(null);
      return;
    }

    setLinkSaving(true);
    setLinkMessage(null);
    setLinkError(null);
    try {
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${taskId}/milestone`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ milestoneId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de mettre à jour le lien jalon (HTTP ${response.status})`);
      await onMilestonesChanged();
      setLinkTaskId('');
      setLinkMessage(milestoneId ? 'Tâche liée au jalon.' : 'Tâche dissociée du jalon.');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Erreur liaison jalon');
    } finally {
      setLinkSaving(false);
    }
  };

  const linkSelectedTaskToMilestone = async () => {
    const selectedTaskId = Number.parseInt(linkTaskId, 10);
    if (!activeLinkMilestone || Number.isNaN(selectedTaskId)) {
      setLinkError('Choisis un jalon et une tâche à lier.');
      return;
    }
    await updateTaskMilestoneLink(selectedTaskId, activeLinkMilestone.id);
  };

  const toggleMilestoneCompletion = async (milestone: ProjectMilestoneItem) => {
    const shouldComplete = !milestone.is_completed;
    const openLinkedTasks = (milestone.linked_tasks || []).filter((task) => !task.is_completed);

    if (shouldComplete && openLinkedTasks.length > 0) {
      const confirmed = window.confirm(`${openLinkedTasks.length} tâche(s) liée(s) ne sont pas terminée(s). Compléter le jalon quand même?`);
      if (!confirmed) return;
    }

    if (isPreviewMode) {
      setMilestoneAction({ savingId: null, message: shouldComplete ? 'Jalon complété en preview.' : 'Jalon réouvert en preview.', error: null });
      return;
    }

    setMilestoneAction({ savingId: milestone.id, message: null, error: null });
    try {
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${milestone.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status_text: shouldComplete ? 'Terminé' : 'En cours',
          percent: shouldComplete ? 100 : Math.min(Number(milestone.linked_tasks_progress || milestone.safe_percent || milestone.percent || 0), 99),
          isjalon: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de ${shouldComplete ? 'compléter' : 'réouvrir'} le jalon (HTTP ${response.status})`);
      await onMilestonesChanged();
      setMilestoneAction({ savingId: null, message: shouldComplete ? 'Jalon complété.' : 'Jalon réouvert.', error: null });
    } catch (err) {
      setMilestoneAction({ savingId: null, message: null, error: err instanceof Error ? err.message : 'Erreur action jalon' });
    }
  };



  const loadContacts = async () => {
    if (isPreviewMode || contacts.length > 0) return;
    const response = await fetch('/api/projet-v2/contacts', { credentials: 'include' });
    if (!response.ok) throw new Error(`Impossible de charger les contacts (HTTP ${response.status})`);
    const data = await response.json();
    setContacts(Array.isArray(data.contacts) ? data.contacts : []);
  };

  const toggleCreateMilestone = () => {
    setNewMilestone((current) => ({ ...current, open: !current.open, message: null, error: null }));
    setLinkMilestoneId(null);
    setEditMilestone((current) => ({ ...current, selectedMilestone: null, open: false }));
    if (!isPreviewMode) loadContacts().catch((err) => setNewMilestone((current) => ({ ...current, error: err instanceof Error ? err.message : 'Erreur contacts' })));
  };

  const createMilestone = async () => {
    const titre = newMilestone.titre.trim();
    if (!titre) {
      setNewMilestone((current) => ({ ...current, error: 'Titre de jalon requis.' }));
      return;
    }
    if (!newMilestone.startDate || !newMilestone.dueDate) {
      setNewMilestone((current) => ({ ...current, error: 'Date de début et date de fin obligatoires.' }));
      return;
    }

    if (isPreviewMode) {
      setNewMilestone({ open: false, titre: '', description: '', statusText: 'À faire', priorityText: 'Moyenne', percent: '0', startDate: '', dueDate: '', assigneeId: '', saving: false, message: null, error: null });
      return;
    }

    try {
      setNewMilestone((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titre,
          description: newMilestone.description,
          status_text: newMilestone.statusText,
          priority_text: newMilestone.priorityText,
          percent: newMilestone.percent,
          date_debut: newMilestone.startDate,
          date_echeance: newMilestone.dueDate,
          assigneeId: newMilestone.assigneeId,
          isjalon: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de créer le jalon (HTTP ${response.status})`);
      await onMilestonesChanged();
      setNewMilestone({ open: false, titre: '', description: '', statusText: 'À faire', priorityText: 'Moyenne', percent: '0', startDate: '', dueDate: '', assigneeId: '', saving: false, message: null, error: null });
    } catch (err) {
      setNewMilestone((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur création jalon' }));
    }
  };

  const openMilestoneEdit = (milestone: ProjectMilestoneItem) => {
    setNewMilestone((current) => ({ ...current, open: false }));
    setLinkMilestoneId(null);
    setEditMilestone({
      selectedMilestone: milestone,
      open: true,
      titre: milestone.titre || '',
      description: milestone.description || '',
      statusText: milestone.status_text || 'À faire',
      priorityText: milestone.priority_text || 'Moyenne',
      percent: String(milestone.safe_percent ?? milestone.percent ?? 0),
      startDate: milestone.date_debut ? String(milestone.date_debut).slice(0, 10) : '',
      dueDate: milestone.date_echeance ? String(milestone.date_echeance).slice(0, 10) : '',
      assigneeId: milestone.assignees?.[0]?.id_contact ? String(milestone.assignees[0].id_contact) : '',
      saving: false,
      message: null,
      error: null,
    });
    if (!isPreviewMode) loadContacts().catch((err) => setEditMilestone((current) => ({ ...current, error: err instanceof Error ? err.message : 'Erreur contacts' })));
  };

  const updateMilestone = async () => {
    const milestone = editMilestone.selectedMilestone;
    if (!milestone) return;
    const titre = editMilestone.titre.trim();
    if (!titre) {
      setEditMilestone((current) => ({ ...current, error: 'Titre de jalon requis.' }));
      return;
    }
    if (!editMilestone.startDate || !editMilestone.dueDate) {
      setEditMilestone((current) => ({ ...current, error: 'Date de début et date de fin obligatoires.' }));
      return;
    }

    if (isPreviewMode) {
      setEditMilestone((current) => ({ ...current, message: 'Modification simulée en preview.', error: null }));
      return;
    }

    try {
      setEditMilestone((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${milestone.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titre,
          description: editMilestone.description,
          status_text: editMilestone.statusText,
          priority_text: editMilestone.priorityText,
          percent: editMilestone.percent,
          date_debut: editMilestone.startDate,
          date_echeance: editMilestone.dueDate,
          isjalon: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de modifier le jalon (HTTP ${response.status})`);

      if (editMilestone.assigneeId) {
        const assignResponse = await fetch(`/api/projet-v2/projects/${projectId}/tasks/${milestone.id}/assignee`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId: editMilestone.assigneeId, responsibility: 'R' }),
        });
        const assignData = await assignResponse.json().catch(() => ({}));
        if (!assignResponse.ok) throw new Error(assignData.error || `Jalon modifié, mais responsable non mis à jour (HTTP ${assignResponse.status})`);
      }

      await onMilestonesChanged();
      setEditMilestone((current) => ({ ...current, saving: false, message: 'Jalon mis à jour.', error: null }));
    } catch (err) {
      setEditMilestone((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur modification jalon' }));
    }
  };

  if (loading) return <OverviewSkeleton />;
  if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  if (!milestonesData) return <EmptyState>Les jalons ne sont pas encore chargés.</EmptyState>;

  const { summary } = milestonesData;
  const healthTone = summary.late > 0 ? 'red' : summary.unscheduled > 0 || summary.atRisk > 0 ? 'amber' : summary.total > 0 ? 'green' : 'slate';
  const healthLabel = summary.total === 0
    ? 'Échéancier à structurer'
    : summary.late > 0
      ? 'À surveiller'
      : summary.unscheduled > 0
        ? 'Dates à compléter'
        : 'Structuré';

  const linkedCoverage = summary.total > 0
    ? Math.round(((milestonesData.milestones.filter((milestone) => (milestone.linked_tasks_count || 0) > 0).length) / summary.total) * 100)
    : 0;
  const milestonesWithoutTasks = milestonesData.milestones.filter((milestone) => !milestone.is_completed && (milestone.linked_tasks_count || 0) === 0);
  const milestonesWithPilotSignals = milestonesData.milestones.filter((milestone) => (milestone.pilot_signals_count || 0) > 0);
  const milestoneAttentionSignals = milestonesData.milestones.reduce((sum, milestone) => sum + (milestone.pilot_signals_attention_count || 0), 0);
  const attentionMilestones = [...milestonesData.milestones]
    .filter((milestone) => !milestone.is_completed && (milestone.is_late || milestone.is_at_risk || !milestone.date_echeance || (milestone.linked_tasks_count || 0) === 0))
    .sort((a, b) => {
      const score = (milestone: ProjectMilestoneItem) => (milestone.is_late ? 0 : milestone.is_at_risk ? 1 : !milestone.date_echeance ? 2 : (milestone.linked_tasks_count || 0) === 0 ? 3 : 4);
      return score(a) - score(b);
    })
    .slice(0, 3);
  const pilotMessage = summary.late > 0
    ? `${summary.late} jalon(s) en retard demandent une action maintenant.`
    : summary.atRisk > 0
      ? `${summary.atRisk} jalon(s) à risque à surveiller.`
      : milestonesWithoutTasks.length > 0
        ? `${milestonesWithoutTasks.length} jalon(s) sans tâche liée : structure à compléter.`
        : summary.nextMilestone
          ? `Prochain jalon : ${summary.nextMilestone.titre}.`
          : 'Échéancier calme pour le moment.';

  const renderMilestonePilotSignals = (milestone: ProjectMilestoneItem, compact = false) => {
    const signals = milestone.pilot_signals || [];
    if (signals.length === 0) return null;
    const attentionCount = milestone.pilot_signals_attention_count || 0;
    return (
      <div className={`mt-3 rounded-xl border ${attentionCount > 0 ? 'border-amber-100 bg-amber-50' : 'border-slate-100 bg-slate-50'} p-3`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className={`text-xs font-semibold uppercase tracking-wide ${attentionCount > 0 ? 'text-amber-700' : 'text-slate-500'}`}>Signaux de pilotage</p>
          <div className="flex flex-wrap gap-2">
            {(milestone.pilot_decisions_open || 0) > 0 ? <Badge tone="blue">{milestone.pilot_decisions_open} décision(s)</Badge> : null}
            {(milestone.pilot_risks_open || 0) > 0 ? <Badge tone="red">{milestone.pilot_risks_open} risque(s)</Badge> : null}
            {(milestone.pilot_comments_count || 0) > 0 ? <Badge tone="green">{milestone.pilot_comments_count} commentaire(s)</Badge> : null}
          </div>
        </div>
        <div className="mt-2 space-y-2">
          {signals.slice(0, compact ? 2 : 4).map((signal) => (
            <div key={signal.id} className="rounded-lg bg-white px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={getJournalTypeTone(signal.entry_type)}>{getJournalTypeLabel(signal.entry_type)}</Badge>
                <Badge tone={signal.is_overdue ? 'red' : signal.needs_attention ? 'amber' : isJournalClosed(signal.status) ? 'green' : 'slate'}>{getJournalStatusLabel(signal.status)}</Badge>
                {signal.linked_task_title ? <span className="text-xs text-slate-500">Tâche : {signal.linked_task_title}</span> : null}
              </div>
              <p className="mt-1 text-sm font-semibold text-slate-900">{signal.title}</p>
              {signal.content ? <p className="mt-1 line-clamp-2 text-xs text-slate-600">{signal.content}</p> : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLinkedTasksUnderMilestone = (milestone: ProjectMilestoneItem, compact = false) => {
    const linkedTasks = milestone.linked_tasks || [];
    if (linkedTasks.length === 0) {
      return (
        <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
          Aucune tâche liée à ce jalon pour l’instant.
        </div>
      );
    }

    const visibleTasks = compact ? linkedTasks.slice(0, 5) : linkedTasks;
    const hiddenCount = linkedTasks.length - visibleTasks.length;

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 bg-blue-100 px-3 py-2">
          <div className="flex items-center gap-2">
            <ListChecks className="h-3.5 w-3.5 text-slate-500" />
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tâches sous ce jalon</p>
          </div>
          <Badge tone="blue">{linkedTasks.length} tâche(s)</Badge>
        </div>
        <div className="divide-y divide-slate-200">
          {visibleTasks.map((task) => {
            const signal = getTaskSignal(task);
            return (
              <div key={task.id} role="button" tabIndex={0} onClick={() => onOpenTask?.(task)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenTask?.(task); } }} className="grid cursor-pointer gap-2 bg-white px-3 py-2.5 hover:bg-blue-50 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="min-w-0 border-l-2 border-blue-200 pl-3">
                  <p className="truncate text-sm font-semibold text-slate-900">{task.titre}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {getTaskDateRangeLabel(task)} · {task.status_text || 'Statut à préciser'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:justify-end">
                  <Badge tone={signal.tone}>{signal.label}</Badge>
                  {task.priority_text ? <Badge tone="amber">{task.priority_text}</Badge> : null}
                </div>
              </div>
            );
          })}
          {hiddenCount > 0 ? (
            <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">+ {hiddenCount} autre(s) tâche(s) — ouvre le détail complet pour tout voir.</div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-md">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-slate-700" />
              <h4 className="text-sm font-semibold text-slate-950">Briefing jalons</h4>
              <Badge tone={healthTone}>{healthLabel}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-600">{pilotMessage}</p>
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Prochain</p><p className="mt-1 truncate text-sm font-semibold text-blue-950">{summary.nextMilestone ? summary.nextMilestone.titre : 'À définir'}</p></div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Attention</p><p className={`mt-1 text-sm font-semibold ${summary.late > 0 ? 'text-rose-700' : summary.atRisk > 0 ? 'text-amber-700' : 'text-slate-800'}`}>{summary.late} retard · {summary.atRisk} risque</p></div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Structure</p><p className="mt-1 text-sm font-semibold text-emerald-950">{linkedCoverage}% liés · {summary.unscheduled} sans date</p></div>
              <div className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 md:col-span-3"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signaux de pilotage</p><p className={`mt-1 text-sm font-semibold ${milestoneAttentionSignals > 0 ? 'text-amber-700' : 'text-slate-800'}`}>{milestonesWithPilotSignals.length} jalon(s) documenté(s) · {milestoneAttentionSignals} signal(aux) à regarder</p></div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={toggleCreateMilestone} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-800">
              <Plus className="h-3.5 w-3.5" />
              {newMilestone.open ? 'Fermer' : 'Créer un jalon'}
            </button>
            <button type="button" onClick={onSelectTasks} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-400 hover:bg-blue-50">Voir tâches</button>
          </div>
        </div>

        {attentionMilestones.length > 0 ? (
          <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">À regarder maintenant</p>
            <div className="mt-2 grid gap-2 lg:grid-cols-3">
              {attentionMilestones.map((milestone) => {
                const signal = getTaskSignal(milestone);
                return (
                  <div key={milestone.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <p className="line-clamp-2 text-sm font-semibold text-slate-900">{milestone.titre}</p>
                      <Badge tone={signal.tone}>{signal.label}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{formatDate(milestone.date_echeance)} · {milestone.linked_tasks_count || 0} tâche(s) liée(s) · {milestone.pilot_signals_attention_count || 0} signal(aux)</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {milestoneAction.message ? <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{milestoneAction.message}</div> : null}
        {milestoneAction.error ? <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{milestoneAction.error}</div> : null}

        {newMilestone.open ? (
          <div className="mt-4 space-y-3 rounded-xl border border-emerald-200 bg-white p-4">
            {newMilestone.error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{newMilestone.error}</div> : null}
            <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <label className="space-y-1 text-xs font-semibold text-slate-600">Titre<input value={newMilestone.titre} onChange={(event) => setNewMilestone((current) => ({ ...current, titre: event.target.value }))} placeholder="Ex. Go / No-Go migration" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Statut<select value={newMilestone.statusText} onChange={(event) => setNewMilestone((current) => ({ ...current, statusText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">{['À faire', 'En cours', 'À risque', 'Bloqué', 'Terminé'].map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Priorité<select value={newMilestone.priorityText} onChange={(event) => setNewMilestone((current) => ({ ...current, priorityText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">{['Basse', 'Moyenne', 'Haute', 'Critique'].map((priority) => <option key={priority} value={priority}>{priority}</option>)}</select></label>
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <label className="space-y-1 text-xs font-semibold text-slate-600">Description<textarea value={newMilestone.description} onChange={(event) => setNewMilestone((current) => ({ ...current, description: event.target.value }))} rows={2} placeholder="Critère de passage, dépendance, livrable attendu..." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Date de début<input type="date" value={newMilestone.startDate} onChange={(event) => setNewMilestone((current) => ({ ...current, startDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Date de fin<input type="date" value={newMilestone.dueDate} onChange={(event) => setNewMilestone((current) => ({ ...current, dueDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Progression<input type="number" min="0" max="100" value={newMilestone.percent} onChange={(event) => setNewMilestone((current) => ({ ...current, percent: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Responsable R<select value={newMilestone.assigneeId} onChange={(event) => setNewMilestone((current) => ({ ...current, assigneeId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">À assigner plus tard</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}{contact.email_primary ? ` — ${contact.email_primary}` : ''}</option>)}</select></label>
            </div>
            <div className="flex justify-end"><button type="button" disabled={newMilestone.saving} onClick={createMilestone} className="rounded-lg bg-emerald-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60">Créer le jalon</button></div>
          </div>
        ) : null}
      </section>

      {editMilestone.selectedMilestone ? (
        <section className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-indigo-950">Modifier le jalon — {editMilestone.selectedMilestone.titre}</h4>
              <p className="mt-1 text-xs text-indigo-800">Mets à jour titre, date cible, statut, priorité, progression et responsable R.</p>
            </div>
            <button type="button" onClick={() => setEditMilestone((current) => ({ ...current, selectedMilestone: null, open: false }))} className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-50">Fermer</button>
          </div>
          {editMilestone.message ? <div className="mb-3 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700">{editMilestone.message}</div> : null}
          {editMilestone.error ? <div className="mb-3 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700">{editMilestone.error}</div> : null}
          <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <label className="space-y-1 text-xs font-semibold text-slate-600">Titre<input value={editMilestone.titre} onChange={(event) => setEditMilestone((current) => ({ ...current, titre: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Statut<select value={editMilestone.statusText} onChange={(event) => setEditMilestone((current) => ({ ...current, statusText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">{['À faire', 'En cours', 'À risque', 'Bloqué', 'Terminé'].map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Priorité<select value={editMilestone.priorityText} onChange={(event) => setEditMilestone((current) => ({ ...current, priorityText: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">{['Basse', 'Moyenne', 'Haute', 'Critique'].map((priority) => <option key={priority} value={priority}>{priority}</option>)}</select></label>
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <label className="space-y-1 text-xs font-semibold text-slate-600">Description<textarea value={editMilestone.description} onChange={(event) => setEditMilestone((current) => ({ ...current, description: event.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Date de début<input type="date" value={editMilestone.startDate} onChange={(event) => setEditMilestone((current) => ({ ...current, startDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Date de fin<input type="date" value={editMilestone.dueDate} onChange={(event) => setEditMilestone((current) => ({ ...current, dueDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Progression<input type="number" min="0" max="100" value={editMilestone.percent} onChange={(event) => setEditMilestone((current) => ({ ...current, percent: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Responsable R<select value={editMilestone.assigneeId} onChange={(event) => setEditMilestone((current) => ({ ...current, assigneeId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">Conserver / aucun</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}{contact.email_primary ? ` — ${contact.email_primary}` : ''}</option>)}</select></label>
          </div>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => editMilestone.selectedMilestone ? onOpenTask?.(editMilestone.selectedMilestone) : onSelectTasks()} className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-800 hover:bg-indigo-50">Voir comme tâche</button>
            <button type="button" disabled={editMilestone.saving} onClick={updateMilestone} className="rounded-lg bg-indigo-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:opacity-60">Mettre à jour le jalon</button>
          </div>
        </section>
      ) : null}

      {activeLinkMilestone ? (
        <section className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-violet-800" />
                <h4 className="text-sm font-semibold text-violet-950">Tâches liées au jalon</h4>
              </div>
              <p className="mt-1 text-sm text-violet-800">Focus sur un seul jalon pour éviter le bruit : on lie, on vérifie, on repart.</p>
            </div>
            <button type="button" onClick={() => setLinkMilestoneId(null)} className="rounded-lg border border-violet-200 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-50">Fermer</button>
          </div>

          <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <div className="rounded-xl border border-violet-100 bg-white p-3">
            <label className="space-y-1 text-xs font-semibold text-slate-600">
              Jalon à structurer
              <select value={linkMilestoneId || ''} onChange={(event) => {
                const selected = milestonesData.milestones.find((milestone) => milestone.id === Number(event.target.value));
                if (selected) openMilestoneLinking(selected);
              }} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">
                <option value="">Choisir un jalon</option>
                {milestonesData.milestones.map((milestone) => (
                  <option key={milestone.id} value={milestone.id}>{milestone.titre}</option>
                ))}
              </select>
            </label>
            {activeLinkMilestone ? (
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <Badge tone="blue">{activeLinkMilestone.linked_tasks_count || 0} tâche(s) liée(s)</Badge>
                  <Badge tone="green">{activeLinkMilestone.linked_tasks_progress || 0}% moyen</Badge>
                </div>
                <p>Échéance : {formatDate(activeLinkMilestone.date_echeance)}</p>
              </div>
            ) : (
              <p className="mt-3 text-xs text-slate-500">Choisis un jalon avec le bouton « Lier tâches » ou via la liste.</p>
            )}
          </div>

          <div className="space-y-3 rounded-xl border border-violet-100 bg-white p-3">
            {linkMessage ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{linkMessage}</div> : null}
            {linkError ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{linkError}</div> : null}
            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
              <select value={linkTaskId} onChange={(event) => setLinkTaskId(event.target.value)} disabled={!activeLinkMilestone || availableTasksForLink.length === 0} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 disabled:bg-slate-50 disabled:text-slate-400">
                <option value="">{availableTasksForLink.length === 0 ? 'Aucune tâche disponible à lier' : 'Choisir une tâche à lier'}</option>
                {availableTasksForLink.map((task) => (
                  <option key={task.id} value={task.id}>{task.titre}{task.id_jalon ? ' — déjà liée à un autre jalon' : ''}</option>
                ))}
              </select>
              <button type="button" disabled={linkSaving || !activeLinkMilestone || !linkTaskId} onClick={linkSelectedTaskToMilestone} className="rounded-lg bg-violet-700 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-violet-800 disabled:opacity-60">Lier</button>
            </div>

            {activeLinkMilestone && activeLinkMilestone.linked_tasks && activeLinkMilestone.linked_tasks.length > 0 ? (
              <div className="space-y-2">
                {activeLinkMilestone.linked_tasks.map((task) => {
                  const signal = getTaskSignal(task);
                  return (
                    <div key={task.id} role="button" tabIndex={0} onClick={() => onOpenTask?.(task)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenTask?.(task); } }} className="flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 hover:border-blue-300 hover:bg-blue-50">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{task.titre}</p>
                        <p className="mt-1 text-xs text-slate-500">{getTaskDateRangeLabel(task)} · {task.status_text || 'Statut à préciser'}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={signal.tone}>{signal.label}</Badge>
                        <button type="button" disabled={linkSaving} onClick={(event) => { event.stopPropagation(); updateTaskMilestoneLink(task.id, null); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Dissocier</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-500">Aucune tâche liée à ce jalon pour l’instant.</div>
            )}
          </div>
        </div>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-slate-700" />
              <h4 className="text-sm font-semibold text-slate-900">Échéancier simple — B3.2</h4>
            </div>
            <p className="mt-1 text-sm text-slate-500">Vue temporelle légère : pas de Gantt, juste les jalons classés par horizon de pilotage.</p>
          </div>
          <Badge tone="green">B3.2 actif</Badge>
        </div>

        <div className="mt-4 grid gap-2 md:grid-cols-3 xl:grid-cols-6">
          {timelineConfigs.map((bucket) => {
            const active = activeTimelineBucket === bucket.key;
            const count = timelineGroups[bucket.key].length;
            return (
              <button
                key={bucket.key}
                type="button"
                onClick={() => setActiveTimelineBucket(bucket.key)}
                className={`rounded-xl border px-3 py-3 text-left shadow-sm transition ${active ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white hover:border-blue-500 hover:bg-blue-50'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-slate-900'}`}>{bucket.title}</p>
                  <Badge tone={bucket.tone}>{count}</Badge>
                </div>
                <p className={`mt-1 text-xs ${active ? 'text-blue-100' : 'text-slate-500'}`}>{bucket.subtitle}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-4 rounded-xl border border-slate-300 bg-slate-100 p-3">
          {timelineGroups[activeTimelineBucket].length === 0 ? (
            <div className="px-2 py-3 text-sm text-slate-500">Aucun jalon dans cet horizon.</div>
          ) : (
            <div className="space-y-2">
              {timelineGroups[activeTimelineBucket].map((milestone) => {
                const signal = getTaskSignal(milestone);
                return (
                  <div key={milestone.id} className="rounded-lg border border-slate-300 border-l-4 border-l-blue-500 bg-white px-3 py-3 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{milestone.titre}</p>
                        <p className="mt-1 text-xs text-slate-500">{formatDate(milestone.date_echeance)} · {milestone.status_text || 'Statut à préciser'} · {typeof milestone.safe_percent === 'number' ? `${milestone.safe_percent}%` : 'Progression n/d'}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={signal.tone}>{signal.label}</Badge>
                        <Badge tone="blue">{milestone.linked_tasks_count || 0} tâche(s)</Badge>
                        {(milestone.pilot_signals_attention_count || 0) > 0 ? <Badge tone="amber">{milestone.pilot_signals_attention_count} signal(aux)</Badge> : null}
                        <button type="button" disabled={milestoneAction.savingId === milestone.id} onClick={() => toggleMilestoneCompletion(milestone)} className={`rounded-lg border px-2 py-1 text-xs font-semibold disabled:opacity-60 ${milestone.is_completed ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'}`}>{milestone.is_completed ? 'Réouvrir' : 'Compléter'}</button>
                        <button type="button" onClick={() => openMilestoneLinking(milestone)} className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-800 hover:bg-violet-100">Lier tâches</button>
                        <button type="button" onClick={() => openMilestoneEdit(milestone)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Modifier</button>
                      </div>
                    </div>
                    {renderLinkedTasksUnderMilestone(milestone, true)}
                    {renderMilestonePilotSignals(milestone, true)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {milestonesData.recommendedActions.length > 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-900">Actions recommandées</h4>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {milestonesData.recommendedActions.map((action) => (
              <div key={action.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                <Badge tone={toneFromValue(action.tone)}>{action.title}</Badge>
                <p className="mt-2 text-sm text-slate-600">{action.description}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {summary.total === 0 ? (
        <EmptyState>Aucun jalon trouvé. B3.1 lit les tâches marquées comme jalons; crée ou transforme une tâche en jalon pour structurer l’échéancier.</EmptyState>
      ) : (
        <details className="rounded-2xl border border-slate-200 bg-white p-4">
          <summary className="cursor-pointer text-sm font-semibold text-slate-900">Voir le détail complet des jalons par statut</summary>
          <p className="mt-1 text-xs text-slate-500">Affiché seulement sur demande : l’écran principal garde le focus sur les exceptions et le prochain horizon.</p>
          <div className="mt-4 grid gap-4 xl:grid-cols-2">
          {groupConfigs.map((group) => (
            <section key={group.key} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-slate-900">{group.title}</h4>
                <Badge tone={group.tone}>{groupedMilestones[group.key].length}</Badge>
              </div>
              {groupedMilestones[group.key].length === 0 ? (
                <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-500">{group.empty}</div>
              ) : (
                <div className="space-y-2">
                  {groupedMilestones[group.key].map((milestone) => {
                    const signal = getTaskSignal(milestone);
                    return (
                      <div key={milestone.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">{milestone.titre}</p>
                            <p className="mt-1 text-xs text-slate-500">Échéance : {formatDate(milestone.date_echeance)}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge tone={signal.tone}>{signal.label}</Badge>
                            <Badge tone="blue">{milestone.linked_tasks_count || 0} tâche(s)</Badge>
                            <button type="button" disabled={milestoneAction.savingId === milestone.id} onClick={() => toggleMilestoneCompletion(milestone)} className={`rounded-lg border px-2 py-1 text-xs font-semibold disabled:opacity-60 ${milestone.is_completed ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'}`}>{milestone.is_completed ? 'Réouvrir' : 'Compléter'}</button>
                            <button type="button" onClick={() => openMilestoneLinking(milestone)} className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-800 hover:bg-violet-100">Lier tâches</button>
                            <button type="button" onClick={() => openMilestoneEdit(milestone)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Modifier</button>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Badge tone="slate">{milestone.status_text || 'Statut à préciser'}</Badge>
                          <Badge tone="amber">{milestone.priority_text || 'Priorité à préciser'}</Badge>
                          <Badge tone="green">{typeof milestone.safe_percent === 'number' ? `${milestone.safe_percent}%` : 'Progression n/d'}</Badge>
                          <Badge tone="blue">{milestone.linked_tasks_count || 0} tâche(s) liées</Badge>
                        </div>
                        {milestone.assignees && milestone.assignees.length > 0 ? (
                          <p className="mt-2 text-xs text-slate-500">Responsable : {getResponsibleLabel(milestone)}</p>
                        ) : null}
                        {renderLinkedTasksUnderMilestone(milestone)}
                        {renderMilestonePilotSignals(milestone)}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
          </div>
        </details>
      )}
    </div>
  );
}

function getJournalTypeLabel(type: string) {
  if (type === 'task_comment') return 'Commentaire tâche';
  if (type === 'decision') return 'Décision';
  if (type === 'risk') return 'Risque';
  if (type === 'communication') return 'Communication';
  return 'Note interne';
}

function getJournalTypeTone(type: string): 'slate' | 'green' | 'amber' | 'blue' | 'red' {
  if (type === 'task_comment') return 'green';
  if (type === 'decision') return 'blue';
  if (type === 'risk') return 'red';
  if (type === 'communication') return 'amber';
  return 'slate';
}

function getJournalStatusLabel(status?: string | null) {
  const value = String(status || 'open').toLowerCase();
  if (value === 'decided') return 'Décidée';
  if (value === 'resolved') return 'Résolu';
  if (value === 'sent') return 'Envoyée';
  if (value === 'posted') return 'Publié';
  if (value === 'archived' || value === 'closed') return 'Fermé';
  return 'Ouvert';
}

function isJournalClosed(status?: string | null) {
  return ['closed', 'resolved', 'decided', 'sent', 'archived'].includes(String(status || '').toLowerCase());
}

function getJournalQuickAction(item: ProjectJournalFeedItem): { status: string; label: string; doneLabel: string } | null {
  if (item.source !== 'journal' || !item.source_id || isJournalClosed(item.status)) return null;
  if (item.entry_type === 'decision') return { status: 'decided', label: 'Décider', doneLabel: 'Décision fermée' };
  if (item.entry_type === 'risk') return { status: 'resolved', label: 'Résoudre', doneLabel: 'Risque résolu' };
  if (item.entry_type === 'communication') return { status: 'sent', label: 'Marquer envoyé', doneLabel: 'Communication envoyée' };
  if (item.entry_type === 'note') return { status: 'archived', label: 'Archiver', doneLabel: 'Note archivée' };
  return null;
}

type JournalViewFilter = 'feed' | 'attention' | 'recent' | 'next_milestone' | 'task_comment' | 'decision' | 'risk' | 'communication' | 'note';

function ProjectJournalSection({ projectId, journalData, tasksData, milestonesData, loading, error, isPreviewMode, onJournalChanged }: { projectId: number; journalData: ProjectJournalResponse | null; tasksData: ProjectTasksResponse | null; milestonesData: ProjectMilestonesResponse | null; loading: boolean; error: string | null; isPreviewMode: boolean; onJournalChanged: () => Promise<void> }) {
  const [activeFilter, setActiveFilter] = useState<JournalViewFilter>('feed');
  const [contacts, setContacts] = useState<ProjectContactOption[]>([]);
  const [form, setForm] = useState<JournalFormState>({ open: false, entryType: 'note', title: '', content: '', priority: 'normal', targetDate: '', visibility: 'internal', ownerContactId: '', linkedTaskId: '', linkedMilestoneId: '', saving: false, message: null, error: null });
  const [actionState, setActionState] = useState<JournalActionState>({ savingId: null, message: null, error: null });
  const [editState, setEditState] = useState<JournalEditState>({ itemId: null, sourceId: null, entryType: 'note', title: '', content: '', status: 'open', priority: 'normal', targetDate: '', visibility: 'internal', ownerContactId: '', linkedTaskId: '', linkedMilestoneId: '', saving: false, error: null });

  const loadContacts = async () => {
    if (isPreviewMode || contacts.length > 0) return;
    const response = await fetch('/api/projet-v2/contacts', { credentials: 'include' });
    if (!response.ok) throw new Error(`Impossible de charger les contacts (HTTP ${response.status})`);
    const data = await response.json();
    setContacts(Array.isArray(data.contacts) ? data.contacts : []);
  };

  const openJournalForm = (entryType: JournalFormState['entryType'] = form.entryType) => {
    const defaults: Record<JournalFormState['entryType'], Pick<JournalFormState, 'priority' | 'visibility'>> = {
      note: { priority: 'normal', visibility: 'internal' },
      decision: { priority: 'Haute', visibility: 'internal' },
      risk: { priority: 'Haute', visibility: 'internal' },
      communication: { priority: 'normal', visibility: 'client_candidate' },
    };
    setForm((current) => ({ ...current, open: true, entryType, ...defaults[entryType], message: null, error: null }));
    loadContacts().catch((err) => setForm((current) => ({ ...current, error: err instanceof Error ? err.message : 'Erreur contacts' })));
  };

  const createJournalEntry = async () => {
    const title = form.title.trim();
    if (!title) {
      setForm((current) => ({ ...current, error: 'Titre requis. Capture le point en une phrase claire.' }));
      return;
    }

    if (isPreviewMode) {
      setForm({ open: false, entryType: 'note', title: '', content: '', priority: 'normal', targetDate: '', visibility: 'internal', ownerContactId: '', linkedTaskId: '', linkedMilestoneId: '', saving: false, message: 'Entrée créée en preview.', error: null });
      return;
    }

    try {
      setForm((current) => ({ ...current, saving: true, message: null, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/journal`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry_type: form.entryType,
          title,
          content: form.content,
          priority: form.priority,
          target_date: form.targetDate,
          visibility: form.visibility,
          owner_contact_id: form.ownerContactId,
          linked_task_id: form.linkedTaskId,
          linked_milestone_id: form.linkedMilestoneId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de créer l’entrée (HTTP ${response.status})`);
      await onJournalChanged();
      setActiveFilter(form.entryType);
      setForm({ open: false, entryType: 'note', title: '', content: '', priority: 'normal', targetDate: '', visibility: 'internal', ownerContactId: '', linkedTaskId: '', linkedMilestoneId: '', saving: false, message: 'Entrée ajoutée au journal.', error: null });
    } catch (err) {
      setForm((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur création journal' }));
    }
  };

  const updateJournalEntryStatus = async (item: ProjectJournalFeedItem, status: string, doneLabel: string) => {
    if (item.source !== 'journal' || !item.source_id) return;

    if (isPreviewMode) {
      setActionState({ savingId: null, message: `${doneLabel} en preview.`, error: null });
      return;
    }

    try {
      setActionState({ savingId: item.id, message: null, error: null });
      const response = await fetch(`/api/projet-v2/projects/${projectId}/journal/${item.source_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de mettre à jour le journal (HTTP ${response.status})`);
      await onJournalChanged();
      setActionState({ savingId: null, message: doneLabel, error: null });
    } catch (err) {
      setActionState({ savingId: null, message: null, error: err instanceof Error ? err.message : 'Erreur action journal' });
    }
  };

  const startEditingJournalEntry = (item: ProjectJournalFeedItem) => {
    if (item.source !== 'journal' || !item.source_id) return;
    const entryType = ['note', 'decision', 'risk', 'communication'].includes(String(item.entry_type))
      ? item.entry_type as JournalEditState['entryType']
      : 'note';
    setEditState({
      itemId: item.id,
      sourceId: item.source_id,
      entryType,
      title: item.title || '',
      content: item.content || '',
      status: item.status || 'open',
      priority: item.priority || 'normal',
      targetDate: formatDateInputValue(item.target_date),
      visibility: item.visibility === 'client_visible' ? 'client_visible' : item.visibility === 'client_candidate' ? 'client_candidate' : 'internal',
      ownerContactId: item.owner_contact_id ? String(item.owner_contact_id) : '',
      linkedTaskId: item.linked_task_id ? String(item.linked_task_id) : '',
      linkedMilestoneId: item.linked_milestone_id ? String(item.linked_milestone_id) : '',
      saving: false,
      error: null,
    });
    loadContacts().catch((err) => setEditState((current) => ({ ...current, error: err instanceof Error ? err.message : 'Erreur contacts' })));
  };

  const cancelEditingJournalEntry = () => {
    setEditState({ itemId: null, sourceId: null, entryType: 'note', title: '', content: '', status: 'open', priority: 'normal', targetDate: '', visibility: 'internal', ownerContactId: '', linkedTaskId: '', linkedMilestoneId: '', saving: false, error: null });
  };

  const saveJournalEntryEdit = async () => {
    if (!editState.sourceId || !editState.itemId) return;
    const title = editState.title.trim();
    if (!title) {
      setEditState((current) => ({ ...current, error: 'Titre requis. Une note sans titre, c’est une réunion sans café.' }));
      return;
    }

    if (isPreviewMode) {
      setEditState((current) => ({ ...current, saving: false, error: null }));
      setActionState({ savingId: null, message: 'Note modifiée en preview.', error: null });
      cancelEditingJournalEntry();
      return;
    }

    try {
      setEditState((current) => ({ ...current, saving: true, error: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/journal/${editState.sourceId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry_type: editState.entryType,
          title,
          content: editState.content,
          status: editState.status,
          priority: editState.priority,
          target_date: editState.targetDate,
          visibility: editState.visibility,
          owner_contact_id: editState.ownerContactId,
          linked_task_id: editState.linkedTaskId,
          linked_milestone_id: editState.linkedMilestoneId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de modifier la note (HTTP ${response.status})`);
      await onJournalChanged();
      cancelEditingJournalEntry();
      setActionState({ savingId: null, message: 'Note mise à jour.', error: null });
    } catch (err) {
      setEditState((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur modification note' }));
    }
  };

  if (loading) return <OverviewSkeleton />;
  if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  if (!journalData) return <EmptyState>Notes & suivis n’est pas encore chargé.</EmptyState>;

  const { summary } = journalData;
  const visibleFeedCount = summary.feedItems ?? summary.total;
  const attentionTone = summary.overdue > 0 ? 'red' : summary.attention > 0 ? 'amber' : visibleFeedCount > 0 ? 'green' : 'slate';
  const briefing = summary.overdue > 0
    ? `${summary.overdue} élément(s) du journal sont en retard.`
    : summary.attention > 0
      ? `${summary.attention} élément(s) méritent une attention.`
      : visibleFeedCount > 0
        ? 'Fil calme : les signaux récents sont disponibles sans ouvrir chaque tâche.'
        : 'Aucun élément capturé pour l’instant. Ajoute une entrée ou un commentaire de tâche pour alimenter le fil.';

  const feedItems: ProjectJournalFeedItem[] = journalData.feedItems?.length
    ? journalData.feedItems
    : journalData.entries.map((entry) => ({
      id: `journal-${entry.id}`,
      source: 'journal',
      source_id: entry.id,
      entry_type: entry.entry_type,
      title: entry.title,
      content: entry.content,
      status: entry.status,
      priority: entry.priority,
      target_date: entry.target_date,
      visibility: entry.visibility,
      occurred_at: entry.updated_at || entry.created_at,
      actor_name: entry.owner_name,
      owner_contact_id: entry.owner_contact_id,
      linked_task_id: entry.linked_task_id,
      linked_task_title: entry.linked_task_title,
      linked_milestone_id: entry.linked_milestone_id,
      linked_milestone_title: entry.linked_milestone_title,
      is_overdue: entry.is_overdue,
      needs_attention: entry.needs_attention,
    }));
  const nextMilestoneId = milestonesData?.summary?.nextMilestone?.id;
  const filteredFeedItems = activeFilter === 'feed'
    ? feedItems
    : activeFilter === 'attention'
      ? feedItems.filter((item) => item.needs_attention || item.is_overdue).slice(0, 12)
      : activeFilter === 'recent'
        ? feedItems.slice(0, 12)
        : activeFilter === 'next_milestone'
          ? feedItems.filter((item) => nextMilestoneId && item.linked_milestone_id === nextMilestoneId)
          : activeFilter === 'task_comment'
            ? feedItems.filter((item) => item.source === 'task_comment' || item.entry_type === 'task_comment')
            : feedItems.filter((item) => item.entry_type === activeFilter);

  const filters = [
    { key: 'feed' as const, label: 'Fil projet', count: feedItems.length, tone: 'green' as const },
    { key: 'attention' as const, label: 'À traiter', count: feedItems.filter((item) => item.needs_attention || item.is_overdue).length, tone: attentionTone },
    { key: 'recent' as const, label: 'Récents', count: Math.min(feedItems.length, 12), tone: 'slate' as const },
    { key: 'next_milestone' as const, label: 'Prochain jalon', count: nextMilestoneId ? feedItems.filter((item) => item.linked_milestone_id === nextMilestoneId).length : 0, tone: 'blue' as const },
    { key: 'task_comment' as const, label: 'Commentaires tâches', count: feedItems.filter((item) => item.source === 'task_comment' || item.entry_type === 'task_comment').length, tone: 'green' as const },
    { key: 'decision' as const, label: 'Décisions', count: summary.decisionsOpen, tone: 'blue' as const },
    { key: 'risk' as const, label: 'Risques', count: summary.risksOpen, tone: 'red' as const },
    { key: 'communication' as const, label: 'Communications', count: summary.communicationsOpen, tone: 'amber' as const },
    { key: 'note' as const, label: 'Notes', count: summary.notesOpen, tone: 'slate' as const },
  ];

  const journalTypeCards = [
    { key: 'note' as const, title: 'Note', helper: 'Mémoire interne rapide', tone: 'slate' as const },
    { key: 'decision' as const, title: 'Décision', helper: 'Arbitrage à suivre', tone: 'blue' as const },
    { key: 'risk' as const, title: 'Risque', helper: 'Point qui menace', tone: 'red' as const },
    { key: 'communication' as const, title: 'Suivi client', helper: 'Message ou relance', tone: 'amber' as const },
  ];

  const titlePlaceholder = {
    note: 'Ex. Client préfère un suivi en fin de journée',
    decision: 'Ex. Décider si la vague 3 inclut les archives',
    risk: 'Ex. Approbation fournisseur non confirmée',
    communication: 'Ex. Préparer un suivi client sur le changement de date',
  }[form.entryType];

  const contentPlaceholder = {
    note: 'Contexte utile, observation, détail à ne pas perdre...',
    decision: 'Ce qui doit être tranché, options possibles, impact si non décidé...',
    risk: 'Ce qui peut arriver, impact probable, mitigation envisagée...',
    communication: 'Audience, message à préparer, promesse faite ou relance à effectuer...',
  }[form.entryType];

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-md">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <NotebookPen className="h-4 w-4 text-slate-700" />
              <h4 className="text-sm font-semibold text-slate-950">Notes & suivis — journal de pilotage BZ</h4>
              <Badge tone={attentionTone}>{summary.attention > 0 ? 'Attention' : 'Lecture'}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-600">{briefing}</p>
            <div className="mt-3 grid gap-2 md:grid-cols-4">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Fil projet</p><p className="mt-1 text-sm font-semibold text-emerald-800">{summary.feedItems ?? feedItems.length}</p></div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Décisions</p><p className="mt-1 text-sm font-semibold text-blue-800">{summary.decisionsOpen}</p></div>
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Risques</p><p className="mt-1 text-sm font-semibold text-rose-800">{summary.risksOpen}</p></div>
              <div className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commentaires</p><p className="mt-1 text-sm font-semibold text-slate-800">{summary.commentsRecent ?? 0}</p></div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge tone="green">B4.4 actif</Badge>
            <button type="button" onClick={() => openJournalForm('note')} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800">
              <Plus className="h-3.5 w-3.5" />
              Note rapide
            </button>
          </div>
        </div>

        {form.message ? <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{form.message}</div> : null}
        {actionState.message ? <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">{actionState.message}</div> : null}
        {actionState.error ? <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{actionState.error}</div> : null}

        {form.open ? (
          <div className="mt-4 space-y-4 rounded-2xl border border-slate-300 bg-slate-100 p-4 shadow-inner">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h5 className="text-sm font-semibold text-slate-900">Ajouter une note ou un suivi</h5>
                <p className="mt-1 text-xs text-slate-500">Capture rapide : choisis le type, écris l’essentiel, lie au contexte seulement si utile.</p>
              </div>
              <button type="button" onClick={() => setForm((current) => ({ ...current, open: false, error: null }))} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-400 hover:bg-blue-50">Fermer</button>
            </div>

            <div className="grid gap-2 md:grid-cols-4">
              {journalTypeCards.map((card) => (
                <button key={card.key} type="button" onClick={() => openJournalForm(card.key)} className={`rounded-xl border p-3 text-left shadow-sm transition ${form.entryType === card.key ? 'border-blue-700 bg-white ring-2 ring-blue-100' : 'border-slate-300 bg-white hover:border-blue-500 hover:bg-blue-50'}`}>
                  <Badge tone={card.tone}>{card.title}</Badge>
                  <p className="mt-2 text-xs text-slate-500">{card.helper}</p>
                </button>
              ))}
            </div>

            {form.error ? <div className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700">{form.error}</div> : null}

            <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <label className="space-y-1 text-xs font-semibold text-slate-600">Titre<input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder={titlePlaceholder} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Priorité<select value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value as JournalFormState['priority'] }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="normal">Normale</option><option value="Haute">Haute</option><option value="Critique">Critique</option></select></label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">Date cible<input type="date" value={form.targetDate} onChange={(event) => setForm((current) => ({ ...current, targetDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            </div>

            <label className="block space-y-1 text-xs font-semibold text-slate-600">Résumé<textarea value={form.content} onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))} rows={8} placeholder={contentPlaceholder} className="min-h-[220px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal leading-relaxed text-slate-800" /></label>

            <details className="rounded-xl border border-slate-200 bg-white p-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-700">Contexte optionnel</summary>
              <div className="mt-3 grid gap-3 lg:grid-cols-4">
                <label className="space-y-1 text-xs font-semibold text-slate-600">Responsable<select value={form.ownerContactId} onChange={(event) => setForm((current) => ({ ...current, ownerContactId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">Non assigné</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}{contact.email_primary ? ` — ${contact.email_primary}` : ''}</option>)}</select></label>
                <label className="space-y-1 text-xs font-semibold text-slate-600">Visibilité<select value={form.visibility} onChange={(event) => setForm((current) => ({ ...current, visibility: event.target.value as JournalFormState['visibility'] }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="internal">Interne</option><option value="client_candidate">Client potentiel</option></select></label>
                <label className="space-y-1 text-xs font-semibold text-slate-600">Tâche liée<select value={form.linkedTaskId} onChange={(event) => setForm((current) => ({ ...current, linkedTaskId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">Aucune</option>{(tasksData?.tasks || []).filter((task) => !task.isjalon).map((task) => <option key={task.id} value={task.id}>{task.titre}</option>)}</select></label>
                <label className="space-y-1 text-xs font-semibold text-slate-600">Jalon lié<select value={form.linkedMilestoneId} onChange={(event) => setForm((current) => ({ ...current, linkedMilestoneId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">Aucun</option>{(milestonesData?.milestones || []).map((milestone) => <option key={milestone.id} value={milestone.id}>{milestone.titre}</option>)}</select></label>
              </div>
            </details>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Astuce UX : note simple par défaut; décision, risque ou suivi client seulement quand ça mérite un traitement.</p>
              <button type="button" disabled={form.saving} onClick={createJournalEntry} className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60">Enregistrer le suivi</button>
            </div>
          </div>
        ) : null}
      </section>

      {journalData.recommendedActions.length > 0 ? (
        <section className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <h4 className="text-sm font-semibold text-blue-950">Actions recommandées</h4>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {journalData.recommendedActions.map((action) => (
              <div key={action.id} className="rounded-xl border border-blue-100 bg-white p-3">
                <Badge tone={toneFromValue(action.tone)}>{action.title}</Badge>
                <p className="mt-2 text-sm text-slate-600">{action.description}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Fil Notes & suivis</h4>
            <p className="mt-1 text-sm text-slate-500">Notes projet + décisions + risques + commentaires de tâches au même endroit, pour voir ce qui se passe sans ouvrir chaque objet.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {filters.map((filter) => (
            <button key={filter.key} type="button" onClick={() => setActiveFilter(filter.key)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${activeFilter === filter.key ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}>
              {filter.label} · {filter.count}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          {filteredFeedItems.length === 0 ? (
            <EmptyState>{feedItems.length === 0 ? 'Aucun signal dans le fil pour l’instant. Les entrées journal et commentaires de tâches apparaîtront ici.' : 'Aucun élément dans ce filtre.'}</EmptyState>
          ) : filteredFeedItems.slice(0, activeFilter === 'feed' ? 20 : 12).map((item) => {
            const quickAction = getJournalQuickAction(item);
            const isEditing = editState.itemId === item.id;
            return (
              <div
                key={item.id}
                onClick={() => !isEditing && item.source === 'journal' && startEditingJournalEntry(item)}
                className={`rounded-xl border border-l-4 shadow-sm transition ${isEditing ? 'p-5' : 'p-4'} ${item.source === 'journal' ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/40' : ''} ${isJournalClosed(item.status) ? 'border-slate-200 border-l-slate-300 bg-white opacity-75' : 'border-slate-300 border-l-blue-500 bg-slate-50'}`}
                title={item.source === 'journal' ? 'Clique pour modifier cette note directement dans la carte' : undefined}
              >
                {isEditing ? (
                  <div className="space-y-3" onClick={(event) => event.stopPropagation()}>
                    <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Titre<input value={editState.title} onChange={(event) => setEditState((current) => ({ ...current, title: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm" /></label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Type<select value={editState.entryType} onChange={(event) => setEditState((current) => ({ ...current, entryType: event.target.value as JournalEditState['entryType'] }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="note">Note</option><option value="decision">Décision</option><option value="risk">Risque</option><option value="communication">Suivi client</option></select></label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Statut<select value={editState.status} onChange={(event) => setEditState((current) => ({ ...current, status: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="open">Ouvert</option><option value="closed">Fermé</option><option value="resolved">Résolu</option><option value="decided">Décidé</option><option value="sent">Envoyé</option><option value="archived">Archivé</option></select></label>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-3">
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Priorité<select value={editState.priority} onChange={(event) => setEditState((current) => ({ ...current, priority: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="normal">Normale</option><option value="Haute">Haute</option><option value="Critique">Critique</option></select></label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Date cible<input type="date" value={editState.targetDate} onChange={(event) => setEditState((current) => ({ ...current, targetDate: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm" /></label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Visibilité<select value={editState.visibility} onChange={(event) => setEditState((current) => ({ ...current, visibility: event.target.value as JournalEditState['visibility'] }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="internal">Interne</option><option value="client_candidate">Client potentiel</option><option value="client_visible">Visible client</option></select></label>
                    </div>

                    <label className="block space-y-1 text-xs font-semibold text-slate-600">Résumé<textarea value={editState.content} onChange={(event) => setEditState((current) => ({ ...current, content: event.target.value }))} rows={12} className="min-h-[340px] w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-sm font-normal leading-relaxed text-slate-900 shadow-sm" /></label>

                    <div className="grid gap-3 lg:grid-cols-3">
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Responsable<select value={editState.ownerContactId} onChange={(event) => setEditState((current) => ({ ...current, ownerContactId: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="">Non assigné</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}{contact.email_primary ? ` — ${contact.email_primary}` : ''}</option>)}</select></label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Tâche liée<select value={editState.linkedTaskId} onChange={(event) => setEditState((current) => ({ ...current, linkedTaskId: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="">Aucune</option>{(tasksData?.tasks || []).filter((task) => !task.isjalon).map((task) => <option key={task.id} value={task.id}>{task.titre}</option>)}</select></label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">Jalon lié<select value={editState.linkedMilestoneId} onChange={(event) => setEditState((current) => ({ ...current, linkedMilestoneId: event.target.value }))} className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm"><option value="">Aucun</option>{(milestonesData?.milestones || []).map((milestone) => <option key={milestone.id} value={milestone.id}>{milestone.titre}</option>)}</select></label>
                    </div>

                    {editState.error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{editState.error}</div> : null}

                    <div className="flex flex-wrap justify-end gap-2">
                      <button type="button" disabled={editState.saving} onClick={cancelEditingJournalEntry} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-400 hover:bg-blue-50 disabled:opacity-60">Annuler</button>
                      <button type="button" disabled={editState.saving} onClick={saveJournalEntryEdit} className="rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-800 disabled:opacity-60">{editState.saving ? 'Sauvegarde…' : 'Sauvegarder'}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge tone={getJournalTypeTone(item.entry_type)}>{getJournalTypeLabel(item.entry_type)}</Badge>
                          <Badge tone={item.is_overdue ? 'red' : item.needs_attention ? 'amber' : isJournalClosed(item.status) ? 'green' : 'slate'}>{getJournalStatusLabel(item.status)}</Badge>
                          {item.source === 'task_comment' ? <Badge tone="green">Feed tâche</Badge> : item.visibility === 'client_candidate' ? <Badge tone="amber">Client potentiel</Badge> : item.visibility === 'client_visible' ? <Badge tone="blue">Visible client</Badge> : <Badge tone="slate">Interne</Badge>}
                        </div>
                        <p className="mt-2 text-sm font-semibold text-slate-900">{item.title}</p>
                        {item.content ? <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600 line-clamp-6">{item.content}</p> : null}
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <p>{formatDate(item.occurred_at)}</p>
                        {item.actor_name ? <p className="mt-1">Par {item.actor_name}</p> : null}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                        {item.target_date ? <span className="rounded-lg bg-white px-2 py-1">Date cible : {formatDate(item.target_date)}</span> : null}
                        {item.linked_milestone_title ? <span className="rounded-lg bg-white px-2 py-1">Lié au jalon : {item.linked_milestone_title}</span> : null}
                        {item.linked_task_title ? <span className="rounded-lg bg-white px-2 py-1">Lié à la tâche : {item.linked_task_title}</span> : null}
                      </div>
                      <div className="flex flex-wrap gap-2" onClick={(event) => event.stopPropagation()}>
                        {item.source === 'journal' ? (
                          <button type="button" onClick={() => startEditingJournalEntry(item)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-400 hover:bg-blue-50">Modifier</button>
                        ) : null}
                        {quickAction ? (
                          <button type="button" disabled={actionState.savingId === item.id} onClick={() => updateJournalEntryStatus(item, quickAction.status, quickAction.doneLabel)} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">
                            {actionState.savingId === item.id ? 'Mise à jour…' : quickAction.label}
                          </button>
                        ) : null}
                        {item.source === 'journal' && item.source_id && isJournalClosed(item.status) ? (
                          <button type="button" disabled={actionState.savingId === item.id} onClick={() => updateJournalEntryStatus(item, 'open', 'Entrée réouverte')} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-400 hover:bg-blue-50 disabled:opacity-60">
                            Réouvrir
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

const DELIVERABLE_STATUSES: Array<{ key: DeliverableStatus; label: string; tone: 'slate' | 'green' | 'amber' | 'blue' | 'red' }> = [
  { key: 'to_produce', label: 'À produire', tone: 'slate' },
  { key: 'in_progress', label: 'En cours', tone: 'blue' },
  { key: 'in_review', label: 'En validation', tone: 'amber' },
  { key: 'delivered', label: 'Livré', tone: 'green' },
  { key: 'blocked', label: 'Bloqué', tone: 'red' },
];

const DELIVERABLE_VISIBILITIES: Array<{ key: DeliverableVisibility; label: string; helper: string; tone: 'slate' | 'amber' | 'blue' }> = [
  { key: 'internal', label: 'Interne seulement', helper: 'Non exposé au client', tone: 'slate' },
  { key: 'client_candidate', label: 'Candidat client', helper: 'À valider avant diffusion', tone: 'amber' },
  { key: 'client_visible', label: 'Visible client', helper: 'Peut alimenter la vue client minimale', tone: 'blue' },
];

function getDeliverableStatusMeta(status?: string) {
  return DELIVERABLE_STATUSES.find((item) => item.key === status) || DELIVERABLE_STATUSES[0];
}

function getDeliverableVisibilityMeta(visibility?: string) {
  return DELIVERABLE_VISIBILITIES.find((item) => item.key === visibility) || DELIVERABLE_VISIBILITIES[0];
}

function ProjectDeliverablesSection({
  projectId,
  deliverablesData,
  tasksData,
  milestonesData,
  loading,
  error,
  isPreviewMode,
  onDeliverablesChanged,
}: {
  projectId: number;
  deliverablesData: ProjectDeliverablesResponse | null;
  tasksData: ProjectTasksResponse | null;
  milestonesData: ProjectMilestonesResponse | null;
  loading: boolean;
  error: string | null;
  isPreviewMode: boolean;
  onDeliverablesChanged: () => Promise<void> | void;
}) {
  const [activeStatus, setActiveStatus] = useState<'all' | DeliverableStatus>('all');
  const [contacts, setContacts] = useState<ProjectContactOption[]>([]);
  const [form, setForm] = useState<DeliverableFormState>({ open: false, title: '', description: '', status: 'to_produce', targetDate: '', ownerContactId: '', visibility: 'internal', linkedTaskId: '', linkedMilestoneId: '', internalNotes: '', saving: false, message: null, error: null });
  const [actionState, setActionState] = useState<DeliverableActionState>({ savingId: null, message: null, error: null });

  useEffect(() => {
    if (isPreviewMode) return;
    let cancelled = false;
    const loadContacts = async () => {
      const response = await fetch('/api/projet-v2/contacts', { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      if (!cancelled) setContacts(Array.isArray(data.contacts) ? data.contacts : []);
    };
    loadContacts().catch(() => undefined);
    return () => { cancelled = true; };
  }, [isPreviewMode]);

  const deliverables = deliverablesData?.deliverables || [];
  const filteredDeliverables = activeStatus === 'all' ? deliverables : deliverables.filter((item) => item.status === activeStatus);
  const tasks = tasksData?.tasks?.filter((task) => !task.isjalon) || [];
  const milestones = milestonesData?.milestones || [];

  const submitDeliverable = async () => {
    if (!form.title.trim()) {
      setForm((current) => ({ ...current, error: 'Titre requis pour le livrable.' }));
      return;
    }

    if (isPreviewMode) {
      setForm((current) => ({ ...current, message: 'Preview seulement : le livrable serait créé dans la base de dev.', error: null }));
      return;
    }

    try {
      setForm((current) => ({ ...current, saving: true, error: null, message: null }));
      const response = await fetch(`/api/projet-v2/projects/${projectId}/deliverables`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          status: form.status,
          target_date: form.targetDate || null,
          owner_contact_id: form.ownerContactId || null,
          visibility: form.visibility,
          linked_task_id: form.linkedTaskId || null,
          linked_milestone_id: form.linkedMilestoneId || null,
          internal_notes: form.internalNotes,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      setForm({ open: false, title: '', description: '', status: 'to_produce', targetDate: '', ownerContactId: '', visibility: 'internal', linkedTaskId: '', linkedMilestoneId: '', internalNotes: '', saving: false, message: null, error: null });
      await onDeliverablesChanged();
    } catch (err) {
      setForm((current) => ({ ...current, saving: false, error: err instanceof Error ? err.message : 'Erreur inconnue' }));
    }
  };

  const updateDeliverable = async (deliverable: ProjectDeliverable, patch: Partial<Pick<ProjectDeliverable, 'status' | 'visibility'>>) => {
    if (isPreviewMode) {
      setActionState({ savingId: null, message: 'Preview seulement : la modification serait persistée dans la base de dev.', error: null });
      return;
    }

    try {
      setActionState({ savingId: deliverable.id, message: null, error: null });
      const response = await fetch(`/api/projet-v2/projects/${projectId}/deliverables/${deliverable.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      setActionState({ savingId: null, message: 'Livrable mis à jour.', error: null });
      await onDeliverablesChanged();
    } catch (err) {
      setActionState({ savingId: null, message: null, error: err instanceof Error ? err.message : 'Erreur inconnue' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Livrables projet</h4>
          <p className="text-xs text-slate-500">Outputs concrets à produire, valider, remettre ou exposer au client.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {deliverablesData ? (
            <>
              <Badge tone="slate">{deliverablesData.summary.total} total</Badge>
              <Badge tone="blue">{deliverablesData.summary.open} ouverts</Badge>
              <Badge tone="green">{deliverablesData.summary.delivered} livrés</Badge>
              <Badge tone={deliverablesData.summary.blocked > 0 ? 'red' : 'green'}>{deliverablesData.summary.blocked} bloqués</Badge>
              <Badge tone="blue">{deliverablesData.summary.clientVisible} visibles client</Badge>
            </>
          ) : null}
          <button type="button" onClick={() => setForm((current) => ({ ...current, open: !current.open, error: null, message: null }))} className="rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-800">
            {form.open ? 'Fermer' : 'Ajouter un livrable'}
          </button>
        </div>
      </div>

      {loading ? <div className="h-28 animate-pulse rounded-xl bg-slate-100" /> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {actionState.message ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{actionState.message}</div> : null}
      {actionState.error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{actionState.error}</div> : null}

      {form.open ? (
        <section className="rounded-2xl border border-blue-300 bg-blue-50 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h5 className="text-sm font-semibold text-blue-950">Nouveau livrable</h5>
              <p className="text-xs text-blue-800">Garde-le simple : ce qui doit être remis, pour quand, et à qui c’est visible.</p>
            </div>
            <Badge tone="blue">MVP léger</Badge>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-xs font-semibold text-slate-600">Titre<input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="Ex. Rapport de recommandations v1" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Statut<select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as DeliverableStatus }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">{DELIVERABLE_STATUSES.map((status) => <option key={status.key} value={status.key}>{status.label}</option>)}</select></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Date cible<input type="date" value={form.targetDate} onChange={(event) => setForm((current) => ({ ...current, targetDate: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Visibilité<select value={form.visibility} onChange={(event) => setForm((current) => ({ ...current, visibility: event.target.value as DeliverableVisibility }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800">{DELIVERABLE_VISIBILITIES.map((visibility) => <option key={visibility.key} value={visibility.key}>{visibility.label}</option>)}</select></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Responsable<select value={form.ownerContactId} onChange={(event) => setForm((current) => ({ ...current, ownerContactId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">À préciser</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.nom_client}</option>)}</select></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600">Jalon lié<select value={form.linkedMilestoneId} onChange={(event) => setForm((current) => ({ ...current, linkedMilestoneId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">Aucun</option>{milestones.map((milestone) => <option key={milestone.id} value={milestone.id}>{milestone.titre}</option>)}</select></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600 md:col-span-2">Tâche liée<select value={form.linkedTaskId} onChange={(event) => setForm((current) => ({ ...current, linkedTaskId: event.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800"><option value="">Aucune</option>{tasks.map((task) => <option key={task.id} value={task.id}>{task.titre}</option>)}</select></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600 md:col-span-2">Description<textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} rows={2} placeholder="Ce que contient le livrable et le résultat attendu." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
            <label className="space-y-1 text-xs font-semibold text-slate-600 md:col-span-2">Notes internes<textarea value={form.internalNotes} onChange={(event) => setForm((current) => ({ ...current, internalNotes: event.target.value }))} rows={2} placeholder="Contexte interne non destiné au client." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-800" /></label>
          </div>
          {form.error ? <p className="mt-3 text-sm text-rose-700">{form.error}</p> : null}
          {form.message ? <p className="mt-3 text-sm text-emerald-700">{form.message}</p> : null}
          <div className="mt-4 flex justify-end">
            <button type="button" disabled={form.saving} onClick={submitDeliverable} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:opacity-60">{form.saving ? 'Création…' : 'Créer le livrable'}</button>
          </div>
        </section>
      ) : null}

      {deliverablesData?.recommendedActions?.length ? (
        <div className="grid gap-2 md:grid-cols-2">
          {deliverablesData.recommendedActions.map((action) => <div key={action.id} className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"><Badge tone={toneFromValue(action.tone)}>{action.title}</Badge><p className="mt-2 text-sm text-blue-900">{action.description}</p></div>)}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setActiveStatus('all')} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${activeStatus === 'all' ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}>Tous</button>
        {DELIVERABLE_STATUSES.map((status) => <button key={status.key} type="button" onClick={() => setActiveStatus(status.key)} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${activeStatus === status.key ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}>{status.label}</button>)}
      </div>

      {!loading && !error && filteredDeliverables.length === 0 ? (
        <EmptyState>{deliverables.length === 0 ? 'Aucun livrable encore créé. Ajoute les outputs concrets attendus du projet.' : 'Aucun livrable ne correspond à ce filtre.'}</EmptyState>
      ) : null}

      <div className="grid gap-3">
        {filteredDeliverables.map((deliverable) => {
          const status = getDeliverableStatusMeta(deliverable.status);
          const visibility = getDeliverableVisibilityMeta(deliverable.visibility);
          return (
            <article key={deliverable.id} className={`rounded-2xl border border-l-4 p-4 shadow-sm ${deliverable.is_overdue || deliverable.status === 'blocked' ? 'border-rose-300 border-l-rose-600 bg-rose-50/70' : 'border-slate-300 border-l-blue-500 bg-white'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h5 className="text-sm font-semibold text-slate-900">{deliverable.title}</h5>
                    <Badge tone={status.tone}>{status.label}</Badge>
                    <Badge tone={visibility.tone}>{visibility.label}</Badge>
                    {deliverable.is_overdue ? <Badge tone="red">En retard</Badge> : null}
                  </div>
                  {deliverable.description ? <p className="mt-2 text-sm text-slate-600">{deliverable.description}</p> : null}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                    <span className="rounded-lg bg-slate-100 px-2 py-1">Cible : {formatDate(deliverable.target_date)}</span>
                    <span className="rounded-lg bg-slate-100 px-2 py-1">Resp. : {deliverable.owner_name || 'À préciser'}</span>
                    {deliverable.linked_milestone_title ? <span className="rounded-lg bg-slate-100 px-2 py-1">Jalon : {deliverable.linked_milestone_title}</span> : null}
                    {deliverable.linked_task_title ? <span className="rounded-lg bg-slate-100 px-2 py-1">Tâche : {deliverable.linked_task_title}</span> : null}
                  </div>
                  {deliverable.internal_notes ? <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"><span className="font-semibold">Note interne :</span> {deliverable.internal_notes}</p> : null}
                </div>
                <div className="flex min-w-[220px] flex-col gap-2">
                  <select value={deliverable.status} disabled={actionState.savingId === deliverable.id} onChange={(event) => updateDeliverable(deliverable, { status: event.target.value as DeliverableStatus })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                    {DELIVERABLE_STATUSES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  </select>
                  <select value={deliverable.visibility} disabled={actionState.savingId === deliverable.id} onChange={(event) => updateDeliverable(deliverable, { visibility: event.target.value as DeliverableVisibility })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                    {DELIVERABLE_VISIBILITIES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  </select>
                  <p className="text-xs text-slate-500">{visibility.helper}</p>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function getAgentSeverityTone(severity?: string): 'slate' | 'green' | 'amber' | 'blue' | 'red' {
  if (severity === 'elevee') return 'red';
  if (severity === 'moyenne') return 'amber';
  if (severity === 'info') return 'blue';
  return 'slate';
}

function getAgentActionLabel(action?: string) {
  const labels: Record<string, string> = {
    set_due_date: 'Proposer une échéance',
    assign_owner: 'Clarifier le responsable',
    requalify: 'Requalifier',
    create_task: 'Créer une tâche',
  };
  return action ? labels[action] || action : 'Décision humaine';
}

function formatAgentSchedule(schedule?: AgentSchedule) {
  const freq = schedule?.freq === 'hebdo'
    ? `Hebdo ${schedule.days?.join(', ') || 'vendredi'}`
    : schedule?.freq === 'quotidien'
      ? 'Chaque jour'
      : 'Jours ouvrables';
  return `${freq} à ${schedule?.time || '06:30'} (${schedule?.tz || 'America/Toronto'})`;
}

function formatAgentScope(scope?: { kind?: string; id?: number }) {
  if (!scope?.kind || scope.kind === 'mon_portefeuille' || scope.kind === 'mes_projets') return 'Mon portefeuille';
  if (scope?.kind === 'portefeuille') return `Portefeuille #${scope.id || ''}`;
  if (scope?.kind === 'client') return `Client #${scope.id || ''}`;
  if (scope?.kind === 'projet') return `Projet #${scope.id || ''}`;
  return 'Mon portefeuille';
}

function formatAgentRunDuration(seconds?: number | null) {
  if (!seconds && seconds !== 0) return 'n/d';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.round(seconds / 60)} min`;
}

const REJECT_REASONS = [
  { value: 'pas_pertinent', label: 'Pas pertinent' },
  { value: 'deja_regle', label: 'Déjà réglé' },
  { value: 'donnee_fausse', label: 'Donnée fausse' },
  { value: 'trop_tot', label: 'Trop tôt' },
];

function formatScoreBreakdown(scoreBreakdown?: Record<string, unknown> | null) {
  if (!scoreBreakdown) return 'Score non disponible.';
  const factors = (scoreBreakdown.factors || {}) as Record<string, number>;
  const weights = (scoreBreakdown.weights || {}) as Record<string, number>;
  const parts = [
    `Score ${scoreBreakdown.score ?? 'n/d'}`,
    `urgence ${factors.urgence_echeance ?? 0} x ${weights.urgence_echeance ?? 40}`,
    `retard ${factors.retard ?? 0} x ${weights.retard ?? 25}`,
    `priorité ${factors.priorite ?? 0} x ${weights.priorite ?? 15}`,
    `blocage ${factors.bloquee ?? 0} x ${weights.bloquee ?? 10}`,
    `fraîcheur ${factors.fraicheur_projet ?? 0} x ${weights.fraicheur_projet ?? 10}`,
  ];
  if (scoreBreakdown.capped_days_overdue != null) parts.push(`retard plafonné à ${scoreBreakdown.capped_days_overdue} j`);
  return parts.join(' · ');
}

function getAgentWorkItemWhyNow(item: AgentWorkItem) {
  if (item.reason) return item.reason;
  if (item.due_at) return `Échéance détectée: ${formatDate(item.due_at)}.`;
  if (item.severity === 'elevee') return 'Signal élevé dans le portefeuille: à traiter avant qu’il devienne un retard visible.';
  if (item.severity === 'moyenne') return 'Signal moyen: bon candidat pour une intervention courte avant accumulation.';
  return 'Signal de vigilance: utile pour garder le portefeuille propre.';
}

function getAgentWorkItemRisk(item: AgentWorkItem) {
  if (item.type?.includes('blocked') || item.body?.toLowerCase().includes('bloqu')) return 'Blocage ou attente qui peut ralentir la suite.';
  if (item.due_at) return 'Risque d’échéance, de retard ou de suivi client oublié.';
  if (item.type?.includes('owner') || item.body?.toLowerCase().includes('responsable')) return 'Responsabilité floue: personne ne porte clairement la prochaine action.';
  if (item.severity === 'elevee') return 'Risque opérationnel élevé si le signal reste sans décision.';
  return 'Risque faible à moyen, mais bruit évitable si on le classe maintenant.';
}

function getAgentWorkItemNextAction(item: AgentWorkItem) {
  const action = item.proposed_action?.action;
  if (action === 'create_task') return 'Créer ou confirmer l’action proposée.';
  if (action === 'assign_owner') return 'Nommer un responsable clair.';
  if (action === 'set_due_date') return 'Ajouter une échéance réaliste.';
  if (action === 'requalify') return 'Revoir la priorité ou le statut.';
  return 'Ouvrir le contexte, décider, puis fermer la boucle.';
}

function getAgentSuggestionLinkedTask(suggestion: AgentSuggestion) {
  const taskEvidence = suggestion.evidence?.find((entry) => entry.kind === 'tache');
  const proposedTaskId = suggestion.proposed_action?.payload?.task_id;
  const rawTaskId = proposedTaskId ?? taskEvidence?.id;
  const taskId = Number.parseInt(String(rawTaskId || ''), 10);
  return {
    id: Number.isFinite(taskId) ? taskId : null,
    title: taskEvidence?.label || null,
  };
}

function SuggestionCard({
  suggestion,
  compact = false,
  saving = false,
  onDecide,
  onUndo,
  onOpenEvidence,
  onOpenWorkContext,
}: {
  suggestion: AgentSuggestion;
  compact?: boolean;
  saving?: boolean;
  onDecide: (suggestion: AgentSuggestion, action: 'accept' | 'convert' | 'reject' | 'snooze', rejectReason?: string) => void;
  onUndo?: (suggestion: AgentSuggestion) => void;
  onOpenEvidence?: (evidence: { kind?: string; id?: number | string; label?: string }, suggestion: AgentSuggestion) => void;
  onOpenWorkContext?: (context: { projectId?: number | null; taskId?: number | null }) => void;
}) {
  const [showWhy, setShowWhy] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const border = suggestion.severity === 'elevee' ? 'border-rose-300 border-l-rose-600' : suggestion.severity === 'moyenne' ? 'border-amber-300 border-l-amber-500' : 'border-blue-200 border-l-blue-500';
  const body = (suggestion.body || '').split(/(?<=[.!?])\s+/).slice(0, 2).join(' ');
  const linkedTask = getAgentSuggestionLinkedTask(suggestion);

  return (
    <article className={`rounded-xl border border-l-4 bg-white ${compact ? 'p-3' : 'p-4'} shadow-sm ${border}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="slate">{suggestion.type}</Badge>
            <Badge tone={getAgentSeverityTone(suggestion.severity)}>{suggestion.severity}</Badge>
            <Badge tone="blue">{suggestion.confidence}%</Badge>
            {suggestion.project_title ? <Badge tone="slate">{suggestion.project_title}</Badge> : null}
          </div>
          <h5 className="mt-2 line-clamp-1 text-sm font-semibold text-slate-900">{suggestion.title}</h5>
          <p className={`${compact ? 'mt-1 text-xs' : 'mt-1 text-sm'} text-slate-600`}>{body}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {(suggestion.evidence || []).slice(0, compact ? 2 : 6).map((item) => (
              <button
                key={`${item.kind}-${item.id}`}
                type="button"
                onClick={() => onOpenEvidence?.(item, suggestion)}
                className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
              >
                {item.kind}: {item.label || item.id}
              </button>
            ))}
            <button type="button" onClick={() => setShowWhy((value) => !value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-600 hover:bg-slate-50">
              Pourquoi ce signal
            </button>
          </div>
          {showWhy ? (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {formatScoreBreakdown(suggestion.score_breakdown)}
            </div>
          ) : null}
        </div>
        <div className="flex min-w-[220px] flex-wrap justify-end gap-2">
          {onOpenWorkContext && suggestion.project_id ? (
            <button type="button" disabled={saving} onClick={() => onOpenWorkContext?.({ projectId: suggestion.project_id, taskId: linkedTask.id })} className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-white px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-50 disabled:opacity-60">
              {linkedTask.id ? 'Ouvrir la tâche' : 'Ouvrir le projet'}
            </button>
          ) : null}
          <button type="button" disabled={saving} onClick={() => onDecide(suggestion, 'convert')} className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-60">
            <Plus className="h-4 w-4" /> Créer une action
          </button>
          <button type="button" disabled={saving} onClick={() => onDecide(suggestion, 'snooze')} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Plus tard
          </button>
          <button type="button" disabled={saving} onClick={() => onDecide(suggestion, 'reject', 'pas_pertinent')} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Pas utile
          </button>
          <button type="button" disabled={saving} onClick={() => setShowMoreActions((value) => !value)} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60">
            Feedback
          </button>
          {showMoreActions ? (
            <div className="flex w-full flex-wrap justify-end gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
              <button type="button" disabled={saving} onClick={() => onDecide(suggestion, 'accept')} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">
                <CheckCircle2 className="h-4 w-4" /> C’est noté
              </button>
              {REJECT_REASONS.filter((reason) => reason.value !== 'pas_pertinent').map((reason) => (
                <button key={reason.value} type="button" disabled={saving} onClick={() => onDecide(suggestion, 'reject', reason.value)} className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-60">
                  {reason.label}
                </button>
              ))}
              {onUndo && suggestion.decided_at ? (
                <button type="button" disabled={saving} onClick={() => onUndo(suggestion)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60">
                  Annuler
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ProjectAgentSuggestionsSection({ projectId, isPreviewMode, onOpenWorkContext }: { projectId?: number; isPreviewMode?: boolean; onOpenWorkContext?: (context: { projectId?: number | null; taskId?: number | null }) => void }) {
  const [data, setData] = useState<AgentSuggestionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const loadSuggestions = useCallback(async () => {
    if (isPreviewMode) {
      setData({ suggestions: [], summary: { total: 0, elevee: 0, moyenne: 0, info: 0 } });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const query = projectId ? `?projectId=${projectId}` : '';
      const response = await fetch(`/api/projet-v2/agent/suggestions${query}`, { credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setData(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isPreviewMode, projectId]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const runScan = async () => {
    if (isPreviewMode) {
      setMessage('Preview seulement : le scan créerait des suggestions dans la base de dev.');
      return;
    }
    try {
      setRunning(true);
      setMessage(null);
      setError(null);
      const scope = projectId ? { kind: 'projet', id: projectId } : { kind: 'mon_portefeuille' };
      const response = await fetch('/api/projet-v2/agent/run', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, cap: projectId ? 25 : 50 }),
      });
      const body: AgentRunResult & { error?: string } = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage(`Scan terminé: ${body.stats?.creees || 0} créée(s), ${body.stats?.dedupliquees || 0} dédupliquée(s), ${body.stats?.resolues || 0} résolue(s).`);
      await loadSuggestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setRunning(false);
    }
  };

  const removeSuggestionFromList = (suggestion: AgentSuggestion) => {
    setData((current) => {
      if (!current) return current;
      const removed = current.suggestions.some((item) => item.id === suggestion.id);
      if (!removed) return current;
      return {
        ...current,
        suggestions: current.suggestions.filter((item) => item.id !== suggestion.id),
        summary: {
          total: Math.max(0, current.summary.total - 1),
          elevee: suggestion.severity === 'elevee' ? Math.max(0, current.summary.elevee - 1) : current.summary.elevee,
          moyenne: suggestion.severity === 'moyenne' ? Math.max(0, current.summary.moyenne - 1) : current.summary.moyenne,
          info: suggestion.severity === 'info' ? Math.max(0, current.summary.info - 1) : current.summary.info,
        },
      };
    });
  };

  const decideSuggestion = async (suggestion: AgentSuggestion, action: 'accept' | 'convert' | 'reject' | 'snooze', rejectReason?: string) => {
    if (isPreviewMode) {
      setMessage('Preview seulement : la décision serait enregistrée dans agent_suggestions.');
      return;
    }
    try {
      setSavingId(suggestion.id);
      setError(null);
      const response = await fetch(`/api/projet-v2/agent/suggestions/${suggestion.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'reject' ? { action, rejectReason } : action === 'snooze' ? { action, snoozeDays: 7 } : { action }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage(action === 'convert'
        ? 'Suggestion traitée en action.'
        : action === 'snooze'
          ? 'Suggestion reportée de 7 jours.'
          : action === 'reject'
            ? 'Suggestion ignorée.'
            : 'Suggestion notée.');
      removeSuggestionFromList(suggestion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSavingId(null);
    }
  };

  const undoSuggestion = async (suggestion: AgentSuggestion) => {
    if (isPreviewMode) {
      setMessage('Preview seulement : annulation simulée.');
      return;
    }
    try {
      setSavingId(suggestion.id);
      setError(null);
      const response = await fetch(`/api/projet-v2/agent/suggestions/${suggestion.id}/undo`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage('Décision annulée.');
      await loadSuggestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSavingId(null);
    }
  };

  const suggestions = data?.suggestions || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="blue">Aide à décider</Badge>
            <Badge tone="slate">Validation humaine</Badge>
          </div>
          <h4 className="mt-2 text-sm font-semibold text-slate-900">{projectId ? 'Suggestions du projet' : 'Suggestions de mes projets'}</h4>
          <p className="mt-1 text-xs text-slate-500">PRISME détecte les points à traiter. Tu choisis quoi faire: créer une action, revoir plus tard, ou ignorer.</p>
        </div>
        <button type="button" disabled={running || loading} onClick={runScan} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-800 disabled:opacity-60">
          <Play className="h-4 w-4" /> {running ? 'Scan…' : 'Scanner maintenant'}
        </button>
      </div>

      {data ? (
        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-sm"><p className="text-xs text-slate-500">Actives</p><p className="mt-1 text-2xl font-semibold text-slate-950">{data.summary.total}</p></div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"><p className="text-xs text-rose-700">Élevées</p><p className="mt-1 text-2xl font-semibold text-rose-950">{data.summary.elevee}</p></div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"><p className="text-xs text-amber-700">Moyennes</p><p className="mt-1 text-2xl font-semibold text-amber-950">{data.summary.moyenne}</p></div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"><p className="text-xs text-blue-700">Info</p><p className="mt-1 text-2xl font-semibold text-blue-950">{data.summary.info}</p></div>
        </div>
      ) : null}

      {message ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {loading ? <div className="h-24 animate-pulse rounded-xl bg-slate-100" /> : null}

      {!loading && suggestions.length === 0 ? (
        <EmptyState>Aucune suggestion active. Lance un scan pour vérifier ton portefeuille.</EmptyState>
      ) : null}

      <div className="grid gap-3">
        {suggestions.map((suggestion) => (
          <SuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            saving={savingId === suggestion.id}
            onDecide={decideSuggestion}
            onUndo={undoSuggestion}
            onOpenWorkContext={onOpenWorkContext}
          />
        ))}
      </div>
    </div>
  );
}

function formatAgentBlockTime(value?: string | null) {
  if (!value) return 'À placer';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'À placer';
  return new Intl.DateTimeFormat('fr-CA', { hour: '2-digit', minute: '2-digit' }).format(date);
}

function getAgentWorkItemLinkedTask(item: AgentWorkItem) {
  const taskEvidence = item.evidence?.find((entry) => entry.kind === 'tache');
  const proposedTaskId = item.proposed_action?.payload?.task_id;
  const rawTaskId = proposedTaskId ?? taskEvidence?.id ?? (item.source_type === 'task' ? item.source_id : null);
  const taskId = Number.parseInt(String(rawTaskId || ''), 10);
  return {
    id: Number.isFinite(taskId) ? taskId : null,
    title: taskEvidence?.label || (item.source_type === 'task' ? item.title : null),
  };
}

function ProjectAgentTodaySection({
  isPreviewMode,
  period = 'day',
  onOpenWorkContext,
}: {
  isPreviewMode?: boolean;
  period?: 'day' | 'week' | 'month';
  onOpenWorkContext?: (context: { projectId?: number | null; taskId?: number | null }) => void;
}) {
  const [plan, setPlan] = useState<AgentDailyPlanResponse | null>(null);
  const [selectedRole, setSelectedRole] = useState<'pm' | 'cyber' | 'ti'>('pm');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [reasoningId, setReasoningId] = useState<number | null>(null);
  const [reasoningById, setReasoningById] = useState<Record<number, { reasoning: AgentWorkItemReasoning; usedLlm: boolean }>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadPlan = useCallback(async () => {
    if (isPreviewMode) {
      setPlan(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const endpoint = period === 'week' ? 'week-plan' : period === 'month' ? 'month-plan' : 'daily-plan';
      const response = await fetch(`/api/projet-v2/agent/${endpoint}?role=${selectedRole}`, { credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setPlan(body);
    } catch (err) {
      setPlan(null);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [isPreviewMode, period, selectedRole]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const removeWorkItemFromPlan = (item: AgentWorkItem) => {
    setPlan((current) => {
      if (!current) return current;
      const wasVisible = current.priorities.some((priority) => priority.id === item.id) || current.later.some((laterItem) => laterItem.id === item.id);
      const wasElevated = wasVisible && item.severity === 'elevee';
      return {
        ...current,
        priorities: current.priorities.filter((priority) => priority.id !== item.id),
        later: current.later.filter((laterItem) => laterItem.id !== item.id),
        blocks: current.blocks.filter((block) => block.workItemId !== item.id),
        summary: {
          ...current.summary,
          total: wasVisible ? Math.max(0, current.summary.total - 1) : current.summary.total,
          elevated: wasElevated ? Math.max(0, current.summary.elevated - 1) : current.summary.elevated,
          plannedMinutes: Math.max(0, (current.summary.plannedMinutes || 0) - (item.estimated_minutes || 0)),
        },
      };
    });
  };

  const decideWorkItem = async (item: AgentWorkItem, action: 'plan' | 'done' | 'dismiss' | 'snooze') => {
    if (isPreviewMode) {
      setMessage('Preview seulement : la décision serait enregistrée dans agent_work_items.');
      return;
    }
    try {
      setSavingId(item.id);
      setError(null);
      const response = await fetch(`/api/projet-v2/agent/work-items/${item.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'snooze' ? { action, snoozeDays: 1 } : { action }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage(action === 'plan' ? 'Bloc planifié dans Mon agent.' : action === 'done' ? 'Action marquée faite.' : action === 'snooze' ? 'Action reportée à demain.' : 'Action retirée du plan.');
      removeWorkItemFromPlan(item);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSavingId(null);
    }
  };

  const enrichWorkItem = async (item: AgentWorkItem) => {
    if (isPreviewMode) {
      setReasoningById((current) => ({
        ...current,
        [item.id]: {
          usedLlm: false,
          reasoning: {
            executiveSummary: item.reason || item.body,
            nextBestAction: 'Valider le prochain pas et garder la trace projet a jour.',
            whyNow: 'Preview seulement.',
          },
        },
      }));
      return;
    }
    try {
      setReasoningId(item.id);
      setError(null);
      const response = await fetch(`/api/projet-v2/agent/work-items/${item.id}/enrich`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setReasoningById((current) => ({ ...current, [item.id]: { reasoning: body.reasoning || {}, usedLlm: Boolean(body.usedLlm) } }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setReasoningId(null);
    }
  };

  const roleOptions: Array<{ code: 'pm' | 'cyber' | 'ti'; label: string }> = [
    { code: 'pm', label: 'PM' },
    { code: 'cyber', label: 'Cyber' },
    { code: 'ti', label: 'TI' },
  ];
  const priorities = plan?.priorities || [];
  const blocks = plan?.blocks || [];
  const titleByPeriod = period === 'week' ? 'Plan de semaine priorisé' : period === 'month' ? 'Plan mensuel priorisé' : 'Briefing agent du jour';
  const subtitleByPeriod = period === 'week'
    ? 'L’agent répartit les priorités de la semaine selon charge, échéances et rôle.'
    : period === 'month'
      ? 'L’agent révèle la charge à venir, les livrables et les risques qui méritent une avance.'
      : 'Ce briefing explique quoi traiter, pourquoi maintenant, quel risque éviter et quelle action rapide poser.';

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="blue">{period === 'week' ? 'Semaine' : period === 'month' ? 'Mois' : 'Aujourd’hui'}</Badge>
              <Badge tone="slate">{plan?.role.label || 'Profil agent'}</Badge>
            </div>
            <h4 className="mt-2 text-base font-semibold text-slate-950">{titleByPeriod}</h4>
            <p className="mt-1 text-sm text-slate-600">{subtitleByPeriod}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {roleOptions.map((role) => (
              <button key={role.code} type="button" onClick={() => setSelectedRole(role.code)} className={`rounded-lg border px-3 py-2 text-xs font-semibold ${selectedRole === role.code ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50'}`}>
                {role.label}
              </button>
            ))}
            <button type="button" disabled={loading} onClick={loadPlan} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:border-blue-400 hover:bg-blue-50 disabled:opacity-60">
              <Activity className="h-4 w-4" /> Rafraîchir
            </button>
          </div>
        </div>

        {plan ? (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-xs text-blue-700">Actions pertinentes</p>
              <p className="mt-1 text-2xl font-semibold text-blue-950">{plan.summary.total}</p>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
              <p className="text-xs text-rose-700">Priorité élevée</p>
              <p className="mt-1 text-2xl font-semibold text-rose-950">{plan.summary.elevated}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs text-emerald-700">Charge estimée</p>
              <p className="mt-1 text-2xl font-semibold text-emerald-950">{Math.round((plan.summary.plannedMinutes || 0) / 60 * 10) / 10} h</p>
              {typeof plan.summary.loadPercent === 'number' ? <p className="mt-1 text-xs text-emerald-700">{plan.summary.loadPercent}% de la capacité</p> : null}
            </div>
          </div>
        ) : null}
        {period === 'day' ? (
          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase text-slate-400">Lecture</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">L’agent filtre le bruit du portefeuille.</p>
              <p className="mt-1 text-xs text-slate-600">Il fait ressortir retards, blocages, responsabilités floues et suivis à risque.</p>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase text-blue-500">Action</p>
              <p className="mt-1 text-sm font-semibold text-blue-950">Chaque carte propose une décision courte.</p>
              <p className="mt-1 text-xs text-blue-800">Planifier, marquer fait, reporter, ouvrir le projet ou demander le pourquoi.</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase text-emerald-500">Feedback</p>
              <p className="mt-1 text-sm font-semibold text-emerald-950">Les refus et reports nettoient le signal.</p>
              <p className="mt-1 text-xs text-emerald-800">Le gestionnaire garde le contrôle et l’agent apprend ce qui aide vraiment.</p>
            </div>
          </div>
        ) : null}
      </div>

      {message ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {loading ? <div className="h-28 animate-pulse rounded-xl bg-slate-100" /> : null}

      {!loading && !plan ? <EmptyState>Aucun plan disponible pour le moment.</EmptyState> : null}
      {!loading && plan && priorities.length === 0 ? <EmptyState>Aucune action prioritaire détectée pour aujourd’hui.</EmptyState> : null}

      {priorities.length > 0 ? (
        <section className="grid gap-3 lg:grid-cols-3">
          {priorities.map((item) => (
            <article key={item.id} className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={getAgentSeverityTone(item.severity)}>{item.severity}</Badge>
                <Badge tone="slate">{item.type}</Badge>
                {item.estimated_minutes ? <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600"><Clock3 className="h-3 w-3" /> {item.estimated_minutes} min</span> : null}
              </div>
              <h5 className="mt-3 text-sm font-semibold text-slate-950">{item.title}</h5>
              <p className="mt-2 text-xs leading-5 text-slate-600">{item.body}</p>
              <div className="mt-3 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-700">
                <p><span className="font-semibold text-slate-950">Pourquoi maintenant:</span> {getAgentWorkItemWhyNow(item)}</p>
                <p><span className="font-semibold text-slate-950">Risque si ignoré:</span> {getAgentWorkItemRisk(item)}</p>
                <p><span className="font-semibold text-slate-950">Prochaine action:</span> {getAgentWorkItemNextAction(item)}</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                {item.project_title ? <span className="rounded-lg bg-slate-100 px-2 py-1">{item.project_title}</span> : null}
                {item.client_name ? <span className="rounded-lg bg-slate-100 px-2 py-1">{item.client_name}</span> : null}
                {item.due_at ? <span className="rounded-lg bg-slate-100 px-2 py-1">Échéance: {formatDate(item.due_at)}</span> : null}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {item.project_id ? (
                  <button type="button" onClick={() => onOpenWorkContext?.({ projectId: item.project_id, taskId: getAgentWorkItemLinkedTask(item).id })} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:border-blue-400 hover:bg-blue-50">
                    {getAgentWorkItemLinkedTask(item).id ? 'Ouvrir la tâche' : 'Ouvrir le projet'}
                  </button>
                ) : null}
                <button type="button" disabled={savingId === item.id} onClick={() => decideWorkItem(item, 'plan')} className="rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60">Planifier</button>
                <button type="button" disabled={savingId === item.id} onClick={() => decideWorkItem(item, 'done')} className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">Fait</button>
                <button type="button" disabled={savingId === item.id} onClick={() => decideWorkItem(item, 'snooze')} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Demain</button>
                <button type="button" disabled={savingId === item.id} onClick={() => decideWorkItem(item, 'dismiss')} className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-60">Pas utile</button>
                <button type="button" disabled={reasoningId === item.id} onClick={() => enrichWorkItem(item)} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-60">{reasoningId === item.id ? 'Analyse…' : 'Pourquoi'}</button>
              </div>
              {reasoningById[item.id] ? (
                <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-950">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="blue">{reasoningById[item.id].usedLlm ? 'LLM contrôlé' : 'Fallback'}</Badge>
                    <span className="font-semibold">{reasoningById[item.id].reasoning.nextBestAction || 'Prochaine action à valider.'}</span>
                  </div>
                  <p className="mt-1">{reasoningById[item.id].reasoning.whyNow || reasoningById[item.id].reasoning.executiveSummary}</p>
                </div>
              ) : null}
            </article>
          ))}
        </section>
      ) : null}

      {blocks.length > 0 ? (
        <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Blocs suggérés</h4>
          <div className="mt-3 grid gap-2">
            {blocks.map((block) => (
              <div key={`${block.workItemId}-${block.startsAt}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-10 w-16 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-semibold text-slate-700">{formatAgentBlockTime(block.startsAt)}</span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{block.title}</p>
                    <p className="text-xs text-slate-500">{block.durationMinutes || 30} min · {block.reason || 'Priorité agentique'}</p>
                    <div className="mt-1 flex flex-wrap gap-1 text-xs text-slate-500">
                      {block.projectTitle ? <span className="rounded-md bg-white px-2 py-0.5">Projet: {block.projectTitle}</span> : block.projectId ? <span className="rounded-md bg-white px-2 py-0.5">Projet #{block.projectId}</span> : null}
                      {block.clientName ? <span className="rounded-md bg-white px-2 py-0.5">Client: {block.clientName}</span> : null}
                      {block.linkedTaskTitle ? <span className="rounded-md bg-white px-2 py-0.5">Tâche: {block.linkedTaskTitle}</span> : null}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {block.projectId ? (
                    <button type="button" onClick={() => onOpenWorkContext?.({ projectId: block.projectId, taskId: block.linkedTaskId })} className="rounded-lg border border-blue-300 bg-white px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-50">
                      {block.linkedTaskId ? 'Ouvrir la tâche' : 'Ouvrir le projet'}
                    </button>
                  ) : null}
                  <Badge tone={getAgentSeverityTone(block.severity)}>{block.severity || 'info'}</Badge>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
}

function ProjectAgentControlCenterSection({ isPreviewMode }: { isPreviewMode?: boolean }) {
  const [data, setData] = useState<AgentControlCenterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadControlCenter = useCallback(async () => {
    if (isPreviewMode) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/projet-v2/agent/control-center', { credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setData(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [isPreviewMode]);

  useEffect(() => {
    loadControlCenter();
  }, [loadControlCenter]);

  const syncSignals = async () => {
    if (isPreviewMode) {
      setMessage('Preview seulement : les signaux externes seraient synchronisés.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/projet-v2/agent/external-signals/sync', { method: 'POST', credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage(`Signaux synchronisés : ${body.stats?.email || 0} courriel(s), ${body.stats?.document || 0} document(s).`);
      await loadControlCenter();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const metrics = data?.metrics || {};

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="blue">Agent OS</Badge>
              <Badge tone="slate">Qualité et signaux</Badge>
            </div>
            <h4 className="mt-2 text-base font-semibold text-slate-950">Centre de contrôle personnel</h4>
            <p className="mt-1 text-sm text-slate-600">Vue courte sur le bruit, l’adoption, les signaux augmentés et les projets qui ressortent.</p>
          </div>
          <button type="button" disabled={loading} onClick={syncSignals} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60">
            <Activity className="h-4 w-4" /> Synchroniser
          </button>
        </div>
        {message ? <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{message}</div> : null}
        {error ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
      </div>

      {loading ? <div className="h-24 animate-pulse rounded-xl bg-slate-100" /> : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"><p className="text-xs text-blue-700">Work items actifs</p><p className="mt-1 text-2xl font-semibold text-blue-950">{metrics.active_work_items || 0}</p></div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"><p className="text-xs text-emerald-700">Actions 30 jours</p><p className="mt-1 text-2xl font-semibold text-emerald-950">{metrics.actions_30d || 0}</p></div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"><p className="text-xs text-amber-700">Signaux augmentés</p><p className="mt-1 text-2xl font-semibold text-amber-950">{metrics.active_external_signals || 0}</p></div>
      </div>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Règles les plus bruyantes</h4>
          <div className="mt-3 grid gap-2">
            {(data?.byRule || []).slice(0, 8).map((row) => (
              <div key={`${row.rule}-${row.status}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="font-medium text-slate-800">{row.rule}</span>
                <span className="text-xs text-slate-500">{row.status} · {row.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Projets qui ressortent</h4>
          <div className="mt-3 grid gap-2">
            {(data?.byProject || []).slice(0, 8).map((row) => (
              <div key={row.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="font-medium text-slate-800">{row.title}</span>
                <span className="text-xs text-slate-500">{row.count} · urgence {row.max_urgency || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </section>
  );
}

function ProjectAgentHomeSection({ isPreviewMode, onOpenWorkContext }: { isPreviewMode?: boolean; onOpenWorkContext?: (context: { projectId?: number | null; taskId?: number | null }) => void }) {
  const [templates, setTemplates] = useState<AgentRoutineTemplate[]>([]);
  const [routines, setRoutines] = useState<UserAgentRoutine[]>([]);
  const [metrics, setMetrics] = useState<AgentMetrics>({ activeRoutines: 0, suggestions7d: 0, acceptanceRate: 0 });
  const [runs, setRuns] = useState<AgentRunLog[]>([]);
  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'month' | 'routines' | 'journal' | 'control'>('today');
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [routineConfigLoading, setRoutineConfigLoading] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [savingTemplateId, setSavingTemplateId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAgentMetrics = useCallback(async () => {
    if (isPreviewMode) return;
    try {
      setMetricsLoading(true);
      setError(null);
      const response = await fetch('/api/projet-v2/agent/metrics', { credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `Metrics HTTP ${response.status}`);
      setMetrics(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setMetricsLoading(false);
    }
  }, [isPreviewMode]);

  const loadRoutineConfig = useCallback(async () => {
    if (isPreviewMode) return;
    try {
      setRoutineConfigLoading(true);
      setError(null);
      const [templatesResponse, routinesResponse] = await Promise.all([
        fetch('/api/projet-v2/agent/routine-templates', { credentials: 'include' }),
        fetch('/api/projet-v2/agent/routines', { credentials: 'include' }),
      ]);
      const [templatesBody, routinesBody] = await Promise.all([
        templatesResponse.json().catch(() => ({})),
        routinesResponse.json().catch(() => ({})),
      ]);
      if (!templatesResponse.ok) throw new Error(templatesBody.error || `Templates HTTP ${templatesResponse.status}`);
      if (!routinesResponse.ok) throw new Error(routinesBody.error || `Routines HTTP ${routinesResponse.status}`);
      setTemplates(Array.isArray(templatesBody.templates) ? templatesBody.templates : []);
      setRoutines(Array.isArray(routinesBody.routines) ? routinesBody.routines : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setRoutineConfigLoading(false);
    }
  }, [isPreviewMode]);

  const loadRuns = useCallback(async () => {
    if (isPreviewMode) return;
    try {
      setRunsLoading(true);
      setError(null);
      const response = await fetch('/api/projet-v2/agent/runs', { credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `Journal HTTP ${response.status}`);
      setRuns(Array.isArray(body.runs) ? body.runs : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setRunsLoading(false);
    }
  }, [isPreviewMode]);

  useEffect(() => {
    loadAgentMetrics();
  }, [loadAgentMetrics]);

  useEffect(() => {
    if (activeTab === 'routines') loadRoutineConfig();
    if (activeTab === 'journal') loadRuns();
  }, [activeTab, loadRoutineConfig, loadRuns]);

  const enableTemplate = async (template: AgentRoutineTemplate) => {
    if (isPreviewMode) {
      setMessage('Preview seulement : la routine serait configurée pour ton utilisateur.');
      return;
    }
    try {
      setSavingTemplateId(template.id);
      setError(null);
      const response = await fetch('/api/projet-v2/agent/routines', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: template.id,
          schedule: template.default_schedule,
          scope: { kind: 'mon_portefeuille' },
          channel: 'inapp',
          cap: 5,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage('Routine activée dans Mon agent.');
      await Promise.all([loadRoutineConfig(), loadAgentMetrics()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSavingTemplateId(null);
    }
  };

  const toggleRoutine = async (routine: UserAgentRoutine) => {
    if (isPreviewMode) return;
    try {
      setError(null);
      const response = await fetch(`/api/projet-v2/agent/routines/${routine.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !routine.enabled }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage(!routine.enabled ? 'Routine activée.' : 'Routine mise en pause.');
      await Promise.all([loadRoutineConfig(), loadAgentMetrics()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const runRoutine = async (routine: UserAgentRoutine) => {
    if (isPreviewMode) return;
    try {
      setError(null);
      const response = await fetch(`/api/projet-v2/agent/routines/${routine.id}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: true }),
      });
      const body: AgentRunResult & { error?: string } = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
      setMessage(`Test terminé sans écriture: ${body.stats?.evaluees || 0} suggestion(s) détectée(s).`);
      await Promise.all([loadRoutineConfig(), loadAgentMetrics()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const activeTemplateIds = new Set(routines.map((routine) => routine.template_id));

  return (
    <div className="space-y-5 p-5">
      <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="blue">Mon agent</Badge>
              <Badge tone="slate">Work Graph</Badge>
            </div>
            <h3 className="mt-2 text-base font-semibold text-blue-950">Briefing, actions rapides et feedback avec validation humaine</h3>
            <p className="mt-1 text-sm text-blue-900">L’agent commence par augmenter la journée: il explique les priorités, propose l’action suivante et garde l’humain en contrôle.</p>
          </div>
          <Settings className="h-5 w-5 text-blue-700" />
        </div>
      </section>

      {message ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {metricsLoading ? <div className="h-24 animate-pulse rounded-xl bg-slate-100" /> : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-sm"><p className="text-xs text-slate-500">Routines actives</p><p className="mt-1 text-2xl font-semibold text-slate-950">{metrics.activeRoutines}</p></div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"><p className="text-xs text-blue-700">Suggestions 7 jours</p><p className="mt-1 text-2xl font-semibold text-blue-950">{metrics.suggestions7d}</p></div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"><p className="text-xs text-emerald-700">Taux d’acceptation</p><p className="mt-1 text-2xl font-semibold text-emerald-950">{metrics.acceptanceRate}%</p></div>
      </div>

      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-300 bg-slate-100 p-2">
        <button type="button" onClick={() => setActiveTab('today')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeTab === 'today' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700'}`}>Briefing</button>
        <button type="button" onClick={() => setActiveTab('week')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeTab === 'week' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700'}`}>Semaine</button>
        <button type="button" onClick={() => setActiveTab('month')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeTab === 'month' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700'}`}>Mois</button>
        <button type="button" onClick={() => setActiveTab('routines')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeTab === 'routines' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700'}`}>Routines</button>
        <button type="button" onClick={() => setActiveTab('journal')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeTab === 'journal' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700'}`}>Journal</button>
        <button type="button" onClick={() => setActiveTab('control')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${activeTab === 'control' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700'}`}>Contrôle</button>
      </div>

      {activeTab === 'today' ? <ProjectAgentTodaySection isPreviewMode={isPreviewMode} onOpenWorkContext={onOpenWorkContext} /> : null}
      {activeTab === 'week' ? <ProjectAgentTodaySection isPreviewMode={isPreviewMode} period="week" onOpenWorkContext={onOpenWorkContext} /> : null}
      {activeTab === 'month' ? <ProjectAgentTodaySection isPreviewMode={isPreviewMode} period="month" onOpenWorkContext={onOpenWorkContext} /> : null}
      {activeTab === 'control' ? <ProjectAgentControlCenterSection isPreviewMode={isPreviewMode} /> : null}

      {activeTab === 'routines' ? <ProjectAgentSuggestionsSection isPreviewMode={isPreviewMode} onOpenWorkContext={onOpenWorkContext} /> : null}

      {activeTab === 'routines' ? <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Gabarits disponibles</h4>
          {routineConfigLoading ? <div className="mt-3 h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
          <div className="mt-3 grid gap-3">
            {templates.map((template) => (
              <article key={template.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{template.name}</p>
                    <p className="mt-1 text-xs text-slate-600">{template.description}</p>
                    <div className="mt-2 flex flex-wrap gap-1">{template.rule_codes?.map((code) => <Badge key={code} tone="slate">{code}</Badge>)}</div>
                  </div>
                  <button type="button" disabled={activeTemplateIds.has(template.id) || savingTemplateId === template.id} onClick={() => enableTemplate(template)} className="rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-800 disabled:bg-slate-300">
                    {activeTemplateIds.has(template.id) ? 'Active' : savingTemplateId === template.id ? 'Activation…' : 'Activer'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Mes routines</h4>
          {routineConfigLoading ? <div className="mt-3 h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
          <div className="mt-3 grid gap-3">
            {!routineConfigLoading && routines.length === 0 ? <EmptyState>Aucune routine configurée pour le moment.</EmptyState> : null}
            {routines.map((routine) => (
              <article key={routine.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{routine.template_name || `Routine #${routine.id}`}</p>
                      <Badge tone={routine.enabled ? 'green' : 'slate'}>{routine.enabled ? 'Active' : 'Pause'}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-slate-600">{routine.template_description}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                      <span className="rounded-lg bg-slate-100 px-2 py-1">{formatAgentSchedule(routine.schedule)}</span>
                      <span className="rounded-lg bg-slate-100 px-2 py-1">{formatAgentScope(routine.scope)}</span>
                      <span className="rounded-lg bg-slate-100 px-2 py-1">Cap {routine.cap || 5}</span>
                      <span className="rounded-lg bg-slate-100 px-2 py-1">Canal {routine.channel || 'inapp'}</span>
                      <span className="rounded-lg bg-slate-100 px-2 py-1">Dernière: {formatDate(routine.last_run_at)}</span>
                      <span className="rounded-lg bg-slate-100 px-2 py-1">Prochaine: {formatDate(routine.next_run_at)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <button type="button" onClick={() => runRoutine(routine)} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100">Tester maintenant</button>
                    <button type="button" onClick={() => toggleRoutine(routine)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">{routine.enabled ? 'Pause' : 'Activer'}</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section> : null}

      {activeTab === 'journal' ? (
        <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Journal des exécutions</h4>
          {runsLoading ? <div className="mt-3 h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
          <div className="mt-3 grid gap-3">
            {!runsLoading && runs.length === 0 ? <EmptyState>Aucune exécution journalisée.</EmptyState> : null}
            {runs.map((run) => (
              <article key={run.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={run.status === 'ok' ? 'green' : run.status === 'partiel' ? 'amber' : 'red'}>{run.status}</Badge>
                      <p className="text-sm font-semibold text-slate-900">{run.routine_name || run.routine_code || `Run #${run.id}`}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                      <span className="rounded-lg bg-white px-2 py-1">Début: {formatDate(run.started_at)}</span>
                      <span className="rounded-lg bg-white px-2 py-1">Durée: {formatAgentRunDuration(run.duration_seconds)}</span>
                      <span className="rounded-lg bg-white px-2 py-1">Stats: {JSON.stringify(run.stats || {})}</span>
                    </div>
                    {run.error ? <p className="mt-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs text-rose-700">{run.error}</p> : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ProjectSectionPlaceholder({ section, onSelectSection }: { section: SectionCard; onSelectSection: (section: SectionKey) => void }) {
  const Icon = section.icon;
  const nextActions: Record<SectionKey, string[]> = {
    overview: ['Lire la santé projet', 'Identifier les points d’attention', 'Basculer vers la bonne section'],
    cadrage: ['Remplir la charte légère', 'Clarifier la portée et les exclusions', 'Identifier approbateurs et parties prenantes'],
    tasks: ['Consulter la liste, le Kanban ou le calendrier', 'Filtrer critiques / retards / blocages', 'Ajouter les actions rapides'],
    journal: ['Lire les décisions, risques, notes et communications au même endroit', 'Prioriser les éléments à traiter', 'Préparer la création rapide B4.2'],
    deliverables: ['Lister les documents et livrables', 'Ajouter état et date cible', 'Préparer visibilité client minimale'],
    agent: ['Scanner les règles déterministes', 'Décider humainement chaque suggestion', 'Convertir seulement après validation'],
  };

  return (
    <div className="rounded-2xl border border-slate-300 bg-slate-100 p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-slate-900">{section.title}</h4>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">{section.description}</p>
          </div>
        </div>
        <Badge tone={section.state === 'ready_next' ? 'blue' : 'slate'}>{section.state === 'ready_next' ? 'Prochaine étape B2' : 'Prévu V1'}</Badge>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {nextActions[section.key].map((action, index) => (
          <div key={action} className="rounded-xl border border-slate-300 border-l-4 border-l-blue-500 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Étape {index + 1}</p>
            <p className="mt-2 text-sm font-medium text-slate-800">{action}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button type="button" onClick={() => onSelectSection('overview')} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-400 hover:bg-blue-50">Retour à la vue d’ensemble</button>
        {section.key !== 'tasks' ? (
          <button type="button" onClick={() => onSelectSection('tasks')} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100">Aller vers B2 — Tâches</button>
        ) : null}
      </div>
    </div>
  );
}

export function MonAgentPage() {
  return (
    <div className="min-h-full w-full max-w-full overflow-x-hidden bg-slate-100">
      <Header title="LEX Assistant PRIsME" subtitle="Briefing quotidien, actions rapides, feedback et journal d’exécution" />
      <div className="px-6 pb-6">
        <section className="rounded-2xl border border-slate-300 bg-white shadow-md">
          <ProjectAgentHomeSection />
        </section>
      </div>
    </div>
  );
}

export default function ProjetsV2({ previewMode = false }: ProjetsV2Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [homeView, setHomeView] = useState<AgentHomeView>('portfolio');
  const [activeProjectListView, setActiveProjectListView] = useState<ProjectListView>('all');
  const defaultProjectListViewAppliedRef = useRef(false);
  const [orgPortfolios, setOrgPortfolios] = useState<SharedPortfolioRecord[]>([]);
  const [unassignedProjects, setUnassignedProjects] = useState<ProjectRecord[]>([]);
  const [projectArchiveFilter, setProjectArchiveFilter] = useState<ProjectArchiveFilter>('active');
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createProjectForm, setCreateProjectForm] = useState<ProjectCreateFormState>(emptyProjectCreateForm);
  const [createProjectSaving, setCreateProjectSaving] = useState(false);
  const [createProjectError, setCreateProjectError] = useState<string | null>(null);
  const [createProjectClients, setCreateProjectClients] = useState<OfficialClientOption[]>([]);
  const [createProjectClientsLoading, setCreateProjectClientsLoading] = useState(false);
  const [createProjectContacts, setCreateProjectContacts] = useState<ProjectContactOption[]>([]);
  const [createProjectContactsLoading, setCreateProjectContactsLoading] = useState(false);
  const [createProjectContactSearch, setCreateProjectContactSearch] = useState('');
  const [projectTemplates, setProjectTemplates] = useState<ProjectTemplateDefinition[]>([]);
  const [projectTemplateBlocks, setProjectTemplateBlocks] = useState<ProjectTemplateBlock[]>([]);
  const [projectTemplatesLoading, setProjectTemplatesLoading] = useState(false);
  const [blockConfigOpen, setBlockConfigOpen] = useState(false);
  const [blockConfigSaving, setBlockConfigSaving] = useState(false);
  const [blockConfigError, setBlockConfigError] = useState<string | null>(null);
  const [projectResponsibleContactsLoading, setProjectResponsibleContactsLoading] = useState(false);
  const [copyProjectTarget, setCopyProjectTarget] = useState<ProjectRecord | null>(null);
  const [copyProjectForm, setCopyProjectForm] = useState<ProjectCopyFormState | null>(null);
  const [copyProjectSaving, setCopyProjectSaving] = useState(false);
  const [copyProjectError, setCopyProjectError] = useState<string | null>(null);
  const [archiveProjectTarget, setArchiveProjectTarget] = useState<ProjectRecord | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveProjectSaving, setArchiveProjectSaving] = useState(false);
  const [archiveProjectError, setArchiveProjectError] = useState<string | null>(null);
  const [portfolioMessage, setPortfolioMessage] = useState<string | null>(null);
  const [takingChargeProjectId, setTakingChargeProjectId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [overview, setOverview] = useState<ProjectOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [tasksData, setTasksData] = useState<ProjectTasksResponse | null>(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [milestonesData, setMilestonesData] = useState<ProjectMilestonesResponse | null>(null);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [milestonesError, setMilestonesError] = useState<string | null>(null);
  const [journalData, setJournalData] = useState<ProjectJournalResponse | null>(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [deliverablesData, setDeliverablesData] = useState<ProjectDeliverablesResponse | null>(null);
  const [deliverablesLoading, setDeliverablesLoading] = useState(false);
  const [deliverablesError, setDeliverablesError] = useState<string | null>(null);
  const [myTasksData, setMyTasksData] = useState<MyTasksResponse | null>(null);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [myTasksError, setMyTasksError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>('tasks');
  const [taskMode, setTaskMode] = useState<ProjectTaskMode>('list');
  const [manualFocusedTaskId, setFocusedTaskId] = useState<number | null>(null);
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const [projectListOrders, setProjectListOrders] = useState<ProjectListOrders>({});
  const [editingProjectTitle, setEditingProjectTitle] = useState(false);
  const [projectTitleDraft, setProjectTitleDraft] = useState('');
  const [projectTitleSaving, setProjectTitleSaving] = useState(false);
  const [projectTitleError, setProjectTitleError] = useState<string | null>(null);
  const [portfolioHealthSuggestions, setPortfolioHealthSuggestions] = useState<AgentSuggestion[]>([]);
  const [portfolioHealthDrawerOpen, setPortfolioHealthDrawerOpen] = useState(false);
  const [createPortfolioOpen, setCreatePortfolioOpen] = useState(false);
  const [createPortfolioForm, setCreatePortfolioForm] = useState<PortfolioCreateFormState>(emptyPortfolioCreateForm);
  const [createPortfolioSaving, setCreatePortfolioSaving] = useState(false);
  const [createPortfolioError, setCreatePortfolioError] = useState<string | null>(null);
  const [portfolioOwnerContacts, setPortfolioOwnerContacts] = useState<ProjectContactOption[]>([]);
  const [portfolioOwnerContactsLoading, setPortfolioOwnerContactsLoading] = useState(false);
  const [portfolioOwnerSearch, setPortfolioOwnerSearch] = useState('');
  const [portfolioStakeholderContacts, setPortfolioStakeholderContacts] = useState<ProjectContactOption[]>([]);
  const [portfolioStakeholderContactsLoading, setPortfolioStakeholderContactsLoading] = useState(false);
  const [portfolioStakeholderSearch, setPortfolioStakeholderSearch] = useState('');
  const [projectResponsibleContacts, setProjectResponsibleContacts] = useState<ProjectContactOption[]>([]);

  const projectRouteMatch = location.pathname.match(/^\/(?:preview-projets|projets-v2)\/(\d+)/);
  const routeProjectId = projectRouteMatch ? Number(projectRouteMatch[1]) : null;
  const routeSearchParams = new URLSearchParams(location.search);
  const routeFocusedTaskId = routeSearchParams.get('taskId') ? Number(routeSearchParams.get('taskId')) : null;
  const focusedTaskId = manualFocusedTaskId ?? routeFocusedTaskId;
  const routePreviewMode = location.pathname.startsWith('/preview-projets');
  const isPreviewMode = previewMode || routePreviewMode;
  const canAccess = isPreviewMode || (!!user?.email && PROJETS_ALLOWED_EMAILS.includes(user.email.toLowerCase()));
  const defaultProjectListView = useMemo(() => getDefaultProjectListView(user?.email), [user?.email]);

  useEffect(() => {
    defaultProjectListViewAppliedRef.current = false;
  }, [user?.email]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/', { replace: true });
      return;
    }
  }, [canAccess, navigate]);

  useEffect(() => {
    if (!canAccess) return;

    if (isPreviewMode) {
      setProjects(PREVIEW_PROJECTS);
      setUnassignedProjects(PREVIEW_PROJECTS.filter((project) => project.id === 104));
      setOrgPortfolios(PREVIEW_SHARED_PORTFOLIOS.map((portfolio) => ({ ...portfolio, key: getPortfolioKey(portfolio) })));
      setSelectedProjectId((current) => current ?? PREVIEW_PROJECTS[0]?.id ?? null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const archiveQuery = projectArchiveFilter === 'archived' ? '?archived=true' : '';
        const accessibleResponse = await fetch(`/api/projet-v2/accessible-projects${archiveQuery}`, { credentials: 'include' });
        if (!accessibleResponse.ok) {
          throw new Error(`Impossible de charger les projets accessibles (HTTP ${accessibleResponse.status})`);
        }
        const data = await accessibleResponse.json();
        const accessibleData: ProjectRecord[] = Array.isArray(data.projects) ? data.projects : [];

        const unassignedResponse = await fetch(`/api/projet-v2/unassigned-projects${archiveQuery}`, { credentials: 'include' });
        if (!unassignedResponse.ok) {
          throw new Error(`Impossible de charger les projets à assigner (HTTP ${unassignedResponse.status})`);
        }
        const unassignedData = await unassignedResponse.json();
        const unassignedProjectsData: ProjectRecord[] = Array.isArray(unassignedData.projects) ? unassignedData.projects : [];

        const orgPortfolioResponse = await fetch('/api/projet-v2/org-portfolios', { credentials: 'include' });
        if (!orgPortfolioResponse.ok) {
          throw new Error(`Impossible de charger les portefeuilles organisationnels (HTTP ${orgPortfolioResponse.status})`);
        }
        const orgPortfolioData = await orgPortfolioResponse.json();
        const orgPortfoliosData: SharedPortfolioRecord[] = Array.isArray(orgPortfolioData.portfolios) ? orgPortfolioData.portfolios : [];

        const orderResponse = await fetch('/api/projet-v2/project-list-orders', { credentials: 'include' });
        const orderData = orderResponse.ok ? await orderResponse.json() : {};
        const loadedOrders = orderData?.orders && typeof orderData.orders === 'object' ? orderData.orders as ProjectListOrders : {};

        if (cancelled) return;
        setProjects(accessibleData);
        setUnassignedProjects(unassignedProjectsData);
        setOrgPortfolios(orgPortfoliosData);
        setProjectListOrders(loadedOrders);
        if (accessibleData.length > 0) {
          setSelectedProjectId((current) => current ?? accessibleData[0].id);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [canAccess, isPreviewMode, projectArchiveFilter]);

  const portfolioByKey = useMemo(() => Object.fromEntries(
    orgPortfolios.map((portfolio) => [getPortfolioKey(portfolio), portfolio]),
  ) as Record<string, SharedPortfolioRecord>, [orgPortfolios]);

  const projectListViews = useMemo(() => buildProjectListViews(orgPortfolios), [orgPortfolios]);

  useEffect(() => {
    if (!canAccess || routeProjectId || defaultProjectListViewAppliedRef.current) return;
    if (defaultProjectListView === 'all') {
      defaultProjectListViewAppliedRef.current = true;
      return;
    }
    if (!portfolioByKey[defaultProjectListView]) return;
    setActiveProjectListView(defaultProjectListView);
    defaultProjectListViewAppliedRef.current = true;
  }, [canAccess, defaultProjectListView, portfolioByKey, routeProjectId]);

  const allSelectableProjects = useMemo(
    () => [
      ...projects,
      ...unassignedProjects,
      ...orgPortfolios.flatMap((portfolio) => portfolio.projects.filter((project) => project.has_access !== false)),
    ].filter((project, index, list) => list.findIndex((candidate) => candidate.id === project.id) === index),
    [projects, orgPortfolios, unassignedProjects],
  );

  const selectedProject = useMemo(
    () => allSelectableProjects.find((project) => project.id === selectedProjectId) ?? null,
    [allSelectableProjects, selectedProjectId],
  );

  const portfolioProjectIds = useMemo(() => Object.fromEntries(
    orgPortfolios.map((portfolio) => [getPortfolioKey(portfolio), (portfolio.projects || []).map((project) => project.id)]),
  ) as Record<string, number[]>, [orgPortfolios]);
  const unassignedProjectIds = useMemo(
    () => new Set(unassignedProjects.map((project) => project.id)),
    [unassignedProjects],
  );

  const visibleProjectList = useMemo(() => {
    const list = activeProjectListView === 'all'
      ? projects
      : activeProjectListView === UNASSIGNED_PROJECTS_VIEW
        ? unassignedProjects
        : portfolioByKey[activeProjectListView]?.projects || [];
    return list.filter((project) => projectArchiveFilter === 'archived' ? Boolean(project.archived) : !project.archived);
  }, [activeProjectListView, portfolioByKey, projects, projectArchiveFilter, unassignedProjects]);

  const orderedVisibleProjectList = useMemo(
    () => orderProjectsByIds(visibleProjectList, activeProjectListView === 'all' ? projectListOrders.all || [] : []),
    [activeProjectListView, projectListOrders, visibleProjectList],
  );

  const projectListCounts = useMemo(() => ({
    all: projects.length,
    [UNASSIGNED_PROJECTS_VIEW]: unassignedProjects.length,
    ...Object.fromEntries(orgPortfolios.map((portfolio) => [getPortfolioKey(portfolio), portfolio.projects?.length || 0])),
  }) as Record<string, number>, [orgPortfolios, projects.length, unassignedProjects.length]);

  const activeProjectListViewMeta = projectListViews.find((view) => view.key === activeProjectListView) || projectListViews[0];
  const activePortfolioId = activeProjectListView === 'all' || activeProjectListView === UNASSIGNED_PROJECTS_VIEW ? null : portfolioByKey[activeProjectListView]?.id || null;
  const portfolioOwnerOptions = isPreviewMode
    ? [
        { id: 111, nom_client: 'Philippe Bolduc', email_primary: 'philippe.bolduc@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'PB' },
        { id: 33, nom_client: 'Mikael Drouin-Bouchard', email_primary: 'mikael.drouin-bouchard@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'MD' },
        { id: 44, nom_client: 'Éric Hains', email_primary: 'eric.hains@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'EH' },
      ]
    : portfolioOwnerContacts;
  const portfolioStakeholderOptions = isPreviewMode
    ? [
        ...portfolioOwnerOptions,
        { id: 222, nom_client: 'Sponsor Client', email_primary: 'sponsor.client@example.com', type_client: 'Client', avatar: 'SC' },
      ]
    : portfolioStakeholderContacts;

  useEffect(() => {
    if (isPreviewMode || !canAccess || homeView !== 'portfolio' || !activePortfolioId) {
      setPortfolioHealthSuggestions([]);
      return;
    }
    let cancelled = false;
    fetch(`/api/projet-v2/agent/suggestions?portfolioId=${activePortfolioId}&type=sante&limit=50`, { credentials: 'include' })
      .then((response) => response.json().then((payload) => ({ ok: response.ok, payload })))
      .then(({ ok, payload }) => {
        if (!cancelled && ok) setPortfolioHealthSuggestions(payload.suggestions || []);
      })
      .catch(() => {
        if (!cancelled) setPortfolioHealthSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activePortfolioId, canAccess, homeView, isPreviewMode]);

  useEffect(() => {
    if (!createPortfolioOpen || isPreviewMode || !canAccess) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setPortfolioOwnerContactsLoading(true);
        const params = new URLSearchParams();
        params.set('scope', 'users');
        if (portfolioOwnerSearch.trim()) params.set('search', portfolioOwnerSearch.trim());
        const response = await fetch(`/api/projet-v2/contacts${params.toString() ? `?${params.toString()}` : ''}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setPortfolioOwnerContacts(Array.isArray(data.contacts) ? data.contacts : []);
      } catch {
        if (!cancelled) setPortfolioOwnerContacts([]);
      } finally {
        if (!cancelled) setPortfolioOwnerContactsLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [canAccess, createPortfolioOpen, isPreviewMode, portfolioOwnerSearch]);

  useEffect(() => {
    if (!createPortfolioOpen || isPreviewMode || !canAccess) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setPortfolioStakeholderContactsLoading(true);
        const params = new URLSearchParams();
        if (portfolioStakeholderSearch.trim()) params.set('search', portfolioStakeholderSearch.trim());
        const response = await fetch(`/api/projet-v2/contacts${params.toString() ? `?${params.toString()}` : ''}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setPortfolioStakeholderContacts(Array.isArray(data.contacts) ? data.contacts : []);
      } catch {
        if (!cancelled) setPortfolioStakeholderContacts([]);
      } finally {
        if (!cancelled) setPortfolioStakeholderContactsLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [canAccess, createPortfolioOpen, isPreviewMode, portfolioStakeholderSearch]);

  useEffect(() => {
    if (!createProjectOpen || isPreviewMode || !canAccess) return;
    let cancelled = false;

    const loadClientsAndTemplates = async () => {
      try {
        setCreateProjectClientsLoading(true);
        setProjectTemplatesLoading(true);
        const [response, templatesResponse] = await Promise.all([
          fetch('/api/clients', { credentials: 'include' }),
          fetch('/api/projet-v2/project-templates', { credentials: 'include' }),
        ]);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const clients = (Array.isArray(data) ? data : [])
          .map((client) => ({
            ...client,
            id: Number(client.id),
            nom_client: String(client.nom_client || '').trim(),
          }))
          .filter((client) => Number.isInteger(client.id) && client.id > 0 && client.nom_client && client.statut !== false);
        const templatesData = templatesResponse.ok ? await templatesResponse.json().catch(() => ({})) : {};
        if (!cancelled) {
          setCreateProjectClients(clients);
          const loadedTemplates = Array.isArray(templatesData.templates) ? templatesData.templates : [];
          const loadedBlocks = Array.isArray(templatesData.blocks) ? templatesData.blocks : [];
          setProjectTemplates(loadedTemplates);
          setProjectTemplateBlocks(loadedBlocks);
          setCreateProjectForm((current) => {
            const currentTemplate = loadedTemplates.find((template: ProjectTemplateDefinition) => template.code === current.project_template) || loadedTemplates[0];
            return currentTemplate && current.selectedBlockCodes.length === 0 ? ({
              ...current,
              project_template: currentTemplate.code,
              category: currentTemplate.category || current.category,
              project_type: currentTemplate.project_type || current.project_type,
              priority: currentTemplate.priority || current.priority,
              selectedBlockCodes: (currentTemplate.blocks || []).filter((block: ProjectTemplateBlock) => block.default_selected !== false || block.required).map((block: ProjectTemplateBlock) => block.code),
            }) : current;
          });
        }
      } catch {
        if (!cancelled) {
          setCreateProjectClients([]);
          setProjectTemplates(fallbackProjectTemplates);
          setProjectTemplateBlocks([]);
        }
      } finally {
        if (!cancelled) {
          setCreateProjectClientsLoading(false);
          setProjectTemplatesLoading(false);
        }
      }
    };

    loadClientsAndTemplates();
    return () => {
      cancelled = true;
    };
  }, [canAccess, createProjectOpen, isPreviewMode]);

  useEffect(() => {
    if (!createProjectOpen || isPreviewMode || !canAccess) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setCreateProjectContactsLoading(true);
        const params = new URLSearchParams();
        if (createProjectContactSearch.trim()) params.set('search', createProjectContactSearch.trim());
        const response = await fetch(`/api/projet-v2/contacts${params.toString() ? `?${params.toString()}` : ''}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setCreateProjectContacts(Array.isArray(data.contacts) ? data.contacts : []);
      } catch {
        if (!cancelled) setCreateProjectContacts([]);
      } finally {
        if (!cancelled) setCreateProjectContactsLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [canAccess, createProjectContactSearch, createProjectOpen, isPreviewMode]);

  useEffect(() => {
    if (isPreviewMode) {
      setProjectResponsibleContactsLoading(false);
      setProjectResponsibleContacts([
        { id: 111, nom_client: 'PM BZ', email_primary: 'pm@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'PB' },
        { id: 333, nom_client: 'Expert technique', email_primary: 'expert@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'ET' },
      ]);
      return;
    }
    if (!canAccess) return;

    let cancelled = false;
    const loadResponsibleContacts = async () => {
      try {
        setProjectResponsibleContactsLoading(true);
        const response = await fetch('/api/projet-v2/contacts?scope=users', { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setProjectResponsibleContacts(Array.isArray(data.contacts) ? data.contacts : []);
      } catch {
        if (!cancelled) setProjectResponsibleContacts([]);
      } finally {
        if (!cancelled) setProjectResponsibleContactsLoading(false);
      }
    };

    loadResponsibleContacts();
    return () => {
      cancelled = true;
    };
  }, [canAccess, isPreviewMode]);

  const patchOrgPortfolioProjects = (portfolioKey: PortfolioKey, updater: (projects: ProjectRecord[]) => ProjectRecord[]) => {
    setOrgPortfolios((current) => current.map((portfolio) => {
      const key = getPortfolioKey(portfolio);
      return key === portfolioKey ? { ...portfolio, projects: updater(portfolio.projects || []) } : portfolio;
    }));
  };

  const openCreateProjectWizard = () => {
    setCreateProjectError(null);
    setCreateProjectContactSearch('');
    setCreateProjectForm(emptyProjectCreateForm(activePortfolioId));
    if (isPreviewMode) {
      setCreateProjectClients([
        { id: 101, nom_client: 'Client Alpha', type_client: 'Manufacturier', statut: true },
        { id: 102, nom_client: 'Client Beta', type_client: 'Services professionnels', statut: true },
      ]);
      setCreateProjectContacts([
        { id: 111, nom_client: 'PM BZ', email_primary: 'pm@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'PB' },
        { id: 222, nom_client: 'Sponsor Client', email_primary: 'sponsor.client@example.com', type_client: 'Client', avatar: 'SC' },
        { id: 333, nom_client: 'Expert technique', email_primary: 'expert@bzinc.ca', type_client: 'Utilisateur PRIsME', avatar: 'ET' },
      ]);
    }
    setCreateProjectOpen(true);
  };

  const createProject = async () => {
    const title = createProjectForm.title.trim();
    if (!title) {
      setCreateProjectError('Nom de projet requis.');
      return;
    }
    if (createProjectForm.start_date && createProjectForm.end_date && createProjectForm.start_date > createProjectForm.end_date) {
      setCreateProjectError('La date de fin doit être égale ou postérieure à la date de début.');
      return;
    }

    const buildRaciPayload = () => {
      // Le wizard ne crée qu’un responsable initial. Le RACI complet est la
      // source officielle du cadrage et se complète dans l’onglet Cadrage PMBOK.
      const contactId = Number(createProjectForm.ownerContactId);
      return Number.isInteger(contactId) && contactId > 0 ? [{ contactId, role: 'R' }] : [];
    };

    try {
      setCreateProjectSaving(true);
      setCreateProjectError(null);

      if (isPreviewMode) {
        const client = createProjectClients.find((item) => String(item.id) === createProjectForm.idclient);
        const createdProject: ProjectRecord = {
          id: Date.now(),
          title,
          description: createProjectForm.description.trim() || undefined,
          status: createProjectForm.status,
          priority: createProjectForm.priority,
          progress: 0,
          start_date: createProjectForm.start_date || null,
          end_date: createProjectForm.end_date || null,
          project_type: createProjectForm.project_type,
          idclient: createProjectForm.idclient ? Number(createProjectForm.idclient) : null,
          nom_client: client?.nom_client || undefined,
          archived: false,
          has_access: true,
        };
        setProjects((current) => [createdProject, ...current]);
        if (createProjectForm.portfolioId) {
          setOrgPortfolios((current) => current.map((portfolio) => (
            String(portfolio.id) === createProjectForm.portfolioId
              ? { ...portfolio, projects: [createdProject, ...(portfolio.projects || [])] }
              : portfolio
          )));
        }
        if (!createProjectForm.ownerContactId && createProjectForm.responsibleContacts.length === 0) {
          setUnassignedProjects((current) => [createdProject, ...current]);
        }
        setCreateProjectOpen(false);
        setPortfolioMessage(`Projet créé en preview : ${title}.`);
        openProject(createdProject.id);
        return;
      }

      const response = await fetch('/api/projet-v2/projects', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          idclient: createProjectForm.idclient || null,
          description: createProjectForm.description.trim() || null,
          portfolioId: createProjectForm.portfolioId || null,
          project_template: createProjectForm.project_template,
          project_type: createProjectForm.project_type,
          category: createProjectForm.category,
          priority: createProjectForm.priority,
          status: createProjectForm.status,
          start_date: createProjectForm.start_date || null,
          end_date: createProjectForm.end_date || null,
          tech: createProjectForm.tech,
          is_remuneration_variable: createProjectForm.is_remuneration_variable,
          raciContacts: buildRaciPayload(),
          createStarterTasks: createProjectForm.createStarterTasks,
          createStarterMilestones: createProjectForm.createStarterMilestones,
          selectedBlockCodes: createProjectForm.selectedBlockCodes,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de créer le projet (HTTP ${response.status})`);
      const createdProject = data.project as ProjectRecord | undefined;
      await reloadProjectSnapshots();
      setCreateProjectOpen(false);
      setCreateProjectForm(emptyProjectCreateForm(activePortfolioId));
      setPortfolioMessage(`Projet créé : ${createdProject?.title || title}.`);
      if (createdProject?.id) openProject(createdProject.id);
    } catch (err) {
      setCreateProjectError(err instanceof Error ? err.message : 'Impossible de créer le projet.');
    } finally {
      setCreateProjectSaving(false);
    }
  };

  const saveProjectTemplateBlock = async (block: ProjectTemplateBlock, items: ProjectTemplateBlockItem[]) => {
    if (isPreviewMode) {
      setBlockConfigOpen(false);
      setPortfolioMessage('Preview seulement : le bloc serait sauvegardé en base.');
      return;
    }
    try {
      setBlockConfigSaving(true);
      setBlockConfigError(null);
      const response = await fetch(`/api/projet-v2/project-template-blocks/${encodeURIComponent(block.code)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: block.name,
          description: block.description || null,
          category: block.category || null,
          block_type: block.block_type || 'static',
          active: block.active !== false,
          items,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de sauvegarder le bloc (HTTP ${response.status})`);
      const catalog = data.catalog || {};
      setProjectTemplates(Array.isArray(catalog.templates) ? catalog.templates : projectTemplates);
      setProjectTemplateBlocks(Array.isArray(catalog.blocks) ? catalog.blocks : projectTemplateBlocks);
      setPortfolioMessage(`Bloc sauvegardé : ${block.name}.`);
      setBlockConfigOpen(false);
    } catch (err) {
      setBlockConfigError(err instanceof Error ? err.message : 'Impossible de sauvegarder le bloc.');
    } finally {
      setBlockConfigSaving(false);
    }
  };

  const addProjectToPortfolio = async (projectId: number, portfolioKey: PortfolioKey) => {
    const portfolio = portfolioByKey[portfolioKey];
    const project = allSelectableProjects.find((candidate) => candidate.id === projectId);
    if (!portfolio || !project) return;
    const previousPortfolios = orgPortfolios;
    patchOrgPortfolioProjects(portfolioKey, (current) => current.some((item) => item.id === projectId) ? current : [...current, project]);
    setPortfolioMessage(`Projet ajouté au ${portfolio.name}.`);
    if (isPreviewMode) return;

    try {
      const response = await fetch(`/api/projet-v2/portfolios/${portfolio.id}/items`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch {
      setOrgPortfolios(previousPortfolios);
      setPortfolioMessage(`Impossible d’ajouter ce projet au ${portfolio.name}.`);
    }
  };

  const assignProjectResponsible = async (projectId: number, contactId: number) => {
    const contact = projectResponsibleContacts.find((candidate) => Number(candidate.id) === Number(contactId));
    const responsiblePatch: Partial<ProjectRecord> = {
      responsible_contact_id: contactId,
      responsible_contact_name: contact?.nom_client || null,
      responsible_contact_email: contact?.email_primary || null,
    };

    if (isPreviewMode) {
      patchProjectEverywhere(projectId, responsiblePatch);
      setUnassignedProjects((current) => current.filter((project) => project.id !== projectId));
      setPortfolioMessage(`Responsable assigné : ${contact?.nom_client || 'contact sélectionné'}.`);
      return;
    }

    const response = await fetch(`/api/projet-v2/projects/${projectId}/responsible`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contactId }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Impossible d’assigner le responsable (HTTP ${response.status})`);
    const serverProject = data.project || {};
    const persistedPatch: Partial<ProjectRecord> = {
      ...serverProject,
      responsible_contact_id: serverProject.responsible_contact_id ?? responsiblePatch.responsible_contact_id,
      responsible_contact_name: serverProject.responsible_contact_name ?? responsiblePatch.responsible_contact_name,
      responsible_contact_email: serverProject.responsible_contact_email ?? responsiblePatch.responsible_contact_email,
    };
    setUnassignedProjects((current) => current.filter((project) => project.id !== projectId));
    patchProjectEverywhere(projectId, persistedPatch);
    setPortfolioMessage(`Responsable assigné : ${contact?.nom_client || data.project?.title || 'contact sélectionné'}.`);
    await reloadProjectSnapshots();
    patchProjectEverywhere(projectId, persistedPatch);
    setUnassignedProjects((current) => current.filter((project) => project.id !== projectId));
  };

  const takeChargeProject = async (project: ProjectRecord) => {
    if (isPreviewMode) {
      setProjects((current) => current.map((candidate) => candidate.id === project.id ? { ...candidate, project_operational_status: 'taken_in_charge' } : candidate));
      setPortfolioMessage(`${project.title} pris en charge.`);
      return;
    }
    try {
      setTakingChargeProjectId(project.id);
      const response = await fetch(`/api/projet-v2/projects/${project.id}/take-charge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'intake-transition' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de prendre en charge (HTTP ${response.status})`);
      await reloadProjectSnapshots();
      setPortfolioMessage(`${data.project?.title || project.title} pris en charge.`);
    } catch (err) {
      setPortfolioMessage(err instanceof Error ? err.message : 'Impossible de prendre en charge le projet.');
    } finally {
      setTakingChargeProjectId(null);
    }
  };

  const createPortfolio = async () => {
    const ownerUserId = createPortfolioForm.ownerUserId.trim();
    const ownerName = createPortfolioForm.ownerName.trim();
    const name = createPortfolioForm.name.trim();
    const ownerEmail = createPortfolioForm.ownerEmail.trim();
    const description = createPortfolioForm.description.trim();
    const stakeholderUserIds = createPortfolioForm.stakeholderUsers
      .map((contact) => Number(contact.id))
      .filter((id, index, list) => Number.isInteger(id) && id > 0 && list.indexOf(id) === index && String(id) !== ownerUserId);

    if (!ownerUserId) {
      setCreatePortfolioError('Choisis un responsable dans la liste.');
      return;
    }
    if (!name) {
      setCreatePortfolioError('Nom du portefeuille requis.');
      return;
    }

    if (isPreviewMode) {
      const created: SharedPortfolioRecord = {
        id: Date.now(),
        key: `portfolio-${Date.now()}`,
        name,
        description: description || null,
        owner_name: ownerName,
        owner_email: ownerEmail || null,
        role: 'editor',
        visibility: createPortfolioForm.visibility,
        members: createPortfolioForm.stakeholderUsers,
        projects: [],
      };
      setOrgPortfolios((current) => [...current, created]);
      setActiveProjectListView(created.key!);
      setHomeView('portfolio');
      setCreatePortfolioOpen(false);
      setCreatePortfolioForm(emptyPortfolioCreateForm());
      setPortfolioMessage(`${created.name} créé en preview.`);
      return;
    }

    try {
      setCreatePortfolioSaving(true);
      setCreatePortfolioError(null);
      const response = await fetch('/api/projet-v2/org-portfolios', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerUserId,
          ownerName,
          ownerEmail: ownerEmail || null,
          name,
          description: description || null,
          visibility: createPortfolioForm.visibility,
          stakeholderUserIds,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de créer le portefeuille (HTTP ${response.status})`);
      const createdPortfolio = data.portfolio as SharedPortfolioRecord | undefined;
      await reloadProjectSnapshots();
      if (createdPortfolio) setActiveProjectListView(getPortfolioKey(createdPortfolio));
      setHomeView('portfolio');
      setCreatePortfolioOpen(false);
      setCreatePortfolioForm(emptyPortfolioCreateForm());
      setPortfolioMessage(`${createdPortfolio?.name || name} créé.`);
    } catch (err) {
      setCreatePortfolioError(err instanceof Error ? err.message : 'Impossible de créer le portefeuille.');
    } finally {
      setCreatePortfolioSaving(false);
    }
  };

  const removeProjectFromPortfolio = async (projectId: number, portfolioKey: PortfolioKey) => {
    const portfolio = portfolioByKey[portfolioKey];
    if (!portfolio) return;
    const previousPortfolios = orgPortfolios;
    patchOrgPortfolioProjects(portfolioKey, (current) => current.filter((project) => project.id !== projectId));
    setPortfolioMessage(`Projet retiré du ${portfolio.name}.`);
    if (isPreviewMode) return;

    try {
      const response = await fetch(`/api/projet-v2/portfolios/${portfolio.id}/items/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch {
      setOrgPortfolios(previousPortfolios);
      setPortfolioMessage(`Impossible de retirer ce projet du ${portfolio.name}.`);
    }
  };

  const updateProjectFollowup = (projectId: number, followup: string, updatedAt?: string | null) => {
    const patchProject = (project: ProjectRecord): ProjectRecord => project.id === projectId
      ? { ...project, followup_text: followup, followup_updated_at: updatedAt ?? new Date().toISOString() }
      : project;
    setProjects((current) => current.map(patchProject));
    setOrgPortfolios((current) => current.map((portfolio) => ({
      ...portfolio,
      projects: portfolio.projects.map(patchProject),
    })));
  };

  const patchProjectEverywhere = (projectId: number, updates: Partial<ProjectRecord>) => {
    const patchProject = (project: ProjectRecord): ProjectRecord => project.id === projectId ? { ...project, ...updates } : project;
    setProjects((current) => current.map(patchProject));
    setOrgPortfolios((current) => current.map((portfolio) => ({
      ...portfolio,
      projects: portfolio.projects.map(patchProject),
    })));
    setOverview((current) => current && current.project.id === projectId
      ? { ...current, project: { ...current.project, ...updates } }
      : current);
  };

  const saveProjectPatch = async (projectId: number, updates: Partial<ProjectRecord>) => {
    const previousProjects = projects;
    const previousPortfolios = orgPortfolios;
    const previousOverview = overview;

    patchProjectEverywhere(projectId, updates);
    setPortfolioMessage(null);

    if (isPreviewMode) return;

    try {
      const response = await fetch(`/api/projet-v2/projects/${projectId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de sauvegarder le projet (HTTP ${response.status})`);
      if (data.project) patchProjectEverywhere(projectId, data.project);
    } catch (err) {
      setProjects(previousProjects);
      setOrgPortfolios(previousPortfolios);
      setOverview(previousOverview);
      throw err;
    }
  };

  const openCopyProjectModal = (project: ProjectRecord) => {
    setCopyProjectTarget(project);
    setCopyProjectForm(buildProjectCopyForm(project));
    setCopyProjectError(null);
  };

  const closeCopyProjectModal = () => {
    if (copyProjectSaving) return;
    setCopyProjectTarget(null);
    setCopyProjectForm(null);
    setCopyProjectError(null);
  };

  const copyProject = async () => {
    if (!copyProjectTarget || !copyProjectForm) return;
    const title = copyProjectForm.title.trim();
    if (!title) {
      setCopyProjectError('Nom du nouveau projet requis.');
      return;
    }
    try {
      setCopyProjectSaving(true);
      setCopyProjectError(null);
      if (isPreviewMode) {
        const copiedProject: ProjectRecord = { ...copyProjectTarget, id: Date.now(), title, status: copyProjectForm.status, archived: false, progress: 0 };
        setProjects((current) => [copiedProject, ...current]);
        setPortfolioMessage(`Projet copié : ${title}.`);
        setCopyProjectTarget(null);
        setCopyProjectForm(null);
        return;
      }
      const response = await fetch(`/api/projet-v2/projects/${copyProjectTarget.id}/copy`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...copyProjectForm, title, shiftDays: Number.parseInt(copyProjectForm.shiftDays || '0', 10) || 0 }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de copier le projet (HTTP ${response.status})`);
      if (data.project) setProjects((current) => [data.project, ...current]);
      setPortfolioMessage(`Projet copié : ${data.project?.title || title}.`);
      setCopyProjectTarget(null);
      setCopyProjectForm(null);
    } catch (err) {
      setCopyProjectError(err instanceof Error ? err.message : 'Impossible de copier le projet.');
    } finally {
      setCopyProjectSaving(false);
    }
  };

  const openArchiveProjectModal = (project: ProjectRecord) => {
    setArchiveProjectTarget(project);
    setArchiveReason('');
    setArchiveProjectError(null);
  };

  const closeArchiveProjectModal = () => {
    if (archiveProjectSaving) return;
    setArchiveProjectTarget(null);
    setArchiveReason('');
    setArchiveProjectError(null);
  };

  const archiveProject = async () => {
    if (!archiveProjectTarget) return;
    try {
      setArchiveProjectSaving(true);
      setArchiveProjectError(null);
      if (isPreviewMode) {
        setProjects((current) => current.filter((project) => project.id !== archiveProjectTarget.id));
        setPortfolioMessage(`Projet archivé : ${archiveProjectTarget.title}.`);
        setArchiveProjectTarget(null);
        return;
      }
      const response = await fetch(`/api/projet-v2/projects/${archiveProjectTarget.id}/archive`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archivedReason: archiveReason.trim() || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible d’archiver le projet (HTTP ${response.status})`);
      setProjects((current) => current.filter((project) => project.id !== archiveProjectTarget.id));
      setOrgPortfolios((current) => current.map((portfolio) => ({ ...portfolio, projects: portfolio.projects.filter((project) => project.id !== archiveProjectTarget.id) })));
      setPortfolioMessage(`Projet archivé : ${data.project?.title || archiveProjectTarget.title}.`);
      setArchiveProjectTarget(null);
    } catch (err) {
      setArchiveProjectError(err instanceof Error ? err.message : 'Impossible d’archiver le projet.');
    } finally {
      setArchiveProjectSaving(false);
    }
  };

  const restoreProject = async (project: ProjectRecord) => {
    try {
      setPortfolioMessage(null);
      if (isPreviewMode) {
        setProjects((current) => current.filter((item) => item.id !== project.id));
        setPortfolioMessage(`Projet restauré : ${project.title}.`);
        return;
      }
      const response = await fetch(`/api/projet-v2/projects/${project.id}/restore`, { method: 'POST', credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de restaurer le projet (HTTP ${response.status})`);
      setProjects((current) => current.filter((item) => item.id !== project.id));
      setPortfolioMessage(`Projet restauré : ${data.project?.title || project.title}.`);
    } catch (err) {
      setPortfolioMessage(err instanceof Error ? err.message : 'Impossible de restaurer le projet.');
    }
  };

  const startEditingProjectTitle = () => {
    if (!selectedProject) return;
    setProjectTitleDraft(selectedProject.title || '');
    setProjectTitleError(null);
    setEditingProjectTitle(true);
  };

  const cancelEditingProjectTitle = () => {
    setEditingProjectTitle(false);
    setProjectTitleDraft(selectedProject?.title || '');
    setProjectTitleError(null);
  };

  const saveProjectTitle = async () => {
    if (!selectedProject) return;
    if (projectTitleSaving) return;
    const title = projectTitleDraft.trim();
    if (!title) {
      setProjectTitleError('Nom de projet requis.');
      return;
    }
    if (title === selectedProject.title) {
      setEditingProjectTitle(false);
      return;
    }

    try {
      setProjectTitleSaving(true);
      setProjectTitleError(null);
      await saveProjectPatch(selectedProject.id, { title });
      setEditingProjectTitle(false);
    } catch (err) {
      setProjectTitleError(err instanceof Error ? err.message : 'Impossible de modifier le nom du projet.');
    } finally {
      setProjectTitleSaving(false);
    }
  };

  const saveProjectListOrder = async (reorderedVisibleProjectIds: number[]) => {
    if (activeProjectListView === UNASSIGNED_PROJECTS_VIEW) return;
    const currentOrderedProjects = orderProjectsByIds(visibleProjectList, activeProjectListView === 'all' ? projectListOrders.all || [] : []);
    const nextProjectIds = mergeReorderedVisibleProjects(currentOrderedProjects, reorderedVisibleProjectIds);
    const previousOrders = projectListOrders;
    const previousPortfolios = orgPortfolios;

    if (activeProjectListView === 'all') {
      setProjectListOrders((current) => ({ ...current, all: nextProjectIds }));
    } else {
      setOrgPortfolios((current) => current.map((portfolio) => (
        getPortfolioKey(portfolio) === activeProjectListView
          ? { ...portfolio, projects: orderProjectsByIds(portfolio.projects, nextProjectIds) }
          : portfolio
      )));
    }
    setPortfolioMessage(null);

    if (isPreviewMode) return;

    try {
      const portfolio = activeProjectListView === 'all' ? null : portfolioByKey[activeProjectListView];
      const endpoint = activeProjectListView === 'all'
        ? `/api/projet-v2/project-list-orders/${activeProjectListView}`
        : `/api/projet-v2/portfolios/${portfolio?.id}/items/reorder`;

      if (activeProjectListView !== 'all' && !portfolio?.id) {
        throw new Error('Portefeuille introuvable pour la sauvegarde.');
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectIds: nextProjectIds }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch {
      setProjectListOrders(previousOrders);
      setOrgPortfolios(previousPortfolios);
      setPortfolioMessage('Impossible de sauvegarder le nouvel ordre des projets.');
    }
  };

  const reloadProjectSnapshots = useCallback(async () => {
    if (!canAccess || isPreviewMode) return;
    try {
      const [accessibleResponse, unassignedResponse, orgPortfolioResponse] = await Promise.all([
        fetch('/api/projet-v2/accessible-projects', { credentials: 'include' }),
        fetch('/api/projet-v2/unassigned-projects', { credentials: 'include' }),
        fetch('/api/projet-v2/org-portfolios', { credentials: 'include' }),
      ]);
      if (!accessibleResponse.ok) throw new Error(`Projets accessibles HTTP ${accessibleResponse.status}`);
      if (!unassignedResponse.ok) throw new Error(`Projets à assigner HTTP ${unassignedResponse.status}`);
      if (!orgPortfolioResponse.ok) throw new Error(`Portefeuilles HTTP ${orgPortfolioResponse.status}`);

      const [accessibleData, unassignedData, orgPortfolioData] = await Promise.all([
        accessibleResponse.json(),
        unassignedResponse.json(),
        orgPortfolioResponse.json(),
      ]);

      setProjects(Array.isArray(accessibleData.projects) ? accessibleData.projects : []);
      setUnassignedProjects(Array.isArray(unassignedData.projects) ? unassignedData.projects : []);
      setOrgPortfolios(Array.isArray(orgPortfolioData.portfolios) ? orgPortfolioData.portfolios : []);
    } catch (err) {
      console.warn('Impossible de rafraîchir les progressions projets:', err);
    }
  }, [canAccess, isPreviewMode]);

  useEffect(() => {
    if (routeProjectId) setSelectedProjectId(routeProjectId);
  }, [routeProjectId]);

  useEffect(() => {
    setActiveSection('tasks');
    setTaskMode('list');
    setFocusedTaskId(null);
    setEditingProjectTitle(false);
    setProjectTitleError(null);
  }, [selectedProjectId]);

  useEffect(() => {
    if (routeProjectId) return;
    if (orderedVisibleProjectList.length === 0) return;
    const selectableProjects = orderedVisibleProjectList.filter((project) => project.has_access !== false);
    if (selectableProjects.length === 0) return;
    if (!selectedProjectId || !selectableProjects.some((project) => project.id === selectedProjectId)) {
      setSelectedProjectId(selectableProjects[0].id);
    }
  }, [orderedVisibleProjectList, selectedProjectId, routeProjectId]);

  useEffect(() => {
    if (!canAccess || !selectedProject) {
      setOverview(null);
      return;
    }
    const projectId = selectedProject.id;

    if (isPreviewMode) {
      setOverview(PREVIEW_OVERVIEWS[projectId] ?? null);
      setOverviewLoading(false);
      setOverviewError(null);
      return;
    }

    let cancelled = false;

    const loadOverview = async () => {
      try {
        setOverviewLoading(true);
        setOverviewError(null);
        const response = await fetch(`/api/projet-v2/projects/${projectId}/overview`, { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Impossible de charger la vue d’ensemble (HTTP ${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) setOverview(data);
      } catch (err) {
        if (!cancelled) {
          setOverview(null);
          setOverviewError(err instanceof Error ? err.message : 'Erreur inconnue');
        }
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [canAccess, isPreviewMode, selectedProject?.id]);

  const reloadTasks = useCallback(async (options: { showLoading?: boolean } = {}) => {
    if (!canAccess || !selectedProject) {
      setTasksData(null);
      return;
    }
    const projectId = selectedProject.id;
    const showLoading = options.showLoading ?? true;

    if (isPreviewMode) {
      setTasksData(PREVIEW_TASKS[projectId] ?? null);
      setTasksLoading(false);
      setTasksError(null);
      return;
    }

    try {
      if (showLoading) setTasksLoading(true);
      setTasksError(null);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/tasks`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Impossible de charger les tâches (HTTP ${response.status})`);
      }
      const data = await response.json();
      setTasksData(data);
    } catch (err) {
      if (showLoading) setTasksData(null);
      setTasksError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      if (showLoading) setTasksLoading(false);
    }
  }, [canAccess, isPreviewMode, selectedProject?.id]);

  useEffect(() => {
    reloadTasks();
  }, [reloadTasks]);

  const reloadMilestones = useCallback(async () => {
    if (!canAccess || !selectedProject) {
      setMilestonesData(null);
      return;
    }
    const projectId = selectedProject.id;

    if (isPreviewMode) {
      setMilestonesData(buildPreviewMilestones(projectId));
      setMilestonesLoading(false);
      setMilestonesError(null);
      return;
    }

    try {
      setMilestonesLoading(true);
      setMilestonesError(null);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/milestones`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Impossible de charger les jalons (HTTP ${response.status})`);
      }
      const data = await response.json();
      setMilestonesData(data);
    } catch (err) {
      setMilestonesData(null);
      setMilestonesError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setMilestonesLoading(false);
    }
  }, [canAccess, isPreviewMode, selectedProject?.id]);

  useEffect(() => {
    reloadMilestones();
  }, [reloadMilestones]);

  const reloadJournal = useCallback(async () => {
    if (!canAccess || !selectedProject) {
      setJournalData(null);
      return;
    }
    const projectId = selectedProject.id;

    if (isPreviewMode) {
      setJournalData(buildPreviewJournal(projectId));
      setJournalLoading(false);
      setJournalError(null);
      return;
    }

    try {
      setJournalLoading(true);
      setJournalError(null);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/journal`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Impossible de charger le journal de pilotage (HTTP ${response.status})`);
      }
      const data = await response.json();
      setJournalData(data);
    } catch (err) {
      setJournalData(null);
      setJournalError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setJournalLoading(false);
    }
  }, [canAccess, isPreviewMode, selectedProject?.id]);

  useEffect(() => {
    reloadJournal();
  }, [reloadJournal]);

  const reloadDeliverables = useCallback(async () => {
    if (!canAccess || !selectedProject) {
      setDeliverablesData(null);
      return;
    }
    const projectId = selectedProject.id;

    if (isPreviewMode) {
      setDeliverablesData(PREVIEW_DELIVERABLES[projectId] ?? PREVIEW_DELIVERABLES[103]);
      setDeliverablesLoading(false);
      setDeliverablesError(null);
      return;
    }

    try {
      setDeliverablesLoading(true);
      setDeliverablesError(null);
      const response = await fetch(`/api/projet-v2/projects/${projectId}/deliverables`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Impossible de charger les livrables (HTTP ${response.status})`);
      }
      const data = await response.json();
      setDeliverablesData(data);
    } catch (err) {
      setDeliverablesData(null);
      setDeliverablesError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setDeliverablesLoading(false);
    }
  }, [canAccess, isPreviewMode, selectedProject?.id]);

  useEffect(() => {
    reloadDeliverables();
  }, [reloadDeliverables]);

  const reloadMyTasks = useCallback(async (options: { showLoading?: boolean } = {}) => {
    const showLoading = options.showLoading ?? true;
    if (!canAccess) {
      setMyTasksData(null);
      return;
    }

    if (isPreviewMode) {
      setMyTasksData(PREVIEW_MY_TASKS);
      setMyTasksLoading(false);
      setMyTasksError(null);
      return;
    }

    try {
      if (showLoading) setMyTasksLoading(true);
      setMyTasksError(null);
      const response = await fetch('/api/projet-v2/my-tasks', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Impossible de charger mes tâches (HTTP ${response.status})`);
      }
      const data = await response.json();
      setMyTasksData(data);
    } catch (err) {
      setMyTasksData(null);
      setMyTasksError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      if (showLoading) setMyTasksLoading(false);
    }
  }, [canAccess, isPreviewMode]);

  useEffect(() => {
    reloadMyTasks();
  }, [reloadMyTasks]);

  const patchTaskInCurrentProject = useCallback((taskId: number, updates: Partial<ProjectTaskItem>) => {
    setTasksData((current) => current
      ? {
          ...current,
          tasks: current.tasks.map((task) => (task.id === taskId ? { ...task, ...updates } : task)),
        }
      : current);
  }, []);

  const handleProjectTasksChanged = useCallback(async () => {
    await reloadTasks({ showLoading: false });
    await Promise.all([reloadProjectSnapshots(), reloadMyTasks()]);
  }, [reloadMyTasks, reloadProjectSnapshots, reloadTasks]);

  const openTaskFromAlternateView = useCallback((task: ProjectTaskItem) => {
    setFocusedTaskId(task.id);
    setTaskMode('list');
  }, []);

  if (!canAccess) return null;

  const projectDetailPath = (projectId: number) => isPreviewMode ? `/preview-projets/${projectId}` : `/projets-v2/${projectId}`;
  const projectListPath = isPreviewMode ? '/preview-projets' : '/projets-v2';
  const openProject = (projectId: number, section: SectionKey = 'tasks') => {
    setSelectedProjectId(projectId);
    setActiveSection(section);
    setTaskMode(section === 'tasks' ? 'list' : taskMode);
    navigate(projectDetailPath(projectId));
  };
  const openAgentWorkContext = ({ projectId, taskId }: { projectId?: number | null; taskId?: number | null }) => {
    if (!projectId) return;
    setSelectedProjectId(projectId);
    setActiveSection('tasks');
    setTaskMode('list');
    setFocusedTaskId(taskId || null);
    const query = taskId ? `?taskId=${taskId}` : '';
    navigate(`${projectDetailPath(projectId)}${query}`);
  };
  const returnToProjectList = async () => {
    await reloadProjectSnapshots();
    navigate(projectListPath);
  };

  const normalizedSearch = portfolioSearch.trim().toLowerCase();
  const filteredPortfolioProjects = orderedVisibleProjectList.filter((project) => {
    if (!normalizedSearch) return true;
    return [project.title, project.nom_client, project.status, project.project_type]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch));
  });

  const portfolioStats = {
    total: filteredPortfolioProjects.length,
    delayed: filteredPortfolioProjects.filter((project) => (project.status || '').toLowerCase().includes('retard')).length,
    averageProgress: filteredPortfolioProjects.length
      ? Math.round(filteredPortfolioProjects.reduce((sum, project) => sum + (typeof project.progress === 'number' ? project.progress : 0), 0) / filteredPortfolioProjects.length)
      : 0,
  };

  const projectViews = [
    { key: 'overview' as const, label: 'Vue d’ensemble', icon: FolderKanban },
    { key: 'cadrage' as const, label: 'Cadrage PMBOK', icon: ClipboardCheck },
    { key: 'tasks' as const, label: 'Tâches', icon: ListTodo },
    { key: 'agent' as const, label: 'Suggestions', icon: Bot },
    { key: 'journal' as const, label: 'Suivis de projet', icon: NotebookPen },
    { key: 'deliverables' as const, label: 'Documents', icon: FileText },
  ];
  const homeTitle = homeView === 'my-tasks' ? 'Mes tâches' : homeView === 'agent' ? 'Mon agent' : activeProjectListViewMeta.label;
  const homeDescription = homeView === 'my-tasks'
    ? 'La bonne information, dans le bon ordre, pour exécuter sans bruit.'
    : homeView === 'agent'
      ? 'Plan quotidien personnalisé, routines déterministes et suggestions en attente de décision humaine.'
    : activeProjectListViewMeta.description;
  const selectedOperationalStatus = getProjectOperationalStatus(selectedProject);
  const selectedPrimaryContacts = selectedProject?.primary_contacts ?? [];
  const selectedSourceLink = selectedProject ? getProjectSourceLink(selectedProject) : null;
  const selectedCadrageChecklist = selectedProject ? getCadrageChecklistSummary(selectedProject) : { items: [], done: 0, total: 0, complete: true };
  const selectedProjectProgress = (() => {
    const projectTasks = (tasksData?.tasks || []).filter((task) => !task.isjalon);
    if (projectTasks.length > 0) {
      return Math.round(projectTasks.reduce((sum, task) => sum + getTaskEffectiveProgress(task), 0) / projectTasks.length);
    }
    return typeof selectedProject?.progress === 'number' ? Math.max(0, Math.min(100, selectedProject.progress)) : null;
  })();

  if (!routeProjectId) {
    return (
      <div className="min-h-full w-full max-w-full overflow-x-hidden bg-slate-100">
        <Header
          title="Pilotage projets"
          subtitle={isPreviewMode ? 'Preview publique — portefeuille avec données simulées' : 'Tous les projets, priorités, suivis et portefeuilles au même endroit'}
        />

        <div className="min-w-0 space-y-5 px-6 pb-6">
          <section className="min-w-0 rounded-2xl border border-slate-300 bg-white shadow-md">
            <div className="border-b border-slate-300 px-5 py-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    {homeView === 'my-tasks' && <Badge tone="blue">Exécution</Badge>}
                    <h2 className="text-xl font-semibold text-slate-950">{homeTitle}</h2>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{homeDescription}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={openCreateProjectWizard} className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-900 shadow-sm hover:border-blue-500 hover:bg-blue-100">
                    <Plus className="h-4 w-4" /> Nouveau projet
                  </button>
                  <button type="button" onClick={() => { setCreatePortfolioError(null); setPortfolioOwnerSearch(''); setPortfolioStakeholderSearch(''); setCreatePortfolioOpen(true); }} className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-900 shadow-sm hover:border-emerald-500 hover:bg-emerald-100">
                    <Plus className="h-4 w-4" /> Nouveau portefeuille
                  </button>
                </div>
              </div>

              {homeView === 'portfolio' ? (
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Projets affichés</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{portfolioStats.total}</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Progression moyenne</p>
                  <p className="mt-1 text-2xl font-semibold text-emerald-950">{portfolioStats.averageProgress}%</p>
                </div>
                <button type="button" onClick={() => setPortfolioHealthDrawerOpen(true)} className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-left transition hover:border-rose-400 hover:bg-rose-100">
                  <p className="text-xs font-medium uppercase tracking-wide text-rose-700">À surveiller</p>
                  <p className="mt-1 text-2xl font-semibold text-rose-950">{activePortfolioId ? portfolioHealthSuggestions.length : portfolioStats.delayed}</p>
                </button>
              </div>
              ) : null}
            </div>

            {homeView === 'portfolio' ? (
              <div className="border-b border-slate-200 px-5 py-5">
                <ProjectIntakeTransitionSection projects={projects} onOpenProject={openProject} onTakeCharge={takeChargeProject} takingChargeId={takingChargeProjectId} />
              </div>
            ) : null}

            <div className="px-5 py-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setHomeView('my-tasks')}
                    className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition ${homeView === 'my-tasks' ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-blue-300 bg-white text-blue-800 hover:border-blue-600 hover:bg-blue-50'}`}
                  >
                    <ListTodo className="h-4 w-4" /> Mes tâches
                    <Badge tone={homeView === 'my-tasks' ? 'blue' : 'slate'}>{myTasksData?.summary.active || 0}</Badge>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHomeView('agent')}
                    className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition ${homeView === 'agent' ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-blue-300 bg-white text-blue-800 hover:border-blue-600 hover:bg-blue-50'}`}
                  >
                    <Bot className="h-4 w-4" /> Mon agent
                  </button>
                  {projectListViews.map((view) => {
                    const Icon = view.icon;
                    const active = homeView === 'portfolio' && activeProjectListView === view.key;
                    return (
                      <button key={view.key} type="button" onClick={() => { setHomeView('portfolio'); setActiveProjectListView(view.key); }} className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${active ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}>
                        <Icon className="h-4 w-4" /> {view.label}
                        <Badge tone={active ? 'blue' : 'slate'}>{projectListCounts[view.key] || 0}</Badge>
                      </button>
                    );
                  })}
                </div>
                {homeView === 'portfolio' ? (
                <div className="flex min-w-[260px] flex-1 flex-wrap items-center justify-end gap-2 sm:max-w-2xl">
                  <div className="inline-flex rounded-xl border border-slate-300 bg-white p-1 shadow-sm">
                    {(['active', 'archived'] as ProjectArchiveFilter[]).map((filter) => (
                      <button key={filter} type="button" onClick={() => { setProjectArchiveFilter(filter); setActiveProjectListView('all'); }} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${projectArchiveFilter === filter ? 'bg-blue-700 text-white' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-800'}`}>
                        {filter === 'active' ? 'Actifs' : 'Archivés'}
                      </button>
                    ))}
                  </div>
                  <div className="relative min-w-[260px] flex-1 sm:max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={portfolioSearch} onChange={(event) => setPortfolioSearch(event.target.value)} placeholder="Rechercher projet, client, priorité…" className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100" />
                  </div>
                </div>
                ) : homeView === 'my-tasks' ? (
                  <button type="button" onClick={() => reloadMyTasks()} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:border-blue-400 hover:bg-blue-50">
                    Rafraîchir
                  </button>
                ) : null}
              </div>
              {portfolioMessage ? <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{portfolioMessage}</div> : null}
            </div>

            {homeView === 'agent' ? (
              <ProjectAgentHomeSection isPreviewMode={isPreviewMode} onOpenWorkContext={openAgentWorkContext} />
            ) : homeView === 'my-tasks' ? (
              <ProjectMyTasksSection data={myTasksData} loading={myTasksLoading} error={myTasksError} onReload={reloadMyTasks} onOpenProject={openProject} isPreviewMode={isPreviewMode} />
            ) : loading ? (
              <div className="space-y-3 p-5">{[...Array(5)].map((_, index) => <div key={index} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}</div>
            ) : error ? (
              <div className="m-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
            ) : filteredPortfolioProjects.length === 0 ? (
              <div className="p-5"><EmptyState>{activeProjectListView === 'all' ? 'Aucun projet ne correspond aux filtres.' : activeProjectListView === UNASSIGNED_PROJECTS_VIEW ? 'Aucun projet sans responsable pour le moment.' : 'Ce portefeuille est vide ou aucun projet ne correspond aux filtres.'}</EmptyState></div>
            ) : (
              <PortfolioProjectTable projects={filteredPortfolioProjects} orgPortfolios={orgPortfolios} portfolioProjectIds={portfolioProjectIds} unassignedProjectIds={unassignedProjectIds} activeProjectListView={activeProjectListView} onOpenProject={openProject} onAddProject={addProjectToPortfolio} onRemoveProject={removeProjectFromPortfolio} onFollowupSaved={updateProjectFollowup} onProjectUpdated={saveProjectPatch} onReorderProjects={saveProjectListOrder} onAssignProjectResponsible={assignProjectResponsible} responsibleContacts={projectResponsibleContacts} onCopyProject={openCopyProjectModal} onArchiveProject={openArchiveProjectModal} onRestoreProject={restoreProject} isPreviewMode={isPreviewMode} />
            )}
          </section>
          {portfolioHealthDrawerOpen ? (
            <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/20" onClick={() => setPortfolioHealthDrawerOpen(false)}>
              <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-200 bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-950">Suggestions santé à surveiller</h3>
                    <p className="mt-1 text-sm text-slate-500">Signaux `sante` du portefeuille affiché.</p>
                  </div>
                  <button type="button" onClick={() => setPortfolioHealthDrawerOpen(false)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Fermer</button>
                </div>
                <div className="grid gap-3">
                  {portfolioHealthSuggestions.length === 0 ? <EmptyState>Aucune suggestion santé active pour ce portefeuille.</EmptyState> : null}
                  {portfolioHealthSuggestions.map((suggestion) => (
                    <div key={suggestion.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={getAgentSeverityTone(suggestion.severity)}>{suggestion.severity}</Badge>
                        <Badge tone="slate">{suggestion.rule_code}</Badge>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{suggestion.title}</p>
                      <p className="mt-1 text-xs text-slate-600">{suggestion.body}</p>
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          ) : null}
          {createPortfolioOpen ? (
            <CreatePortfolioModal
              form={createPortfolioForm}
              saving={createPortfolioSaving}
              error={createPortfolioError}
              ownerContacts={portfolioOwnerOptions}
              ownerContactsLoading={portfolioOwnerContactsLoading}
              ownerSearch={portfolioOwnerSearch}
              stakeholderContacts={portfolioStakeholderOptions}
              stakeholderContactsLoading={portfolioStakeholderContactsLoading}
              stakeholderSearch={portfolioStakeholderSearch}
              onOwnerSearchChange={setPortfolioOwnerSearch}
              onStakeholderSearchChange={setPortfolioStakeholderSearch}
              onChange={(updates) => setCreatePortfolioForm((current) => ({ ...current, ...updates }))}
              onClose={() => {
                if (createPortfolioSaving) return;
                setCreatePortfolioOpen(false);
                setCreatePortfolioError(null);
              }}
              onSubmit={createPortfolio}
            />
          ) : null}
          {createProjectOpen ? (
            <ProjectCreateWizardModal
              form={createProjectForm}
              saving={createProjectSaving}
              error={createProjectError}
              clients={createProjectClients}
              clientsLoading={createProjectClientsLoading}
              contacts={createProjectContacts}
              contactsLoading={createProjectContactsLoading}
              contactSearch={createProjectContactSearch}
              responsibleOptions={projectResponsibleContacts}
              responsibleOptionsLoading={projectResponsibleContactsLoading}
              portfolios={orgPortfolios}
              templates={projectTemplates.length > 0 ? projectTemplates : fallbackProjectTemplates}
              allBlocks={projectTemplateBlocks}
              templatesLoading={projectTemplatesLoading}
              onOpenBlockConfig={() => {
                setBlockConfigError(null);
                setBlockConfigOpen(true);
              }}
              onContactSearchChange={setCreateProjectContactSearch}
              onChange={(updates) => setCreateProjectForm((current) => ({ ...current, ...updates }))}
              onClose={() => {
                if (createProjectSaving) return;
                setCreateProjectOpen(false);
                setCreateProjectError(null);
              }}
              onSubmit={createProject}
            />
          ) : null}
          {blockConfigOpen ? (
            <ProjectBlockConfigModal
              blocks={projectTemplateBlocks}
              saving={blockConfigSaving}
              error={blockConfigError}
              onClose={() => {
                if (blockConfigSaving) return;
                setBlockConfigOpen(false);
                setBlockConfigError(null);
              }}
              onSave={saveProjectTemplateBlock}
            />
          ) : null}
          {copyProjectTarget && copyProjectForm ? (
            <ProjectCopyModal project={copyProjectTarget} form={copyProjectForm} saving={copyProjectSaving} error={copyProjectError} onChange={(updates) => setCopyProjectForm((current) => current ? { ...current, ...updates } : current)} onClose={closeCopyProjectModal} onSubmit={copyProject} />
          ) : null}
          {archiveProjectTarget ? (
            <ProjectArchiveModal project={archiveProjectTarget} reason={archiveReason} saving={archiveProjectSaving} error={archiveProjectError} onReasonChange={setArchiveReason} onClose={closeArchiveProjectModal} onSubmit={archiveProject} />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full max-w-full overflow-x-hidden bg-slate-100">
      <Header
        title="Espace projet"
        subtitle={isPreviewMode ? 'Preview publique du détail projet — données simulées' : 'Vue d’ensemble, tâches, suivis de projet et documents'}
      />

      <div className="min-w-0 space-y-5 px-6 pb-6">
        <button type="button" onClick={() => { void returnToProjectList(); }} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:border-blue-400 hover:bg-blue-50">
          <ArrowLeft className="h-4 w-4" /> Retour aux projets
        </button>

        {!selectedProject ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">Chargement du projet #{routeProjectId}…</section>
        ) : (
          <>
            <section className="min-w-0 rounded-2xl border border-slate-300 bg-white shadow-md">
              <div className="px-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone="slate">Projet #{selectedProject.id}</Badge>
                    </div>
                    {editingProjectTitle ? (
                      <div className="mt-3 max-w-3xl">
                        <input
                          autoFocus
                          value={projectTitleDraft}
                          disabled={projectTitleSaving}
                          onChange={(event) => setProjectTitleDraft(event.target.value)}
                          onBlur={saveProjectTitle}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              saveProjectTitle();
                            }
                            if (event.key === 'Escape') {
                              event.preventDefault();
                              cancelEditingProjectTitle();
                            }
                          }}
                          className="w-full rounded-xl border border-blue-300 bg-white px-3 py-2 text-2xl font-semibold text-slate-950 outline-none ring-2 ring-blue-100 disabled:opacity-60"
                        />
                        {projectTitleError ? <p className="mt-2 text-sm font-semibold text-rose-700">{projectTitleError}</p> : null}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onDoubleClick={startEditingProjectTitle}
                        title="Double-clique pour modifier le nom du projet"
                        className="mt-3 block max-w-3xl rounded-xl border border-transparent px-1 py-1 text-left text-2xl font-semibold text-slate-950 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900"
                      >
                        {selectedProject.title}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 shadow-sm"><p className="text-xs text-slate-500">Client</p><p className="mt-1 font-semibold text-slate-900">{selectedProject.nom_client || 'À confirmer'}</p></div>
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm"><p className="text-xs text-blue-700">Statut</p><p className="mt-1 font-semibold text-blue-950">{selectedProject.status || 'À structurer'}</p></div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm"><p className="text-xs text-emerald-700">Progression</p><p className="mt-1 font-semibold text-emerald-950">{selectedProjectProgress !== null ? `${selectedProjectProgress}%` : 'n/d'}</p></div>
                </div>

                <div className="mt-4 grid gap-3 xl:grid-cols-[0.9fr_1.1fr]">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-black text-slate-950">Contacts principaux</h3>
                      <Badge tone={selectedPrimaryContacts.length ? 'green' : 'amber'}>{selectedPrimaryContacts.length ? `${selectedPrimaryContacts.length} contact(s)` : 'À compléter'}</Badge>
                    </div>
                    <div className="mt-3 grid gap-2">
                      {selectedPrimaryContacts.length ? selectedPrimaryContacts.slice(0, 4).map((contact) => (
                        <div key={contact.id || contact.role_code || contact.contact_email} className="rounded-xl border border-white bg-white px-3 py-2 text-xs shadow-sm">
                          <p className="font-black text-slate-800">{contact.role_label || 'Contact projet'}</p>
                          <p className="mt-0.5 text-slate-700">{contact.contact_name || 'Nom à confirmer'}</p>
                          {contact.contact_email ? <p className="truncate text-blue-700">{contact.contact_email}</p> : null}
                          {contact.contact_phone ? <p className="text-slate-500">{contact.contact_phone}</p> : null}
                        </div>
                      )) : <p className="rounded-xl border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-500">Aucun contact principal défini. À compléter avant le passage en opérations.</p>}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-black text-slate-950">Origine de l’offre</h3>
                      <Badge tone={selectedOperationalStatus.tone}>{selectedOperationalStatus.label}</Badge>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                      <div className="rounded-xl bg-white px-3 py-2 shadow-sm">
                        <p className="font-black text-slate-800">Profil du prospect / qualification</p>
                        <p className="mt-1 line-clamp-3">{selectedSourceLink?.summary || selectedProject.source_summary || 'Aucune origine PRIsME liée.'}</p>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2 shadow-sm">
                        <p className="font-black text-slate-800">Date de fin du contrat</p>
                        <p className="mt-1">{formatDate(selectedSourceLink?.contract_end_date || selectedProject.contract_end_date)}</p>
                        {selectedSourceLink?.amount_label ? <p className="mt-1 text-slate-500">Montant: {selectedSourceLink.amount_label}</p> : null}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedSourceLink?.source_url ? <a href={selectedSourceLink.source_url} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-800 hover:bg-blue-50">Ouvrir l’offre</a> : null}
                      <button type="button" onClick={() => setActiveSection('cadrage')} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-800 hover:bg-blue-50">Voir le briefing / cadrage</button>
                      <button type="button" onClick={() => setActiveSection('deliverables')} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-800 hover:bg-blue-50">Voir le visuel client / livrables</button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-black text-slate-950">Checklist de cadrage avant opérations</h3>
                    <Badge tone={selectedCadrageChecklist.complete ? 'green' : 'amber'}>{selectedCadrageChecklist.done}/{selectedCadrageChecklist.total}</Badge>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-5">
                    {selectedCadrageChecklist.items.map((item) => (
                      <div key={item.label} className="rounded-xl bg-white px-3 py-2 shadow-sm">
                        <p className="flex items-center gap-1 font-bold text-slate-800">{item.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}{item.label}</p>
                      </div>
                    ))}
                  </div>
                  {!selectedCadrageChecklist.complete ? <p className="mt-3 text-xs font-bold text-amber-900">À compléter avant opérations — passage en opérations non recommandé.</p> : null}
                </div>
              </div>

              <div className="border-t border-slate-300 bg-slate-50 px-3 py-3">
                <div className="flex flex-wrap gap-2 pb-1">
                  {projectViews.map((view) => {
                    const Icon = view.icon;
                    const active = activeSection === view.key;
                    return (
                      <button key={`${view.label}-${view.key}`} type="button" onClick={() => setActiveSection(view.key)} className={`flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${active ? 'border-blue-700 bg-blue-700 text-white ring-2 ring-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}>
                        <Icon className="h-4 w-4" /> {view.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="min-w-0 rounded-2xl border border-slate-300 bg-white p-4 shadow-md">
              {activeSection === 'overview' ? (
                <div>
                  {overviewLoading ? <OverviewSkeleton /> : null}
                  {!overviewLoading && overviewError ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{overviewError}</div> : null}
                  {!overviewLoading && !overviewError && overview ? <ProjectOverviewSection overview={overview} onSelectSection={setActiveSection} /> : null}
                </div>
              ) : activeSection === 'cadrage' ? (
                <ProjectCadrageSection project={selectedProject} isPreviewMode={isPreviewMode} onProjectUpdated={saveProjectPatch} />
              ) : activeSection === 'tasks' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 rounded-xl border border-slate-300 bg-slate-100 p-2 shadow-inner">
                    {PROJECT_TASK_MODES.map((mode) => {
                      const Icon = mode.icon;
                      const active = taskMode === mode.key;
                      return (
                        <button key={mode.key} type="button" onClick={() => setTaskMode(mode.key)} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold shadow-sm transition ${active ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-800'}`}>
                          <Icon className="h-4 w-4" /> {mode.label}
                        </button>
                      );
                    })}
                  </div>
                  {taskMode === 'list' ? (
                    <ProjectTasksSection projectId={selectedProject.id} tasksData={tasksData} deliverablesData={deliverablesData} loading={tasksLoading} error={tasksError} isPreviewMode={isPreviewMode} onTasksChanged={handleProjectTasksChanged} onTaskOptimisticUpdate={patchTaskInCurrentProject} focusedTaskId={focusedTaskId} />
                  ) : taskMode === 'kanban' ? (
                    <ProjectTasksKanban tasksData={tasksData} loading={tasksLoading} error={tasksError} onOpenTask={openTaskFromAlternateView} />
                  ) : taskMode === 'gantt' ? (
                    <ProjectTasksGantt tasksData={tasksData} loading={tasksLoading} error={tasksError} onOpenTask={openTaskFromAlternateView} />
                  ) : (
                    <ProjectMilestonesSection projectId={selectedProject.id} milestonesData={milestonesData} tasksData={tasksData} loading={milestonesLoading} error={milestonesError} isPreviewMode={isPreviewMode} onSelectTasks={() => { setActiveSection('tasks'); setTaskMode('list'); }} onOpenTask={openTaskFromAlternateView} onMilestonesChanged={async () => { await Promise.all([reloadMilestones(), handleProjectTasksChanged()]); }} />
                  )}
                </div>
              ) : activeSection === 'agent' ? (
                <ProjectAgentSuggestionsSection projectId={selectedProject.id} isPreviewMode={isPreviewMode} onOpenWorkContext={openAgentWorkContext} />
              ) : activeSection === 'journal' ? (
                <ProjectJournalSection projectId={selectedProject.id} journalData={journalData} tasksData={tasksData} milestonesData={milestonesData} loading={journalLoading} error={journalError} isPreviewMode={isPreviewMode} onJournalChanged={reloadJournal} />
              ) : activeSection === 'deliverables' ? (
                <ProjectDeliverablesSection projectId={selectedProject.id} deliverablesData={deliverablesData} tasksData={tasksData} milestonesData={milestonesData} loading={deliverablesLoading} error={deliverablesError} isPreviewMode={isPreviewMode} onDeliverablesChanged={reloadDeliverables} />
              ) : (
                <ProjectSectionPlaceholder section={SECTION_CARDS.find((section) => section.key === activeSection)!} onSelectSection={setActiveSection} />
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
